import type { BrowserRecognitionEvent } from "./voice/browser-recognition-handlers";
import { createRenderReadiness } from "./utils/render-readiness";
import { activityDependencies } from "./activity-dependencies";
import { prepareRendererModules } from "./renderer-preparation";
import { loadDictationRuntime, getDictationRuntimeSync, type DictationRuntimeModule } from "./dictation-runtime-loader";
import { getComposerActionsSync, loadComposerActions, type ComposerActionsModule } from "./composer-actions-loader";
import { createScrollAnimation, createScrollToLatest } from "./utils/scroll-animation";
import { createScrollToBottomControl } from "./components/scroll-to-bottom";
import { createLayoutSlots } from "./utils/layout-slots";
import { isBrowserLocalApproval } from "./utils/restored-browser-state";
import { applyStatusIndicatorState, placeStatusIndicator } from "./utils/status-indicator";
import type { DictationSession } from "./dictation-session";
import { usesSessionVoice, usesSessionVoiceForConfig } from "./utils/voice-support";
import { escapeHtml, createMarkdownProcessorFromConfig, preprocessStreamingMarkdown } from "./postprocessors";
import { resolveSanitizer } from "./utils/sanitize";
import { wrapScrollableTables, refreshTableScrollFades } from "./utils/table-scroll-fade";
import { getMarkdownParsersSync } from "./markdown-parsers-loader";
import { loadClientStream } from "./client-stream-loader";
import { HistoryClientError } from "./client";
import { HistoryProviderError } from "./internal/history-provider";
import { getUiExtrasSync, loadUiExtras, type UiExtrasModule } from "./ui-extras-loader";
import type { AskUserSheetContext, AskUserSheetHandlers } from "./ui-ask-user-sheet";
import {
  AgentWidgetSession,
  AgentWidgetSessionStatus,
  SessionHistoryError,
  type SessionHistoryNotice,
  type SessionHistoryState
} from "./session";
import {
  DictationState,
  DictationRenderHandle,
  DictationProviderConfig,
  AgentWidgetConfig,
  AgentWidgetConfigPatch,
  AgentWidgetSessionInitHook,
  AgentWidgetVoicePrewarmHook,
  AgentWidgetApprovalDecisionOptions,
  AgentWidgetMessage,
  AgentWidgetEvent,
  AgentWidgetStorageAdapter,
  AgentWidgetStoredState,
  WidgetHistoryInternals,
  AgentWidgetControllerEventMap,
  AgentWidgetVoiceStateEvent,
  AgentWidgetStateEvent,
  AgentWidgetStateSnapshot,
  WidgetLayoutSlot,
  SlotRenderer,
  AgentWidgetMessageFeedback,
  ContentPart,
  InjectMessageOptions,
  InjectAssistantMessageOptions,
  InjectUserMessageOptions,
  InjectSystemMessageOptions,
  InjectComponentDirectiveOptions,
  LoadingIndicatorRenderContext,
  IdleIndicatorRenderContext,
  VoiceStatus,
  ReadAloudState,
  PersonaArtifactRecord,
  PersonaArtifactManualUpsert,
  PersonaArtifactFileMeta,
  PersonaArtifactActionContext,
  AgentWidgetContentSegment,
  AgentWidgetContextMentionRef,
  AgentWidgetStoredDraft,
  AgentWidgetOpenConversationOptions,
  AgentWidgetRevealToolCallOptions,
  AgentWidgetSuggestion,
  AgentWidgetSuggestionSource,
  AgentWidgetWelcomeIcon,
  HistoryConversationSummary,
  HistoryIdentityStatus,
  HistoryReturnSurface,
  HistoryScope,
  PendingDisplayProjections,
  ResolvedHistoryPresentation,
  ComposerAction,
  ComposerActionContext,
  ComposerAttachmentState,
  ComposerMode,
  ComposerOptionsPayload,
  ComposerQuote,
  ComposerState,
  ComposerStreamingSubmitBehavior,
  ComposerSubmissionSnapshot,
  AgentWidgetReasoning
} from "./types";
import type { AgentWidgetComponentRenderer, AgentWidgetComponentRenderContext, AgentWidgetComponentRenderHandle } from "./types";
import { AttachmentManager, type PendingAttachment } from "./utils/attachment-manager";
import { createComposerStore } from "./utils/composer-store";
import {
  bindComposerSurface,
  type ComposerSurfaceBindings,
} from "./components/composer-bindings";
import {
  COMPOSER_ACTION_ORDER,
  COMPOSER_ACTION_ATTR,
  type ComposerActionRenderer,
  type ComposerBuiltInDescriptor,
} from "./components/composer-actions";
import type { createComposerModeChipRow } from "./components/composer-mode-chips";
import {
  COMPOSER_CHIP_ROW_SELECTOR,
  ensureComposerChipRow,
} from "./components/composer-chip-row";
import { createComposerQuoteBanner } from "./components/composer-quote-banner";
import { createComposerPendingCard } from "./components/composer-pending-card";
import {
  createMessageEditEditor,
  type MessageEditEditor
} from "./components/message-edit-editor";
import {
  canResubmitUserMessage,
  findPrecedingUserMessageId,
  findRetryableAssistantMessageId,
  isEditableUserMessage
} from "./utils/resubmit-turn";
import {
  buildStoredDraft,
  createDraftWriter,
  rehydrateStoredDraft,
  type DraftWriter
} from "./utils/composer-draft";
import {
  chipVisibleComposerModes,
  clearOnceComposerModes,
  composerModeActionId,
  pruneComposerModes,
  resolveComposerModePlaceholder,
  toggleComposerMode,
  selectComposerModes,
} from "./utils/composer-modes";
import {
  createComposerCompactLatch,
  isComposerCompact,
  isEditorWrapped,
} from "./utils/composer-compact";
import { createComposerFitObserver } from "./utils/composer-fit";
import { requestFormSubmit } from "./utils/composer-input";
import {
  DEFAULT_COMPOSER_MAX_LINES,
  PILL_COMPOSER_MAX_LINES,
  applyComposerEnterKeyHint,
  applyComposerInputAttributes,
  applyComposerMaxLines,
  isCoarsePointer,
  isSubmitKeydown,
  resolveComposerLock,
  resolveMaxLines,
} from "./utils/composer-input-config";
import {
  canSubmitComposer,
  runBeforeSend,
  toPublicSubmissionSnapshot,
  type BeforeSendOutcome,
  type InternalSubmissionSnapshot,
} from "./utils/composer-submission";
import type { ContextMentionOrchestrator } from "./utils/context-mention-orchestrator";
import { loadContextMentions } from "./context-mentions-loader";
import { loadContextMentionsInline } from "./context-mentions-inline-loader";
import { createMentionButton } from "./components/context-mention-button";
import type { MentionSubmitBundle } from "./utils/context-mention-manager";
import { createTextPart, ALL_SUPPORTED_MIME_TYPES } from "./utils/content";
import { applyThemeVariables, createThemeObserver, getActiveTheme, getColorScheme } from "./utils/theme";
import { resolveTokenValue, resolveThemeDefaults } from "./utils/tokens";
import { deepMerge } from "./utils/deep-merge";
import { Activity, Check, Copy } from "lucide";
import { renderLucideIcon } from "./utils/icons";
import { renderIconNode } from "./utils/icon-node";
import { createElement } from "./utils/dom";
import { resolveContentMaxWidth } from "./utils/content-width";
import {
  computeComposerLift,
  DEFAULT_ANCHOR_COMPOSER_TOP,
  DEFAULT_COMPOSER_GAP,
  parseAnchorFraction,
  resolveComposerPlacement,
} from "./utils/composer-placement";
import { attachTooltip, hideTooltipFor, configureTooltipTiming } from "./utils/tooltip";
import {
  DEFAULT_TOOLTIP_DELAY_MS,
  DEFAULT_TOOLTIP_SKIP_DELAY_MS,
} from "./utils/tooltip-timing";
import { createShortcutRegistry, parseCombo, formatCombo, ariaCombo } from "./utils/shortcuts";
// The artifact download/copy helpers live in the lazy artifacts-ui chunk;
// reached via getArtifactsUiSync() in the delegated click handlers (which can
// only fire on DOM the adopted chunk rendered).
import { morphMessages } from "./utils/morph";
import { getVoiceRuntimeSync, loadVoiceRuntime, type VoiceRuntimeModule } from "./voice-runtime-loader";
import type { MicStateStyles } from "./voice/mic-state-styles";
import { normalizeCopiedSelectionText } from "./utils/copy-selection";
import {
  navigateComposerHistory,
  INITIAL_HISTORY_STATE,
  type ComposerHistoryState
} from "./utils/composer-history";
import { computeMessageFingerprint, createMessageCache, getCachedWrapper, setCachedWrapper, pruneCache } from "./utils/message-fingerprint";
import {
  computeAnchorScrollState,
  computeShrunkSpacerHeight,
  createFollowStateController,
  getScrollBottomOffset,
  hasSelectionWithin,
  isElementNearBottom,
  resolveFollowStateFromScroll,
  resolveFollowStateFromWheel
} from "./utils/auto-follow";
import {
  statusCopy,
  DEFAULT_OVERLAY_Z_INDEX,
} from "./utils/constants";
import {
  applyStreamBuffer,
  createSkeletonPlaceholder,
  createStreamCaret,
  detachAllPlugins,
  ensurePluginActive,
  LAZY_BUILTIN_STREAM_ANIMATIONS,
  registerStreamAnimationPlugin,
  resolveStreamAnimation,
  resolveStreamAnimationPlugin,
  wrapStreamAnimation,
} from "./utils/stream-animation";
import { loadAnimationsExtra } from "./animations-extra-loader";
import { syncOverlayHostStacking } from "./utils/overlay-host-stacking";
import { acquireScrollLock } from "./utils/scroll-lock";
import { isComposerBarMountMode, isDockedMountMode, resolveDockConfig } from "./utils/dock";
import { LauncherSurface } from "./components/launcher";
import {
  buildHeader,
  buildComposer,
  attachHeaderToContainer,
  COMPOSER_BAR_CLEAR_CHAT_ICON_SIZE,
  COMPOSER_BAR_CLOSE_ICON_SIZE,
  resolvePanelGeometry,
  resolveInlinePanelWidth,
} from "./components/panel";
import { buildPillComposer } from "./components/pill-composer-builder";
import {
  COMPOSER_CONTROL_CLASS,
  COMPOSER_CONTROL_GLYPH_CLASS,
  COMPOSER_CONTROL_ICON_FALLBACK_PX,
  applySendButtonStyle,
  applyMicButtonStyle,
  createMicButton,
} from "./components/composer-parts";
import { createWidgetView, resolveLauncher } from "./components/widget-view";
import {
  animateComposerLiftChange,
  animateWelcomeOut,
  applyWelcomeConfig,
  applyWelcomeVisibility,
  clearWelcomePluginContent,
  mountWelcomePluginContent,
  renderWelcomeGreeting,
} from "./components/welcome";
import { createPluginStorageFactory } from "./utils/plugin-storage";
import {
  isWelcomeVisible,
  resolveConversationState,
  resolveWelcomeConfig,
} from "./welcome";
import { HEADER_THEME_CSS } from "./components/header-builder";
import {
  applyHeaderControlStyle,
  createHeaderIconButton,
} from "./components/header-parts";
import { buildHeaderWithLayout } from "./components/header-layouts";
import { positionMap } from "./utils/positioning";
import type { HeaderElements as _HeaderElements, ComposerElements as _ComposerElements } from "./components/panel";
import { MessageTransform, MessageActionCallbacks, MessageActionEligibility } from "./components/message-bubble";
import {
  applyUserCollapseState,
  isUserMessageCollapseEnabled,
  resolveUserMessageCollapseLabels,
  resolveUserMessageCollapseMaxHeight,
  userMessageOverflows,
  USER_COLLAPSE_REGION_ATTR,
  USER_COLLAPSE_TOGGLE_ATTR,
  type UserMessageCollapseRender,
  type UserMessageCollapseState,
} from "./components/user-message-collapse";
import {
  createStandardBubble,
  createTypingIndicator,
  getBubbleClasses,
  applyBubbleGeometry,
  CUSTOM_MESSAGE_ACTION_PREFIX,
} from "./components/message-bubble";
import { getActivityUiSync, type ActivityUiModule } from "./activity-ui-loader";
import { getLoadingLabelRuntimeSync, type LoadingLabelRuntime } from "./loading-label-runtime-loader";
import { copyToolDetail } from "./components/tool-details";

import {
  ensureAskUserQuestionSheet,
  isAskUserQuestionMessage,
  parseAskUserQuestionPayload,
  removeAskUserQuestionSheet,
} from "./components/ask-user-question-bubble";
import {
  isSuggestRepliesMessage,
  latestAgentSuggestions,
  resolveFollowUpsFeature,
} from "./suggest-replies-tool";
import { isShortReasoning } from "./utils/formatting";
import { loadApprovalUi, getApprovalUiSync, type ApprovalUiModule } from "./approval-ui-loader";
import { getWebMcpToolDisplayTitle } from "./webmcp-bridge";
import type { AgentWidgetPlugin } from "./plugins/types";
import {
  createSuggestionElement,
  createSuggestions,
  normalizeSuggestion,
} from "./components/suggestions";
// Type-only: the capture/persistence classes ship in the lazy
// event-stream-view chunk and are constructed at chunk adoption.
import type { EventStreamBuffer } from "./utils/event-stream-buffer";
import type { EventStreamStore } from "./utils/event-stream-store";
import type { ThroughputTracker } from "./utils/throughput-tracker";
import { loadEventStreamView } from "./event-stream-view-loader";
import type { EventStreamViewHandle } from "./event-stream-view-entry";
import type { ArtifactPaneApi } from "./components/artifact-pane";
import {
  loadArtifactsUi,
  getArtifactsUiSync,
  onArtifactsUiReady,
  type ArtifactsUiModule,
} from "./artifacts-ui-loader";
import {
  artifactsSidebarEnabled,
  applyArtifactLayoutCssVars,
  artifactStyle,
  applyArtifactPaneAppearance,
  isArtifactPaneAppearanceDetached,
  shouldExpandLauncherForArtifacts
} from "./utils/artifact-gate";
import { resolveArtifactDisplayMode } from "./utils/artifact-display";
import { resolveConfigPreferences } from "./utils/feature-preferences";
import {
  ARTIFACT_RESIZE_CHAT_MIN_PX,
  ARTIFACT_RESIZE_RAILED_TRANSCRIPT_MIN_PX,
} from "./utils/artifact-resize";
import { loadFormsUi, getFormsUiSync } from "./forms-ui-loader";
import { pluginRegistry } from "./plugins/registry";
import { DEFAULT_HEADER_ICON_SIZE, mergeWithDefaults } from "./defaults";
import { mergeConfigUpdate } from "./utils/config-merge";
import { createLegacyDiagnosticContext, updateLegacyDiagnostics, type LegacyDiagnosticContext } from "./utils/legacy-diagnostics";
import { getPanelAliasProvenance, isEmbeddedLayout, isHeaderVisible } from "./utils/panel-config";
import { createEventBus } from "./utils/events";
import {
  createActionManager,
  defaultActionHandlers,
  defaultJsonActionParser
} from "./utils/actions";
import { createLocalStorageAdapter, transcriptStorageKey } from "./utils/storage";
import { isLocalOnlyMessage } from "./utils/local-only-message";
import { createVisitorStore, type VisitorStore } from "./utils/visitor-store";
import { loadHistoryView } from "./history-view-loader";
import { getHistoryShellSync, loadHistoryShell, type HistoryShellModule } from "./history-shell-loader";
import type { HistoryShell, HistoryShellContext } from "./history-shell";
import { getHistoryProviderFactory } from "./internal/history-provider-registry";
import { resolveComponentOptions, resolveComponentRenderer } from "./components/registry";
import {
  renderComponentDirective,
  extractComponentDirectiveFromMessage,
  hasComponentDirective
} from "./utils/component-middleware";
import {
  createCSATFeedback,
  createNPSFeedback,
  type CSATFeedbackOptions,
  type NPSFeedbackOptions
} from "./components/feedback";

// Default localStorage key for chat history (automatically cleared on clear chat)
const DEFAULT_CHAT_HISTORY_STORAGE_KEY = "persona-chat-history";
const VOICE_STATE_RESTORE_WINDOW = 30 * 1000;
const VOICE_RESUME_NOTICE = "Tap the mic to resume your voice call";
// Split desktop boundary; must match widget.css artifact media queries (min-width:641px).
const ARTIFACT_SPLIT_DESKTOP_MIN = 641;
// Covers the default 160ms V5 disclosure transition.
const REVEAL_SETTLE_MS = 200;

/** Marks the status region while a composer lock reason owns it. */
const COMPOSER_REASON_ATTR = "data-persona-composer-reason";
/** Marks the status region while a transient composer notice owns it. */
const COMPOSER_NOTICE_ATTR = "data-persona-composer-notice";

const IMAGE_FILE_EXTENSION_BY_MIME_TYPE: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/svg+xml": "svg",
  "image/bmp": "bmp",
  "image/tiff": "tiff"
};

function getClipboardImageFiles(clipboardData: DataTransfer | null): File[] {
  if (!clipboardData) return [];

  const imageFiles: File[] = [];
  const clipboardItems = Array.from(clipboardData.items ?? []);

  for (const item of clipboardItems) {
    if (item.kind !== "file" || !item.type.startsWith("image/")) continue;
    const file = item.getAsFile();
    if (!file) continue;

    if (file.name) {
      imageFiles.push(file);
      continue;
    }

    const extension = IMAGE_FILE_EXTENSION_BY_MIME_TYPE[file.type] ?? "png";
    imageFiles.push(
      new File(
        [file],
        `clipboard-image-${Date.now()}.${extension}`,
        {
          type: file.type,
          lastModified: Date.now()
        }
      )
    );
  }

  if (imageFiles.length > 0) {
    return imageFiles;
  }

  for (const file of Array.from(clipboardData.files ?? [])) {
    if (file.type.startsWith("image/")) {
      imageFiles.push(file);
    }
  }

  return imageFiles;
}

function dataTransferHasFiles(
  dataTransfer: DataTransfer | null
): dataTransfer is DataTransfer {
  if (!dataTransfer) return false;
  const types = dataTransfer.types;
  if (!types) return false;
  // Real browsers return DOMStringList which has .contains(); test polyfills use plain arrays.
  if (typeof (types as unknown as { contains?: unknown }).contains === "function") {
    return (types as unknown as DOMStringList).contains("Files");
  }
  return Array.from(types).includes("Files");
}

// ============================================================================
// PERSIST STATE HELPERS
// ============================================================================

type NormalizedPersistConfig = {
  storage: 'local' | 'session';
  keyPrefix: string;
  persist: {
    openState: boolean;
    voiceState: boolean;
    focusInput: boolean;
  };
  clearOnChatClear: boolean;
};

/**
 * Normalize persistState config - handles both boolean and object forms
 */
function normalizePersistStateConfig(
  config: boolean | { storage?: 'local' | 'session'; keyPrefix?: string; persist?: { openState?: boolean; voiceState?: boolean; focusInput?: boolean }; clearOnChatClear?: boolean } | undefined
): NormalizedPersistConfig | null {
  if (!config) return null;

  const settings = config === true ? {} : config;

  // Object config - merge with defaults
  return {
    storage: settings.storage ?? 'session',
    keyPrefix: settings.keyPrefix ?? 'persona-',
    persist: {
      openState: settings.persist?.openState ?? true,
      voiceState: settings.persist?.voiceState ?? true,
      focusInput: settings.persist?.focusInput ?? true
    },
    clearOnChatClear: settings.clearOnChatClear ?? true
  };
}

/**
 * Get the storage object based on config
 */
function getPersistStorage(storageType: 'local' | 'session'): Storage | null {
  try {
    const storage = storageType === 'local' ? localStorage : sessionStorage;
    // Test that storage is actually available
    const testKey = '__persist_test__';
    storage.setItem(testKey, '1');
    storage.removeItem(testKey);
    return storage;
  } catch {
    return null;
  }
}

const ensureRecord = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== "object") {
    return {};
  }
  return { ...(value as Record<string, unknown>) };
};

const messagesForStorage = (messages: AgentWidgetMessage[]) =>
  messages
    .filter((message) => !isLocalOnlyMessage(message))
    .map((message) => ({
      ...message,
      streaming: false
    }));

type Controller = {
  /** Resolves once the configured UI and its styles can be shown together.
   * Rejects on a preparation failure; update() retries missing modules. */
  readonly ready: Promise<void>;
  readonly instanceId: string;
  update: (config: AgentWidgetConfigPatch) => void;
  destroy: () => void;
  open: () => void;
  close: () => void;
  toggle: () => void;
  clearChat: () => void;
  setMessage: (message: string) => boolean;
  submitMessage: (message?: string) => boolean;
  /**
   * Frozen snapshot of composer state (text, attachments, mentions, phase).
   * The same payload rides the `persona:composer:state` DOM event.
   */
  getComposerState: () => Readonly<ComposerState>;
  /** Set valid active modes; last requested mode wins each single-selection group. */
  setComposerModes: (ids: readonly string[]) => void;
  /**
   * Set the composer quote/reply-to. Renders a dismissible banner above the
   * editor; the next send carries the quote as a delimited block ahead of the
   * user's text and keeps the structured source on the sent message. A blank
   * `text` is ignored.
   */
  setQuote: (quote: ComposerQuote) => void;
  /** Drop the active quote without sending. */
  clearQuote: () => void;
  /**
   * Manually retry a dropped durable stream (e.g. from a "Reconnect" button).
   * No-op unless a resumable durable turn dropped and `reconnectStream` is set.
   */
  reconnect: () => void;
  startDictation: () => boolean;
  finishDictation: () => boolean;
  cancelDictation: () => boolean;
  sendDictation: () => boolean;
  getDictationState: () => DictationState;
  startVoiceRecognition: () => boolean;
  stopVoiceRecognition: () => boolean;
  /**
   * Inject a message into the conversation with dual-content support.
   * Auto-opens the widget if closed and launcher is enabled.
   */
  injectMessage: (options: InjectMessageOptions) => AgentWidgetMessage;
  /**
   * Convenience method for injecting assistant messages.
   */
  injectAssistantMessage: (options: InjectAssistantMessageOptions) => AgentWidgetMessage;
  /**
   * Convenience method for injecting user messages.
   */
  injectUserMessage: (options: InjectUserMessageOptions) => AgentWidgetMessage;
  /**
   * Convenience method for injecting system messages.
   */
  injectSystemMessage: (options: InjectSystemMessageOptions) => AgentWidgetMessage;
  /**
   * Inject multiple messages in a single batch with one sort and one render pass.
   */
  injectMessageBatch: (optionsList: InjectMessageOptions[]) => AgentWidgetMessage[];
  /**
   * Convenience method for injecting an assistant message that renders as a
   * registered component: same shape Persona produces from a streamed
   * `{ "text": "...", "component": "...", "props": {...} }` payload.
   */
  injectComponentDirective: (
    options: InjectComponentDirectiveOptions
  ) => AgentWidgetMessage;
  /** Replace the props of an existing component directive; false if not found. */
  updateComponentProps: (messageId: string, props: Record<string, unknown>) => boolean;
  /** Remove an existing component directive and retire its renderer lifetime. */
  removeComponentMessage: (messageId: string) => boolean;
  /**
   * @deprecated Use injectMessage() instead.
   */
  injectTestMessage: (event: AgentWidgetEvent) => void;
  /**
   * Show follow-up suggestions from host code. An ephemeral UI overlay: the
   * items never enter the transcript, the wire payload, or persisted state, so
   * they do not survive a refresh. Cleared automatically when the next user
   * message appends. Latest writer wins against the agent's `suggest_replies`
   * payloads: this call overrides chips shown now, and a payload arriving
   * afterwards overrides these. Renders through the `suggestions.followUps`
   * presentation config and the full plugin and `persona:suggestReplies:*`
   * event pipeline, with source `"host"`. An empty array clears.
   */
  setFollowUpSuggestions: (items: AgentWidgetSuggestion[]) => void;
  /** Remove host-set follow-up suggestions. Agent-set chips are untouched. */
  clearFollowUpSuggestions: () => void;
  getMessages: () => AgentWidgetMessage[];
  getStatus: () => AgentWidgetSessionStatus;
  getPersistentMetadata: () => Record<string, unknown>;
  updatePersistentMetadata: (
    updater: (prev: Record<string, unknown>) => Record<string, unknown>
  ) => void;
  on: <K extends keyof AgentWidgetControllerEventMap>(
    event: K,
    handler: (payload: AgentWidgetControllerEventMap[K], context: Readonly<{ instanceId: string }>) => void
  ) => () => void;
  off: <K extends keyof AgentWidgetControllerEventMap>(
    event: K,
    handler: (payload: AgentWidgetControllerEventMap[K], context: Readonly<{ instanceId: string }>) => void
  ) => void;
  // State query methods
  isOpen: () => boolean;
  isVoiceActive: () => boolean;
  // Read-aloud (text-to-speech) methods
  toggleReadAloud: (messageId: string) => void;
  stopReadAloud: () => void;
  getReadAloudState: (messageId: string) => ReadAloudState;
  onReadAloudChange: (
    listener: (activeId: string | null, state: ReadAloudState) => void
  ) => () => void;
  getState: () => AgentWidgetStateSnapshot;
  // Feedback methods (CSAT/NPS)
  showCSATFeedback: (options?: Partial<CSATFeedbackOptions>) => void;
  showNPSFeedback: (options?: Partial<NPSFeedbackOptions>) => void;
  submitCSATFeedback: (rating: number, comment?: string) => Promise<void>;
  submitNPSFeedback: (rating: number, comment?: string) => Promise<void>;
  /**
   * Connect an external SSE stream and process it through the SDK's
   * native event pipeline (tools, reasoning, streaming text, etc.).
   */
  connectStream: (
    stream: ReadableStream<Uint8Array>,
    options?: { assistantMessageId?: string }
  ) => Promise<void>;
  /** Push a raw event into the event stream buffer (for testing/debugging) */
  __pushEventStreamEvent: (event: { type: string; payload: unknown }) => void;
  /** Opens the event stream panel */
  showEventStream: () => void;
  /** Closes the event stream panel */
  hideEventStream: () => void;
  /** Returns current visibility state of the event stream panel */
  isEventStreamVisible: () => boolean;
  /** Show artifact sidebar (no-op if features.artifacts.enabled is false) */
  showArtifacts: () => void;
  /** Hide artifact sidebar */
  hideArtifacts: () => void;
  /** Upsert an artifact programmatically */
  upsertArtifact: (manual: PersonaArtifactManualUpsert) => PersonaArtifactRecord | null;
  selectArtifact: (id: string) => void;
  clearArtifacts: () => void;
  /** Read current artifacts (useful on init to rebuild host-side tab state after hydration). */
  getArtifacts: () => PersonaArtifactRecord[];
  /** Read the currently selected artifact id (paired with `getArtifacts`). */
  getSelectedArtifactId: () => string | null;
  /**
   * Focus the chat input. Returns true if focus succeeded, false if panel is closed
   * (launcher mode) or textarea is unavailable.
   */
  focusInput: () => boolean;
  /**
   * Bring a tool call into view: opens any collapsed group holding it, expands its details
   * (unless `expand: false`), scrolls it to the center of the transcript and briefly marks it
   * with `data-persona-revealed`. Returns false when no rendered tool bubble has that id.
   */
  revealToolCall: (toolCallId: string, options?: AgentWidgetRevealToolCallOptions) => boolean;
  /**
   * Programmatically resolve a pending approval.
   * @param approvalId - The approval ID to resolve
   * @param decision - "approved" or "denied"
   * @param options - Optional decision context (e.g. `{ remember: true }`),
   *   forwarded to `config.approval.onDecision`.
   */
  resolveApproval: (
    approvalId: string,
    decision: 'approved' | 'denied',
    options?: AgentWidgetApprovalDecisionOptions
  ) => Promise<void>;

  // --- Conversation history (client-token mode; see D8) ---------------------

  /** Pass-through list, for headless hosts rendering their own navigation. */
  listConversations: (opts?: {
    cursor?: string;
    limit?: number;
    targetId?: string;
    scope?: HistoryScope;
  }) => Promise<{
    items: HistoryConversationSummary[];
    nextCursor: string | null;
  }>;
  /** The open conversation's record id; null for a fresh unsaved chat. */
  getActiveConversationId: () => string | null;
  /**
   * Full open flow: transactional resume, hydration, and scroll restore.
   * `focus: true` moves focus to the composer; the programmatic default
   * leaves focus alone (an embedded widget must not scroll the host page
   * on a boot-time reopen).
   */
  openConversation: (
    conversationId: string,
    opts?: AgentWidgetOpenConversationOptions
  ) => Promise<void>;
  /** Clean local state plus a new owned server conversation. `focus` as on `openConversation`. */
  startNewConversation: (opts?: { focus?: boolean }) => Promise<void>;
  /**
   * Client token mode: start `/v1/client/init` now, ahead of the first send
   * (e.g. when the visitor hovers your own "Chat with us" button).
   * Fire-and-forget, at most once per session lifetime, errors swallowed; the
   * send reuses the in-flight init. No-op in other modes. See `sessionInit`.
   */
  warmSession: () => void;
  /** Permanently delete one server record; the active one is replaced. */
  deleteConversation: (conversationId: string) => Promise<void>;
  /**
   * Visitor-scoped rename. Requires a provider with the update capability;
   * a user-set title pins, so a later auto-generated title never overwrites it.
   */
  renameConversation: (
    conversationId: string,
    title: string
  ) => Promise<HistoryConversationSummary>;
  /** Visitor-scoped star/unstar. Requires a provider with the update capability. */
  setConversationStarred: (
    conversationId: string,
    starred: boolean
  ) => Promise<HistoryConversationSummary>;
  /**
   * Permanently delete every record in the resolved scope. Defaults to the
   * active target filter; `allTargets` is a deliberate client-token-wide delete.
   */
  clearConversationHistory: (opts?: {
    targetId?: string;
    allTargets?: boolean;
    scope?: HistoryScope;
  }) => Promise<{ deleted: number }>;
  /**
   * Revoke this browser's history credential and wipe it locally. Resolves
   * (never rejects) on remote failure; rejection is reserved for misuse.
   * Required host logout wiring.
   */
  resetHistoryIdentity: () => Promise<{ remoteRevocationConfirmed: boolean }>;
  /**
   * Read the current browser visitor credential for a trusted voice integration.
   * Returns null before minting, outside client-token mode, or after teardown.
   * Never put this bearer credential in logs, URLs, or model context.
   */
  getVisitorToken: () => Promise<string | null>;
  /** Latest sanitized identity state; carries no token, proof, or identity id. */
  getHistoryIdentityStatus: () => HistoryIdentityStatus;
  /** `focus: false` keeps focus where it is (programmatic/preview opens). */
  showHistory: (opts?: {
    returnSurface?: HistoryReturnSurface;
    focus?: boolean;
  }) => Promise<void>;
  /** `restoreFocus: false` leaves focus untouched on close. */
  hideHistory: (opts?: { restoreFocus?: boolean }) => void;
  isHistoryVisible: () => boolean;
};

export const buildPostprocessor = (
  cfg: AgentWidgetConfig | undefined,
  actionManager?: ReturnType<typeof createActionManager>,
  onResubmitRequested?: () => void
): MessageTransform => {
  // Create markdown processor from config if markdown config is provided
  // This allows users to enable markdown rendering via config.markdown
  const markdownProcessor = cfg?.markdown
    ? createMarkdownProcessorFromConfig(cfg.markdown)
    : null;

  // Resolve sanitizer: enabled by default, can be disabled or replaced
  const sanitize = resolveSanitizer(cfg?.sanitize);

  // Warn developers when a custom postprocessor is used with the default sanitizer,
  // since DOMPurify will strip any tags/attributes not in the allowlist.
  if (cfg?.postprocessMessage && sanitize && cfg?.sanitize === undefined) {
    console.warn(
      "[Persona] postprocessMessage HTML is sanitized: unsupported tags/attributes are stripped. Provide a custom sanitize function to allow them, or sanitize: false only for trusted HTML."
    );
  }

  // A throwing custom renderer/postprocessor used to propagate out of the
  // bubble builder and abort the whole streaming turn. One log per transform,
  // then escaped plain text for the rest of the stream.
  let reportedTransformFailure = false;

  const render = (context: Parameters<MessageTransform>[0]): string => {
    let nextText = context.text ?? "";
    const rawPayload = context.message.rawContent ?? null;

    if (actionManager) {
      const actionResult = actionManager.process({
        text: nextText,
        raw: rawPayload ?? nextText,
        message: context.message,
        streaming: context.streaming
      });
      if (actionResult !== null) {
        nextText = actionResult.text;
        // Mark message as non-persistable if persist is false
        if (!actionResult.persist) {
          (context.message as any).__skipPersist = true;
        }
        // Request deferred resubmit if handler requested it (and message is complete)
        // The actual resubmit will be triggered when injectAssistantMessage is called
        if (actionResult.resubmit && !context.streaming && onResubmitRequested) {
          onResubmitRequested();
        }
      }
    }

    // Priority: postprocessMessage > markdown config > escapeHtml.
    //
    // Degraded path (IIFE/CDN build before `markdown-parsers.js` resolves, or if
    // it never loads): the markdown processor and the sanitizer BOTH fall back to
    // escapeHtml, so the old `sanitize(markdownProcessor(text))` escaped twice and
    // displayed entities literally (I'll -> &amp;#39;). Only run the sanitizer when
    // it can actually parse HTML; escapeHtml output is already inert. Checked per
    // render (not once at setup): the chunk lands later and the self-heal re-renders.
    const parsers = getMarkdownParsersSync();
    const parsersReady = parsers !== null;
    let html: string;
    if (cfg?.postprocessMessage) {
      const out = cfg.postprocessMessage({
        ...context,
        text: nextText,
        raw: rawPayload ?? context.text ?? ""
      });
      // Custom HTML is NOT pre-escaped, so this stays a single pass even via the
      // sanitizer's degraded fallback. Honors `sanitize: false` (pass-through) as before.
      html = sanitize ? sanitize(out) : out;
    } else if (markdownProcessor) {
      // Stabilize incomplete Markdown only for display during streaming. The
      // final render and the stored response always use the original text.
      const source = preprocessStreamingMarkdown(nextText, context.streaming);
      // Already escapeHtml(text) (single, safe) while parsers are not loaded.
      const out = markdownProcessor(source);
      html = sanitize && parsersReady ? sanitize(out) : out;
    } else {
      // Plain text: escapeHtml output is inert — never re-sanitize (the second escape).
      html = escapeHtml(nextText);
    }

    return html;
  };

  return (context) => {
    try {
      return render(context);
    } catch (error) {
      if (!reportedTransformFailure) {
        reportedTransformFailure = true;
        console.error(
          "[Persona] markdown rendering failed; falling back to plain text:",
          error
        );
      }
      return escapeHtml(context.text ?? "");
    }
  };
};

function buildDropOverlay(
  dropCfg?: NonNullable<AgentWidgetConfig["attachments"]>["dropOverlay"]
): HTMLElement {
  const overlay = createElement("div", "persona-attachment-drop-overlay");
  if (dropCfg?.background) overlay.style.setProperty("--persona-drop-overlay-bg", dropCfg.background);
  if (dropCfg?.backdropBlur !== undefined) overlay.style.setProperty("--persona-drop-overlay-blur", dropCfg.backdropBlur);
  if (dropCfg?.border) overlay.style.setProperty("--persona-drop-overlay-border", dropCfg.border);
  if (dropCfg?.borderRadius) overlay.style.setProperty("--persona-drop-overlay-radius", dropCfg.borderRadius);
  if (dropCfg?.inset) overlay.style.setProperty("--persona-drop-overlay-inset", dropCfg.inset);
  if (dropCfg?.labelSize) overlay.style.setProperty("--persona-drop-overlay-label-size", dropCfg.labelSize);
  if (dropCfg?.labelColor) overlay.style.setProperty("--persona-drop-overlay-label-color", dropCfg.labelColor);

  const iconName = dropCfg?.iconName ?? "upload";
  const iconSize = dropCfg?.iconSize ?? "48px";
  const iconColor = dropCfg?.iconColor ?? "rgba(59, 130, 246, 0.6)";
  const iconStrokeWidth = dropCfg?.iconStrokeWidth ?? 0.5;
  const iconSvg = renderLucideIcon(iconName, iconSize, iconColor, iconStrokeWidth);
  if (iconSvg) overlay.appendChild(iconSvg);

  if (dropCfg?.label) {
    const labelEl = createElement("span", "persona-drop-overlay-label");
    labelEl.textContent = dropCfg.label;
    overlay.appendChild(labelEl);
  }
  return overlay;
}

// Deep-merge two mention context maps (`{ [sourceId]: { [itemId]: unknown } }`).
// A source contributing to both bundles keeps BOTH per-item maps; a shallow
// spread would let one sourceId's inner map wholesale replace the other's.
export const mergeMentionContext = (
  a: MentionSubmitBundle["context"],
  b: MentionSubmitBundle["context"]
): MentionSubmitBundle["context"] => {
  const out: MentionSubmitBundle["context"] = { ...a };
  for (const [sourceId, items] of Object.entries(b)) {
    const existing = out[sourceId];
    out[sourceId] = existing ? { ...existing, ...items } : items;
  }
  return out;
};

// Concatenate the fulfilled halves of two finalize() results (either may be
// absent when its side rejected), deep-merging their per-source context.
export const mergeFinalizedMentions = (
  bundles: MentionSubmitBundle[]
): MentionSubmitBundle =>
  bundles.reduce<MentionSubmitBundle>(
    (acc, b) => ({
      blocks: [...acc.blocks, ...b.blocks],
      contentParts: [...acc.contentParts, ...b.contentParts],
      context: mergeMentionContext(acc.context, b.context),
    }),
    { blocks: [], contentParts: [], context: {} }
  );

export const createAgentExperience = (
  mount: HTMLElement,
  initialConfig?: AgentWidgetConfig,
  runtimeOptions?: { debugTools?: boolean; registerHostMove?: (move: (reparent: () => void) => void) => void; diagnostics?: LegacyDiagnosticContext; preparation?: Promise<unknown>[] }
): Controller => {
  if (mount == null) {
    throw new Error(
      "createAgentExperience: mount must be a non-null HTMLElement (e.g. pass document.getElementById(\"my-root\") after the node exists)."
    );
  }
  // Preserve original mount id as data attribute for window event instance scoping
  if (mount.id && !mount.getAttribute("data-persona-instance")) {
    mount.setAttribute("data-persona-instance", mount.id);
  }
  const instanceId = mount.getAttribute("data-persona-instance") ||
    `persona-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  mount.setAttribute("data-persona-instance", instanceId);
  // Ensure root marker is present for Tailwind scoping and DOM traversal
  if (!mount.hasAttribute("data-persona-root")) {
    mount.setAttribute("data-persona-root", "true");
  }

  let config = mergeWithDefaults(initialConfig) as AgentWidgetConfig;
  const renderReadiness = createRenderReadiness(mount);
  const diagnostics = runtimeOptions?.diagnostics ?? createLegacyDiagnosticContext(initialConfig);
  // Message ids are only unique within a widget session. Keep disclosure state
  // beside the widget that renders it, not in component module scope.
  let activityUi: ActivityUiModule | null = null;
  let loadingLabelLifecycle: ReturnType<LoadingLabelRuntime["createLoadingLabelLifecycle"]> | undefined;
  let activityLifecycle: ReturnType<ActivityUiModule["createActivityLifecycle"]> | undefined;
  const ensureActivityUi = (): ActivityUiModule | null => {
    const loadingLabels = getLoadingLabelRuntimeSync();
    loadingLabelLifecycle ??= loadingLabels?.createLoadingLabelLifecycle(mount, () => config);
    if (activityUi) return activityUi;
    const ready = getActivityUiSync();
    if (ready && loadingLabels) {
      ready.initActivityUi(renderLucideIcon, loadingLabels, activityDependencies);
      activityLifecycle = ready.createActivityLifecycle(
        mount, () => config, toolExpansionState, reasoningExpansionState, messageCache, queryMessageBubble
      );
      return activityUi = ready;
    }
    return null;
  };
  const toolExpansionState = new Set<string>();
  const reasoningExpansionState = new Set<string>();
  const approvalDetailsExpansionState = new Map<string, boolean>();
  // Long user message collapse, per message id: the measured overflow and the
  // reader's own toggle (true = expanded). Render state derives from both, so
  // re-renders and morphs never re-collapse or re-expand a toggled bubble.
  const userCollapseOverflow = new Map<string, boolean>();
  const userCollapseToggled = new Map<string, boolean>();
  // Voice transcripts rendered while interim. Their final render stays
  // expanded ("settling") so the post-morph sync applies the collapse with
  // the viewport held, rather than the morph collapsing it underneath.
  const userCollapseLive = new Set<string>();
  const resolveUserCollapse = (message: AgentWidgetMessage): UserMessageCollapseRender | undefined => {
    if (message.role !== "user" || !isUserMessageCollapseEnabled(config)) return undefined;
    if (message.voiceProcessing) userCollapseLive.add(message.id);
    const state: UserMessageCollapseState = message.voiceProcessing
      ? "live"
      : userCollapseLive.has(message.id)
        ? "settling"
        : !userCollapseOverflow.get(message.id)
          ? "fit"
          : userCollapseToggled.get(message.id) ? "expanded" : "collapsed";
    return {
      state,
      maxHeight: resolveUserMessageCollapseMaxHeight(config),
      ...resolveUserMessageCollapseLabels(config),
    };
  };
  const applyTooltipTiming = (): void => {
    configureTooltipTiming({
      delayMs: config.tooltip?.delayMs ?? DEFAULT_TOOLTIP_DELAY_MS,
      skipDelayMs: config.tooltip?.skipDelayMs ?? DEFAULT_TOOLTIP_SKIP_DELAY_MS,
    });
  };
  applyTooltipTiming();
  // `config.features` holds the preference-resolved view every read site uses;
  // `baseFeatures` keeps the pre-preference base so update() re-resolves from
  // it instead of stacking new preferences onto already-overlaid values.
  let baseFeatures = config.features;
  config = resolveConfigPreferences(config);
  // Note: applyThemeVariables is called after applyFullHeightStyles() below
  // because applyFullHeightStyles resets mount.style.cssText

  // Get plugins for this instance
  const plugins = pluginRegistry.getForInstance(config.plugins);

  // One keydown listener per instance. Widget-scoped bindings resolve against
  // the mount; Escape is reserved and never routed through here.
  const shortcuts = createShortcutRegistry(mount);
  let releaseShortcuts: Array<() => void> = [];

  // Guards the composer/welcome hooks, which can run before `session` exists
  // (its `let` is declared far below and is in the temporal dead zone here).
  let sessionInitialized = false;

  // One store per widget instance, namespaced per plugin id. Shared by the
  // `renderWelcome` and `renderComposer` contexts.
  const pluginStorageFor = createPluginStorageFactory(() => config);

  // The built-in approval renderer, shaped as a plugin. Resolved as a FALLBACK
  // (not pushed into `plugins`) so a user `renderApproval` plugin always wins
  // and a later config-update plugin push can't reorder ahead of it.
  //
  // The whole approval UI (bubble + built-in plugin) lives in the lazy
  // approval-ui chunk: nothing loads until the first approval message arrives.
  // While the chunk is in flight that message renders as an empty preserved
  // row; `onApprovalUiReady` (assigned at the end of the factory) clears the
  // cache and re-renders so the real card appears. The approval is parked on
  // the user's decision anyway, so the delay costs nothing semantically.
  let approvalUi: ApprovalUiModule | null = null;
  let builtInApprovalPlugin: AgentWidgetPlugin | null = null;
  let teardownBuiltInApprovals: (() => void) | null = null;
  let approvalUiDisposed = false;
  let onApprovalUiReady: (() => void) | null = null;
  const adoptApprovalUi = (mod: ApprovalUiModule, controls: ComposerActionsModule): void => {
    approvalUi = mod;
    // Inject the core bridge's webmcp title map: the chunk is bundled
    // noExternal and must not read its own (empty) copy.
    mod.initApprovalUi({
      webMcpToolTitle: getWebMcpToolDisplayTitle,
      popover: controls,
      icons: { render: renderLucideIcon, node: renderIconNode },
    });
    const built = mod.createBuiltInApprovalPlugin(approvalDetailsExpansionState);
    builtInApprovalPlugin = built.plugin;
    teardownBuiltInApprovals = built.teardown;
  };
  const ensureApprovalUi = (): ApprovalUiModule | null => {
    if (approvalUi) return approvalUi;
    // Synchronous when the module was provided eagerly (tests) or already
    // loaded by another widget instance on the page.
    const sync = getApprovalUiSync();
    const controls = getComposerActionsSync();
    if (sync && controls) {
      adoptApprovalUi(sync, controls);
      return sync;
    }
    // No local in-flight flag: the chunk loader dedupes concurrent loads,
    // caches success, and clears a rejection so the next render retries.
    void Promise.all([loadApprovalUi(), loadComposerActions()])
      .then(([mod, controls]) => {
        if (approvalUiDisposed || approvalUi) return;
        adoptApprovalUi(mod, controls);
        onApprovalUiReady?.();
      })
      .catch(() => {
        // Failed fetch: renders keep holding empty rows; a later render
        // retries via the loader's rejection-retry semantics.
      });
    return null;
  };

  // The `[data-tv-form]` placeholder enhancement lives in the lazy forms-ui
  // chunk: nothing loads until a rendered bubble actually contains a
  // placeholder. While the chunk is in flight the placeholder stays the bare
  // div the postprocessor emitted; `onFormsUiReady` (assigned at the end of
  // the factory) clears the cache and re-renders so the form appears — the
  // same heal as approval-ui above.
  let formsUiDisposed = false;
  let formsUiReadyNotified = false;
  let onFormsUiReady: (() => void) | null = null;
  const enhanceWithFormsLazy = (
    bubble: HTMLElement,
    message: AgentWidgetMessage,
    widgetConfig: AgentWidgetConfig,
    widgetSession: AgentWidgetSession
  ): void => {
    if (!bubble.querySelector("[data-tv-form]")) return;
    const sync = getFormsUiSync();
    if (sync) {
      sync.enhanceWithForms(bubble, message, widgetConfig, widgetSession);
      return;
    }
    // No local in-flight flag: the chunk loader dedupes concurrent loads,
    // caches success, and clears a rejection so the next render retries.
    void loadFormsUi()
      .then(() => {
        // A resolution before the factory-end assignment needs no heal: the
        // module is cached, so the next render enhances synchronously.
        if (formsUiDisposed || formsUiReadyNotified || !onFormsUiReady) return;
        formsUiReadyNotified = true;
        onFormsUiReady();
      })
      .catch(() => {
        // Failed fetch: the placeholder stays bare; a later render retries
        // via the loader's rejection-retry semantics.
      });
  };

  // Config component registrations are resolved per instance, never copied globally.
  const eventBus = createEventBus<AgentWidgetControllerEventMap>(Object.freeze({ instanceId }));

  // When persistState is explicitly false, message-history persistence is
  // disabled: including any user-supplied storageAdapter. This is the strict
  // kill-switch semantic; pass `persistState: true` (or omit it) to opt in.
  const messagePersistenceDisabled = config.persistState === false;
  const storageAdapter: AgentWidgetStorageAdapter | null =
    messagePersistenceDisabled
      ? null
      : (config.storageAdapter ??
        createLocalStorageAdapter(
          transcriptStorageKey(
            typeof config.persistState === "object" ? config.persistState.keyPrefix : undefined
          )
        ));
  const personaOwnsClientTokenRecovery =
    Boolean(config.clientToken) && typeof config.reconnectStream !== "function";
  let persistentMetadata: Record<string, unknown> = {};
  let pendingStoredState: Promise<AgentWidgetStoredState | null> | null = null;
  // Resolves once stored state has been applied (or its load failed). Every
  // history-capable init path waits on it, so a fast network can never race an
  // async storage adapter. Always resolves, never rejects.
  let resolveHistoryBootstrap!: () => void;
  const historyBootstrapReady = new Promise<void>((resolve) => {
    resolveHistoryBootstrap = resolve;
  });
  // With history enabled at mount, history-capable init also waits for the
  // shell, which installs the provider. Only the IIFE build loads it async
  // (always resolves, even when the chunk fails).
  let resolveHistoryShellGate: () => void = () => {};
  const historyShellGate = new Promise<void>((resolve) => {
    resolveHistoryShellGate = resolve;
  });
  /** Synchronously loaded draft, replayed into the composer after it mounts. */
  let loadedStoredDraft: AgentWidgetStoredDraft | undefined;

  let shouldOpenAfterStateLoaded = false;

  // Helper to apply onStateLoaded hook and extract state.
  // Supports both the legacy plain-state return and the new { state, open? } return.
  const applyStateLoadedHook = (state: AgentWidgetStoredState): AgentWidgetStoredState => {
    if (config.onStateLoaded) {
      try {
        const result = config.onStateLoaded(state);
        if (result && typeof result === 'object' && 'state' in result) {
          const { state: processedState, open } = result as { state: AgentWidgetStoredState; open?: boolean };
          if (open) shouldOpenAfterStateLoaded = true;
          return processedState;
        }
        return result as AgentWidgetStoredState;
      } catch (error) {
        if (typeof console !== "undefined") {
          // eslint-disable-next-line no-console
          console.error("[AgentWidget] onStateLoaded hook failed:", error);
        }
      }
    }
    return state;
  };

  if (storageAdapter?.load) {
    try {
      const storedState = storageAdapter.load();
      if (storedState && typeof (storedState as Promise<any>).then === "function") {
        // For async storage, apply hook when promise resolves
        pendingStoredState = (storedState as Promise<AgentWidgetStoredState | null>).then(
          (resolved) => {
            const state = resolved ?? { messages: [], metadata: {} };
            return applyStateLoadedHook(state);
          }
        );
      } else {
        // Apply hook to synchronously loaded state (or empty state if nothing stored)
        const baseState = (storedState as AgentWidgetStoredState) ?? { messages: [], metadata: {} };
        const processedState = applyStateLoadedHook(baseState);
        if (processedState.metadata) {
          persistentMetadata = ensureRecord(processedState.metadata);
        }
        if (processedState.messages?.length) {
          config = { ...config, initialMessages: processedState.messages };
        }
        if (processedState.artifacts?.length) {
          config = {
            ...config,
            initialArtifacts: processedState.artifacts,
            initialSelectedArtifactId: processedState.selectedArtifactId ?? null
          };
        }
        // Applied once the composer exists (see `restoreComposerDraft`).
        loadedStoredDraft = processedState.draft;
      }
    } catch (error) {
      if (typeof console !== "undefined") {
        // eslint-disable-next-line no-console
        console.error("[AgentWidget] Failed to load stored state:", error);
      }
    }
  } else if (config.onStateLoaded) {
    // No storage adapter but hook exists - call with empty state
    try {
      const processedState = applyStateLoadedHook({ messages: [], metadata: {} });
      if (processedState.messages?.length) {
        config = { ...config, initialMessages: processedState.messages };
      }
    } catch (error) {
      if (typeof console !== "undefined") {
        // eslint-disable-next-line no-console
        console.error("[AgentWidget] onStateLoaded hook failed:", error);
      }
    }
  }

  // Only the async adapter path defers the gate (resolved with hydration
  // below); every synchronous exit above, including a load throw, is done here.
  if (!pendingStoredState) resolveHistoryBootstrap();

  const getSessionMetadata = () => persistentMetadata;
  const updateSessionMetadata = (
    updater: (prev: Record<string, unknown>) => Record<string, unknown>
  ) => {
    const next = updater({ ...persistentMetadata }) ?? {};
    persistentMetadata = next;
    persistState();
  };

  const resolvedActionParsers =
    config.actionParsers && config.actionParsers.length
      ? config.actionParsers
      : [defaultJsonActionParser];

  const resolvedActionHandlers =
    config.actionHandlers && config.actionHandlers.length
      ? config.actionHandlers
      : [defaultActionHandlers.message, defaultActionHandlers.messageAndClick];

  let actionManager = createActionManager({
    parsers: resolvedActionParsers,
    handlers: resolvedActionHandlers,
    getSessionMetadata,
    updateSessionMetadata,
    emit: eventBus.emit,
    captureResubmitGuard: () => {
      const generation = restartGeneration;
      return () => canRestart(generation);
    },
    documentRef: typeof document !== "undefined" ? document : null
  });
  actionManager.syncFromMetadata();

  let launcherEnabled = config.launcher?.enabled ?? true;
  let autoExpand = config.launcher?.autoExpand ?? false;
  const autoFocusInput = config.autoFocusInput ?? false;
  let prevAutoExpand = autoExpand;
  let prevLauncherEnabled = launcherEnabled;
  let prevHeaderLayout = config.layout?.header?.layout;
  let wasMobileFullscreen = false;
  // Composer-bar mode behaves like a launcher-enabled panel for state/toggle
  // purposes (open/close maps to expand/collapse) but does not render a
  // launcher button. `isPanelToggleable()` covers both modes; checks that
  // gate the launcher button itself stay on the raw `launcherEnabled` flag.
  const isComposerBar = () => isComposerBarMountMode(config);
  const isPanelToggleable = () => launcherEnabled || isComposerBar();
  // Composer-bar starts collapsed (open=false). Inline embed (no launcher)
  // is always open. Launcher mode honors `autoExpand`.
  let open = isComposerBar() ? false : (launcherEnabled ? autoExpand : true);

  // Track pending resubmit state for injection-triggered resubmit
  // When a handler returns resubmit: true, we wait for injectAssistantMessage()
  // to be called before triggering the actual resubmit (to avoid race conditions)
  // Cached restoration reuses this controller, so work queued by the old page
  // lifecycle must remain stale even after pageshow makes the page active again.
  let restartGeneration = 0;
  let destroyed = false;
  const canRestart = (generation: number) =>
    !destroyed && !pageHiding && generation === restartGeneration;
  const invalidatePendingRestarts = () => {
    restartGeneration++;
    pendingResubmit = false;
    if (pendingResubmitTimeout !== null) clearTimeout(pendingResubmitTimeout);
    pendingResubmitTimeout = null;
  };
  const scheduleResubmit = () => {
    const generation = restartGeneration;
    if (!canRestart(generation)) return;
    setTimeout(() => {
      if (canRestart(generation) && !session.isStreaming()) session.continueConversation();
    }, 100);
  };

  let pendingResubmit = false;
  let pendingResubmitTimeout: ReturnType<typeof setTimeout> | null = null;

  const handleResubmitRequested = () => {
    if (!canRestart(restartGeneration)) return;
    pendingResubmit = true;
    // Clear any existing timeout
    if (pendingResubmitTimeout) {
      clearTimeout(pendingResubmitTimeout);
    }
    // Safety timeout - clear flag after 10s if no injection occurs
    pendingResubmitTimeout = setTimeout(() => {
      if (pendingResubmit) {
        if (typeof console !== "undefined") {
          // eslint-disable-next-line no-console
          console.warn("[AgentWidget] Resubmit requested but no injection occurred within 10s");
        }
        pendingResubmit = false;
      }
    }, 10000);
  };

  let postprocess = buildPostprocessor(config, actionManager, handleResubmitRequested);
  let showReasoning = config.features?.showReasoning ?? true;
  let showToolCalls = config.features?.showToolCalls ?? true;
  let showEventStreamToggle = config.features?.showEventStreamToggle ?? false;
  let scrollBehaviorFeature = config.features?.scrollBehavior ?? {};
  // Live-read: `update()` may change the persistState shape, which re-keys the
  // visitor store.
  const currentKeyPrefix = () =>
    (typeof config.persistState === 'object' ? config.persistState?.keyPrefix : undefined) ?? "persona-";
  const persistKeyPrefix = currentKeyPrefix();
  const eventStreamDbName = `${persistKeyPrefix}event-stream`;
  // The capture runtime (store + buffer + throughput tracker) lives in the
  // lazy event-stream-view chunk; when the toggle is on, the chunk is fetched
  // at init and the instances are constructed at adoption. Events tapped while
  // the chunk is in flight are staged in `pendingEventStreamEvents` and
  // replayed in order.
  let eventStreamStore: EventStreamStore | null = null;
  const eventStreamMaxEvents = config.features?.eventStream?.maxEvents ?? 2000;
  let eventStreamBuffer: EventStreamBuffer | null = null;
  // Passive output-throughput tracker, fed from the same SSE tap as the buffer.
  let throughputTracker: ThroughputTracker | null = null;
  let pendingEventStreamEvents: Array<{ type: string; payload: unknown }> | null = null;
  let eventStreamRuntimeDisposed = false;
  let eventStreamView: EventStreamViewHandle | null = null;
  let eventStreamVisible = false;
  let eventStreamRAF: number | null = null;
  let eventStreamLastUpdate = 0;

  const pushToEventStreamBuffer = (type: string, payload: unknown): void => {
    eventStreamBuffer?.push({
      id: `evt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type,
      timestamp: Date.now(),
      payload: JSON.stringify(payload),
    });
  };

  /** SSE tap target: buffers live events, stages them while the chunk loads. */
  const feedEventStream = (type: string, payload: unknown): void => {
    throughputTracker?.processEvent(type, payload);
    if (eventStreamBuffer) {
      pushToEventStreamBuffer(type, payload);
    } else if (pendingEventStreamEvents) {
      // Stage bounded to the buffer's own cap: keep the newest, like the ring.
      if (pendingEventStreamEvents.length >= eventStreamMaxEvents) {
        pendingEventStreamEvents.shift();
      }
      pendingEventStreamEvents.push({ type, payload });
    }
  };

  const ensureEventStreamRuntime = (): void => {
    if (eventStreamBuffer || !showEventStreamToggle) return;
    pendingEventStreamEvents ??= [];
    // The chunk loader dedupes concurrent loads and clears a rejection so a
    // later call retries.
    void loadEventStreamView()
      .then((mod) => {
        if (eventStreamRuntimeDisposed || eventStreamBuffer || !showEventStreamToggle) return;
        eventStreamStore = new mod.EventStreamStore(eventStreamDbName);
        eventStreamBuffer = new mod.EventStreamBuffer(eventStreamMaxEvents, eventStreamStore);
        throughputTracker = throughputTracker ?? new mod.ThroughputTracker();
        // Open IndexedDB and restore persisted events into the buffer.
        eventStreamStore.open().then(() => eventStreamBuffer?.restore()).catch(err => {
          if (config.debug) console.warn('[AgentWidget] IndexedDB not available for event stream:', err);
        });
        // Replay events tapped while the chunk was in flight, in order. (Live
        // pushes race the async restore the same way they always have.)
        const staged = pendingEventStreamEvents ?? [];
        pendingEventStreamEvents = null;
        for (const evt of staged) {
          throughputTracker?.processEvent(evt.type, evt.payload);
          pushToEventStreamBuffer(evt.type, evt.payload);
        }
      })
      .catch(() => {
        // Failed fetch: staged events keep accumulating (bounded below) and
        // the next ensure call retries.
      });
  };
  if (showEventStreamToggle) ensureEventStreamRuntime();

  // --- history (Messages) shell state ---------------------------------------
  // Declared here because the scroll-affordance gate, `syncPanelChrome`, and the
  // session callbacks all run before the history block installs its behavior.
  let historyVisible = false;
  let historyPresentation: ResolvedHistoryPresentation | null = null;
  /** Re-asserts panel-host inert state after a chrome pass. */
  let reapplyHistoryHostChrome: () => void = () => {};
  /** Re-derives rail geometry from config; no-op unless a rail is mounted. */
  let applyRailChrome: () => void = () => {};
  let historyStateHandler: (state: SessionHistoryState) => void = () => {};
  let historyNoticeHandler: (notice: SessionHistoryNotice) => void = () => {};
  /** Header button/affordance refresh; a turn's busy state gates the button. */
  let historyChromeSync: () => void = () => {};
  /** A surface that replaces the whole panel body: no scroll affordance over it. */
  const fullPanelOverlayVisible = (): boolean =>
    eventStreamVisible || (historyVisible && historyPresentation === "panel");


  // Create message action callbacks that emit events and optionally send to API
  const messageActionCallbacks: MessageActionCallbacks = {
    onCopy: (message: AgentWidgetMessage) => {
      eventBus.emit("message:copy", message);
      // Send copy feedback to API if in client token mode. Operator messages
      // are host-injected and unknown to the API, so they never report.
      if (session?.isClientTokenMode() && message.role !== "operator") {
        session.submitMessageFeedback(message.id, 'copy').catch((error) => {
          if (config.debug) {
            // eslint-disable-next-line no-console
            console.error("[AgentWidget] Failed to submit copy feedback:", error);
          }
        });
      }
      // Call user-provided callback
      config.messageActions?.onCopy?.(message);
    },
    onFeedback: (feedback: AgentWidgetMessageFeedback) => {
      eventBus.emit("message:feedback", feedback);
      // Send feedback to API if in client token mode
      if (session?.isClientTokenMode()) {
        session.submitMessageFeedback(feedback.messageId, feedback.type).catch((error) => {
          if (config.debug) {
            // eslint-disable-next-line no-console
            console.error("[AgentWidget] Failed to submit feedback:", error);
          }
        });
      }
      // Call user-provided callback
      config.messageActions?.onFeedback?.(feedback);
    }
  };

  // Get status indicator config
  const statusConfig = config.statusIndicator ?? {};
  const _getStatusText = (status: AgentWidgetSessionStatus, statusConfig = config.statusIndicator ?? {}): string => {
    if (status === "idle") return statusConfig.idleText ?? statusCopy.idle;
    if (status === "connecting") return statusConfig.connectingText ?? statusCopy.connecting;
    if (status === "connected") return statusConfig.connectedText ?? statusCopy.connected;
    if (status === "error") return statusConfig.errorText ?? statusCopy.error;
    if (status === "paused") return statusConfig.pausedText ?? statusCopy.paused;
    if (status === "resuming") return statusConfig.resumingText ?? statusCopy.resuming;
    return statusCopy[status];
  };

  /** Update statusText element, rendering a link for idle status when idleLink is configured. */
  function applyStatusToElement(el: HTMLElement, text: string, statusCfg: typeof statusConfig, status: string): void {
    if (!isComposerBar() && (statusCfg.mode === "transient" || el.classList.contains("persona-mb-2"))) {
      placeStatusIndicator(el, composerForm, statusCfg);
      applyStatusIndicatorState(el, statusCfg, status);
    }
    // A composer lock reason owns the status region until the lock clears, and
    // a notice until its timer ends (which re-applies the status).
    if (el.hasAttribute(COMPOSER_REASON_ATTR) || el.hasAttribute(COMPOSER_NOTICE_ATTR)) return;
    if (status === "idle" && statusCfg.idleLink) {
      el.textContent = "";
      const link = document.createElement("a");
      link.href = statusCfg.idleLink;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.textContent = text;
      link.style.color = "inherit";
      link.style.textDecoration = "none";
      el.appendChild(link);
    } else {
      el.textContent = text;
    }
  }

  // The view layer (`components/widget-view.ts`) owns the one-time structural
  // assembly (shell + panel) and groups the resulting refs into named regions.
  // Behavior stays here in ui.ts; the locals below mirror the grouped refs so
  // the surrounding orchestration code keeps its existing variable names.
  const view = createWidgetView({ config, showClose: isPanelToggleable() });
  const { wrapper, panel, pillRoot } = view.shell;
  const panelElements = view.panelElements;
  let {
    container,
    body,
    messagesWrapper,
    starterSuggestions,
    transcriptSuggestions,
    suggestions,
    textarea,
    sendButton,
    composerForm,
    statusText,
    welcomeHost,
    welcomeIconHolder,
    greetingHost,
    introKicker,
    introTitle,
    introSubtitle,
    closeButton,
    iconHolder,
    headerTitle,
    headerSubtitle,
    header,
    footer,
    actionsRow: _actionsRow
  } = panelElements;
  // Nullable + reassignable: a plugin `replaceComposer` swaps the footer, and
  // `bindComposerRefsFromFooter` must repoint these at the NEW clusters (or
  // null them so the fallbacks fire) — a stale ref would insert the
  // mention/attachment/mic buttons into the detached old subtree.
  let leftActions: HTMLElement | null = panelElements.leftActions;
  let rightActions: HTMLElement | null = panelElements.rightActions;
  let sendButtonWrapper: HTMLElement | null = panelElements.sendButtonWrapper;
  let setSendButtonMode = panelElements.setSendButtonMode;

  // Use mutable references for mic button so we can update them dynamically
  let micButton: HTMLButtonElement | null = panelElements.micButton;
  let micButtonWrapper: HTMLElement | null = panelElements.micButtonWrapper;

  // Use mutable references for attachment elements so we can create them dynamically
  let attachmentButton: HTMLButtonElement | null = panelElements.attachmentButton;
  let attachmentButtonWrapper: HTMLElement | null = panelElements.attachmentButtonWrapper;
  let attachmentInput: HTMLInputElement | null = panelElements.attachmentInput;
  let attachmentPreviewsContainer: HTMLElement | null = panelElements.attachmentPreviewsContainer;
  container.classList.add("persona-relative");
  body.classList.add("persona-relative");
  const SCROLL_TO_BOTTOM_EDGE_OFFSET = 16;

  const isScrollToBottomEnabled = () => config.features?.scrollToBottom?.enabled !== false;
  // Default is "anchor-top" (see DEFAULT_WIDGET_CONFIG). This `??` only applies
  // when a partial config sets `scrollBehavior.mode` to undefined explicitly;
  // it must agree with the declared default.
  const getScrollMode = () => scrollBehaviorFeature.mode ?? "anchor-top";
  // "Effectively following the bottom" for streaming auto-scroll: true in
  // follow mode, and in anchor-top when the current turn has no anchor (the
  // no-anchor fallback). Drives `scheduleAutoScroll`, `handleContentResize`,
  // and `isAwayFromLatest` so a no-anchor anchor-top turn behaves like follow.
  const isFollowEffective = () =>
    getScrollMode() === "follow" ||
    (getScrollMode() === "anchor-top" && followFallbackActive);
  const getAnchorTopOffset = () => scrollBehaviorFeature.anchorTopOffset ?? 16;
  const getScrollRestorePosition = () =>
    scrollBehaviorFeature.restorePosition ?? "bottom";
  const isPauseOnInteractionEnabled = () =>
    scrollBehaviorFeature.pauseOnInteraction === true;
  // Defaults on alongside the anchor-top default so the pinned-turn UX keeps the
  // unread count + "streaming below" hint; opt out with `false`.
  const isActivityWhilePinnedEnabled = () =>
    scrollBehaviorFeature.showActivityWhilePinned !== false;
  const isAnnounceEnabled = () => scrollBehaviorFeature.announce === true;
  const getScrollbarPolicy = () => scrollBehaviorFeature.scrollbar ?? "on-scroll";
  const scrollToBottomControl = createScrollToBottomControl(renderLucideIcon, "transcript", body);
  const scrollToBottomButton = scrollToBottomControl.element;
  container.appendChild(scrollToBottomButton);
  // The docked history rail gives the transcript its own positioned column.
  // Follow that live parent so 50% stays centered over the conversation through
  // rail mounting, resizing, and restoration to the full-width container.
  const scrollToBottomHost = () => body.parentElement ?? container;
  // Persistent-composer follow-ups stay on the panel surface, outside the
  // scrolling transcript, rather than floating over the host page.
  const panelSuggestions = createElement("div", "persona-panel-suggestions");
  panelSuggestions.setAttribute("data-persona-panel-suggestions", "");
  panelSuggestions.hidden = true;
  container.appendChild(panelSuggestions);
  const panelSuggestionsFade = createElement("div", "persona-panel-suggestions-fade");
  panelSuggestionsFade.setAttribute("aria-hidden", "true");
  panelSuggestionsFade.hidden = true;
  container.appendChild(panelSuggestionsFade);

  // Anchor-top scroll mode: zero-height spacer kept after the messages
  // wrapper. Sized on send so the just-sent user message can be scrolled to
  // the top of the viewport before the streamed response is tall enough to
  // make that position reachable; shrinks as real content fills the space.
  const anchorSpacer = createElement("div", "persona-stream-anchor-spacer");
  anchorSpacer.setAttribute("aria-hidden", "true");
  anchorSpacer.setAttribute("data-persona-anchor-spacer", "");
  anchorSpacer.style.flexShrink = "0";
  anchorSpacer.style.pointerEvents = "none";
  anchorSpacer.style.height = "0px";
  // Hidden while unsized: at 0px it still counts as a flex child, so the
  // body's gap would add phantom scrollable space below the transcript.
  anchorSpacer.style.display = "none";
  body.appendChild(anchorSpacer);

  // Visually-hidden polite live region for screen-reader announcements
  // (Principle 15: announce important events at a comfortable pace, never
  // token-by-token). Created unconditionally but only written to when
  // `features.scrollBehavior.announce` is opted in.
  const liveRegion = createElement("div", "persona-sr-only");
  liveRegion.setAttribute("aria-live", "polite");
  liveRegion.setAttribute("aria-atomic", "true");
  liveRegion.setAttribute("role", "status");
  liveRegion.setAttribute("data-persona-live-region", "");
  Object.assign(liveRegion.style, {
    position: "absolute",
    width: "1px",
    height: "1px",
    margin: "-1px",
    padding: "0",
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    whiteSpace: "nowrap",
    border: "0",
  } satisfies Partial<CSSStyleDeclaration>);
  container.appendChild(liveRegion);
  // Debounce announcements so a fast event sequence (e.g. several messages
  // landing while away) collapses into one calm spoken update.
  let announceTimer: ReturnType<typeof setTimeout> | null = null;
  let pendingAnnouncement: string | null = null;
  const announce = (message: string) => {
    if (!isAnnounceEnabled() || !message) return;
    pendingAnnouncement = message;
    if (announceTimer !== null) return;
    announceTimer = setTimeout(() => {
      announceTimer = null;
      // Re-check: `update()` may have disabled `announce` within the debounce
      // window, and a stale message must not slip through to the live region.
      if (pendingAnnouncement && isAnnounceEnabled()) {
        liveRegion.textContent = pendingAnnouncement;
      }
      pendingAnnouncement = null;
    }, 400);
  };

  const isWelcomeFullscreen = () => {
    const win = mount.ownerDocument.defaultView ?? window;
    const mobile = (config.launcher?.mobileFullscreen ?? true) &&
      win.innerWidth <= (config.launcher?.mobileBreakpoint ?? 640) &&
      (launcherEnabled || isDockedMountMode(config));
    return mobile || (config.launcher?.fullHeight === true &&
      !config.launcher.sidebarMode && !isDockedMountMode(config));
  };
  const getWelcomeConfig = () => resolveWelcomeConfig(config, isWelcomeFullscreen());

  // Derived from the session's user messages on every welcome render; the
  // public `data-persona-conversation-state` contract mirrors it.
  let conversationState: "empty" | "active" = "empty";
  // Empty-state lift under `placement: "block"`, where the footer is raised
  // with a bottom margin instead of the overlay vars. 0 in every other case.
  let blockCenterLiftPx = 0;

  const readLiftPx = () =>
    blockCenterLiftPx ||
    parseFloat(mount.style.getPropertyValue("--persona-composer-lift")) ||
    0;

  // Walk offsetParents up to the positioned ancestor (`body` by default) to get a
  // node's top relative to the scroll content. offsetTop avoids skew from any
  // in-flight entrance transforms. Mirrors the anchor-top geometry.
  const offsetTopWithinBody = (el: HTMLElement, ancestor: HTMLElement = body): number => {
    let top = 0;
    let node: HTMLElement | null = el;
    while (node && node !== ancestor) {
      top += node.offsetTop;
      node = node.offsetParent as HTMLElement | null;
    }
    return top;
  };

  const updateScrollToBottomButtonOffset = () => {
    const footerHidden = footer.style.display === "none";
    const host = scrollToBottomHost();
    if (isComposerBar()) {
      // The fixed composer is a sibling of the transcript. Anchored panels
      // already clear it; only reserve space when it overlaps the panel
      // (for example in fullscreen mode).
      const overlap = footerHidden || !footer.offsetHeight ? 0 :
        Math.max(0, host.getBoundingClientRect().bottom - footer.getBoundingClientRect().top);
      const suggestionOverlap = panelSuggestions.hidden ? 0 :
        Math.max(0, host.getBoundingClientRect().bottom - panelSuggestions.getBoundingClientRect().top);
      scrollToBottomButton.style.bottom = `${Math.max(overlap, suggestionOverlap) + SCROLL_TO_BOTTOM_EDGE_OFFSET}px`;
      return;
    }
    let footerHeight = footerHidden ? 0 : footer.offsetHeight;
    if (footerHeight > 0 && resolveComposerPlacement(config, isComposerBar()) === "overlay") {
      let contentTop = Infinity;
      // Overlay padding is transparent; block placement clears the full footer.
      // Header children also cover the pill's display:contents attachment row.
      for (const child of footer.querySelectorAll<HTMLElement>(":scope > *, [data-persona-composer-header] > *")) {
        if (child.hidden || !child.offsetHeight || getComputedStyle(child).visibility === "hidden") continue;
        contentTop = Math.min(contentTop, offsetTopWithinBody(child, host));
      }
      if (contentTop < Infinity) footerHeight -= contentTop - offsetTopWithinBody(footer, host);
    }
    scrollToBottomButton.style.bottom = `${footerHeight + (footerHidden ? 0 : readLiftPx()) + SCROLL_TO_BOTTOM_EDGE_OFFSET}px`;
  };
  updateScrollToBottomButtonOffset();

  const syncComposerBarClearance = () => {
    if (!isComposerBar()) return;
    // Measure the visible composer, including suggestions and attachments.
    // The invisible peek banner still occupies layout space outside it.
    const height = footer.style.display === "none" ? 0 : footer.offsetHeight;
    if (height > 0) {
      wrapper.style.setProperty("--persona-measured-pill-area-height", `${height + 8}px`);
    } else {
      wrapper.style.removeProperty("--persona-measured-pill-area-height");
    }
    wrapper.style.setProperty("--persona-pill-bottom", config.launcher?.composerBar?.bottomOffset ?? "16px");
  };

  // block+center raises the footer with flex spacing, not absolute
  // positioning: the container is a flex column with body { flex: 1 1 0% }, so
  // a footer bottom margin shrinks the body and lifts the footer.
  const applyBlockCenterLift = () => {
    // A plugin-owned footer's inline state belongs to the plugin.
    if (composerIsPluginOwned) return;
    footer.style.marginBottom =
      blockCenterLiftPx > 0 ? `${blockCenterLiftPx}px` : "";
  };

  // Only writer of the two px vars. Never calls back into itself:
  // updateScrollToBottomButtonOffset reads them, never writes.
  const syncComposerOverlayMetrics = () => {
    const placement = resolveComposerPlacement(config, isComposerBar());
    const resolved = getWelcomeConfig();
    mount.setAttribute("data-persona-welcome-fullscreen", String(isWelcomeFullscreen()));
    mount.setAttribute("data-persona-welcome-layout", resolved.layout ?? "top");
    mount.setAttribute("data-persona-welcome-layout-explicit", config.welcome?.layout ?? "");
    mount.setAttribute("data-persona-welcome-anchor", resolved.anchor ?? "bottom");
    const footerHidden = footer.style.display === "none";
    const footerHeight = footerHidden ? 0 : footer.offsetHeight;

    const fraction =
      resolved.anchor === "center" && conversationState === "empty"
        ? parseAnchorFraction(resolved.anchorComposerTop) ??
          parseAnchorFraction(DEFAULT_ANCHOR_COMPOSER_TOP)!
        : null;
    // The implicit fullscreen layout centers the complete greeting/composer
    // group. Explicit anchoring retains its percentage-based geometry.
    const centerGroup = fraction !== null && resolved.layout === "centered" &&
      config.welcome?.anchor === undefined &&
      config.welcome?.anchorComposerTop === undefined;
    const measuredBodyStyle = centerGroup ? getComputedStyle(body) : null;
    const previousOverlayHeight = parseFloat(mount.style.getPropertyValue("--persona-composer-overlay-height")) || 0;
    const previousOverlayLift = parseFloat(mount.style.getPropertyValue("--persona-composer-lift")) || 0;
    const groupGap = Math.max(0, (parseFloat(measuredBodyStyle?.paddingBottom ?? "") || 0) -
      (placement === "overlay" ? previousOverlayHeight + previousOverlayLift : 0));
    const columnTop = centerGroup ? body.getBoundingClientRect().top - container.getBoundingClientRect().top : 0;
    const greetingHeight = !centerGroup || welcomeHost.hidden ? 0 :
      (welcomeHost.querySelector<HTMLElement>("[data-persona-welcome-plugin]") ?? welcomeHost).offsetHeight;
    const lift =
      fraction === null || isComposerBar()
        ? 0
        : centerGroup
          ? Math.max(0, Math.round((container.clientHeight - columnTop - greetingHeight - groupGap - footerHeight) / 2))
          : computeComposerLift({
            columnHeight: container.clientHeight,
            footerHeight,
            fraction,
          });

    mount.style.setProperty(
      "--persona-composer-overlay-height",
      `${footerHeight}px`
    );
    mount.style.setProperty(
      "--persona-composer-lift",
      `${placement === "overlay" ? lift : 0}px`
    );
    blockCenterLiftPx = placement === "block" ? lift : 0;
    applyBlockCenterLift();
    syncComposerBarClearance();
    updateScrollToBottomButtonOffset();
    syncTranscriptEdgeFades();
  };

  scrollToBottomControl.update(config, false);

  // Initialized after composer plugins rebind footer DOM (see `bindComposerRefsFromFooter`)
  let attachmentManager: AttachmentManager | null = null;
  // Context mentions orchestrator (core, tiny); lazy-loads the heavy runtime on
  // first use. Null when `contextMentions` is disabled / has no sources.
  let mentionOrchestrator: ContextMentionOrchestrator | null = null;

  // --- Composer state store -------------------------------------------------
  // Single source of truth for composer text/attachments/mentions/phase. Mirrors
  // are pushed in from the input adapter and the attachment manager; nothing
  // re-reads the DOM to answer `getComposerState()`.
  const composerStore = createComposerStore();
  const emitComposerStateEvent = (state: Readonly<ComposerState>): void => {
    try {
      container.dispatchEvent(
        new CustomEvent("persona:composer:state", {
          detail: { ...state, instanceId },
          bubbles: true,
          composed: true,
        })
      );
    } catch {
      /* CustomEvent unavailable — ignore */
    }
  };
  composerStore.subscribe(emitComposerStateEvent);

  /** Mirror the live input value into the store (textarea or inline adapter). */
  const syncComposerText = (): void => {
    composerStore.setText(textarea?.value ?? "");
  };
  const syncComposerMentions = (): void => {
    composerStore.setMentionRefs(mentionOrchestrator?.getMentionRefs() ?? []);
  };
  /**
   * Public projection of one pending attachment: no `File` handle, no content
   * part, no DOM node. The default base64 adapter reports `processing` then
   * `ready`; a host adapter also reports `uploading` with progress and `error`.
   */
  const toComposerAttachmentState = (
    attachment: PendingAttachment
  ): ComposerAttachmentState => ({
    id: attachment.id,
    name: attachment.file.name,
    mimeType: attachment.file.type,
    size: attachment.file.size,
    status: attachment.status,
    progress: attachment.progress,
    error: attachment.error,
  });
  const attachmentManagerConfig = () => ({
    allowedTypes: config.attachments?.allowedTypes,
    maxFileSize: config.attachments?.maxFileSize,
    maxFiles: config.attachments?.maxFiles,
    adapter: config.attachments?.adapter,
    onFileRejected: config.attachments?.onFileRejected,
    instanceId, widgetConfig: config, preview: config.attachments?.preview,
    renderAttachmentPreview: renderAttachmentPreviewForInstance,
  });
  const renderAttachmentPreviewForInstance = (context: import("./types").AttachmentPreviewRenderContext) => {
    for (const plugin of pluginRegistry.getForInstance(config.plugins)) {
      try {
        const rendered = plugin.renderAttachmentPreview?.(context);
        if (rendered) return rendered;
      } catch (error) { console.error(`[AgentWidget] plugin ${plugin.id} attachment preview failed:`, error); }
    }
    return null;
  };
  const createScopedAttachmentManager = (): AttachmentManager => {
    const manager = AttachmentManager.fromConfig(config.attachments, (attachments) => {
      if (attachmentManager === manager) syncComposerAttachments(attachments);
    });
    manager.updateConfig(attachmentManagerConfig());
    return manager;
  };
  const syncComposerAttachments = (attachments: PendingAttachment[]): void => {
    const states = Object.freeze(attachments.map(toComposerAttachmentState));
    composerStore.setAttachments(states);
    // The public change event fires on exactly the attachment transitions, not
    // on every composer-state change.
    try {
      config.attachments?.onChange?.(states);
    } catch (error) {
      console.error("[AgentWidget] attachments.onChange failed:", error);
    }
    syncComposerSendAvailability();
  };

  // --- Model + mode selection (roadmap section 9) ---------------------------
  // Both live in composer state, never in config: config carries the catalog
  // (`composer.models`, `composer.modes`) and the INITIAL selection only.
  composerStore.setSelectedModelId(config.composer?.selectedModelId);

  const composerModes = (): readonly ComposerMode[] => config.composer?.modes ?? [];

  /**
   * Modes that still earn a header chip: a segmented track shows its group's
   * state itself, so a chip beside it would be a second copy of it.
   */
  const composerChipModes = (): readonly ComposerMode[] =>
    chipVisibleComposerModes(composerModes(), config.composer?.modeGroups, Boolean(config.future?.v5Defaults));

  // Authored initial selection. Everything downstream reads the store, and a
  // restored draft overwrites this in `restoreComposerDraft`.
  {
    const seeded = pruneComposerModes(
      config.composer?.defaultActiveModeIds ?? [],
      composerModes()
    );
    if (seeded.length) composerStore.setActiveModeIds(seeded);
  }

  /** Write a selection to the store and notify the host. Config is untouched. */
  function selectComposerModel(modelId: string): void {
    if (composerStore.getState().selectedModelId === modelId) return;
    composerStore.setSelectedModelId(modelId);
    composerCoreControls?.repaint();
    try {
      config.composer?.onModelChange?.(modelId);
    } catch (error) {
      console.error("[AgentWidget] composer.onModelChange failed:", error);
    }
  }

  function setActiveComposerModes(ids: readonly string[]): void {
    composerStore.setActiveModeIds(ids);
    composerModeChips?.render(ids, composerChipModes());
    composerCoreControls?.repaint();
    // `pressed` is read off config at resolve time, so the row must re-resolve.
    composerActionRenderer?.resolve();
    updateCopy();
    syncComposerCompact();
  }

  function toggleComposerModeById(modeId: string): void {
    const root = mount.getRootNode();
    const focused = root instanceof ShadowRoot ? root.activeElement : mount.ownerDocument.activeElement;
    const actionId = composerModeActionId(modeId);
    const menu = composerActionRenderer?.getOverflowMenu();
    const ownsFocus = focused && (
      focused.closest(`[${COMPOSER_ACTION_ATTR}]`)?.getAttribute(COMPOSER_ACTION_ATTR) === actionId ||
      menu?.trigger.contains(focused)
    );
    setActiveComposerModes(
      toggleComposerMode(
        composerStore.getState().activeModeIds,
        modeId,
        composerModes(),
        config.composer?.modeGroups
      )
    );
    // Selecting the last folded mode removes the menu trigger; deselecting a
    // pinned mode can fold its focused button into a detached menu. Keep focus
    // on the equivalent visible control without moving unrelated editor/slot focus.
    if (ownsFocus && !focused.isConnected) {
      const visibleAction = Array.from(mount.querySelectorAll<HTMLElement>(`[${COMPOSER_ACTION_ATTR}]`))
        .find((element) => element.getAttribute(COMPOSER_ACTION_ATTR) === actionId);
      const replacement = visibleAction?.querySelector<HTMLButtonElement>("button") ??
        composerActionRenderer?.getOverflowMenu()?.triggerButton;
      if (replacement?.isConnected) replacement.focus();
    }
  }

  let composerCoreControls: ReturnType<ComposerActionsModule["createComposerCoreControls"]> | null = null;
  let composerModeChips: ReturnType<typeof createComposerModeChipRow> | null = null;

  // --- Quote / reply-to (roadmap section 13) --------------------------------
  let composerQuoteBanner: ReturnType<typeof createComposerQuoteBanner> | null = null;

  function setComposerQuote(quote: ComposerQuote | undefined): void {
    composerStore.setQuote(quote);
    composerQuoteBanner?.render(composerStore.getState().quote);
    syncComposerCompact();
    scheduleDraftPersist();
  }

  // --- Submit during streaming (roadmap section 11) -------------------------
  let warnedInterruptFallback = false;

  /**
   * Effective policy. `interrupt` is a client-token capability (decision 19.1):
   * only that endpoint accepts `turnId` / `submitMode`, so every other transport
   * falls back to `block`.
   */
  function resolveStreamingSubmitBehavior(): ComposerStreamingSubmitBehavior {
    const configured = config.composer?.streamingSubmitBehavior ?? "block";
    if (configured !== "interrupt") return configured;
    if (config.clientToken) return "interrupt";
    if (!warnedInterruptFallback && config.debug === true) {
      warnedInterruptFallback = true;
      if (typeof console !== "undefined") {
        // eslint-disable-next-line no-console
        console.warn(
          '[persona] composer.streamingSubmitBehavior "interrupt" requires the client-token transport; falling back to "block".'
        );
      }
    }
    return "block";
  }

  // --- Deferred submission (roadmap section 11) -----------------------------
  let composerPendingCard: ReturnType<typeof createComposerPendingCard> | null = null;

  function setPendingSubmission(
    snapshot: Readonly<ComposerSubmissionSnapshot> | undefined
  ): void {
    composerStore.setPendingSubmission(snapshot);
    composerPendingCard?.render(composerStore.getState().pendingSubmission);
    syncComposerCompact();
  }

  const collectComposerCoreActions = (): ComposerAction[] => {
    composerCoreControls ??= getComposerActionsSync()!.createComposerCoreControls({
      getConfig: () => config, getState: () => composerStore.getState(),
      getModes: composerModes, getChipModes: composerChipModes,
      hasComposerPlugin: () => plugins.some(plugin => plugin.renderComposer),
      renderIcon: renderLucideIcon, onModelChange: selectComposerModel, onModeChange: toggleComposerModeById,
    });
    return composerCoreControls.collect();
  };

  /** Wired after `handleMicButtonClick` is defined; used by `renderComposer` `onVoiceToggle`. */
  let composerVoiceBridge: (() => void) | null = null;

  // Plugin hook: renderHeader - allow plugins to provide custom header
  const headerPlugin = plugins.find(p => p.renderHeader);
  if (headerPlugin?.renderHeader) {
    const customHeader = headerPlugin.renderHeader({
      config,
      defaultRenderer: () => {
        const headerElements = buildHeader({ config, showClose: isPanelToggleable() });
        attachHeaderToContainer(container, headerElements, config);
        return headerElements.header;
      },
      onClose: () => setOpenState(false, "user")
    });
    if (customHeader) {
      // Replace the default header with custom header. By the live binding, not
      // a container lookup: while a rail is mounted the header sits inside it.
      const existingHeader = header;
      if (existingHeader) {
        existingHeader.replaceWith(customHeader);
        header = customHeader;
        // Keep the view's tracked header element in sync so a later
        // header-layout rebuild (view.replaceHeader) targets the mounted node.
        view.header.element = customHeader;
      }
    }
  }

  // Event stream toggle functions (lifted to outer scope for controller access)
  const mountEventStreamView = () => {
    if (!eventStreamView) return;
    body.style.display = "none";
    footer.parentNode?.insertBefore(eventStreamView.element, footer);
    eventStreamView.update();
  };

  const toggleEventStreamOn = () => {
    if (!eventStreamBuffer) {
      // Runtime chunk not adopted yet (or a failed fetch): retry the load; the
      // user can click again once it lands.
      ensureEventStreamRuntime();
      return;
    }
    latestScroll.cancel();
    eventStreamVisible = true;
    if (!eventStreamView) {
      // The panel lives in a lazy chunk; mount when it resolves, unless the
      // toggle was flipped back off (or the widget destroyed) meanwhile.
      void loadEventStreamView()
        .then((mod) => {
          if (eventStreamView || !eventStreamVisible || !eventStreamBuffer) return;
          eventStreamView = mod.createEventStreamView({
            scroll: { createScrollToLatest, createScrollToBottomControl },
            buffer: eventStreamBuffer,
            getFullHistory: () => eventStreamBuffer!.getAllFromStore(),
            onClose: () => toggleEventStreamOff(),
            config,
            plugins,
            getThroughput: () =>
              throughputTracker?.getMetric() ?? { status: "idle" },
            // Inject the string-name resolver: the chunk carries no registry.
            renderIconByName: renderLucideIcon,
            getResponseActive: isScrollResponseActive,
            getConfig: () => config,
            getViewVisible: () => eventStreamVisible,
          });
          mountEventStreamView();
        })
        .catch(() => {
          // Chunk failure resets the toggle; the next click retries the load.
          toggleEventStreamOff();
        });
    } else {
      mountEventStreamView();
    }
    if (eventStreamToggleBtn) {
      eventStreamToggleBtn.style.boxShadow = `inset 0 0 0 1.5px ${HEADER_THEME_CSS.actionIconColor}`;
      const activeClasses = config.features?.eventStream?.classNames?.toggleButtonActive;
      if (activeClasses) activeClasses.split(/\s+/).forEach(c => c && eventStreamToggleBtn!.classList.add(c));
    }
    // Start RAF-based update loop (throttled to ~200ms)
    const rafLoop = () => {
      if (!eventStreamVisible) return;
      const now = Date.now();
      if (now - eventStreamLastUpdate >= 200) {
        eventStreamView?.update();
        eventStreamLastUpdate = now;
      }
      eventStreamRAF = requestAnimationFrame(rafLoop);
    };
    eventStreamLastUpdate = 0;
    eventStreamRAF = requestAnimationFrame(rafLoop);
    syncScrollToBottomButton();
    eventBus.emit("eventStream:opened", { timestamp: Date.now() });
  };

  const toggleEventStreamOff = () => {
    if (!eventStreamVisible) return;
    eventStreamVisible = false;
    if (eventStreamView) {
      eventStreamView.update();
      eventStreamView.element.remove();
    }
    body.style.display = "";
    if (eventStreamToggleBtn) {
      eventStreamToggleBtn.style.boxShadow = "";
      const activeClasses = config.features?.eventStream?.classNames?.toggleButtonActive;
      if (activeClasses) activeClasses.split(/\s+/).forEach(c => c && eventStreamToggleBtn!.classList.remove(c));
    }
    // Cancel RAF update loop
    if (eventStreamRAF !== null) {
      cancelAnimationFrame(eventStreamRAF);
      eventStreamRAF = null;
    }
    syncScrollToBottomButton();
    eventBus.emit("eventStream:closed", { timestamp: Date.now() });
  };

  // Event stream toggle button
  let eventStreamToggleBtn: HTMLButtonElement | null = null;
  if (showEventStreamToggle) {
    const esClassNames = config.features?.eventStream?.classNames;
    const toggleBtnClasses = "persona-inline-flex persona-items-center persona-justify-center persona-rounded-full hover:persona-opacity-80 persona-cursor-pointer persona-border-none persona-bg-transparent persona-p-1" + (esClassNames?.toggleButton ? " " + esClassNames.toggleButton : "");
    eventStreamToggleBtn = createElement("button", toggleBtnClasses) as HTMLButtonElement;
    eventStreamToggleBtn.style.width = "28px";
    eventStreamToggleBtn.style.height = "28px";
    eventStreamToggleBtn.style.color = HEADER_THEME_CSS.actionIconColor;
    eventStreamToggleBtn.type = "button";
    eventStreamToggleBtn.setAttribute("aria-label", "Event Stream");
    eventStreamToggleBtn.title = "Event Stream";
    const activityIcon = renderIconNode(Activity, "18px", "currentColor", 1.5);
    if (activityIcon) eventStreamToggleBtn.appendChild(activityIcon);

    // Insert before clear chat button wrapper or close button wrapper
    const clearChatWrapper = panelElements.clearChatButtonWrapper;
    const closeWrapper = panelElements.closeButtonWrapper;
    const insertBefore = clearChatWrapper || closeWrapper;
    if (insertBefore && insertBefore.parentNode === header) {
      header.insertBefore(eventStreamToggleBtn, insertBefore);
    } else {
      header.appendChild(eventStreamToggleBtn);
    }

    eventStreamToggleBtn.addEventListener("click", () => {
      if (eventStreamVisible) {
        toggleEventStreamOff();
      } else {
        toggleEventStreamOn();
      }
    });
  }

  const ensureComposerAttachmentSurface = (rootFooter: HTMLElement) => {
    const att = config.attachments;
    if (!att?.enabled) return;
    let previews =
      rootFooter.querySelector<HTMLElement>("[data-persona-composer-attachment-previews]") ??
      rootFooter.querySelector<HTMLElement>(".persona-attachment-previews");
    if (!previews) {
      previews = createElement(
        "div",
        "persona-attachment-previews persona-flex persona-flex-wrap persona-gap-2 persona-mb-2"
      );
      previews.setAttribute("data-persona-composer-attachment-previews", "");
      previews.style.display = "none";
      const form = rootFooter.querySelector("[data-persona-composer-form]");
      if (form?.parentNode) {
        form.parentNode.insertBefore(previews, form);
      } else {
        rootFooter.insertBefore(previews, rootFooter.firstChild);
      }
    }
    const hasFileInput =
      rootFooter.querySelector<HTMLInputElement>("[data-persona-composer-attachment-input]") ??
      rootFooter.querySelector<HTMLInputElement>('input[type="file"]');
    if (!hasFileInput) {
      const fileIn = createElement("input") as HTMLInputElement;
      fileIn.type = "file";
      fileIn.setAttribute("data-persona-composer-attachment-input", "");
      fileIn.accept = (att.allowedTypes ?? ALL_SUPPORTED_MIME_TYPES).join(",");
      fileIn.multiple = (att.maxFiles ?? 4) > 1;
      fileIn.style.display = "none";
      fileIn.setAttribute("aria-label", att.buttonTooltipText ?? "Attach files");
      rootFooter.appendChild(fileIn);
    }
  };

  // Plugin hook: renderComposer - allow plugins to provide custom composer.
  // Arbitration runs inside this function so every rebuild re-arbitrates
  // against the CURRENT registry (`update()` mutates `plugins` in place).
  // True while the footer content came from a plugin (not composition via
  // defaultRenderer). Plugin composers own their copy: updateCopy must not
  // stamp inputPlaceholder/sendButtonLabel onto DOM the core did not build.
  let composerIsPluginOwned = false;
  const renderComposerFromPlugins = (
    defaultRenderer: () => HTMLElement
  ): HTMLElement | null => {
    const composerPlugin = plugins.find(p => p.renderComposer);
    if (!composerPlugin?.renderComposer) return null;
    const composerCfg = config.composer;
    const streaming = sessionInitialized ? session.isStreaming() : false;
    return composerPlugin.renderComposer({
      config,
      defaultRenderer,
      requestRender: () => rebuildComposer(),
      storage: pluginStorageFor(composerPlugin.id),
      onSubmit: (text: string) => {
        if (!session || session.isStreaming()) return;
        // The shared submission path (snapshot + onBeforeSend + dispatch). A
        // plugin composer owns its own input, so the draft is left alone.
        submitText(text.trim(), { consumeDraft: false });
      },
      streaming,
      disabled: streaming,
      openAttachmentPicker: () => {
        if (isComposerInputDisabled()) return;
        attachmentInput?.click();
      },
      models: composerCfg?.models,
      selectedModelId:
        composerStore.getState().selectedModelId ?? composerCfg?.selectedModelId,
      // UI state is not authority: the selection lands in composer state and is
      // snapshotted per send. It writes neither `config.composer` nor
      // `config.agent` — an inline agent takes the model per request instead.
      onModelChange: (modelId: string) => selectComposerModel(modelId),
      onVoiceToggle:
        config.voiceRecognition?.enabled === true
          ? () => {
              composerVoiceBridge?.();
            }
          : undefined
    });
  };

  const customComposer = renderComposerFromPlugins(
    () => buildComposer({ config }).footer
  );
  if (customComposer) {
    // Replace the default footer with custom composer (keeps view.composer.footer in sync).
    view.replaceComposer(customComposer);
    footer = view.composer.footer;
    composerIsPluginOwned = true;
  }

  /**
   * The live composer surface: one object, swapped atomically. Null when the
   * mounted footer has no bindable form/input (a gating plugin composer), in
   * which case nothing composer-scoped stays wired.
   */
  let composerBindings: ComposerSurfaceBindings | null = null;

  /**
   * Legacy locals mirror the binding for the ~200 call sites that still read
   * them. Only FOUND regions are mirrored: a synthesized region must not become
   * the insertion target for controls that previously fell back to the form.
   */
  const mirrorComposerBindings = (next: ComposerSurfaceBindings): void => {
    composerForm = next.form;
    textarea = next.input as HTMLTextAreaElement;
    sendButtonWrapper = next.sendButton
      ? (next.sendButton.parentElement as HTMLElement | null)
      : null;
    if (next.sendButton) sendButton = next.sendButton;
    if (next.micButton) {
      micButton = next.micButton;
      micButtonWrapper = next.micButton.parentElement as HTMLElement | null;
    }
    if (next.found.status) statusText = next.found.status;
    if (next.found.suggestions) suggestions = next.found.suggestions;
    if (next.attachmentButton) {
      attachmentButton = next.attachmentButton;
      attachmentButtonWrapper =
        next.attachmentButton.parentElement as HTMLElement | null;
    }
    attachmentInput = next.attachmentInput ?? null;
    attachmentPreviewsContainer = next.attachmentPreviews ?? null;
    if (next.found.actionsRow) _actionsRow = next.found.actionsRow;
    leftActions = next.found.actionsStart;
    rightActions = next.found.actionsEnd;
  };

  /**
   * Push `composer.submitKey`, `composer.inputAttributes`, and
   * `composer.maxLines` onto the live editor. Config read at build time must
   * re-sync, so this runs on mount, on every rebuild, after the inline
   * contenteditable swap, and from `controller.update()`.
   */
  function applyComposerInputConfig(): void {
    const el = textarea as unknown as HTMLElement | null;
    if (!el) return;
    const attributes = config.composer?.inputAttributes;
    // The inline editor derives its accessible name from the placeholder when
    // the host sets none; re-applying the allowlist must not strip that.
    const derivedLabel =
      !attributes?.ariaLabel && !(el instanceof HTMLTextAreaElement)
        ? el.getAttribute("aria-label")
        : null;
    applyComposerInputAttributes(el, attributes);
    if (derivedLabel) el.setAttribute("aria-label", derivedLabel);
    applyComposerEnterKeyHint(el, {
      submitKey: config.composer?.submitKey,
      insertNewlineOnTouchEnter: config.composer?.insertNewlineOnTouchEnter,
      coarsePointer: isCoarsePointer(el),
    });
    applyComposerMaxLines(
      el,
      resolveMaxLines(
        config.composer?.maxLines,
        isComposerBar() ? PILL_COMPOSER_MAX_LINES : DEFAULT_COMPOSER_MAX_LINES
      )
    );
  }

  const bindComposerRefsFromFooter = (rootFooter: HTMLElement) => {
    composerBindings = bindComposerSurface(rootFooter, {
      debug: config.debug === true,
    });
    if (composerBindings) mirrorComposerBindings(composerBindings);
    applyComposerInputConfig();
  };
  ensureComposerAttachmentSurface(footer);
  bindComposerRefsFromFooter(footer);
  syncComposerText();

  // Center the content column. Defaults: 768px panels, 720px composer-bar
  // (resolveContentMaxWidth); "none" opts out. Only engages on panels wider
  // than the cap, so launcher-width panels are unaffected.
  const contentMaxWidth = resolveContentMaxWidth(config, isComposerBar());
  if (contentMaxWidth) {
    messagesWrapper.style.maxWidth = contentMaxWidth;
    messagesWrapper.style.marginLeft = "auto";
    messagesWrapper.style.marginRight = "auto";
    messagesWrapper.style.width = "100%";
    // The greeting bubble is a transcript sibling: same column geometry.
    greetingHost.style.maxWidth = contentMaxWidth;
    greetingHost.style.marginLeft = "auto";
    greetingHost.style.marginRight = "auto";
    greetingHost.style.width = "100%";
    starterSuggestions.style.maxWidth = contentMaxWidth;
    transcriptSuggestions.style.maxWidth = contentMaxWidth;
    transcriptSuggestions.style.marginLeft = "auto";
    transcriptSuggestions.style.marginRight = "auto";
    transcriptSuggestions.style.width = "100%";
  }
  // The pill IS the composer in composer-bar mode and should match the
  // wrapper's responsive width (50vw / 70vw / 90vw), not be capped by
  // contentMaxWidth (which is a centered-column convention for the
  // expanded panel's body, not the pill input itself).
  // Re-run after a composer rebuild: the footer's elements are new.
  const applyComposerContentMaxWidth = () => {
    const max = resolveContentMaxWidth(config, isComposerBar());
    if (!max || isComposerBar()) return;
    for (const element of [composerForm, suggestions, attachmentPreviewsContainer, statusText]) {
      if (!element) continue;
      element.style.maxWidth = max;
      element.style.marginLeft = "auto";
      element.style.marginRight = "auto";
      // Auto cross-axis margins defeat flex stretch: the previews container
      // sits inside the flex-column form and would shrink-center without this.
      element.style.width = "100%";
    }
  };
  applyComposerContentMaxWidth();

  /**
   * Bind the attachment surface of the CURRENT footer. On a rebuild the manager
   * survives and its pending previews are repainted into the new container.
   */
  const setupComposerAttachments = () => {
    if (!config.attachments?.enabled || !attachmentInput || !attachmentPreviewsContainer) {
      return;
    }
    if (!attachmentManager) {
      attachmentManager = createScopedAttachmentManager();
      attachmentManager.setPreviewsContainer(attachmentPreviewsContainer);
    } else {
      attachmentManager.remountPreviews(attachmentPreviewsContainer);
    }
    composerBindings?.addListener(attachmentInput, "change", (e) => {
      const target = e.target as HTMLInputElement;
      attachmentManager?.handleFileSelect(target.files);
      target.value = "";
    });

    if (!container.querySelector(".persona-attachment-drop-overlay")) {
      container.appendChild(buildDropOverlay(config.attachments.dropOverlay));
    }
  };
  setupComposerAttachments();

  // Context mentions: render the affordance button + chip row eagerly (so the
  // feature is discoverable) and lazy-load the heavy runtime on first use. The
  // orchestrator returns null when disabled or sourceless.
  const mentionPrefetch = () => mentionOrchestrator?.prefetch();
  /**
   * Create the orchestrator for the CURRENT composer input. A rebuild destroys
   * the previous one (its bindings point at the detached textarea), so any
   * uncommitted mention chips are cleared with it.
   */
  const setupMentionOrchestrator = () => {
    if (!config.contextMentions?.enabled || !textarea) return;
    // The orchestrator rides the lazy ui-extras chunk on the CDN: mount it (and
    // re-collect the composer actions for its affordance buttons) once the
    // chunk lands, unless a composer rebuild replaced this textarea meanwhile
    // (the rebuild re-runs this function) or the widget was destroyed.
    const extras = getUiExtrasSync();
    if (!extras) {
      const forTextarea = textarea;
      loadUiExtras().then(() => {
        if (mentionOrchestrator || textarea !== forTextarea || !forTextarea.isConnected) return;
        setupMentionOrchestrator();
        composerActionRenderer?.resolve();
      }, () => {});
      return;
    }
    // Slash-command dispatch (prompt macros write text / submit; client actions
    // read/replace the value) and submission are owned by the composer input
    // surface itself — the mention runtime builds a textarea (chip) or
    // contenteditable (inline) adapter from this textarea. Broader host actions
    // (clear transcript, theme) are still wired via closures over the controller.
    // Mention chips share the header chip row with the mode chips: one rail,
    // modes first. Without a header region (a plugin surface that binds none)
    // the orchestrator falls back to its own row, placed above the editor.
    const header = composerBindings?.header ?? null;
    const chipRow = header ? ensureComposerChipRow(header) : undefined;

    mentionOrchestrator = extras.createContextMentionOrchestrator({
      // Core's stateful collaborators, not the chunk's copies.
      deps: { loadContextMentions, loadContextMentionsInline, renderLucideIcon, createMentionButton },
      config,
      textarea,
      anchor: composerForm ?? textarea,
      getMessages: () => session.getMessages(),
      // The engine chunk creates the mention live regions in this container on
      // mount, keeping the live-region helper out of the core bundle.
      liveRegionHost: container,
      chipRow,
    });

    if (mentionOrchestrator) {
      if (!chipRow) {
        const ta = textarea;
        ta.parentElement?.insertBefore(mentionOrchestrator.contextRow, ta);
      }
      // Each channel's "add context" affordance is a secondary control that
      // augments the outgoing message, so it joins the LEFT action cluster
      // (beside the attachment button) — never the right cluster with mic +
      // send. Buttons are inserted leftmost in channel order so they read as
      // "add to my message" and stay clear of the primary send action. Both
      // composer builders (full + pill) always ship `leftActions`; the form
      // fallback is purely defensive.
      // Placement is the action registry's job (order 100 + channel index):
      // `setupComposerActions` inserts them leftmost, in channel order. This
      // function stays the source of their click behavior.
      //
      // The focus-prefetch listener (warm the chunk on first focus so the first
      // `@` is instant) is registered through the shared `composerListeners`
      // registry below, so it survives the inline contenteditable swap too.
    }
  };
  setupMentionOrchestrator();

  // --- Composer action registry (roadmap section 6) --------------------------
  // One ordered row from three contributors: core built-ins, host
  // `composer.actions`, and every plugin's `contributeComposerActions`.
  let composerActionRenderer: ComposerActionRenderer | null = null;

  const composerActionContext: ComposerActionContext = {
    getState: () => composerStore.getState(),
    getValue: () => textarea?.value ?? "",
    setValue: (value: string) => {
      if (!textarea) return;
      textarea.value = value;
      // Same programmatic-edit shape as history recall: auto-resize and the
      // mention parser both listen for `input`.
      textarea.dispatchEvent(new Event("input", { bubbles: true }));
      syncComposerText();
    },
    submit: () => {
      requestFormSubmit(composerBindings?.form ?? null);
    },
    openAttachmentPicker: () => {
      if (isComposerInputDisabled()) return;
      attachmentInput?.click();
    },
    toggleVoice: () => {
      composerVoiceBridge?.();
    },
    requestRender: () => composerActionRenderer?.resolve(),
  };

  /**
   * Core controls, in documented order. Only the mention affordances are
   * `managed` (the registry inserts them); attachment, mic, and send are
   * positional placeholders whose elements the composer builders already
   * mounted, so built-in DOM is byte-identical to before.
   */
  const collectComposerBuiltIns = (): ComposerBuiltInDescriptor[] => {
    const builtIns: ComposerBuiltInDescriptor[] = [];
    const affordances = mentionOrchestrator?.affordanceButtons ?? [];
    affordances.forEach((element, index) => {
      builtIns.push({
        id: `core:mention-${index}`,
        placement: "start",
        order: COMPOSER_ACTION_ORDER.mention + index,
        element,
        managed: true,
      });
    });
    if (attachmentButtonWrapper) {
      builtIns.push({
        id: "core:attachment",
        placement: "start",
        order: COMPOSER_ACTION_ORDER.attachment,
        element: attachmentButtonWrapper,
      });
    }
    if (micButtonWrapper) {
      builtIns.push({
        id: "core:mic",
        placement: "end",
        order: COMPOSER_ACTION_ORDER.mic,
        element: micButtonWrapper,
      });
    }
    if (sendButtonWrapper) {
      builtIns.push({
        id: "core:send",
        placement: "end",
        order: COMPOSER_ACTION_ORDER.send,
        element: sendButtonWrapper,
      });
    }
    return builtIns;
  };

  /** Rebuild the renderer against the CURRENT bindings and resolve once. */
  let composerActionsTornDown = false;
  const setupComposerActions = (): void => {
    if (composerActionsTornDown) return;
    const runtime = getComposerActionsSync();
    if (!runtime) return;
    composerActionRenderer?.destroy();
    composerActionRenderer = runtime.createComposerActionRenderer({
      deps: { renderLucideIcon, attachTooltip, hideTooltipFor },
      getBindings: () => composerBindings,
      collect: () => ({
        builtIns: collectComposerBuiltIns(),
        // Core-contributed controls (modes, model picker) first, so a host
        // action reusing one of their ids loses the claim deterministically.
        configActions: [
          ...collectComposerCoreActions(),
          ...(config.composer?.actions ?? []),
        ],
        plugins,
        contributionContext: {
          config,
          getState: () => composerStore.getState(),
          requestRender: () => composerActionRenderer?.resolve(),
        },
        debug: config.debug === true,
      }),
      actionContext: composerActionContext,
      getState: () => composerStore.getState(),
      // No getButtonSize: registry buttons and the overflow trigger size from
      // `--persona-composer-control-size` like every other composer control.
      getOverflow: () => config.composer?.actionOverflow,
      reportError: (error, info) => {
        if (typeof console !== "undefined") {
          // eslint-disable-next-line no-console
          console.error(
            `[AgentWidget] composer action "${info.actionId}" failed:`,
            error
          );
        }
      },
    });
    composerActionRenderer.resolve();
  };
  setupComposerActions();

  /**
   * Mount the active-mode chips into the CURRENT header's shared chip row (the
   * one mention chips also render into). Rebuilt with the composer: the
   * outgoing header is detached with the old footer.
   */
  const setupComposerModeChips = (): void => {
    composerModeChips?.destroy();
    composerModeChips = null;
    if (!composerChipModes().length) return;
    const header = composerBindings?.header;
    if (!header) return;
    const runtime = getComposerActionsSync();
    if (!runtime) return;
    composerModeChips = runtime.createComposerModeChipRow({
      renderIcon: renderLucideIcon,
      row: ensureComposerChipRow(header),
      onRemove: (modeId) => toggleComposerModeById(modeId),
      // A rebuild hands the new row the chips the user is already looking at;
      // those must not replay their entrance.
      initialIds: composerStore.getState().activeModeIds,
    });
    composerModeChips.render(
      composerStore.getState().activeModeIds,
      composerChipModes()
    );
  };
  setupComposerModeChips();

  /**
   * Quote banner + deferred-submission card share the header region with the
   * mode chips, and are rebuilt with the composer for the same reason: the
   * outgoing header is detached with the old footer.
   */
  const setupComposerHeaderCards = (): void => {
    composerQuoteBanner?.destroy();
    composerQuoteBanner = null;
    composerPendingCard?.destroy();
    composerPendingCard = null;
    const header = composerBindings?.header;
    if (!header) return;
    composerQuoteBanner = createComposerQuoteBanner({
      onDismiss: () => setComposerQuote(undefined),
    });
    header.appendChild(composerQuoteBanner.element);
    composerQuoteBanner.render(composerStore.getState().quote);

    if (resolveStreamingSubmitBehavior() === "defer-one") {
      composerPendingCard = createComposerPendingCard({
        onEdit: () => restorePendingSubmissionToDraft(),
        onRemove: () => setPendingSubmission(undefined),
      });
      header.appendChild(composerPendingCard.element);
      composerPendingCard.render(composerStore.getState().pendingSubmission);
    }
  };
  setupComposerHeaderCards();

  // Draft expansion and action-width fit have separate lifetimes: the wrap
  // latch survives edits, but a narrow host must recover when it widens.
  const composerCompactLatch = createComposerCompactLatch();
  const composerFit = createComposerFitObserver({
    getSurface: () => {
      if (isComposerBar() || !composerBindings) return null;
      return {
        form: composerBindings.form,
        input: composerBindings.input,
        start: composerBindings.actionsStart,
        end: composerBindings.actionsEnd,
        resizeInput: config.composer?.layout === "single-row",
      };
    },
    onMeasure: () => syncComposerCompact(),
  });
  /**
   * Chips of either kind, read from the one shared header row. Falls back to
   * composer state on a surface that binds no header (no row to read).
   */
  const hasComposerChips = (): boolean => {
    const row = composerBindings?.header?.querySelector<HTMLElement>(
      COMPOSER_CHIP_ROW_SELECTOR
    );
    if (row) return row.childElementCount > 0;
    const state = composerStore.getState();
    return (
      state.mentionRefs.length > 0 ||
      pruneComposerModes(state.activeModeIds, composerChipModes()).length > 0
    );
  };
  function syncComposerCompact(): void {
    const state = composerStore.getState();
    const measuredFit = composerFit.measure();
    const fits = config.composer?.layout !== "single-row" && config.composer?.attachmentLayout !== "single-row" || measuredFit;
    const crowded = composerFit.toolbarCrowded();
    if (footer.hasAttribute("data-persona-composer-crowded") !== crowded) {
      footer.toggleAttribute("data-persona-composer-crowded", crowded);
    }
    if (footer.hasAttribute("data-persona-composer-fit") !== fits) {
      footer.toggleAttribute("data-persona-composer-fit", fits);
    }
    const wrapped = composerCompactLatch.observe(
      fits && isEditorWrapped(textarea as unknown as HTMLElement | null),
      state.text
    );
    footer.toggleAttribute("data-persona-has-attachments", state.attachments.length > 0);
    footer.toggleAttribute("data-persona-attachment-row-compact", isComposerCompact({ text: state.text, wrapped, hasAttachments: false, hasChips: hasComposerChips(), hasQuote: state.quote !== undefined, hasPendingSubmission: state.pendingSubmission !== undefined }));
    const compact = isComposerCompact({
      text: state.text,
      wrapped,
      hasAttachments: state.attachments.length > 0,
      hasChips: hasComposerChips(),
      hasQuote: state.quote !== undefined,
      hasPendingSubmission: state.pendingSubmission !== undefined,
    });
    if (footer.hasAttribute("data-persona-composer-compact") !== compact) {
      footer.toggleAttribute("data-persona-composer-compact", compact);
    }
    const expanded = !compact || !fits || config.composer?.layout !== "single-row";
    if (footer.hasAttribute("data-persona-composer-expanded") !== expanded) {
      footer.toggleAttribute("data-persona-composer-expanded", expanded);
    }
  }

  /**
   * `composer.layout`. Stamped only when `"single-row"` is configured: an unset
   * layout must leave the footer untouched so host CSS keeps owning the form.
   * The pill composer is already one row and owns its own grid.
   */
  function syncComposerLayout(): void {
    if (config.composer?.layout === "single-row" && !isComposerBar()) {
      footer.setAttribute("data-persona-composer-layout", "single-row");
    } else {
      footer.removeAttribute("data-persona-composer-layout");
    }
    if (config.composer?.attachmentLayout) footer.setAttribute("data-persona-attachment-layout", config.composer.attachmentLayout);
    else footer.removeAttribute("data-persona-attachment-layout");
    syncComposerCompact();
  }
  syncComposerLayout();

  // `visible`/`disabled` predicates re-evaluate on every composer-state change,
  // and the send button follows the same state (locks + attachment readiness).
  composerStore.subscribe(() => {
    composerActionRenderer?.sync();
    syncComposerSendAvailability();
    syncSendButtonVisibility();
    syncComposerCompact();
    scheduleDraftPersist();
  });
  syncSendButtonVisibility();

  // `messages` and `composer` are public but have never been implemented;
  // insertSlotContent drops them. Warn once per widget instead of silently
  // ignoring a configured renderer.
  let warnedInertSlots = false;
  const warnInertSlots = (slots: Partial<Record<WidgetLayoutSlot, SlotRenderer>>) => {
    if (warnedInertSlots || config.debug !== true) return;
    const inert = (["messages", "composer"] as const).filter((slot) => slots[slot]);
    if (!inert.length) return;
    warnedInertSlots = true;
    console.warn(
      `[persona] Ignoring deprecated layout.slots.${inert.join(" / layout.slots.")}. Use message render hooks or the renderComposer plugin hook.`
    );
  };

  // Preserve replacement-slot defaults so null/removal restores the same nodes.
  const slotDefaults = new Map<WidgetLayoutSlot, { anchor: Comment; element: HTMLElement }>();
  const slotWrappers = new Map<WidgetLayoutSlot, HTMLElement>();
  const defaultSlotContent = (slot: WidgetLayoutSlot): HTMLElement | null => {
    const saved = slotDefaults.get(slot);
    if (saved) return saved.element;
    let element: HTMLElement | null = null;
    if (slot === "body-top") element = body.querySelector("[data-persona-intro-card]");
    if (slot === "messages") return messagesWrapper;
    if (slot === "composer") return composerForm;
    if (slot === "footer-top") element = suggestions;
    if (slot === "footer-bottom") element = statusText;
    if (element?.parentNode) {
      const anchor = document.createComment(`persona:${slot}`);
      element.before(anchor);
      slotDefaults.set(slot, { anchor, element });
    }
    return element;
  };
  const slots = createLayoutSlots({
    config: () => config,
    defaultContent: defaultSlotContent,
    remove(slot, element) {
      // Unsupported slots never own DOM, even if their renderer returns a core node.
      if (slot === "messages" || slot === "composer") return;
      element?.remove();
      slotWrappers.get(slot)?.remove();
      slotWrappers.delete(slot);
      const saved = slotDefaults.get(slot);
      if (saved) {
        saved.anchor.replaceWith(saved.element);
        slotDefaults.delete(slot);
      }
    },
    mount(slot, element) {
      // Retain the legacy inert-slot invocation but never attach its output.
      if (slot === "messages" || slot === "composer") return;
      if (element.parentNode && wrapper.contains(element)) return;
      if (slot === "body-top" || slot === "footer-top" || slot === "footer-bottom") {
        const original = defaultSlotContent(slot);
        const saved = slotDefaults.get(slot);
        if (original && saved?.anchor.parentNode) {
          saved.anchor.after(element);
          if (original !== element && !element.contains(original)) original.remove();
          return;
        }
      }
      switch (slot) {
        case "header-left": header.prepend(element); break;
        case "header-right": header.append(element); break;
        case "header-center": {
          const title = header.querySelector(".persona-flex-col");
          if (title) title.after(element); else header.append(element);
          break;
        }
        case "body-top": body.prepend(element); break;
        case "body-bottom": body.append(element); break;
        case "footer-top": footer.prepend(element); break;
        case "footer-bottom": footer.append(element); break;
        case "transcript-top": {
          const host = document.createElement("div");
          host.setAttribute("data-persona-layout-slot", slot);
          host.style.cssText = "width:100%;min-width:0;max-width:var(--persona-content-max-width, 100%);margin-inline:auto;flex-shrink:0";
          host.append(element);
          messagesWrapper.before(host);
          slotWrappers.set(slot, host);
          break;
        }
        case "composer-start":
        case "composer-end":
        case "composer-before-send": {
          const target = slot === "composer-start" ? leftActions :
            slot === "composer-end" ? rightActions : sendButtonWrapper?.parentElement;
          if (!target) break;
          const host = document.createElement("div");
          host.setAttribute("data-persona-layout-slot", slot);
          host.style.cssText = "display:flex;align-items:center;min-width:0;max-width:100%";
          host.append(element);
          if (slot === "composer-start") target.prepend(host);
          else if (slot === "composer-before-send") sendButtonWrapper?.before(host);
          else target.append(host);
          slotWrappers.set(slot, host);
          break;
        }
      }
    },
  });
  warnInertSlots(config.layout?.slots ?? {});
  slots.update();

  const routeLinkClick = (event: MouseEvent) => {
    if (!config.onLinkClick || event.defaultPrevented || event.button !== 0 ||
        event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const anchor = target.closest<HTMLAnchorElement>("a[href]");
    if (!anchor || !mount.contains(anchor) || anchor.hasAttribute("download")) return;
    if (anchor.closest("[data-persona-root]") !== mount) return;
    const linkTarget = anchor.getAttribute("target") ?? anchor.ownerDocument.querySelector("base[target]")?.getAttribute("target");
    if (linkTarget && linkTarget.toLowerCase() !== "_self") return;
    if (!/^(https?:|mailto:|tel:)/i.test(anchor.href)) return;
    try {
      config.onLinkClick({ url: anchor.href, anchor, originalEvent: event,
        preventDefault: () => event.preventDefault() });
    } catch (error) { console.error("[AgentWidget] onLinkClick failed:", error); }
  };
  mount.addEventListener("click", routeLinkClick);

  messagesWrapper.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest<HTMLButtonElement>("button[data-persona-copy-tool-detail]");
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    void copyToolDetail(button);
  });

  // Approval plugins own their disclosure clicks. Pause follow in capture,
  // before their listener grows the details and ResizeObserver follows it.
  messagesWrapper.addEventListener("click", (event) => {
    const target = event.target;
    if (target instanceof Element && target.closest('.persona-approval-head[data-action="toggle-params"]')) {
      markReaderEngaged();
      pauseAutoScroll();
    }
  }, { capture: true });

  // "Show more" / "Show less" on a long user message. Toggling is reading
  // intent like a tool disclosure: it pauses follow and never re-anchors.
  const setUserCollapseExpanded = (bubble: HTMLElement, expanded: boolean) => {
    const region = bubble.querySelector<HTMLElement>(`[${USER_COLLAPSE_REGION_ATTR}]`);
    const messageId = bubble.getAttribute("data-message-id");
    if (!region || !messageId) return;
    markReaderEngaged();
    pauseAutoScroll();
    userCollapseToggled.set(messageId, expanded);
    applyUserCollapseChange(bubble, region, messageId, {
      state: expanded ? "expanded" : "collapsed",
      maxHeight: resolveUserMessageCollapseMaxHeight(config),
      ...resolveUserMessageCollapseLabels(config),
    }, "toggle");
  };
  messagesWrapper.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const toggle = target.closest<HTMLButtonElement>(`button[${USER_COLLAPSE_TOGGLE_ATTR}]`);
    const bubble = toggle?.closest<HTMLElement>("[data-message-id]");
    if (!toggle || !bubble) return;
    event.preventDefault();
    setUserCollapseExpanded(bubble, toggle.getAttribute("aria-expanded") !== "true");
  });
  // Keyboard focus landing on a link or control inside the clipped part
  // expands the message, so a focused element is never hidden behind the clip.
  messagesWrapper.addEventListener("focusin", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const region = target.closest(`[${USER_COLLAPSE_REGION_ATTR}="collapsed"]`);
    const bubble = region?.closest<HTMLElement>("[data-message-id]");
    if (bubble) setUserCollapseExpanded(bubble, true);
  });

  // Add event delegation for reasoning and tool bubble expansion
  // This handles clicks even after idiomorph morphs the DOM
  const handleBubbleExpansion = (event: Event) => {
    const target = event.target as HTMLElement;

    // Check if the click/keypress is on an expand header button
    const headerButton = target.closest('button[data-expand-header="true"]') as HTMLElement;
    if (headerButton) toggleBubbleHeader(headerButton);
  };

  const toggleBubbleHeader = (headerButton: HTMLElement) => {
    // Find the parent bubble element
    const bubble = headerButton.closest('.persona-reasoning-bubble, .persona-tool-bubble, .persona-approval-bubble') as HTMLElement;
    if (!bubble) return;

    // Get message ID from bubble
    const messageId = bubble.getAttribute('data-message-id');
    if (!messageId) return;

    // Disclosure is reading intent, not new streamed content. Pause before the
    // DOM grows so the resize observer cannot pull the opened details away.
    markReaderEngaged();
    pauseAutoScroll();
    const anchor = anchorState;
    if (anchor) cancelSmoothScroll();
    const finishDisclosure = anchor && activityUi?.holdScroll(
      bubble, body, anchor, measureContentHeight, setAnchorSpacerHeight,
      BOTTOM_THRESHOLD, () => anchorState === anchor
    );
    const bubbleType = headerButton.getAttribute('data-bubble-type');
    if (bubble.classList.contains("persona-activity-row")) {
      activityLifecycle?.manual(messageId);
      const group = bubble.parentElement?.closest<HTMLElement>("[data-persona-tool-group]");
      if (group?.dataset.messageId) activityLifecycle?.manual(group.dataset.messageId);
    }


    // Toggle expansion state
    if (bubbleType === 'reasoning' || bubbleType === 'tool') {
      const reasoning = bubbleType === 'reasoning';
      const expansionState = reasoning ? reasoningExpansionState : toolExpansionState;
      if (expansionState.has(messageId)) expansionState.delete(messageId);
      else expansionState.add(messageId);
      if (activityUi) (reasoning ? activityUi.updateReasoningBubbleUI : activityUi.updateToolBubbleUI)(
        messageId, bubble, expansionState, config
      );
    } else if (bubbleType === 'approval' && approvalUi) {
      // approvalUi is always set here: approval bubbles only exist after the
      // chunk was adopted. The guard keeps a stray click on a stub harmless.
      const approvalConfig = config.approval !== false ? config.approval : undefined;
      const defaultExpanded = (approvalConfig?.detailsDisplay ?? 'collapsed') === 'expanded';
      const expanded = approvalDetailsExpansionState.get(messageId) ?? defaultExpanded;
      approvalDetailsExpansionState.set(messageId, !expanded);
      approvalUi.updateApprovalDetailsUI(messageId, bubble, config, approvalDetailsExpansionState);
    }
    finishDisclosure?.();
    // Invalidate cached wrapper so next render rebuilds with current expansion state
    messageCache.delete(messageId);
  };

  // Attach event listeners to messagesWrapper for event delegation
  messagesWrapper.addEventListener('pointerdown', (event) => {
    const target = event.target as HTMLElement;
    if (target.closest('button[data-expand-header="true"]')) {
      event.preventDefault();
      handleBubbleExpansion(event);
    }
  });

  messagesWrapper.addEventListener('click', (event) => {
    // Assistive technologies activate buttons with a click rather than a pointer.
    if (event.detail === 0 && (event.target as HTMLElement).closest('.persona-activity-row')) handleBubbleExpansion(event);
  });

  messagesWrapper.addEventListener('keydown', (event) => {
    const target = event.target as HTMLElement;
    if ((event.key === 'Enter' || event.key === ' ') && target.closest('button[data-expand-header="true"]')) {
      event.preventDefault();
      handleBubbleExpansion(event);
    }
  });

  // Normalize manual (triple-click + Ctrl/Cmd-C) copies of message text. The
  // browser serializes the DOM selection, and block-level markdown elements
  // (<p>, <li>, <pre>, …) emit surrounding newlines — so a single-message copy
  // arrives with stray trailing/leading blank lines. Rewrite the clipboard's
  // plain text to the trimmed selection so the buffer matches what was visibly
  // highlighted. (The Copy action button is unaffected; it uses message.content.)
  messagesWrapper.addEventListener('copy', (event) => {
    const { clipboardData } = event;
    if (!clipboardData) return;
    const root = messagesWrapper.getRootNode() as { getSelection?: () => Selection | null };
    const selection =
      typeof root.getSelection === 'function' ? root.getSelection() : window.getSelection();
    if (!selection || selection.isCollapsed) return;
    const raw = selection.toString();
    const normalized = normalizeCopiedSelectionText(raw);
    if (!normalized || normalized === raw) return;
    clipboardData.setData('text/plain', normalized);
    event.preventDefault();
  });

  // Add event delegation for message action buttons (upvote, downvote, copy)
  // This handles clicks even after idiomorph morphs the DOM and strips inline listeners
  const messageVoteState = new Map<string, "upvote" | "downvote">();

  // Reveal state belongs to the message, not its current DOM node: streaming
  // renders can morph or replace the bubble before the highlight expires.
  const revealedToolCalls = new Map<string, number>();
  let revealCenterTimer: number | undefined;
  let revealScrollGeneration = 0;
  const refreshRevealedToolCalls = () => {
    messagesWrapper.querySelectorAll<HTMLElement>(".persona-tool-bubble[data-tool-call-id]").forEach((bubble) => {
      bubble.toggleAttribute("data-persona-revealed", revealedToolCalls.has(bubble.dataset.messageId!));
    });
  };
  const clearRevealedToolCalls = () => {
    window.clearTimeout(revealCenterTimer);
    revealCenterTimer = undefined;
    revealedToolCalls.forEach((timer) => window.clearTimeout(timer));
    revealedToolCalls.clear();
    refreshRevealedToolCalls();
  };

  // Read-aloud (text-to-speech) button state. The ReadAloudController in the
  // session is the source of truth; these mirror its last-known state so the
  // button visuals can be re-applied after every render/morph (which would
  // otherwise revert the swapped icon to the default "volume-2").
  let readAloudActiveId: string | null = null;
  let readAloudActiveState: ReadAloudState = "idle";

  const READ_ALOUD_ICONS: Record<ReadAloudState, { icon: string; label: string }> = {
    idle: { icon: "volume-2", label: "Read aloud" },
    loading: { icon: "loader-circle", label: "Loading…" },
    playing: { icon: "pause", label: "Pause" },
    paused: { icon: "play", label: "Resume" },
  };

  const applyReadAloudButton = (btn: HTMLElement, state: ReadAloudState) => {
    const { icon, label } = READ_ALOUD_ICONS[state];
    btn.setAttribute("aria-label", label);
    btn.title = label;
    btn.setAttribute("aria-pressed", state === "idle" ? "false" : "true");
    btn.classList.toggle("persona-message-action-active", state !== "idle");
    btn.classList.toggle("persona-message-action-loading", state === "loading");
    const svg = renderLucideIcon(icon, 14, "currentColor", 2);
    if (svg) {
      btn.innerHTML = "";
      btn.appendChild(svg);
    }
  };

  // Re-apply the current read-aloud state to every read-aloud button in the
  // thread. Called on state change and after each render so a button that is
  // playing/paused keeps its icon across DOM morphs.
  const refreshReadAloudButtons = () => {
    const buttons = messagesWrapper.querySelectorAll<HTMLElement>('[data-action="read-aloud"]');
    buttons.forEach((btn) => {
      const container = btn.closest("[data-actions-for]");
      const id = container?.getAttribute("data-actions-for") ?? null;
      const state: ReadAloudState = id && id === readAloudActiveId ? readAloudActiveState : "idle";
      applyReadAloudButton(btn, state);
    });
  };

  messagesWrapper.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const actionBtn = target.closest('.persona-message-action-btn[data-action]') as HTMLElement;
    if (!actionBtn) return;

    event.preventDefault();
    event.stopPropagation();

    const actionsContainer = actionBtn.closest('[data-actions-for]') as HTMLElement;
    if (!actionsContainer) return;

    const messageId = actionsContainer.getAttribute('data-actions-for');
    if (!messageId) return;

    const action = actionBtn.getAttribute('data-action');

    if (action === 'copy') {
      const messages = session.getMessages();
      const message = messages.find(m => m.id === messageId);
      if (message && messageActionCallbacks.onCopy) {
        // Copy to clipboard
        const textToCopy = message.content || "";
        navigator.clipboard.writeText(textToCopy).then(() => {
          // Show success feedback - swap icon temporarily
          actionBtn.classList.add("persona-message-action-success");
          const checkIcon = renderIconNode(Check, 14, "currentColor", 2);
          if (checkIcon) {
            actionBtn.innerHTML = "";
            actionBtn.appendChild(checkIcon);
          }
          setTimeout(() => {
            actionBtn.classList.remove("persona-message-action-success");
            const originalIcon = renderIconNode(Copy, 14, "currentColor", 2);
            if (originalIcon) {
              actionBtn.innerHTML = "";
              actionBtn.appendChild(originalIcon);
            }
          }, 2000);
        }).catch((err) => {
          if (typeof console !== "undefined") {
            // eslint-disable-next-line no-console
            console.error("[AgentWidget] Failed to copy message:", err);
          }
        });
        messageActionCallbacks.onCopy(message);
      }
    } else if (action === 'read-aloud') {
      // Toggle play/pause/resume; ReadAloudController drives the engine and
      // notifies onReadAloudChange, which refreshes the button icon.
      session.toggleReadAloud(messageId);
    } else if (action === 'upvote' || action === 'downvote') {
      const currentVote = messageVoteState.get(messageId) ?? null;
      const wasActive = currentVote === action;
      const iconName = action === 'upvote' ? 'thumbs-up' : 'thumbs-down';

      if (wasActive) {
        // Toggle off: revert to outline icon
        messageVoteState.delete(messageId);
        actionBtn.classList.remove("persona-message-action-active");
        const outlineIcon = renderLucideIcon(iconName, 14, "currentColor", 2);
        if (outlineIcon) {
          actionBtn.innerHTML = "";
          actionBtn.appendChild(outlineIcon);
        }
      } else {
        // Clear opposite vote button and revert its icon
        const oppositeAction = action === 'upvote' ? 'downvote' : 'upvote';
        const oppositeBtn = actionsContainer.querySelector(`[data-action="${oppositeAction}"]`);
        if (oppositeBtn) {
          oppositeBtn.classList.remove("persona-message-action-active");
          const oppositeIconName = oppositeAction === 'upvote' ? 'thumbs-up' : 'thumbs-down';
          const outlineIcon = renderLucideIcon(oppositeIconName, 14, "currentColor", 2);
          if (outlineIcon) {
            oppositeBtn.innerHTML = "";
            oppositeBtn.appendChild(outlineIcon);
          }
        }

        messageVoteState.set(messageId, action);
        actionBtn.classList.add("persona-message-action-active");

        // Swap to filled icon
        const filledIcon = renderLucideIcon(iconName, 14, "currentColor", 2);
        if (filledIcon) {
          filledIcon.setAttribute("fill", "currentColor");
          actionBtn.innerHTML = "";
          actionBtn.appendChild(filledIcon);
        }

        // Pop animation
        actionBtn.classList.remove("persona-message-action-pop");
        void actionBtn.offsetWidth; // force reflow to restart animation
        actionBtn.classList.add("persona-message-action-pop");

        // Trigger feedback
        const messages = session.getMessages();
        const message = messages.find(m => m.id === messageId);
        if (message && messageActionCallbacks.onFeedback) {
          messageActionCallbacks.onFeedback({
            type: action,
            messageId: message.id,
            message
          });
        }
      }
    } else if (action === 'regenerate') {
      historyActions?.regenerate(messageId);
    } else if (action === 'edit') {
      historyActions?.edit(messageId);
    } else if (action === 'quote') {
      historyActions?.quote(messageId);
    } else if (action?.startsWith(CUSTOM_MESSAGE_ACTION_PREFIX)) {
      // Resolved off config, never off the element: the row is morphed DOM, so
      // anything stored on the button would not survive a re-render.
      const id = action.slice(CUSTOM_MESSAGE_ACTION_PREFIX.length);
      const custom = config.messageActions?.custom?.find((item) => item.id === id);
      const message = session.getMessages().find((m) => m.id === messageId);
      if (!custom || !message) return;
      try {
        custom.onSelect(message);
      } catch (error) {
        console.error(
          `[AgentWidget] messageActions.custom "${id}" onSelect failed:`,
          error
        );
      }
    }
  });

  /**
   * Retry / edit / quote handlers. Bound later in this closure (they need the
   * composer and the edit renderer), so the delegated listener above reaches
   * them through this slot instead of a forward reference.
   */
  let historyActions: {
    regenerate: (messageId: string) => void;
    edit: (messageId: string) => void;
    quote: (messageId: string) => void;
  } | null = null;

  // Add event delegation for approval action buttons (approve/deny)
  messagesWrapper.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const approvalButton = target.closest('button[data-approval-action]') as HTMLElement;
    if (!approvalButton) return;

    event.preventDefault();
    event.stopPropagation();

    const approvalBubble = approvalButton.closest('.persona-approval-bubble') as HTMLElement;
    if (!approvalBubble) return;

    const messageId = approvalBubble.getAttribute('data-message-id');
    if (!messageId) return;

    const action = approvalButton.getAttribute('data-approval-action') as 'approve' | 'deny';
    if (!action) return;

    const decision = action === 'approve' ? 'approved' as const : 'denied' as const;

    // Find the approval message
    const messages = session.getMessages();
    const approvalMessage = messages.find(m => m.id === messageId);
    if (!approvalMessage?.approval) return;

    // Disable buttons immediately for responsive UI
    const buttonsContainer = approvalBubble.querySelector('[data-approval-buttons]') as HTMLElement;
    if (buttonsContainer) {
      const buttons = buttonsContainer.querySelectorAll('button');
      buttons.forEach(btn => {
        (btn as HTMLButtonElement).disabled = true;
        btn.style.opacity = '0.5';
        btn.style.cursor = 'not-allowed';
      });
    }

    // WebMCP gate approvals resolve a local Promise the bridge is parked on
    // (no server round-trip); server-driven approvals call the API. The
    // `toolType` marker and absent execution id identify browser-local gates.
    if (isBrowserLocalApproval(approvalMessage.approval)) {
      session.resolveWebMcpApproval(messageId, decision);
    } else {
      session.resolveApproval(approvalMessage.approval, decision);
    }
  });

  let artifactPaneApi: ArtifactPaneApi | null = null;
  // Guards the deferred pane graft against post-destroy chunk adoption.
  let artifactsUiDisposed = false;
  let artifactPanelResizeObs: ResizeObserver | null = null;
  // Re-runs panel chrome when the split-chrome mode flips (pane
  // open/close/appearance change). Assigned once applyFullHeightStyles exists.
  let syncPanelChrome: () => void = () => {};
  let appliedSplitMode: "none" | "welded" | "detached" = "none";
  // Resolved welded outer-right radius stashed by applyFullHeightStyles and
  // applied in syncPanelChrome (after applyArtifactPaneAppearance, which no
  // longer manages the var, so the two never fight). Null clears it.
  let weldedOuterRadius: string | null = null;
  let lastArtifactsState: {
    artifacts: PersonaArtifactRecord[];
    selectedId: string | null;
  } = { artifacts: [], selectedId: null };
  let artifactsPaneUserHidden = false;
  // Runtime-only expand state: pane fills the split root and the chat column
  // hides. Reset whenever the pane stops being visible (syncArtifactPane).
  let artifactPaneExpanded = false;
  // Set when the expansion came from an inline block's Expand button: that is
  // an explicit "fullscreen this file" request, so it survives the
  // showExpandToggle gate below (which otherwise collapses the pane for hosts
  // without the toolbar toggle). Cleared whenever the pane collapses or hides.
  let artifactPaneExpandedPinned = false;
  // Whether the user explicitly opened the pane (card click, inline Expand,
  // showArtifacts(), programmatic upsert). Auto-open is otherwise reserved for
  // artifacts whose resolved display mode is "panel": "collapsed" keeps the card as
  // the only affordance and "inline" renders in the transcript. "inline" no
  // longer *auto*-opens the pane, but its Expand control is a deliberate,
  // user-driven open (setting artifactsPaneUserOpened, same as a card click);
  // every mode still writes the session artifact registry (download /
  // getArtifacts / hydration).
  let artifactsPaneUserOpened = false;
  const artifactPaneCanShow = () =>
    artifactsPaneUserOpened ||
    lastArtifactsState.artifacts.some(
      (a) =>
        resolveArtifactDisplayMode(config.features?.artifacts, a) === "panel"
    );
  const artifactPaneVisible = () =>
    lastArtifactsState.artifacts.length > 0 &&
    !artifactsPaneUserHidden &&
    artifactPaneCanShow();
  const sessionRef: { current: AgentWidgetSession | null } = { current: null };

  // Resolve an artifact's content for card actions (download + custom actions).
  // Tries the live session registry first, then falls back to the content
  // persisted in the card message's rawContent props (session state is gone
  // after a page refresh). `fromEl` is the clicked element inside the card.
  const resolveCardArtifactContent = (
    fromEl: HTMLElement,
    artifactId: string
  ): { markdown?: string; title: string; file?: PersonaArtifactFileMeta; artifactType: string } => {
    const artifact = session.getArtifactById(artifactId);
    let markdown = artifact?.markdown;
    let title = artifact?.title || 'artifact';
    let file: PersonaArtifactFileMeta | undefined = artifact?.file;
    let artifactType: string = artifact?.artifactType ?? 'markdown';
    if (!markdown) {
      // Match both reference cards and inline blocks: inline hydration embeds
      // the markdown / file source in the same message rawContent, so the parse
      // below resolves content for inline copy / custom actions after a refresh.
      const cardEl = fromEl.closest('[data-open-artifact], [data-artifact-inline]');
      const msgEl = cardEl?.closest('[data-message-id]');
      const msgId = msgEl?.getAttribute('data-message-id');
      if (msgId) {
        const msgs = session.getMessages();
        const msg = msgs.find(m => m.id === msgId);
        if (msg?.rawContent) {
          try {
            const parsed = JSON.parse(msg.rawContent);
            markdown = parsed?.props?.markdown;
            title = parsed?.props?.title || title;
            if (parsed?.props?.file && typeof parsed.props.file === 'object') {
              file = parsed.props.file as PersonaArtifactFileMeta;
            }
            if (!artifact && typeof parsed?.props?.artifactType === 'string') {
              artifactType = parsed.props.artifactType;
            }
          } catch { /* ignore */ }
        }
      }
    }
    return { markdown, title, file, artifactType };
  };

  // Click delegation for artifact download buttons
  messagesWrapper.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const dlBtn = target.closest('[data-download-artifact]') as HTMLElement;
    if (!dlBtn) return;
    event.preventDefault();
    event.stopPropagation();
    const artifactId = dlBtn.getAttribute('data-download-artifact');
    if (!artifactId) return;
    // Let integrator intercept
    const dlPrevented = config.features?.artifacts?.onArtifactAction?.({ type: 'download', artifactId });
    if (dlPrevented === true) return;
    const { markdown, title, file } = resolveCardArtifactContent(dlBtn, artifactId);
    if (!markdown) return;
    // The button only exists on chunk-rendered DOM, so the module is adopted.
    const artifactsUi = getArtifactsUiSync();
    if (!artifactsUi) return;
    // File artifacts download the raw unfenced source under their real name/MIME;
    // non-file markdown artifacts keep the legacy `<title>.md` / text/markdown path.
    artifactsUi.triggerArtifactDownload(artifactsUi.downloadInfoFor({ title, markdown, file }));
  });

  // Click delegation for integrator-supplied card action buttons. Actions are
  // looked up by id from fresh config at click time so live config updates
  // apply. Like the download listener, its stopPropagation() cannot block the
  // card-open listener on the same element, so that listener skips these too.
  messagesWrapper.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const actionBtn = target.closest('[data-artifact-custom-action]') as HTMLElement;
    if (!actionBtn) return;
    event.preventDefault();
    event.stopPropagation();
    const actionId = actionBtn.getAttribute('data-artifact-custom-action');
    if (!actionId) return;
    // The same attribute serves cards and inline chrome; resolve which surface
    // the button lives in so the action comes from the matching config list and
    // the artifact id from the matching container attribute.
    const inlineEl = actionBtn.closest('[data-artifact-inline]');
    const cardEl = inlineEl ? null : actionBtn.closest('[data-open-artifact]');
    const artifactId = inlineEl
      ? inlineEl.getAttribute('data-artifact-inline')
      : cardEl?.getAttribute('data-open-artifact') ?? null;
    const actionList = inlineEl
      ? config.features?.artifacts?.inlineActions
      : config.features?.artifacts?.cardActions;
    const action = actionList?.find((a) => a.id === actionId);
    if (!action) return;
    const { markdown, title, file, artifactType } = resolveCardArtifactContent(actionBtn, artifactId ?? '');
    const ctx: PersonaArtifactActionContext = { artifactId, title, artifactType, markdown, file };
    try {
      void Promise.resolve(action.onClick(ctx)).catch(() => {});
    } catch {
      /* ignore */
    }
  });

  // Click delegation for the inline chrome Copy button. Like the download /
  // custom-action listeners, it resolves content from the live registry first,
  // then falls back to the persisted inline props after a refresh, and its
  // stopPropagation() cannot block a second listener on the same element (there
  // is none here, but the pattern is kept consistent).
  messagesWrapper.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const copyBtn = target.closest('[data-copy-artifact]') as HTMLElement;
    if (!copyBtn) return;
    event.preventDefault();
    event.stopPropagation();
    const artifactId = copyBtn.getAttribute('data-copy-artifact');
    if (!artifactId) return;
    // Prefer the live record (covers component JSON); fall back to the persisted
    // markdown / file source parsed from the inline block's message rawContent.
    const artifact = session.getArtifactById(artifactId);
    // The button only exists on chunk-rendered DOM, so the module is adopted.
    const artifactsUi = getArtifactsUiSync();
    if (!artifactsUi) return;
    let text = '';
    if (artifact) {
      text = artifactsUi.artifactCopyText(artifact);
    } else {
      const { markdown, file, artifactType } = resolveCardArtifactContent(copyBtn, artifactId);
      if (artifactType === 'markdown') {
        text = artifactsUi.artifactCopyText({
          id: artifactId,
          artifactType: 'markdown',
          status: 'complete',
          markdown: markdown ?? '',
          ...(file ? { file } : {})
        });
      }
    }
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      // Lightweight feedback: swap the copy glyph for a check briefly.
      const checkIcon = renderIconNode(Check, 16, 'currentColor', 2);
      if (checkIcon) {
        copyBtn.replaceChildren(checkIcon);
        setTimeout(() => {
          const copyIcon = renderIconNode(Copy, 16, 'currentColor', 2);
          if (copyIcon) copyBtn.replaceChildren(copyIcon);
        }, 1500);
      }
    }).catch(() => { /* ignore */ });
  });

  // Click delegation for the inline chrome Expand button: open this artifact in
  // the pane. Fires onArtifactAction({ type: "open" }) first so hosts can
  // intercept, then mirrors the card-open path — except the pane opens
  // expanded (fullscreen), never split: the inline block already shows the
  // full preview at chat width, so a split pane would only duplicate it, and
  // the click means "expand this file". The pinned flag keeps it expanded
  // even when layout.showExpandToggle is off; Close is the exit there.
  messagesWrapper.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const expandBtn = target.closest('[data-expand-artifact-inline]') as HTMLElement;
    if (!expandBtn) return;
    event.preventDefault();
    event.stopPropagation();
    const artifactId = expandBtn.getAttribute('data-expand-artifact-inline');
    if (!artifactId) return;
    const openPrevented = config.features?.artifacts?.onArtifactAction?.({ type: 'open', artifactId });
    if (openPrevented === true) return;
    artifactsPaneUserHidden = false;
    // Expand is an explicit open: it overrides the "inline" auto-open
    // suppression for as long as artifacts exist (same as a card click).
    artifactsPaneUserOpened = true;
    artifactPaneExpanded = true;
    artifactPaneExpandedPinned = true;
    session.selectArtifact(artifactId);
    syncArtifactPane();
  });

  // Click delegation for artifact reference cards
  messagesWrapper.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    // Download and custom-action clicks are handled by the listeners above;
    // their stopPropagation() cannot block a second listener on the same
    // element, so skip them here explicitly or the card would also open the panel.
    if (target.closest('[data-download-artifact]')) return;
    if (target.closest('[data-artifact-custom-action]')) return;
    const card = target.closest('[data-open-artifact]') as HTMLElement;
    if (!card) return;
    const artifactId = card.getAttribute('data-open-artifact');
    if (!artifactId) return;
    // Let integrator intercept
    const openPrevented = config.features?.artifacts?.onArtifactAction?.({ type: 'open', artifactId });
    if (openPrevented === true) return;
    event.preventDefault();
    event.stopPropagation();
    artifactsPaneUserHidden = false;
    // Card click is an explicit open: it overrides the "collapsed"/"inline"
    // auto-open suppression for as long as artifacts exist.
    artifactsPaneUserOpened = true;
    session.selectArtifact(artifactId);
    syncArtifactPane();
  });

  // Keyboard support for artifact cards and the clip-mode inline expand hitbox
  // (both expose a data-* attribute on a focusable element; Enter/Space forward
  // to the element's own click, which the delegated click handlers above own).
  messagesWrapper.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const target = event.target as HTMLElement;
    if (
      !target.hasAttribute('data-open-artifact') &&
      !target.hasAttribute('data-expand-artifact-inline')
    )
      return;
    event.preventDefault();
    target.click();
  });

  // --- ask_user_question sheet interaction ---
  // Event delegation for the answer-pill sheet that mounts in the composer
  // overlay. Handles pill pick (single), multi-select toggle + submit, free-
  // text pill expansion + submit, and dismissal. Selection becomes a regular
  // user message via session.sendMessage so the agent resumes on the next turn.
  const askUserOverlay = panelElements.composerOverlay;

  // Warm the handler chunk as the sheet mounts so it is ready before any pick.
  const mountAskUserSheet = (message: AgentWidgetMessage): void => {
    loadUiExtras().catch(() => {});
    ensureAskUserQuestionSheet(message, config, askUserOverlay);
  };

  // Handlers live in the lazy ui-extras chunk (warmed when a sheet mounts).
  // Only events from inside a mounted sheet need them.
  let askUserHandlers: AskUserSheetHandlers | null = null;
  const askUserCtx: AskUserSheetContext = {
    instanceId,
    mount,
    overlay: askUserOverlay,
    sessionRef,
    config: () => config,
  };
  // At most one interaction waits on the chunk: later ones (a double click)
  // are dropped, and the queued one is dropped too if its sheet was removed
  // meanwhile, so an answer can never be submitted twice.
  let askUserEventPending = false;
  const routeAskUserEvent =
    (kind: keyof AskUserSheetHandlers) =>
    (event: Event): void => {
      const target = event.target as HTMLElement | null;
      const sheet = target?.closest?.("[data-persona-ask-sheet-for]");
      if (!sheet) return;
      // Only events the handlers act on (an action control click, Enter in the
      // free-text input) may claim the pending slot; ordinary typing must not
      // block the answer that follows it.
      const actionable =
        kind === "click"
          ? !!target?.closest("[data-ask-user-action]")
          : (event as KeyboardEvent).key === "Enter" &&
            !!target?.matches?.('[data-ask-free-text-input="true"]');
      if (!actionable) return;
      const run = (m: UiExtrasModule) => {
        askUserHandlers ??= m.createAskUserSheetHandlers(askUserCtx);
        (askUserHandlers[kind] as (e: Event) => void)(event);
      };
      const loaded = getUiExtrasSync();
      if (loaded) {
        run(loaded);
        return;
      }
      if (askUserEventPending) return;
      askUserEventPending = true;
      loadUiExtras().then(
        (m) => {
          askUserEventPending = false;
          if (sheet.isConnected) run(m);
        },
        () => {
          askUserEventPending = false;
        }
      );
    };
  askUserOverlay.addEventListener("click", routeAskUserEvent("click"));
  askUserOverlay.addEventListener("keydown", routeAskUserEvent("keydown"));

  // Digit 1–9 → pick option N on the current rows-layout single-select page.
  // Listens on `document` so the shortcut fires regardless of where focus
  // currently sits (host page body, panel chrome, anywhere). The handler
  // gates strictly: only fires when an active sheet is mounted in our
  // overlay, and bails when focus is on any input/textarea/contenteditable
  // (covers the free-text input, the chat composer, and any host-page input).
  const handleAskUserDigitKey = (event: KeyboardEvent): void => {
    if (!/^[1-9]$/.test(event.key)) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target as HTMLElement | null;
    if (
      target?.tagName === "INPUT" ||
      target?.tagName === "TEXTAREA" ||
      target?.isContentEditable
    ) {
      return;
    }
    const sheet = askUserOverlay.querySelector<HTMLElement>("[data-persona-ask-sheet-for]");
    if (!sheet) return;
    if (sheet.getAttribute("data-ask-layout") !== "rows") return;
    if (sheet.getAttribute("data-multi-select") === "true") return;
    const n = Number(event.key);
    const pills = sheet.querySelectorAll<HTMLElement>(
      '[data-ask-pill-list="true"] [data-ask-user-action="pick"], [data-ask-pill-list="true"] [data-ask-user-action="focus-free-text"]'
    );
    const targetPill = pills[n - 1];
    if (!targetPill) return;
    event.preventDefault();
    targetPill.click();
  };
  document.addEventListener("keydown", handleAskUserDigitKey);

  let artifactSplitRoot: HTMLElement | null = null;
  let artifactResizer: ReturnType<ArtifactsUiModule["createArtifactResizer"]> | null = null;
  let reconcileArtifactResize: () => void = () => {};
  // Expanded-state transition tracking: stash the resizer's inline width/maxWidth
  // (which would otherwise beat the expanded class) once per enter, restore once
  // per leave.
  let artifactExpandedApplied = false;
  let artifactStashedWidth = "";
  let artifactStashedMaxWidth = "";

  /**
   * Chat-column minimum for an artifact drag. A docked in-flow rail lives
   * inside the chat column, so the flat chat minimum would let a wide drag
   * squeeze the column under the rail's own responsive floor and re-mode the
   * rail into the panel mid-drag. The floating overlay rail (`railOverlayHost`)
   * takes no flex width, so only the in-flow `railHost` counts, measured live
   * to cover expanded, collapsed, and dragged widths alike.
   */
  const railedChatMinPx = (): number => {
    const railHost = historyShell?.railShell ? historyShell.railHost : null;
    const host = railHost?.isConnected ? railHost : null;
    const railPx = host ? host.getBoundingClientRect().width : 0;
    if (railPx <= 0) return ARTIFACT_RESIZE_CHAT_MIN_PX;
    return railPx + ARTIFACT_RESIZE_RAILED_TRANSCRIPT_MIN_PX;
  };

  /** Flush split: overlay handle on the seam so it does not consume flex gap (extension + resizable). */
  /** No-op until artifact pane is created; replaced below when artifacts are enabled. */
  let applyLauncherArtifactPanelWidth: () => void = () => {};

  // Mobile fullscreen renders a flush, chrome-less panel, so the split chrome
  // must stand down there. This is the union of the two fullscreen predicates
  // applyFullHeightStyles/recalcPanelHeight use: launcher fullscreen
  // (shouldGoFullscreen) and the docked-host variant (dockedHostFullscreen).
  // Unlike the 641px split breakpoint, it honors the configurable mobileBreakpoint.
  const isMobileFullscreenActive = (): boolean => {
    const win = mount.ownerDocument.defaultView ?? window;
    const mobileFullscreen = config.launcher?.mobileFullscreen ?? true;
    const mobileBreakpoint = config.launcher?.mobileBreakpoint ?? 640;
    if (!mobileFullscreen || win.innerWidth > mobileBreakpoint) return false;
    return launcherEnabled || isDockedMountMode(config);
  };

  // Desktop side-by-side split chrome mode. 'welded' = panel|seamless (one card,
  // border+radius wrap both columns); 'detached' = two floating cards; 'none' when
  // the pane is closed, the narrow-host drawer, or mobile fullscreen.
  const splitChromeMode = (): "none" | "welded" | "detached" => {
    if (!artifactsSidebarEnabled(config)) return "none";
    if (!artifactPaneVisible()) return "none";
    if (mount.classList.contains("persona-artifact-narrow-host")) return "none";
    // A fullscreen panel is flush; welded/detached corner chrome would fight it.
    if (isMobileFullscreenActive()) return "none";
    const win = mount.ownerDocument.defaultView ?? window;
    // Must match the artifact split media queries in widget.css (drawer at
    // max-width:640, split at min-width:641); mobileBreakpoint does not move it.
    if (win.innerWidth < ARTIFACT_SPLIT_DESKTOP_MIN) return "none";
    return isArtifactPaneAppearanceDetached(config) ? "detached" : "welded";
  };

  const syncArtifactPane = () => {
    if (!artifactPaneApi || !artifactsSidebarEnabled(config)) return;
    applyArtifactLayoutCssVars(mount, config);
    applyArtifactPaneAppearance(mount, config);
    applyLauncherArtifactPanelWidth();
    const threshold = config.features?.artifacts?.layout?.narrowHostMaxWidth ?? 520;
    const w = panel.getBoundingClientRect().width || 0;
    mount.classList.toggle("persona-artifact-narrow-host", w > 0 && w <= threshold);
    // Thread the single source of truth for "pane surface is shown" into the
    // pane BEFORE update() so it renders lazily: in inline/card display modes
    // the pane stays hidden and must not build a second sandboxed artifact
    // iframe (which would execute artifact scripts twice). The pane records
    // state while hidden and renders on the next reveal. Set first so an
    // update() that arrives while hidden is skipped, not rendered-then-hidden.
    const paneVisible = artifactPaneVisible();
    artifactPaneApi.setVisible(paneVisible);
    artifactPaneApi.update(lastArtifactsState);
    if (artifactsPaneUserHidden) {
      artifactPaneApi.setMobileOpen(false);
      artifactPaneApi.element.classList.add("persona-hidden");
      artifactPaneApi.backdrop?.classList.add("persona-hidden");
      artifactPaneExpanded = false;
      artifactPaneExpandedPinned = false;
    } else if (lastArtifactsState.artifacts.length > 0 && artifactPaneCanShow()) {
      // User chose “show” again (e.g. programmatic showArtifacts): clear dismiss chrome
      // and force drawer open so narrow-host / mobile slide-out is not stuck off-screen.
      // Artifacts whose display mode is "collapsed" or "inline" don't auto-open the
      // pane (artifactPaneCanShow); it stays hidden until an explicit open or
      // until a "panel"-mode artifact arrives.
      artifactPaneApi.element.classList.remove("persona-hidden");
      artifactPaneApi.setMobileOpen(true);
    } else {
      // The pane's own update() unhides itself whenever records exist
      // (applyLayoutVisibility), so re-assert the gate here: card/inline-mode
      // artifacts keep the pane hidden until an explicit open.
      artifactPaneApi.setMobileOpen(false);
      artifactPaneApi.element.classList.add("persona-hidden");
      artifactPaneApi.backdrop?.classList.add("persona-hidden");
      artifactPaneExpanded = false;
      artifactPaneExpandedPinned = false;
    }
    // Re-read the toggle config on every sync so a live config.update() can
    // reveal/remove the button (the pane itself is built once). Disabling the
    // toggle while expanded also collapses the pane — unless the expansion is
    // pinned (inline Expand): that fullscreen request stands on its own and
    // exits via Close.
    const expandToggleEnabled = config.features?.artifacts?.layout?.showExpandToggle === true;
    artifactPaneApi.setExpandToggleVisible(expandToggleEnabled);
    artifactPaneApi.setCopyButtonVisible(
      config.features?.artifacts?.layout?.showCopyButton === true
    );
    artifactPaneApi.setRefreshButtonVisible(
      typeof config.features?.artifacts?.layout?.onDocumentToolbarRefresh === "function"
    );
    artifactPaneApi.setCustomActions(config.features?.artifacts?.toolbarActions ?? []);
    artifactPaneApi.setStyleConfig(config);
    artifactPaneApi.setTabFade(config.features?.artifacts?.layout?.tabFade);
    artifactPaneApi.setRenderTabBar(config.features?.artifacts?.renderTabBar);
    if (!expandToggleEnabled && !artifactPaneExpandedPinned) artifactPaneExpanded = false;
    // Run the resizer stash/restore once per expanded-state transition: the
    // resizer's inline width/maxWidth beats the expanded class, so clear it while
    // expanded and put it back on collapse.
    if (artifactPaneExpanded !== artifactExpandedApplied) {
      const paneEl = artifactPaneApi.element;
      if (artifactPaneExpanded) {
        artifactStashedWidth = paneEl.style.width;
        artifactStashedMaxWidth = paneEl.style.maxWidth;
        paneEl.style.removeProperty("width");
        paneEl.style.removeProperty("max-width");
      } else {
        if (artifactStashedWidth) paneEl.style.width = artifactStashedWidth;
        if (artifactStashedMaxWidth) paneEl.style.maxWidth = artifactStashedMaxWidth;
        artifactStashedWidth = "";
        artifactStashedMaxWidth = "";
      }
      artifactExpandedApplied = artifactPaneExpanded;
    }
    mount.classList.toggle("persona-artifact-expanded", artifactPaneExpanded);
    artifactPaneApi.setExpanded(artifactPaneExpanded);
    // Recompute panel chrome when the split mode changes (pane open/close or
    // appearance) so the welded border and detached shadow suppression land.
    const modeNow = splitChromeMode();
    if (modeNow !== appliedSplitMode) {
      appliedSplitMode = modeNow;
      syncPanelChrome();
    }
    reconcileArtifactResize();
  };

  if (artifactsSidebarEnabled(config)) {
    // The split-root SKELETON is built synchronously so panel layout is
    // byte-identical from first paint; the pane itself lives in the lazy
    // artifacts-ui chunk and is grafted in at adoption. The pane element
    // starts `persona-hidden` and only becomes visible once artifacts exist
    // (`syncArtifactPane`), so deferred attachment is invisible; an artifact
    // arriving while the chunk is in flight is adopted by the
    // `syncArtifactPane()` call below.
    panel.style.position = "relative";
    const chatColumn = createElement(
      "div",
      "persona-flex persona-flex-1 persona-flex-col persona-min-w-0 persona-min-h-0"
    );
    const splitRoot = createElement(
      "div",
      "persona-flex persona-h-full persona-w-full persona-min-h-0 persona-artifact-split-root"
    );
    chatColumn.appendChild(container);
    artifactSplitRoot = splitRoot;
    splitRoot.appendChild(chatColumn);
    panel.appendChild(splitRoot);

    const graftArtifactPane = (mod: ArtifactsUiModule): void => {
        if (artifactsUiDisposed || artifactPaneApi) return;
        artifactPaneApi = mod.createArtifactPane(config, {
          onSelect: (id) => sessionRef.current?.selectArtifact(id),
          onDismiss: () => {
            artifactsPaneUserHidden = true;
            syncArtifactPane();
          },
          onToggleExpand: () => {
            const next = !artifactPaneExpanded;
            const artifactId =
              lastArtifactsState.selectedId ??
              lastArtifactsState.artifacts[lastArtifactsState.artifacts.length - 1]?.id ??
              null;
            const prevented = config.features?.artifacts?.onArtifactAction?.({
              type: "expand",
              artifactId,
              expanded: next,
            });
            if (prevented === true) return;
            artifactPaneExpanded = next;
            if (!next) artifactPaneExpandedPinned = false;
            syncArtifactPane();
          }
        });
        artifactResizer = mod.createArtifactResizer({ mount, splitRoot, pane: artifactPaneApi,
          getConfig: () => config, chatMinWidth: railedChatMinPx, isVisible: artifactPaneVisible });
        artifactPaneApi.element.classList.add("persona-hidden");
        splitRoot.appendChild(artifactPaneApi.element);
        if (artifactPaneApi.backdrop) {
          panel.appendChild(artifactPaneApi.backdrop);
        }
        // Adopt anything that streamed in while the chunk was in flight.
        reconcileArtifactResize();
        syncArtifactPane();
    };
    // Graft on ADOPTION (not this load promise): if this initial fetch fails,
    // a later artifact render's kick (component-middleware) can still resolve
    // the chunk, and the ready subscriber grafts the pane then.
    const syncMod = getArtifactsUiSync();
    if (syncMod) {
      graftArtifactPane(syncMod);
    } else {
      onArtifactsUiReady(() => {
        const mod = getArtifactsUiSync();
        if (mod) graftArtifactPane(mod);
      });
      void loadArtifactsUi().catch(() => {
        // Failed fetch: the sidebar stays absent until a retry succeeds.
      });
    }

    reconcileArtifactResize = () => artifactResizer?.update();

    applyLauncherArtifactPanelWidth = () => {
      if (!launcherEnabled || !artifactPaneApi) return;
      const sidebarMode = config.launcher?.sidebarMode ?? false;
      if (sidebarMode) return;
      if (isDockedMountMode(config) && resolveDockConfig(config).reveal === "emerge") return;
      const ownerWindow = mount.ownerDocument.defaultView ?? window;
      const mobileFullscreen = config.launcher?.mobileFullscreen ?? true;
      const mobileBreakpoint = config.launcher?.mobileBreakpoint ?? 640;
      if (mobileFullscreen && ownerWindow.innerWidth <= mobileBreakpoint) return;
      if (!shouldExpandLauncherForArtifacts(config, launcherEnabled)) return;

      const base = resolvePanelGeometry(config).width;
      const expanded =
        config.features?.artifacts?.layout?.expandedPanelWidth ??
        "min(720px, calc(100vw - 24px))";
      const hasVisible = artifactPaneVisible();
      if (hasVisible) {
        panel.style.width = expanded;
        panel.style.maxWidth = expanded;
      } else {
        panel.style.width = base;
        panel.style.maxWidth = base;
      }
    };

    if (typeof ResizeObserver !== "undefined") {
      artifactPanelResizeObs = new ResizeObserver(() => {
        syncArtifactPane();
      });
      artifactPanelResizeObs.observe(panel);
    }
  } else {
    panel.appendChild(container);
  }

  // Composer-bar mode: the pill (footer) and peek banner live in a
  // viewport-fixed sibling of the wrapper (`pillRoot`) so they're
  // independent of both the wrapper's geometry transitions and the panel's
  // optional artifact split layout. Critical for modal mode: the wrapper
  // there has `transform: translate(-50%, -50%)`, which would establish a
  // containing block trapping any `position: fixed` descendant.
  //
  // Order inside pillRoot: peekBanner (slim row above pill) → footer (pill).
  // pillRoot's `gap` spaces them; the peek is hidden by default until ui.ts
  // toggles `.persona-pill-peek--visible` based on streaming/hover/open state.
  if (isComposerBar() && pillRoot) {
    if (panelElements.peekBanner) {
      pillRoot.appendChild(panelElements.peekBanner);
    }
    pillRoot.appendChild(footer);
  }
  mount.appendChild(wrapper);
  // pillRoot is mounted *after* wrapper so it naturally stacks on top
  // when both share the same z-index (e.g. fullscreen mode where the
  // pill should float above the chat panel chrome).
  if (pillRoot) {
    mount.appendChild(pillRoot);
  }

  // Apply full-height and sidebar styles if enabled
  // This ensures the widget fills its container height with proper flex layout
  const applyFullHeightStyles = () => {
    const fullScreenTranscript = isMobileFullscreenActive() ||
      (isComposerBar() && config.launcher?.composerBar?.expandedSize === "fullscreen") ||
      (config.launcher?.fullHeight === true && !config.launcher?.sidebarMode && !isDockedMountMode(config));
    messagesWrapper.style.gap = fullScreenTranscript
      ? "var(--persona-components-message-fullscreenGap, var(--persona-components-message-gap, 12px))"
      : "var(--persona-components-message-gap, 12px)";
    messagesWrapper.style.setProperty("--persona-transcript-gap", messagesWrapper.style.gap);
    // Composer-bar mode owns its own sizing/chrome. Geometry comes from
    // `applyComposerBarGeometry()` (per-state inline on the wrapper), the
    // pill carries its own chrome via `.persona-pill-composer`, and the
    // expanded chat panel chrome (border + radius + shadow + bg) is painted
    // inline on the `container` (NOT the panel: the panel is a transparent
    // flex column with a gap so the pill renders as a sibling below the
    // chrome). Same theme contract as floating mode
    // (`theme.components.panel.{shadow,border,borderRadius}`); collapsed
    // clears it (container is hidden via display:none anyway), expanded
    // re-applies it, with the `fullscreen` variant intentionally chrome-less.
    if (isComposerBar()) {
      panel.style.width = "100%";
      panel.style.maxWidth = "100%";
      const cb = config.launcher?.composerBar ?? {};
      const isExpanded = wrapper.dataset.state === "expanded";
      const expandedSize = cb.expandedSize ?? "anchored";
      const wantsChrome = isExpanded && expandedSize !== "fullscreen";
      if (!wantsChrome) {
        container.style.background = "";
        container.style.border = "";
        container.style.borderRadius = "";
        container.style.overflow = "";
        container.style.boxShadow = "";
        return;
      }
      const panelPartial = config.theme?.components?.panel;
      const activeTheme = getActiveTheme(config);
      const resolveCb = (raw: string | undefined, fallback: string): string => {
        if (raw == null || raw === "") return fallback;
        return resolveTokenValue(activeTheme, raw) ?? raw;
      };
      const defaultBorder = "1px solid var(--persona-border)";
      const defaultShadow = "var(--persona-palette-shadows-xl, 0 25px 50px -12px rgba(0, 0, 0, 0.25))";
      const defaultRadius = "var(--persona-panel-radius, var(--persona-radius-xl, 0.75rem))";
      container.style.background = "var(--persona-surface, #ffffff)";
      container.style.border = resolveCb(panelPartial?.border, defaultBorder);
      container.style.borderRadius = resolveCb(panelPartial?.borderRadius, defaultRadius);
      container.style.boxShadow = resolveCb(panelPartial?.shadow, defaultShadow);
      container.style.overflow = "hidden";
      return;
    }
    const dockedMode = isDockedMountMode(config);
    const sidebarMode = config.launcher?.sidebarMode ?? false;
    const fullHeight = isEmbeddedLayout(config) || dockedMode || sidebarMode || (config.launcher?.fullHeight ?? false);
    /** Script-tag / div embed: launcher off, host supplies a sized mount. */
    const isInlineEmbed = config.launcher?.enabled === false;
    /** Detached appearance: inset card over a canvas instead of flush chrome. */
    const isDetached = config.launcher?.detachedPanel === true;
    const lightPanelPartial = config.theme?.components?.panel;
    const darkPanelPartial = config.darkTheme?.components?.panel;
    const panelPartial: typeof lightPanelPartial = getColorScheme(config) === "dark"
      ? deepMerge(
          (lightPanelPartial ?? {}) as Record<string, unknown>,
          (darkPanelPartial ?? {}) as Record<string, unknown>
        ) as typeof lightPanelPartial
      : lightPanelPartial;
    const activeTheme = getActiveTheme(config);
    const resolvePanelChrome = (raw: string | undefined, fallback: string): string => {
      if (raw == null || raw === "") return fallback;
      return resolveTokenValue(activeTheme, raw) ?? raw;
    };

    // Mobile fullscreen detection
    // Use mount's ownerDocument window to get correct viewport width when widget is inside an iframe
    const ownerWindow = mount.ownerDocument.defaultView ?? window;
    const mobileFullscreen = config.launcher?.mobileFullscreen ?? true;
    const mobileBreakpoint = config.launcher?.mobileBreakpoint ?? 640;
    const isMobileViewport = ownerWindow.innerWidth <= mobileBreakpoint;
    const shouldGoFullscreen = mobileFullscreen && isMobileViewport && launcherEnabled;
    // Docked host layout forces fullscreen on mobile even when the launcher is
    // off (host-layout pins the slot fixed and clears its chrome). Suppress the
    // detached card so it never paints chrome over a flush fullscreen panel.
    const dockedHostFullscreen = dockedMode && mobileFullscreen && isMobileViewport;

    // Determine panel styling based on mode, with theme overrides
    const position = config.launcher?.position ?? 'bottom-left';
    const isLeftSidebar = position === 'bottom-left' || position === 'top-left';
    const overlayZIndex = config.launcher?.zIndex ?? DEFAULT_OVERLAY_Z_INDEX;

    // Card chrome defaults (floating look): reused to restore detached chrome.
    // Defaults chain through the aliases themeToCssVariables emits so explicit
    // theme.components.panel overrides and these defaults never diverge.
    const modeChrome = (
      mode: "floating" | "inline" | "sidebar" | "docked" | "mobile",
      field: "border" | "shadow" | "borderRadius",
      fallback: string
    ): string => `var(--persona-components-panel-modes-${mode}-${field}, ${fallback})`;
    const configuredFloatingPanel = panelPartial?.modes?.floating;
    const cardBorder = resolvePanelChrome(
      configuredFloatingPanel?.border ?? panelPartial?.border,
      modeChrome("floating", "border", 'var(--persona-panel-border, 1px solid var(--persona-border))')
    );
    const cardShadow = resolvePanelChrome(
      configuredFloatingPanel?.shadow ?? panelPartial?.shadow,
      modeChrome("floating", "shadow", 'var(--persona-panel-shadow, var(--persona-palette-shadows-xl, 0 25px 50px -12px rgba(0, 0, 0, 0.25)))')
    );
    const cardRadius = resolvePanelChrome(
      configuredFloatingPanel?.borderRadius ?? panelPartial?.borderRadius,
      modeChrome("floating", "borderRadius", 'var(--persona-panel-radius, var(--persona-radius-xl, 0.75rem))')
    );
    /** Detached restores card chrome except when a host layout goes fullscreen. */
    const detachedCard = isDetached && !shouldGoFullscreen && !dockedHostFullscreen;
    const panelMode = shouldGoFullscreen || dockedHostFullscreen
      ? "mobile"
      : detachedCard
        ? "floating"
        : dockedMode
          ? "docked"
          : sidebarMode
            ? "sidebar"
            : isInlineEmbed
              ? "inline"
              : "floating";
    const configuredModePanel = panelPartial?.modes?.[panelMode];
    // Stamp reflects rendered chrome: cleared when a fullscreen host layout
    // suppresses the card, so the attribute never lies to the detached CSS.
    if (detachedCard) {
      mount.setAttribute("data-persona-panel-detached", "true");
    } else {
      mount.removeAttribute("data-persona-panel-detached");
    }

    // Default values based on mode
    let defaultPanelBorder = detachedCard
      ? cardBorder
      : (sidebarMode || shouldGoFullscreen) ? 'none' : cardBorder;
    let defaultPanelShadow = detachedCard
      ? cardShadow
      : shouldGoFullscreen
        ? 'none'
        : sidebarMode
          ? (isLeftSidebar ? '2px 0 12px rgba(0, 0, 0, 0.08)' : '-2px 0 12px rgba(0, 0, 0, 0.08)')
          // Flush inline embeds fill their container: no elevation by default
          // (detachedPanel or components.panel.shadow opts back in).
          : isInlineEmbed ? 'none' : cardShadow;

    if (dockedMode && !shouldGoFullscreen && !detachedCard) {
      defaultPanelShadow = 'none';
      defaultPanelBorder = 'none';
    }
    const defaultPanelBorderRadius = detachedCard
      ? cardRadius
      : (sidebarMode || shouldGoFullscreen) ? '0' : cardRadius;

    // Apply theme overrides or defaults (components.panel.*)
    const panelBorder = resolvePanelChrome(
      configuredModePanel?.border ?? panelPartial?.border,
      modeChrome(panelMode, "border", defaultPanelBorder)
    );
    const panelShadow = resolvePanelChrome(
      configuredModePanel?.shadow ?? panelPartial?.shadow,
      modeChrome(panelMode, "shadow", defaultPanelShadow)
    );
    const panelBorderRadius = resolvePanelChrome(
      configuredModePanel?.borderRadius ?? panelPartial?.borderRadius,
      modeChrome(panelMode, "borderRadius", defaultPanelBorderRadius)
    );

    // Split chrome: 'welded' folds the card border onto the outer panel so it
    // wraps both columns as one card (shadow/radius already on the panel);
    // 'detached' pulls them into two individually carded columns.
    const splitMode = splitChromeMode();
    const detachedSplitActive = splitMode === "detached";
    const weldedSplitActive = splitMode === "welded";
    mount.classList.toggle("persona-artifact-detached-split", detachedSplitActive);
    mount.classList.toggle("persona-artifact-welded-split", weldedSplitActive);
    const chatCardShadow =
      'var(--persona-artifact-chat-shadow, var(--persona-artifact-pane-shadow, var(--persona-panel-shadow, var(--persona-palette-shadows-xl, 0 25px 50px -12px rgba(0, 0, 0, 0.25)))))';
    // Welded moves the chat card border onto the panel, so the chat column goes
    // borderless; detached cards it individually; otherwise it keeps the border.
    // 'flush' drops the chat card so the chat is flat flush background and only
    // the pane floats; 'card' (default) keeps two matched cards. Flush is a
    // steady state: it applies whether or not a pane is open, so opening or
    // closing an artifact never flips the chat chrome. Gated to inline embeds
    // (not docked), matching the card-mode perimeter inset: the flush model only
    // fits the container-filling embed. Floating/docked/sidebar keep the card look.
    const rawChatSurface = config.features?.artifacts?.layout?.chatSurface;
    const resolvedChatSurface = rawChatSurface === "flush" ? "flush" : "card";
    const chatFlush =
      resolvedChatSurface === "flush" &&
      artifactsSidebarEnabled(config) &&
      isArtifactPaneAppearanceDetached(config) &&
      isInlineEmbed &&
      !dockedMode;
    mount.classList.toggle("persona-artifact-chat-flush", chatFlush);
    // Flat means flat: flush suppresses the outer panel shadow in every state.
    const outerPanelShadow = detachedSplitActive || chatFlush ? 'none' : panelShadow;
    let chatContainerBorder = detachedSplitActive
      ? cardBorder
      : weldedSplitActive
        ? 'none'
        : panelBorder;
    let chatContainerRadius = detachedSplitActive ? cardRadius : panelBorderRadius;
    if (chatFlush) {
      chatContainerBorder = 'none';
      chatContainerRadius = '0';
    }
    // Flush fills the container flush, so the outer panel squares off by default;
    // the pane keeps its own rounded radius. An explicit panel.borderRadius wins.
    const panelRadiusExplicit =
      (configuredModePanel?.borderRadius != null && configuredModePanel.borderRadius !== '') ||
      (panelPartial?.borderRadius != null && panelPartial.borderRadius !== '');
    const appliedPanelRadius =
      chatFlush && !panelRadiusExplicit ? '0' : panelBorderRadius;

    // Clearing body.style.cssText below wipes the inline `flex: 1 1 0%` /
    // `min-height: 0` / `overflow-y: auto` that make the messages area a
    // scroll container. Between the reset and the mode-specific reapply,
    // the body's clientHeight == scrollHeight momentarily, so the browser
    // clamps scrollTop to 0, and a synchronous restore at the end of this
    // function runs before layout has reflowed, so the write is also
    // clamped. Defer the restore to the next frame, once the reapplied
    // styles have produced a scrollable container again.
    const prevBodyScrollTop = body.scrollTop;

    // Reset all inline styles first to handle mode toggling
    // This ensures styles don't persist when switching between modes
    mount.style.cssText = '';
    wrapper.style.cssText = '';
    panel.style.cssText = '';
    container.style.cssText = '';
    body.style.cssText = '';
    // A plugin-owned footer is the plugin's DOM: its inline state (the home
    // screen's `display: none` placeholder) is not ours to reset.
    if (!composerIsPluginOwned) {
      footer.style.cssText = '';
      // The reset also wipes the hidden state that layout.showFooter set.
      if (config.layout?.showFooter === false) footer.style.display = 'none';
    }

    // Preserve the event-stream takeover across a layout-mode change. The
    // cssText reset above wiped the `display: none` that toggleEventStreamOn
    // set on the messages body, and none of the per-mode reapply branches below
    // touch `display` — so without this the messages would reappear and stack
    // above the event panel when the window crosses the fullscreen breakpoint.
    if (eventStreamVisible) {
      body.style.display = "none";
    }

    // Shrinkable baseline for every fill mount (launcher-off embed OR fullHeight/
    // docked/sidebar), applied outside the fullHeight branch so the launcher-off,
    // no-fullHeight embed keeps it too: the cssText reset above wipes the min-width
    // the host layer set, and without it a wide artifact split isn't forced to
    // shrink within the mount. In practice the column host's own min-width:0 is
    // what caps container overflow (the mount is a cross-axis child), but the
    // baseline is kept here so intent is consistent across every fill mode.
    if (fullHeight || isInlineEmbed) {
      mount.style.minWidth = "0";
    }

    const restoreBodyScrollTop = (): void => {
      if (prevBodyScrollTop <= 0) return;
      const ownerWindow = body.ownerDocument.defaultView ?? window;
      ownerWindow.requestAnimationFrame(() => {
        if (body.scrollTop === prevBodyScrollTop) return;
        // If scrollHeight collapsed (content actually shrank), don't fight it
        const maxScrollTop = body.scrollHeight - body.clientHeight;
        if (maxScrollTop <= 0) return;
        body.scrollTop = Math.min(prevBodyScrollTop, maxScrollTop);
      });
    };

    // Mobile fullscreen: fill entire viewport with no radius/shadow/margins
    if (shouldGoFullscreen) {
      // Remove position offset classes
      wrapper.classList.remove(
        'persona-bottom-6', 'persona-right-6', 'persona-left-6', 'persona-top-6',
        'persona-bottom-4', 'persona-right-4', 'persona-left-4', 'persona-top-4'
      );

      // Wrapper: fill entire viewport
      wrapper.style.cssText = `
        position: fixed !important;
        inset: 0 !important;
        width: 100% !important;
        height: 100% !important;
        max-height: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        display: flex !important;
        flex-direction: column !important;
        z-index: ${overlayZIndex} !important;
        background-color: var(--persona-surface, #ffffff) !important;
      `;

      // Panel: fill wrapper, no radius/shadow
      panel.style.cssText = `
        position: relative !important;
        display: flex !important;
        flex-direction: column !important;
        flex: 1 1 0% !important;
        width: 100% !important;
        max-width: 100% !important;
        height: 100% !important;
        min-height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        box-shadow: none !important;
        border-radius: 0 !important;
      `;

      // Container: fill panel, no radius/border
      container.style.cssText = `
        display: flex !important;
        flex-direction: column !important;
        flex: 1 1 0% !important;
        width: 100% !important;
        height: 100% !important;
        min-height: 0 !important;
        max-height: 100% !important;
        overflow: hidden !important;
        border-radius: 0 !important;
        border: none !important;
      `;

      // Body: scrollable messages
      body.style.flex = '1 1 0%';
      body.style.minHeight = '0';
      body.style.overflowY = 'auto';

      // Footer: pinned at bottom
      footer.style.flexShrink = '0';
      applyBlockCenterLift();

      wasMobileFullscreen = true;
      restoreBodyScrollTop();
      return; // Skip remaining mode logic
    }

    // Re-apply panel width/maxWidth from initial setup
    const geometry = resolvePanelGeometry(config);
    if (!sidebarMode && !dockedMode) {
      if (isInlineEmbed) {
        wrapper.style.width = resolveInlinePanelWidth(config);
        panel.style.width = "100%";
        panel.style.maxWidth = "100%";
      } else {
        panel.style.width = geometry.width;
        panel.style.maxWidth = geometry.maxWidth;
      }
    } else if (dockedMode) {
      const dockReveal = resolveDockConfig(config).reveal;
      if (dockReveal === "emerge" && !isDetached) {
        const dw = resolveDockConfig(config).width;
        panel.style.width = dw;
        panel.style.maxWidth = dw;
      } else {
        panel.style.width = "100%";
        panel.style.maxWidth = "100%";
      }
    }
    applyLauncherArtifactPanelWidth();

    // Apply panel styling
    // Box-shadow is applied to panel (parent) instead of container to avoid
    // rendering artifacts when container has overflow:hidden + border-radius
    // Panel also gets border-radius to make the shadow follow the rounded corners
    panel.style.boxShadow = outerPanelShadow;
    panel.style.borderRadius = appliedPanelRadius;
    if (detachedSplitActive) panel.style.border = 'none';
    else if (weldedSplitActive) panel.style.border = panelBorder;
    container.style.border = chatContainerBorder;
    container.style.borderRadius = chatContainerRadius;
    container.style.boxShadow = detachedSplitActive && !chatFlush ? chatCardShadow : '';
    // Flush chat has no card surface of its own: override the class-painted
    // container/body/footer backgrounds so the wrapper's canvas shows through.
    // The footer's top hairline is chat-card chrome, so it goes too. Element
    // surfaces (bubbles, cards, composer input) keep their own backgrounds.
    if (chatFlush) {
      container.style.background = 'transparent';
      body.style.background = 'transparent';
      footer.style.background = 'transparent';
      footer.style.borderTop = 'none';
    }

    // Welded outer-right radius: derive the pane's outer corners from the same
    // resolved value as the chat card's left corners so a custom
    // components.panel.borderRadius stays symmetric. Explicit
    // unifiedSplitOuterRadius / paneBorderRadius still win. Applied in
    // syncPanelChrome after applyArtifactPaneAppearance (which no longer owns it).
    if (weldedSplitActive) {
      const wl = config.features?.artifacts?.layout;
      weldedOuterRadius =
        wl?.unifiedSplitOuterRadius?.trim() || wl?.paneBorderRadius?.trim() ||
        artifactStyle(config, undefined, "split.outerRadius") ||
        artifactStyle(config, undefined, "pane.borderRadius") || panelBorderRadius;
    } else {
      weldedOuterRadius = null;
    }

    const panelBorderExplicit =
      configuredModePanel?.border != null || panelPartial?.border != null;
    if (dockedMode && !shouldGoFullscreen && !detachedCard && !detachedSplitActive && !weldedSplitActive && !panelBorderExplicit) {
      container.style.border = 'none';
      const dockSide = resolveDockConfig(config).side;
      if (dockSide === 'right') {
        container.style.borderLeft = '1px solid var(--persona-border)';
      } else {
        container.style.borderRight = '1px solid var(--persona-border)';
      }
    }

    // Docked + welded: the welded chrome owner is the outer panel, and docked
    // mode resolves its border to none, so add the dock-facing hairline there so
    // the split still separates from the host page. Mirrors the flush block's
    // side choice (right dock => left edge faces the page).
    if (dockedMode && !shouldGoFullscreen && weldedSplitActive && !panelBorderExplicit) {
      const dockSide = resolveDockConfig(config).side;
      if (dockSide === 'right') {
        panel.style.borderLeft = '1px solid var(--persona-border)';
      } else {
        panel.style.borderRight = '1px solid var(--persona-border)';
      }
    }

    if (fullHeight) {
      // Mount container
      mount.style.display = 'flex';
      mount.style.flexDirection = 'column';
      mount.style.height = '100%';
      mount.style.minHeight = '0';
      if (isEmbeddedLayout(config)) mount.style.flex = '1 1 0%';
      // min-width:0 baseline is applied above (outside this branch) for every
      // fill mount, including the launcher-off, no-fullHeight embed.
      if (isInlineEmbed) {
        mount.style.width = '100%';
      }

      // Wrapper
      // - Inline embed: needs overflow:hidden to contain the flex layout
      // - Launcher mode: no overflow:hidden to allow panel's box-shadow to render fully
      wrapper.style.display = 'flex';
      wrapper.style.flexDirection = 'column';
      wrapper.style.flex = '1 1 0%';
      wrapper.style.minHeight = '0';
      wrapper.style.maxHeight = '100%';
      wrapper.style.height = '100%';
      if (isInlineEmbed) {
        wrapper.style.overflow = 'hidden';
      }

      // Panel
      panel.style.display = 'flex';
      panel.style.flexDirection = 'column';
      panel.style.flex = '1 1 0%';
      panel.style.minHeight = '0';
      panel.style.maxHeight = '100%';
      panel.style.height = '100%';
      // Detached split: each card clips its own content and carries its own
      // shadow, so panel overflow:hidden must not clip the cards' shadows.
      if (!detachedSplitActive) panel.style.overflow = 'hidden';

      // Main container
      container.style.display = 'flex';
      container.style.flexDirection = 'column';
      container.style.flex = '1 1 0%';
      container.style.minHeight = '0';
      container.style.maxHeight = '100%';
      container.style.overflow = 'hidden';

      // Body (scrollable messages area)
      body.style.flex = '1 1 0%';
      body.style.minHeight = '0';
      body.style.overflowY = 'auto';

      // Footer (composer) - should not shrink
      footer.style.flexShrink = '0';
    }

    // Inline embed: pad the wrapper so the canvas shows around the card when the
    // panel is a detached card OR a detached split is open (insets the whole split
    // from the container edges on all sides). Docked owns its inset via
    // host-layout, so exclude it (no double gap).
    // Flush chat must be flush to the container, so it skips the perimeter inset;
    // the wrapper still paints the canvas so one token colors the whole backdrop,
    // in every state (flush is steady, so idle must match pane-open).
    if (isInlineEmbed && (detachedCard || detachedSplitActive || chatFlush) && !dockedMode) {
      if (!chatFlush) wrapper.style.padding = 'var(--persona-panel-inset)';
      wrapper.style.background = 'var(--persona-panel-canvas-bg)';
    }

    // Handle positioning classes based on mode
    // First remove all position classes to reset state
    wrapper.classList.remove(
      'persona-bottom-6', 'persona-right-6', 'persona-left-6', 'persona-top-6',
      'persona-bottom-4', 'persona-right-4', 'persona-left-4', 'persona-top-4'
    );

    if (!sidebarMode && !isInlineEmbed && !dockedMode) {
      // Restore positioning classes when not in sidebar mode (launcher mode only)
      const positionClasses = positionMap[position as keyof typeof positionMap] ?? positionMap['bottom-right'];
      positionClasses.split(' ').forEach(cls => wrapper.classList.add(cls));
    }

    // Apply sidebar-specific styles
    if (sidebarMode) {
      const sidebarWidth = getPanelAliasProvenance(config).sidebarWidth
        ? config.launcher?.sidebarWidth ?? "420px"
        : resolveTokenValue(getActiveTheme(config), "components.panel.width") ?? "420px";

      // Wrapper - fixed position. Detached insets the card off the edges and
      // shrinks its height by the inset on both ends; flush hugs the edges.
      if (isDetached) {
        wrapper.style.cssText = `
          position: fixed !important;
          top: var(--persona-panel-inset) !important;
          bottom: var(--persona-panel-inset) !important;
          width: ${sidebarWidth} !important;
          height: calc(100vh - (2 * var(--persona-panel-inset))) !important;
          max-height: calc(100vh - (2 * var(--persona-panel-inset))) !important;
          margin: 0 !important;
          padding: 0 !important;
          display: flex !important;
          flex-direction: column !important;
          z-index: ${overlayZIndex} !important;
          ${isLeftSidebar ? 'left: var(--persona-panel-inset) !important; right: auto !important;' : 'left: auto !important; right: var(--persona-panel-inset) !important;'}
        `;
      } else {
        wrapper.style.cssText = `
          position: fixed !important;
          top: 0 !important;
          bottom: 0 !important;
          width: ${sidebarWidth} !important;
          height: 100vh !important;
          max-height: 100vh !important;
          margin: 0 !important;
          padding: 0 !important;
          display: flex !important;
          flex-direction: column !important;
          z-index: ${overlayZIndex} !important;
          ${isLeftSidebar ? 'left: 0 !important; right: auto !important;' : 'left: auto !important; right: 0 !important;'}
        `;
      }

      // Panel - fill wrapper (override inline width/max-width from panel.ts)
      // Box-shadow is on panel to avoid rendering artifacts with container's overflow:hidden
      // Border-radius on panel ensures shadow follows rounded corners
      panel.style.cssText = `
        position: relative !important;
        display: flex !important;
        flex-direction: column !important;
        flex: 1 1 0% !important;
        width: 100% !important;
        max-width: 100% !important;
        height: 100% !important;
        min-height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        box-shadow: ${outerPanelShadow} !important;
        border-radius: ${appliedPanelRadius} !important;
        ${detachedSplitActive ? 'border: none !important;' : weldedSplitActive ? `border: ${panelBorder} !important;` : ''}
      `;
      // Force override any inline width/maxWidth that may be set elsewhere
      panel.style.setProperty('width', '100%', 'important');
      panel.style.setProperty('max-width', '100%', 'important');

      // Container - apply configurable styles with sidebar layout. Box-shadow is
      // normally on panel, but a detached split cards the chat column here.
      container.style.cssText = `
        display: flex !important;
        flex-direction: column !important;
        flex: 1 1 0% !important;
        width: 100% !important;
        height: 100% !important;
        min-height: 0 !important;
        max-height: 100% !important;
        overflow: hidden !important;
        border-radius: ${chatContainerRadius} !important;
        border: ${chatContainerBorder} !important;
        ${detachedSplitActive && !chatFlush ? `box-shadow: ${chatCardShadow} !important;` : ''}
        ${chatFlush ? 'background: transparent !important;' : ''}
      `;

      // Remove footer border in sidebar mode (never on a plugin footer: see
      // the cssText reset above)
      if (!composerIsPluginOwned) footer.style.cssText = `
        flex-shrink: 0 !important;
        border-top: none !important;
        padding: 8px 16px 12px 16px !important;
        ${chatFlush ? 'background: transparent !important;' : ''}
        ${config.layout?.showFooter === false ? 'display: none !important;' : ''}
      `;
    }

    // Apply max-height constraints to wrapper to prevent expanding past viewport top
    // Use both -moz-available (Firefox) and stretch (standard) for cross-browser support
    // Append to cssText to allow multiple fallback values for the same property
    // Only apply to launcher mode (not sidebar or inline embed)
    if (!isInlineEmbed && !dockedMode) {
      const maxHeightStyles = 'max-height: -moz-available !important; max-height: stretch !important;';
      const paddingStyles = sidebarMode ? '' : 'padding-top: 1.25em !important;';
      const zIndexStyles = !sidebarMode
        ? `z-index: ${config.launcher?.zIndex ?? DEFAULT_OVERLAY_Z_INDEX} !important;`
        : '';
      wrapper.style.cssText += maxHeightStyles + paddingStyles + zIndexStyles;
    }

    // Runs after every branch that reset footer styles above.
    applyBlockCenterLift();
    restoreBodyScrollTop();
  };
  // Column var for plugin-rendered content (renderWelcome overlay stacks,
  // custom composers): resolved config value, matched without config access.
  // Must land after applyFullHeightStyles, which wipes mount.style.cssText.
  const applyContentMaxWidthVar = () => {
    mount.style.setProperty(
      "--persona-content-max-width",
      resolveContentMaxWidth(config, isComposerBar())
    );
  };
  let warnedComposerBarPlacement = false;
  // mount.style.cssText is wiped by applyFullHeightStyles, so every
  // mount-level var re-stamps here, next to applyContentMaxWidthVar.
  function syncTranscriptEdgeFades() {
    if (config.layout?.topFade && body.scrollTop > 1) body.setAttribute("data-persona-top-fade", "true");
    else body.removeAttribute("data-persona-top-fade");
    // The fixed suggestion row is the lower transcript boundary. Reuse the
    // existing overlay-band theme; transparent disables its paint in V4 or
    // for hosts that opt out. Only show it while content remains below.
    panelSuggestionsFade.hidden = !isComposerBar() || panelSuggestions.hidden ||
      body.scrollHeight - body.clientHeight - body.scrollTop <= 1;
    if (!panelSuggestionsFade.hidden) {
      panelSuggestionsFade.style.bottom = `${container.getBoundingClientRect().bottom -
        panelSuggestions.getBoundingClientRect().top - container.clientTop}px`;
    }
  }
  const applyComposerPlacement = () => {
    syncTranscriptEdgeFades();
    const placement = resolveComposerPlacement(config, isComposerBar());
    if (
      isComposerBar() &&
      config.composer?.placement &&
      config.debug === true &&
      !warnedComposerBarPlacement
    ) {
      warnedComposerBarPlacement = true;
      console.warn(
        "[persona] composer.placement is ignored in composer-bar mount mode."
      );
    }
    const welcome = getWelcomeConfig();
    mount.setAttribute("data-persona-composer-placement", placement);
    mount.setAttribute("data-persona-conversation-state", conversationState);
    // Root-level mirror of the welcome host's own anchor attribute, so the
    // block+center gap rules can target the body without :has().
    mount.setAttribute("data-persona-welcome-anchor", welcome.anchor ?? "bottom");
    // Not `--persona-composer-gap`: that alias is components.composer.gap.
    mount.style.setProperty(
      "--persona-composer-anchor-gap",
      welcome.composerGap ?? DEFAULT_COMPOSER_GAP
    );
    syncComposerOverlayMetrics();
  };
  // Floating launcher panels get a fixed pixel height (recalcPanelHeight owns
  // it on resize), but applyFullHeightStyles wipes panel.style.cssText and only
  // restores width — so every chrome sync must re-stamp the height or the panel
  // grows to fit its content. Guards mirror recalcPanelHeight's path.
  const applyFloatingPanelFixedHeight = () => {
    if (isComposerBar()) return;
    const dockedMode = isDockedMountMode(config);
    const sidebarMode = config.launcher?.sidebarMode ?? false;
    const fullHeight = isEmbeddedLayout(config) || dockedMode || sidebarMode || (config.launcher?.fullHeight ?? false);
    if (fullHeight || !launcherEnabled) return;
    const ownerWindow = mount.ownerDocument.defaultView ?? window;
    const mobileFullscreen = config.launcher?.mobileFullscreen ?? true;
    const mobileBreakpoint = config.launcher?.mobileBreakpoint ?? 640;
    if (mobileFullscreen && ownerWindow.innerWidth <= mobileBreakpoint) return;
    const geometry = resolvePanelGeometry(config);
    panel.style.height = geometry.height;
    panel.style.maxHeight = geometry.maxHeight;
  };
  applyFullHeightStyles();
  // Apply theme variables after applyFullHeightStyles since it resets mount.style.cssText
  applyThemeVariables(mount, config);
  applyArtifactLayoutCssVars(mount, config);
  applyArtifactPaneAppearance(mount, config);
  applyContentMaxWidthVar();
  applyComposerPlacement();

  // applyFullHeightStyles wipes mount.style.cssText, so re-apply the theme +
  // artifact layout vars after it, mirroring the init sequence above.
  syncPanelChrome = () => {
    applyFullHeightStyles();
    applyFloatingPanelFixedHeight();
    applyThemeVariables(mount, config);
    applyArtifactLayoutCssVars(mount, config);
    applyArtifactPaneAppearance(mount, config);
    applyContentMaxWidthVar();
    applyComposerPlacement();
    // Owned here so it lands after applyArtifactPaneAppearance and derives from
    // the same resolved panel radius the chat card uses (see applyFullHeightStyles).
    if (weldedOuterRadius) {
      mount.style.setProperty("--persona-artifact-welded-outer-radius", weldedOuterRadius);
    } else {
      mount.style.removeProperty("--persona-artifact-welded-outer-radius");
    }
    applyRailChrome();
    // A chrome pass can restyle the panel out from under an open history host.
    reapplyHistoryHostChrome();
  };

  const destroyCallbacks: Array<() => void> = [];
  destroyCallbacks.push(() => composerFit.destroy());
  destroyCallbacks.push(() => {
    artifactsUiDisposed = true;
  });
  // Drops every binding (config and plugin) with the document listener.
  destroyCallbacks.push(() => {
    releaseShortcuts.forEach((release) => release());
    shortcuts.destroy();
  });
  // Clean up the document-level digit-key shortcut listener registered earlier.
  destroyCallbacks.push(() => {
    document.removeEventListener("keydown", handleAskUserDigitKey);
  });
  // Clear any pending live-region announcement timer on teardown.
  destroyCallbacks.push(() => {
    if (announceTimer !== null) clearTimeout(announceTimer);
  });

  let teardownHostStacking: (() => void) | null = null;
  let releaseScrollLock: (() => void) | null = null;

  destroyCallbacks.push(() => {
    teardownHostStacking?.();
    teardownHostStacking = null;
    releaseScrollLock?.();
    releaseScrollLock = null;
  });

  if (artifactPanelResizeObs) {
    destroyCallbacks.push(() => {
      artifactPanelResizeObs?.disconnect();
      artifactPanelResizeObs = null;
    });
  }

  destroyCallbacks.push(() => {
    artifactResizer?.destroy();
    artifactResizer = null;
    artifactPaneApi?.element.style.removeProperty("width");
    artifactPaneApi?.element.style.removeProperty("maxWidth");
    // Release the pane preview's pending file-preview timers/listeners.
    artifactPaneApi?.destroy();
  });

  // Event stream cleanup
  if (showEventStreamToggle) {
    destroyCallbacks.push(() => {
      eventStreamRuntimeDisposed = true;
      pendingEventStreamEvents = null;
      if (eventStreamRAF !== null) {
        cancelAnimationFrame(eventStreamRAF);
        eventStreamRAF = null;
      }
      eventStreamView?.destroy();
      eventStreamView = null;
      eventStreamBuffer?.destroy();
      eventStreamBuffer = null;
      eventStreamStore = null;
    });
  }

  // Set up theme observer for auto color scheme detection
  let cleanupThemeObserver: (() => void) | null = null;
  const setupThemeObserver = () => {
    // Clean up existing observer if any
    if (cleanupThemeObserver) {
      cleanupThemeObserver();
      cleanupThemeObserver = null;
    }
    // Set up new observer if colorScheme is 'auto'
    if (config.colorScheme === 'auto') {
      let observedScheme = getColorScheme(config);
      cleanupThemeObserver = createThemeObserver((scheme) => {
        if (scheme === observedScheme) return;
        observedScheme = scheme;
        // Dark-only optional tokens can activate structural hooks, not just variables.
        // Reuse existing chrome and transcript updates without resetting expansion state.
        syncPanelChrome();
        syncArtifactPane();
        if (session) {
          configVersion++;
          renderMessagesWithPlugins(messagesWrapper, session.getMessages(), postprocess);
        }
      });
    }
  };
  setupThemeObserver();
  destroyCallbacks.push(() => {
    if (cleanupThemeObserver) {
      cleanupThemeObserver();
      cleanupThemeObserver = null;
    }
  });

  // Release this widget's pending built-in approval listeners + "Allow once"
  // popovers if it's destroyed while an approval is still open. Scoped to this
  // instance's state, so other widgets on the page are unaffected.
  destroyCallbacks.push(() => {
    approvalUiDisposed = true;
    teardownBuiltInApprovals?.();
  });

  // Guard the forms-ui heal the same way: a chunk resolution after destroy
  // must not re-render into a detached transcript.
  destroyCallbacks.push(() => {
    formsUiDisposed = true;
  });

  // `wipe` / `glyph-cycle` live in the lazy animations-extra chunk (CDN) or
  // the animations/* subpaths (npm). When config selects one that isn't
  // registered yet, kick the chunk load and activate on arrival: text streams
  // unanimated until the chunk lands, then subsequent renders animate.
  // Unregistered custom names never trigger a fetch.
  let streamAnimationLoadDisposed = false;
  destroyCallbacks.push(() => {
    streamAnimationLoadDisposed = true;
  });
  const resolveStreamAnimationPluginLazy: typeof resolveStreamAnimationPlugin = (
    type,
    overrides
  ) => {
    const plugin = resolveStreamAnimationPlugin(type, overrides);
    if (plugin || !LAZY_BUILTIN_STREAM_ANIMATIONS.includes(type)) return plugin;
    void loadAnimationsExtra()
      .then((mod) => {
        if (streamAnimationLoadDisposed) return;
        // Register the chunk's plugin objects into CORE's registry explicitly.
        // The chunk is bundled noExternal, so its import-time self-registration
        // lands in the chunk's own copy of the registry Map — invisible to
        // core's resolver. Idempotent when both copies are one module (ESM/tests).
        registerStreamAnimationPlugin(mod.wipe);
        registerStreamAnimationPlugin(mod.glyphCycle);
        const loaded = resolveStreamAnimationPlugin(type, overrides);
        if (loaded) ensurePluginActive(loaded, mount);
      })
      .catch(() => {
        // Failed fetch: renders fall back to unanimated text; the next
        // resolve retries via the chunk loader's rejection-retry semantics.
      });
    return null;
  };

  // Activate the stream-animation plugin for this widget instance. Plugins
  // with `styles` inject their CSS into the widget root once; plugins with
  // `onAttach` (e.g., glyph-cycle's MutationObserver for real glyph tick
  // loops) can register long-lived DOM listeners here. Detach callbacks are
  // deferred to widget destroy.
  const streamAnimationConfig = config.features?.streamAnimation;
  if (streamAnimationConfig?.type && streamAnimationConfig.type !== "none") {
    const plugin = resolveStreamAnimationPluginLazy(
      streamAnimationConfig.type,
      streamAnimationConfig.plugins
    );
    if (plugin) ensurePluginActive(plugin, mount);
    // Push unconditionally: a lazily-loaded plugin can activate after init.
    destroyCallbacks.push(() => detachAllPlugins(mount));
  }

  // Reassignable: a composer rebuild swaps the row this manager renders into.
  let composerSuggestionsManager = createSuggestions(suggestions);
  const starterSuggestionsManager = createSuggestions(starterSuggestions);
  const transcriptSuggestionsManager = createSuggestions(transcriptSuggestions);
  const panelSuggestionsManager = createSuggestions(panelSuggestions);
  const suggestionManagers = [
    composerSuggestionsManager,
    starterSuggestionsManager,
    transcriptSuggestionsManager,
    panelSuggestionsManager,
  ];
  destroyCallbacks.push(() => {
    suggestionManagers.forEach((manager) => manager.destroy());
  });
  let closeHandler: (() => void) | null = null;
  let session: AgentWidgetSession;

  // Single render rule for the suggestions row, shared by the message-change,
  // initial-paint, and config-update paths: agent-pushed `suggest_replies`
  // chips win when the latest-turn rule yields any (last suggest_replies tool
  // message with no user message after it); otherwise static config chips
  // keep their before-first-user-message behavior. Config updates MUST route
  // through here too: re-rendering with only `config.suggestionChips` would
  // drop a live agent chip row until the next message change.
  // Warn once per widget instance: renderSuggestions runs on every change.
  // Host-set follow-ups (`setFollowUpSuggestions`) are an ephemeral UI overlay:
  // never appended to the session, never sent on the wire, never persisted.
  let hostFollowUps: AgentWidgetSuggestion[] | null = null;
  // Latches: a host that drives the surface itself is a legitimate no-tool
  // integration, so the never-called hint must stay off even after the overlay
  // expires.
  let hostFollowUpsEverSet = false;
  // Latest-writer-wins bookkeeping captured at set time: the overlay is dropped
  // once another user message appends or a newer suggest_replies message lands.
  let hostFollowUpsUserCount = 0;
  let hostFollowUpsAgentId: string | null = null;
  const dismissedFollowUpIds = new Set<string>();
  destroyCallbacks.push(() => dismissedFollowUpIds.clear());

  const countUserMessages = (messages: AgentWidgetMessage[]): number =>
    messages.reduce(
      (total, message) => (message.role === "user" ? total + 1 : total),
      0
    );

  /** Identity of the newest suggest_replies message, regardless of position. */
  const latestSuggestRepliesId = (
    messages: AgentWidgetMessage[]
  ): string | null => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (isSuggestRepliesMessage(messages[i])) return messages[i].id ?? null;
    }
    return null;
  };

  let warnedPinnedStarterPlacement = false;
  const warnPinnedStarterPlacement = () => {
    if (warnedPinnedStarterPlacement || config.debug !== true) return;
    warnedPinnedStarterPlacement = true;
    console.warn(
      '[persona] Starters are hidden: suggestions.starters.placement is "welcome", but welcome is disabled. Use "auto" or "composer".',
    );
  };
  // Debug-only setup hint: a stream this session ended without the agent ever
  // calling suggest_replies, so the model never got the tool. Gated on a
  // COMPLETED stream, not on transcript shape: a restored transcript
  // (initialMessages/onStateLoaded) proves nothing about this session's wiring.
  let hintedFollowUpsNeverCalled = false;
  let completedStreamThisSession = false;
  const hintFollowUpsNeverCalled = (
    messages: AgentWidgetMessage[],
    feature: ReturnType<typeof resolveFollowUpsFeature>
  ) => {
    if (hintedFollowUpsNeverCalled || config.debug !== true) return;
    if (!feature.enabled || feature.expose) return;
    if (hostFollowUpsEverSet) return;
    if (!completedStreamThisSession) return;
    if (session.isStreaming()) return;
    if (messages.some(isSuggestRepliesMessage)) return;
    hintedFollowUpsNeverCalled = true;
    console.info(
      "[persona] No suggest_replies call received. Declare the tool server-side or set suggestions.followUps.expose: true, and instruct the model when to use it. See UI-COMPONENTS.md."
    );
  };

  const renderSuggestions = (messages?: AgentWidgetMessage[]) => {
    if (!session) return;
    const current = messages ?? session.getMessages();
    // Reset first so switching back to legacy starters or follow-ups restores
    // the shared composer surface. The starter branch opts into below again.
    suggestions.removeAttribute("data-persona-suggestions-below");
    if (suggestions.parentElement === footer) footer.insertBefore(suggestions, footer.firstChild);
    if (dismissedFollowUpIds.size) {
      const currentIds = new Set(current.map((message) => message.id));
      for (const id of dismissedFollowUpIds) {
        if (!currentIds.has(id)) dismissedFollowUpIds.delete(id);
      }
    }
    const clearManager = (manager: ReturnType<typeof createSuggestions>) => {
      manager.render([], session, textarea, current);
    };
    const clearOthers = (active: ReturnType<typeof createSuggestions>) => {
      suggestionManagers.forEach((manager) => {
        if (manager !== active) clearManager(manager);
      });
    };
    // Same lifecycle as agent follow-ups: the overlay expires on the next user
    // message, and a suggest_replies message newer than the set call wins.
    if (
      hostFollowUps &&
      (countUserMessages(current) > hostFollowUpsUserCount ||
        latestSuggestRepliesId(current) !== hostFollowUpsAgentId)
    ) {
      hostFollowUps = null;
    }

    // `enabled: false` disables the tool, not the surface: host-set follow-ups
    // still render.
    const followUpsFeature = resolveFollowUpsFeature(config);
    hintFollowUpsNeverCalled(current, followUpsFeature);
    const latestFollowUpId = latestSuggestRepliesId(current);
    const agentSuggestions = followUpsFeature.enabled &&
      !(config.future?.v5Defaults && latestFollowUpId && dismissedFollowUpIds.has(latestFollowUpId))
      ? latestAgentSuggestions(current, config.suggestions?.followUps?.maxItems ??
          (config.future?.v5Defaults ? 5 : 4))
      : null;
    if (config.future?.v5Defaults && session.isStreaming()) {
      suggestionManagers.forEach(clearManager);
      return;
    }
    const followUpSuggestions = hostFollowUps ?? agentSuggestions;
    const followUpSource: AgentWidgetSuggestionSource = hostFollowUps
      ? "host"
      : "agent";

    if (followUpSuggestions) {
      const followUps = config.suggestions?.followUps;
      const requestedPlacement =
        followUps?.placement ?? (followUps ? "auto" : "composer");
      const placement =
        requestedPlacement === "auto"
          ? isComposerBar()
            ? "composer"
            : "after-message"
          : requestedPlacement;
      const manager =
        placement === "after-message"
          ? transcriptSuggestionsManager
          : isComposerBar() ? panelSuggestionsManager : composerSuggestionsManager;
      clearOthers(manager);
      manager.render(
        followUpSuggestions,
        session,
        textarea,
        current,
        config.suggestionChipsConfig,
        {
          source: followUpSource,
          surface: "followUp",
          variant: followUps?.variant ?? "chip",
          behavior: followUps?.behavior ?? "send",
          overflow: followUps?.overflow ?? (config.future?.v5Defaults ? "scroll" : "wrap"),
          maxItems: followUps?.maxItems ?? (config.future?.v5Defaults
            ? isWelcomeFullscreen() ? 5 : 3
            : 4),
          config,
          plugins,
          submitPrompt: submitSuggestionPrompt,
        }
      );
      return;
    }

    if (current.some((message) => message.role === "user")) {
      suggestionManagers.forEach(clearManager);
      return;
    }

    const v5 = config.future?.v5Defaults === true;
    const fullscreen = isWelcomeFullscreen();
    const starters = config.suggestions?.starters ?? (v5 ? {} : undefined);
    if (starters) {
      // The starter host lives inside the welcome surface, so a pinned
      // "welcome" placement has nowhere to render when that surface is hidden.
      const welcomeCardVisible = isWelcomeVisible(
        getWelcomeConfig(),
        current
      );
      const requestedPlacement = starters.placement ?? "auto";
      const placement =
        requestedPlacement === "auto"
          ? welcomeCardVisible
            ? "welcome"
            : "composer"
          : requestedPlacement;
      if (placement === "welcome" && !welcomeCardVisible) {
        warnPinnedStarterPlacement();
        suggestionManagers.forEach(clearManager);
        return;
      }
      suggestions.toggleAttribute("data-persona-suggestions-below", placement === "below-composer");
      if (placement === "below-composer") composerForm.after(suggestions);
      else footer.insertBefore(suggestions, footer.firstChild);
      const manager =
        placement === "welcome"
          ? starterSuggestionsManager
          : composerSuggestionsManager;
      clearOthers(manager);
      manager.render(
        starters.items ?? config.suggestionChips,
        session,
        textarea,
        current,
        config.suggestionChipsConfig,
        {
          surface: "starter",
          variant: starters.variant ?? (v5 && !fullscreen ? "chip" : "card"),
          behavior: starters.behavior ?? "send",
          overflow: starters.overflow ?? "wrap",
          maxItems: starters.maxItems ?? (v5 && !fullscreen ? 3 : 4),
          config,
          plugins,
          submitPrompt: submitSuggestionPrompt,
        }
      );
      return;
    }

    clearOthers(composerSuggestionsManager);
    composerSuggestionsManager.render(
      config.suggestionChips,
      session,
      textarea,
      current,
      config.suggestionChipsConfig,
      {
        surface: "starter",
        variant: "chip",
        behavior: "send",
        overflow: "wrap",
        maxItems: 4,
        config,
        plugins,
        submitPrompt: submitSuggestionPrompt,
      }
    );
  };

  // Dismiss only the current set: fresh suggestions from the next run may render.
  const dismissFollowUps = () => {
    if (!config.future?.v5Defaults || !session) return;
    hostFollowUps = null;
    const id = latestSuggestRepliesId(session.getMessages());
    if (id) dismissedFollowUpIds.add(id);
    renderSuggestions();
  };

  const clearHostFollowUps = () => {
    if (!hostFollowUps) return;
    hostFollowUps = null;
    renderSuggestions();
  };

  const setHostFollowUps = (items: AgentWidgetSuggestion[]) => {
    const list = Array.isArray(items) ? [...items] : [];
    if (!list.length) {
      clearHostFollowUps();
      return;
    }
    const current = session ? session.getMessages() : [];
    hostFollowUps = list;
    hostFollowUpsEverSet = true;
    hostFollowUpsUserCount = countUserMessages(current);
    hostFollowUpsAgentId = latestSuggestRepliesId(current);
    renderSuggestions(current);
  };

  let isStreaming = false;
  const messageCache = createMessageCache();
  // Assistant message ids seen streaming in a coalesced (never-applied)
  // transcript snapshot; see `coalesceStreamingChange`.
  const coalescedStreamingIds = new Set<string>();
  // Fingerprint each live transcript row was last morphed to, by message id.
  // A row whose message still has that fingerprint is left in place by the
  // next render pass instead of being cloned from the cache and re-morphed.
  let liveRowFingerprints = new Map<string, string>();
  // Tracks the last fingerprint we rendered a plugin-rendered ask_user_question
  // bubble for, per message id. Lets us skip unnecessary rebuilds across
  // re-renders so user state inside the plugin (typed text, focus) survives.
  const lastAskBubbleFingerprint = new Map<string, string>();
  // Same idea for component-directive bubbles (registered custom components
  // rendered from JSON directives). The renderer's element is injected into the
  // live DOM post-morph so its event listeners survive; this map gates the
  // expensive rebuild on fingerprint change so user state inside the rendered
  // component (e.g. partially-filled form inputs) is not wiped on every pass.
  const lastComponentDirectiveFingerprint = new Map<string, string>();
  type ComponentLifetime = {
    renderer: AgentWidgetComponentRenderer | undefined;
    component: string;
    controller: AbortController;
    handle: AgentWidgetComponentRenderHandle;
    context: AgentWidgetComponentRenderContext;
  };
  const componentLifetimes = new Map<string, ComponentLifetime>();
  const disposeComponentRender = (id: string): void => {
    const lifetime = componentLifetimes.get(id);
    componentLifetimes.delete(id);
    lastComponentDirectiveFingerprint.delete(id);
    findMessageRow(messagesWrapper, id)?.removeAttribute("data-preserve-runtime");
    if (!lifetime) return;
    lifetime.controller.abort();
    try {
      lifetime.handle.destroy?.();
    } catch (error) {
      console.error("[AgentWidget] Component cleanup failed:", error);
    }
  };
  destroyCallbacks.push(() => {
    for (const id of componentLifetimes.keys()) disposeComponentRender(id);
  });
  // Same idea for plugin-rendered approval bubbles (`renderApproval`). The
  // custom element is injected into the live DOM post-morph so its event
  // listeners (Approve/Deny, an expandable parameters accordion, etc.) survive;
  // this map gates the rebuild on fingerprint change so interactive state (e.g.
  // a collapsed accordion) is not reset on every pass while the approval is
  // pending.
  const lastApprovalBubbleFingerprint = new Map<string, string>();
  const approvalRenderControllers = new Map<string, AbortController>();
  const disposeApprovalRender = (id: string): void => {
    approvalRenderControllers.get(id)?.abort();
    approvalRenderControllers.delete(id);
    lastApprovalBubbleFingerprint.delete(id);
  };
  const disposeApprovalRenders = (): void => {
    for (const id of approvalRenderControllers.keys()) disposeApprovalRender(id);
  };
  destroyCallbacks.push(disposeApprovalRenders);

  // --- Edit-and-resend (roadmap section 10) ---------------------------------
  // "Editing message X" is UI state consumed BY the render path, never a
  // post-hoc DOM swap: the transcript morphs on every render and would strip an
  // externally-inserted editor (and reset its textarea value). The render pass
  // appends a `data-preserve-runtime` stub for the edited message and the live
  // editor is grafted back afterwards, the same stub-and-hydrate contract the
  // plugin ask/approval bubbles use.
  let editingMessageId: string | null = null;
  let messageEditEditor: MessageEditEditor | null = null;

  function cancelMessageEdit(): void {
    if (!editingMessageId) return;
    const id = editingMessageId;
    editingMessageId = null;
    messageEditEditor?.destroy();
    messageEditEditor = null;
    // The stub carried no bubble, so the cached wrapper for this message is
    // stale: drop it and let the next pass rebuild the real bubble.
    messageCache.delete(id);
    container.querySelector(`#wrapper-${id}`)?.removeAttribute("data-preserve-runtime");
    if (session) renderMessagesWithPlugins(messagesWrapper, session.getMessages(), postprocess);
  }

  let configVersion = 0;
  const autoFollow = createFollowStateController();
  let lastScrollTop = 0;
  let scrollRAF: number | null = null;
  let isAutoScrolling = false;
  let hasPendingAutoScroll = false;
  // Messages that arrived while the user was away from the latest content;
  // shown as a badge on the scroll-to-bottom affordance.
  let newMessagesSincePause = 0;
  // Live anchor-top state for the current turn (null when not anchored).
  let anchorState: {
    initialSpacerHeight: number;
    contentHeightAtAnchor: number;
    spacerHeight: number;
    pending: number;
  } | null = null;
  let anchorRAF: number | null = null;
  // Seeded send-detection so restored history doesn't read as a fresh send.
  let scrollSendSeeded = false;
  let suppressScrollSend = false;
  let lastSentUserMessageId: string | null = null;
  // anchor-top no-anchor fallback: anchor-top pins on a USER send. An assistant
  // message that streams when NO user send has anchored the conversation yet
  // (first-load / proactive-first streaming) has nothing to anchor to, so it
  // falls back to follow-to-bottom — otherwise its content streams in
  // off-screen. `true` by default (nothing anchored yet); a user send clears it
  // and the anchor takes over. Inert in follow/none mode (see
  // `isFollowEffective`).
  let followFallbackActive = true;
  // True once a user send has anchored the current conversation (until the chat
  // is cleared). While anchored, follow-on assistant content — the response, a
  // multi-part reply, an injected embed (tweet/image), a tool result — stays
  // pinned and never re-arms the fallback, so a late-loading embed can't yank
  // the viewport down to the bottom.
  let currentTurnAnchored = false;
  // The sent user message the anchor is parked on. It holds for the whole
  // reply, however tall the message or its tool prelude: the jump-to-latest
  // control is how the reader reaches output below the fold.
  let anchoredMessageId: string | null = null;
  // Has the reader taken over positioning this turn? Layout re-pins (a host
  // move) stand down once they demonstrably are, and never move the viewport
  // under them. Reset on each user send (an explicit "take me along").
  //
  // Driven by real INPUT (wheel, touch drag, pointer press, transcript
  // keyboard nav, focus, selection) rather than by scroll deltas: streaming
  // content under a pinned anchor makes the browser clamp and then restore
  // scrollTop, which is indistinguishable from a deliberate scroll if you only
  // look at the scroll event.
  let readerEngagedThisTurn = false;
  const markReaderEngaged = () => {
    // Reader input invalidates only reveal scrolling, including its settle pass.
    // The animation checks this generation before its next scroll write.
    revealScrollGeneration++;
    readerEngagedThisTurn = true;
    revealTranscriptScrollbar();
  };
  // Dedupes assistant-turn detection across token-by-token re-renders.
  let lastHandledAssistantId: string | null = null;

  // Scroll events caused by layout, scroll anchoring, and smooth-scroll
  // easing can easily move by a couple pixels. Keep manual wheel intent
  // responsive, but require a slightly larger raw scroll delta before we
  // treat a plain scroll event as the user breaking away.
  const USER_SCROLL_THRESHOLD = 4;
  const BOTTOM_THRESHOLD = 24;
  const AUTO_SCROLL_SNAP_THRESHOLD = 80;

  // Scrollbar visibility policy (features.scrollBehavior.scrollbar). The
  // resolved policy is stamped on the root once and CSS scopes every
  // scroller off it. Under "on-scroll" the transcript bar reveals here on
  // genuine reader input (markReaderEngaged is the funnel for wheel, touch,
  // pointer, and nav keys, so widget-driven scrolling never reveals it) and
  // hides again after an idle delay, but only at the resting position: away
  // from rest the bar is the positional affordance and stays. The scroll
  // back down re-enters through markReaderEngaged and re-arms the timer.
  const SCROLLBAR_IDLE_MS = 1000;
  let scrollbarIdleTimer: ReturnType<typeof setTimeout> | null = null;
  const applyScrollbarPolicy = () => {
    mount.setAttribute("data-persona-scrollbar", getScrollbarPolicy());
    if (getScrollbarPolicy() !== "on-scroll") {
      if (scrollbarIdleTimer !== null) clearTimeout(scrollbarIdleTimer);
      scrollbarIdleTimer = null;
      body.removeAttribute("data-persona-scrollbar-visible");
    }
  };
  const revealTranscriptScrollbar = () => {
    if (getScrollbarPolicy() !== "on-scroll") return;
    body.setAttribute("data-persona-scrollbar-visible", "");
    if (scrollbarIdleTimer !== null) clearTimeout(scrollbarIdleTimer);
    scrollbarIdleTimer = setTimeout(() => {
      scrollbarIdleTimer = null;
      if (isElementNearBottom(body, BOTTOM_THRESHOLD)) {
        body.removeAttribute("data-persona-scrollbar-visible");
      }
    }, SCROLLBAR_IDLE_MS);
  };
  applyScrollbarPolicy();
  destroyCallbacks.push(() => {
    if (scrollbarIdleTimer !== null) clearTimeout(scrollbarIdleTimer);
  });
  const messageState = new Map<
    string,
    { streaming?: boolean; role: AgentWidgetMessage["role"] }
  >();
  // The live call already showed its AI-disclosure notice; in the composer bar
  // (no status line) it holds the input placeholder, restored at hang-up.
  let voiceDisclosed = false;
  let placeholderBeforeDisclosure: string | null = null;
  const voiceState = {
    active: false,
    manuallyDeactivated: false,
    lastUserMessageWasVoice: false,
    lastUserMessageId: null as string | null
  };
  // First stamp: an untouched composer emits no store change, so the attribute
  // would otherwise not appear until the first keystroke.
  syncComposerCompact();
  const voiceAutoResumeMode = config.voiceRecognition?.autoResume ?? false;
  // A session-voice call is live while listening OR while its continuous
  // (barge-in) mic is hot: a full-duplex call cycles listening → processing →
  // speaking, and `isVoiceActive()` alone (status === listening) would record a
  // live call as hung up and never restore it on the next page.
  const isSessionVoiceLive = () => session.isVoiceActive() || session.isBargeInActive();
  // Set on `pagehide`: the unload tears the call down without the visitor
  // hanging up, so its trailing status changes must not overwrite the
  // persisted "call was live" record the next page restores from.
  let pageHiding = false;
  const emitVoiceState = (source: AgentWidgetVoiceStateEvent["source"]) => {
    eventBus.emit("voice:state", {
      active: voiceState.active,
      source,
      timestamp: Date.now()
    });
  };
  const persistVoiceMetadata = () => {
    updateSessionMetadata((prev) => ({
      ...prev,
      voiceState: {
        active: voiceState.active,
        timestamp: Date.now(),
        manuallyDeactivated: voiceState.manuallyDeactivated
      }
    }));
  };
  const maybeRestoreVoiceFromMetadata = () => {
    if (config.voiceRecognition?.enabled === false || config.voiceRecognition?.dictation || (config.voiceRecognition?.provider?.type === "custom" && config.voiceRecognition.completionBehavior === "review")) return;
    const rawVoiceState = ensureRecord((persistentMetadata as any).voiceState);
    const wasActive = Boolean(rawVoiceState.active);
    const timestamp = Number(rawVoiceState.timestamp ?? 0);
    voiceState.manuallyDeactivated = Boolean(rawVoiceState.manuallyDeactivated);
    if (wasActive && Date.now() - timestamp < VOICE_STATE_RESTORE_WINDOW) {
      const generation = restartGeneration;
      setTimeout(() => {
        if (!canRestart(generation)) return;
        if (!voiceState.active) {
          voiceState.manuallyDeactivated = false;
          if (usesSessionVoice(config.voiceRecognition?.provider)) {
            session.toggleVoice().then(() => {
              if (!canRestart(generation)) return;
              voiceState.active = isSessionVoiceLive();
              emitVoiceState("restore");
              if (voiceState.active) {
                applyRuntypeMicRecordingStyles();
              } else {
                // Typically the autoplay policy: this page has no user gesture
                // yet, so the audio graph can't start. A mic tap resumes it.
                persistVoiceMetadata();
                showComposerNotice(VOICE_RESUME_NOTICE, 10_000);
              }
            });
          } else {
            startVoiceRecognition("restore");
          }
        }
        // Session voice redials at once (a navigation mid-call should feel
        // seamless); toggleVoice() already waits on the provider setup.
      }, usesSessionVoice(config.voiceRecognition?.provider) ? 0 : 1000);
    }
  };
  // Re-stamp a live call as the page unloads: the restore window counts from
  // the navigation, not from when the (possibly long) call was started. Protect
  // that record from the trailing disconnect statuses, including cached hides.
  const handleVoicePageHide = () => {
    pageHiding = true;
    if (voiceState.active) persistVoiceMetadata();
  };
  if (typeof window !== "undefined") {
    window.addEventListener("pagehide", handleVoicePageHide);
    destroyCallbacks.push(() => window.removeEventListener("pagehide", handleVoicePageHide));
  }

  const getMessagesForPersistence = () =>
    session
      ? messagesForStorage(session.getMessages()).filter(msg => !(msg as any).__skipPersist)
      : [];

  let storageMutationTail: Promise<void> | null = null;

  const reportStorageMutationError = (label: string, error: unknown) => {
    if (typeof console !== "undefined") {
      // eslint-disable-next-line no-console
      console.error(label, error);
    }
  };

  const runStorageMutation = (
    mutation: () => void | Promise<void>,
    errorLabel: string
  ) => {
    const run = (): Promise<void> | null => {
      try {
        const result = mutation();
        if (!result || typeof (result as PromiseLike<void>).then !== "function") {
          return null;
        }
        return Promise.resolve(result).catch((error) => {
          reportStorageMutationError(errorLabel, error);
        });
      } catch (error) {
        reportStorageMutationError(errorLabel, error);
        return null;
      }
    };

    const prior = storageMutationTail;
    const next = prior
      ? prior.then(() => run() ?? undefined)
      : run();
    if (!next) return;

    const tail = next.finally(() => {
      if (storageMutationTail === tail) {
        storageMutationTail = null;
      }
    });
    storageMutationTail = tail;
  };

  function persistState(messagesOverride?: AgentWidgetMessage[]) {
    if (conversationPersistenceOwner !== null || !storageAdapter?.save) return;

    // Allow saving even if session doesn't exist yet (for metadata during init)
    const messages = messagesOverride
      ? messagesForStorage(messagesOverride)
      : session
        ? getMessagesForPersistence()
        : [];

    const durableHandle =
      personaOwnsClientTokenRecovery && session?.getResumableHandle();
    if (durableHandle) {
      persistentMetadata = {
        ...persistentMetadata,
        durableResume: {
          executionId: durableHandle.executionId,
          after: durableHandle.lastEventId,
        },
      };
    }
    const payload: AgentWidgetStoredState = {
      messages,
      metadata: persistentMetadata,
      artifacts: lastArtifactsState.artifacts,
      selectedArtifactId: lastArtifactsState.selectedId,
      ...(currentStoredDraft ? { draft: currentStoredDraft } : {})
    };
    runStorageMutation(
      () => storageAdapter.save!(payload),
      "[AgentWidget] Failed to persist state:"
    );
  }

  // --- Draft persistence (roadmap section 12) --------------------------------
  // The draft rides the conversation storage payload, so the host-owned adapter
  // keeps owning conversation scoping. No `File` objects: attachments are not
  // part of a persisted draft in v1.
  let currentStoredDraft: AgentWidgetStoredDraft | undefined;
  let conversationPersistenceOwner: number | null = null;

  const draftPersistenceEnabled = (): boolean => {
    if (!storageAdapter?.save) return false;
    if (config.persistState === false) return false;
    if (typeof config.persistState === "object") {
      return config.persistState.persist?.draft !== false;
    }
    return true;
  };

  const draftWriter: DraftWriter = createDraftWriter({
    write: () => {
      if (!draftPersistenceEnabled()) return;
      const state = composerStore.getState();
      const next = buildStoredDraft({
        text: state.text,
        mentionRefs: state.mentionRefs,
        contentSegments: readInlineContentSegments(),
        selectedModelId: state.selectedModelId,
        activeModeIds: state.activeModeIds,
        quote: state.quote
      });
      // Composer state changes for many reasons (phase, locks, send
      // availability). Only a real draft change is worth a storage write.
      if (JSON.stringify(next) === JSON.stringify(currentStoredDraft)) return;
      currentStoredDraft = next;
      persistState();
    }
  });

  function scheduleDraftPersist(): void {
    if (!draftPersistenceEnabled()) return;
    draftWriter.schedule();
  }

  function flushDraftPersist(): void {
    draftWriter.flush();
  }

  /**
   * Replay a stored draft into the live composer. Text always restores; mention
   * tokens only while their source id still exists in the current config (they
   * degrade to the plain text otherwise, with the unresolved structured context
   * omitted); a stale model id or mode id is dropped.
   */
  function restoreComposerDraft(draft: AgentWidgetStoredDraft | undefined): void {
    if (!draftPersistenceEnabled()) return;
    const restored = rehydrateStoredDraft(draft, {
      mentionSourceIds: (config.contextMentions?.sources ?? []).map((s) => s.id),
      modelIds: (config.composer?.models ?? []).map((model) => model.id),
      modes: config.composer?.modes ?? []
    });
    if (!restored) return;
    currentStoredDraft = draft;
    if (textarea && !textarea.value) {
      textarea.value = restored.text;
      textarea.dispatchEvent(new Event("input", { bubbles: true }));
      syncComposerText();
    }
    if (restored.selectedModelId) {
      composerStore.setSelectedModelId(restored.selectedModelId);
      composerCoreControls?.repaint();
    }
    // Unconditional: a restored draft's selection beats
    // `composer.defaultActiveModeIds`, including when it deselected everything.
    setActiveComposerModes(restored.activeModeIds ?? []);
    if (restored.quote) setComposerQuote(restored.quote);
    // The replay itself is not a new edit: it must not immediately rewrite the
    // stored payload it came from.
    draftWriter.cancel();
  }

  /** Send acceptance and clear-chat both drop the draft immediately. */
  function clearPersistedDraft(): void {
    draftWriter.cancel();
    if (!currentStoredDraft) return;
    currentStoredDraft = undefined;
    if (draftPersistenceEnabled()) persistState();
  }

  // Track ongoing smooth scroll animation
  const smoothScrollAnimation = createScrollAnimation();

  // Get the scrollable container using its unique ID
  const getScrollableContainer = (): HTMLElement => {
    // Use the unique ID for reliable selection
    const scrollable = wrapper.querySelector('#persona-scroll-container') as HTMLElement;
    // Fallback to body if ID not found (shouldn't happen, but safe fallback)
    return scrollable || body;
  };

  const cancelSmoothScroll = () => {
    smoothScrollAnimation.cancel();
    isAutoScrolling = false;
  };

  const cancelAutoScroll = () => {
    if (scrollRAF !== null) {
      cancelAnimationFrame(scrollRAF);
      scrollRAF = null;
    }
    hasPendingAutoScroll = false;
    cancelSmoothScroll();
  };

  // True when a response is streaming in below the reader's current position,
  // i.e. content is arriving out of view. Drives the "still streaming" hint on
  // the scroll-to-bottom affordance (Principle 8: show what's happening out of
  // view). In anchor-top mode this is gated behind `showActivityWhilePinned`
  // so the historical "silent while pinned" behavior is preserved by default.
  // Busy covers network/tool work too. Durable reconnects and waits for user
  // input are idle even while the session retains its busy state.
  const isScrollResponseActive = () => {
    const status = session?.getStatus();
    return isStreaming && status !== "paused" && status !== "resuming" && status !== "error" &&
      !session?.getMessages().some((message) =>
        (message.agentMetadata?.awaitingLocalTool === true && message.agentMetadata.askUserQuestionAnswered !== true) ||
        (message.variant === "approval" && message.approval?.status === "pending"));
  };
  const isStreamingOutOfView = () =>
    isScrollResponseActive() && isAwayFromLatest() &&
    (getScrollMode() !== "anchor-top" || isActivityWhilePinnedEnabled());

  const updateScrollToBottomCountBadge = () => {
    const visible = scrollToBottomButton.getAttribute("data-persona-scroll-to-bottom-visible") === "true";
    scrollToBottomControl.update(config, visible, isStreamingOutOfView(), newMessagesSincePause,
      visible && isApprovalBelow());
  };

  const resetNewMessagesCount = () => {
    if (newMessagesSincePause === 0) return;
    newMessagesSincePause = 0;
    updateScrollToBottomCountBadge();
  };

  // Spacer air and composer reserve are scrollable, but aren't unread content.
  // Compare the real content bottom with the readable viewport instead. An
  // overlay footer clips that viewport; a block footer sits below it.
  const readableViewportHeight = () => {
    const bodyRect = body.getBoundingClientRect();
    const footerRect = footer.getBoundingClientRect();
    return footerRect.height > 0 && footerRect.bottom > bodyRect.top
      ? Math.min(body.clientHeight, Math.max(0, footerRect.top - bodyRect.top - body.clientTop))
      : body.clientHeight;
  };
  const isNearLatestContent = () => {
    const contentHeight = measureContentHeight(anchorState?.spacerHeight ?? 0, false);
    return contentHeight - body.scrollTop - readableViewportHeight() <= BOTTOM_THRESHOLD;
  };

  // A pending approval whose card starts below the readable viewport. The
  // anchor never scrolls the reader to it, so the jump control names it.
  const isBelowFold = (element: HTMLElement) =>
    offsetTopWithinBody(element) > body.scrollTop + readableViewportHeight() - BOTTOM_THRESHOLD;
  const pendingApprovalBubbles = () => (session?.getMessages() ?? [])
    .filter((message) => message.variant === "approval" && message.approval?.status === "pending")
    .map((message) => queryMessageBubble(message.id))
    .filter((bubble): bubble is HTMLElement => Boolean(bubble?.isConnected));
  const isApprovalBelow = () => pendingApprovalBubbles().some(isBelowFold);
  const isFullyInView = (element: HTMLElement) => {
    const top = offsetTopWithinBody(element);
    return top >= body.scrollTop && top + element.offsetHeight <= body.scrollTop + readableViewportHeight();
  };
  // The approvals a jump from the "Approval needed" control was taken for.
  // Once it lands, focus moves to the first of them whose primary action is
  // fully in view, and never to one the reader can't see.
  let approvalJumpTargets: string[] = [];
  const focusApprovalJumpTarget = () => {
    const targets = approvalJumpTargets;
    approvalJumpTargets = [];
    for (const id of targets) {
      const allow = queryMessageBubble(id)?.querySelector<HTMLButtonElement>('[data-action="allow"]');
      if (allow && isFullyInView(allow)) { allow.focus({ preventScroll: true }); return; }
    }
  };

  // Whether the user is currently away from the latest content: drives both
  // the scroll-to-bottom affordance and the new-messages badge. Effective
  // follow must be paused, and every mode requires actual content below the
  // readable viewport. Reaching visible content never changes follow intent.
  const isAwayFromLatest = () =>
    (!isFollowEffective() || !autoFollow.isFollowing()) && !isNearLatestContent();

  // Empty transcript = welcome-only view: there is no "latest message" to
  // follow, jump to, or badge, and bottom-scrolling clips the greeting when
  // the welcome content slightly overflows the viewport.
  const transcriptHasMessages = () =>
    !!session && session.getMessages().length > 0;

  const syncScrollToBottomButton = () => {
    if (!isScrollToBottomEnabled() || fullPanelOverlayVisible()) {
      scrollToBottomControl.update(config, false);
      if (scrollToBottomButton.parentNode) {
        scrollToBottomButton.remove();
      }
      return;
    }
    const host = scrollToBottomHost();
    if (scrollToBottomButton.parentNode !== host) {
      host.insertBefore(scrollToBottomButton, host === container ? panelSuggestions : null);
    }
    updateScrollToBottomButtonOffset();
    const hasOverflow = getScrollBottomOffset(body) > 0;
    const show = !latestScroll.active && hasOverflow && isAwayFromLatest() && transcriptHasMessages();
    if (!show) newMessagesSincePause = 0;
    scrollToBottomControl.update(config, show, isStreamingOutOfView(), newMessagesSincePause, show && isApprovalBelow());
  };

  const pauseAutoScroll = () => {
    if (!autoFollow.pause()) return;
    cancelAutoScroll();
    syncScrollToBottomButton();
  };

  const resumeAutoScroll = () => {
    autoFollow.resume();
    resetNewMessagesCount();
    syncScrollToBottomButton();
  };

  const scheduleAutoScroll = (force = false) => {
    // Auto-follow applies in "follow" mode, and in anchor-top only for a
    // no-anchor fallback turn (see `isFollowEffective`). Anchored anchor-top
    // turns and "none" never chase the bottom during streaming.
    if (latestScroll.active || !isFollowEffective()) return;
    if (!transcriptHasMessages()) return;

    if (!autoFollow.isFollowing()) return;

    if (!force && !isStreaming) return;

    // Only cancel the pending schedule rAF: keep the ongoing smooth scroll
    // animation alive so isAutoScrolling stays true.  This prevents scroll
    // events fired by DOM morphing (between cancel and the next rAF) from
    // being misinterpreted as user-initiated upward scrolls that would
    // permanently pause auto-follow during streaming.
    // smoothScrollToBottom() already calls cancelSmoothScroll() internally
    // before starting its new animation.
    if (scrollRAF !== null) {
      cancelAnimationFrame(scrollRAF);
      scrollRAF = null;
    }

    // Treat the render -> next-rAF window as programmatic scrolling too.
    // This prevents layout/scroll-anchoring scroll events fired before the
    // actual smooth scroll starts from being misread as user intent.
    hasPendingAutoScroll = true;
    scrollRAF = requestAnimationFrame(() => {
      scrollRAF = null;
      hasPendingAutoScroll = false;
      if (!autoFollow.isFollowing()) return;
      smoothScrollToBottom(getScrollableContainer(), force ? 220 : 140);
    });
  };

  // Generic eased scroll animation. `resolveTarget` is re-read every frame so
  // a moving target (the bottom of a streaming transcript) stays accurate;
  // `shouldContinue` lets the caller cancel mid-flight (e.g. when auto-follow
  // pauses). Scroll events emitted by the animation are masked from the
  // user-intent detector via `isAutoScrolling`.
  const animateScrollTo = (
    element: HTMLElement,
    resolveTarget: () => number,
    duration: number,
    shouldContinue: () => boolean = () => true
  ) => {
    cancelSmoothScroll();
    isAutoScrolling = true;
    smoothScrollAnimation.start(element, resolveTarget, duration,
      () => { lastScrollTop = element.scrollTop; },
      () => { isAutoScrolling = false; }, shouldContinue);
  };

  const latestScroll = createScrollToLatest(body, container,
    () => { lastScrollTop = body.scrollTop; },
    () => { resumeAutoScroll(); scheduleAutoScroll(true); syncScrollToBottomButton(); focusApprovalJumpTarget(); },
    () => { approvalJumpTargets = []; autoFollow.pause(); syncScrollToBottomButton(); });

  // Custom smooth scroll animation with easing
  const smoothScrollToBottom = (element: HTMLElement, duration = 500) => {
    const distance = getScrollBottomOffset(element) - element.scrollTop;

    // If already at bottom or very close, skip animation to prevent glitch
    if (Math.abs(distance) < 1) {
      lastScrollTop = element.scrollTop;
      return;
    }

    // If the transcript has fallen noticeably behind, catch up immediately
    // instead of easing over multiple frames. This keeps fast streaming /
    // bursty tool and reasoning updates pinned to the bottom.
    if (Math.abs(distance) >= AUTO_SCROLL_SNAP_THRESHOLD) {
      cancelSmoothScroll();
      isAutoScrolling = true;
      element.scrollTop = getScrollBottomOffset(element);
      lastScrollTop = element.scrollTop;
      isAutoScrolling = false;
      return;
    }

    animateScrollTo(
      element,
      () => getScrollBottomOffset(element),
      duration,
      () => autoFollow.isFollowing()
    );
  };

  // Instant jump used for initial mount / panel open in non-follow scroll
  // modes (where scheduleAutoScroll is inert).
  const jumpToBottomInstant = () => {
    if (!transcriptHasMessages()) {
      syncScrollToBottomButton();
      return;
    }
    const element = getScrollableContainer();
    isAutoScrolling = true;
    element.scrollTop = getScrollBottomOffset(element);
    lastScrollTop = element.scrollTop;
    isAutoScrolling = false;
    syncScrollToBottomButton();
  };

  const getTranscriptSelection = (): Selection | null => {
    // Selections inside a shadow root are not always reflected by
    // document.getSelection(); prefer the shadow root's view when available
    // (non-standard but supported where it matters).
    const root = body.getRootNode();
    const shadowSelection =
      typeof (root as ShadowRoot & { getSelection?: () => Selection | null })
        .getSelection === "function"
        ? (root as ShadowRoot & { getSelection: () => Selection | null }).getSelection()
        : null;
    return shadowSelection ?? body.ownerDocument.getSelection();
  };
  const hasActiveTranscriptSelection = () =>
    hasSelectionWithin(getTranscriptSelection(), body);

  // Principle 11: reopen where the reader left off. When `restorePosition` is
  // "last-user-turn" and there is pre-existing history, land with the last user
  // message pinned near the top of the viewport instead of jumping to the
  // absolute bottom. Returns true when it handled positioning. Opt-in; the
  // default ("bottom") returns false so callers fall back to jump-to-bottom.
  const restoreScrollPosition = (): boolean => {
    if (getScrollRestorePosition() !== "last-user-turn") return false;
    const messages = session?.getMessages() ?? [];
    // A *restore* only makes sense when reopening existing history; a fresh
    // (empty or single-turn) conversation should still start at the latest.
    if (messages.length < 2) return false;
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    if (!lastUser) return false;
    const escapedId =
      typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(lastUser.id)
        : lastUser.id.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    const bubble = body.querySelector<HTMLElement>(
      `[data-message-id="${escapedId}"]`
    );
    if (!bubble) return false;
    const target = Math.min(
      Math.max(0, offsetTopWithinBody(bubble) - getAnchorTopOffset()),
      getScrollBottomOffset(body)
    );
    isAutoScrolling = true;
    body.scrollTop = target;
    lastScrollTop = body.scrollTop;
    isAutoScrolling = false;
    // In follow mode, deliberately landing above the bottom means we are not
    // following; pause so the first streamed token doesn't yank the reader
    // down. (In anchor-top/none there is no follow state to manage.)
    if (
      getScrollMode() === "follow" &&
      !isElementNearBottom(body, BOTTOM_THRESHOLD)
    ) {
      autoFollow.pause();
    }
    syncScrollToBottomButton();
    return true;
  };

  const setAnchorSpacerHeight = (height: number) => {
    const rounded = Math.max(0, Math.round(height));
    anchorSpacer.style.height = `${rounded}px`;
    anchorSpacer.style.display = rounded > 0 ? "" : "none";
    if (anchorState) {
      anchorState.spacerHeight = Math.max(0, height);
    }
  };

  // True scroll-content height, excluding the anchor spacer. Never derive
  // this from `scrollHeight - spacer`: scrollHeight floors at clientHeight,
  // so content shorter than the viewport reads as viewport-sized and the
  // spacer comes up short by exactly the empty space below the content — the
  // anchor target then sits past maxScrollTop, the ease clamps early, and
  // every transient content dip during streaming bounces the viewport.
  // Layoutless environments (jsdom) measure 0; fall back to the naive value.
  const measureContentHeight = (previousSpacerHeight: number, includePadding = true): number => {
    const paddingBottom =
      parseFloat(getComputedStyle(body).paddingBottom) || 0;
    let bottom = 0;
    for (const node of body.children) {
      const child = node as HTMLElement;
      if (child === anchorSpacer || child.offsetHeight === 0) continue;
      bottom = Math.max(bottom, offsetTopWithinBody(child) + child.offsetHeight);
    }
    const naive = body.scrollHeight - previousSpacerHeight;
    const height = bottom > 0 ? Math.min(bottom + paddingBottom, naive) : naive;
    return includePadding ? height : height - paddingBottom;
  };

  // Stream-end spacer trim: the ResizeObserver drain is growth-driven, so it
  // stops the moment content stops growing and can leave leftover reserve —
  // dead scrollable air below the last message, plus a scroll-to-bottom
  // arrow pointing at nothing. Trim to what the reader's current position
  // actually needs plus the anti-bounce slack (shrink-only, like the drain).
  const trimAnchorSpacerToRest = () => {
    if (!anchorState || anchorState.spacerHeight <= 0 || anchorState.pending) return;
    const contentHeight = measureContentHeight(anchorState.spacerHeight);
    const required = Math.max(
      0,
      Math.round(body.scrollTop) +
        body.clientHeight -
        contentHeight +
        BOTTOM_THRESHOLD
    );
    if (required < anchorState.spacerHeight) {
      setAnchorSpacerHeight(required);
      syncScrollToBottomButton();
    }
  };

  const resetAnchorState = () => {
    if (anchorRAF !== null) {
      cancelAnimationFrame(anchorRAF);
      anchorRAF = null;
    }
    // Also stop an in-flight anchor scroll animation: otherwise its
    // remaining frames keep easing scrollTop toward the stale anchor target
    // after a jump-to-latest, chat clear, or scroll-mode change.
    latestScroll.cancel();
    cancelSmoothScroll();
    anchorState = null;
    anchorSpacer.style.height = "0px";
    anchorSpacer.style.display = "none";
  };

  const escapeMessageId = (messageId: string): string =>
    typeof CSS !== "undefined" && typeof CSS.escape === "function"
      ? CSS.escape(messageId)
      : messageId.replace(/\\/g, "\\\\").replace(/"/g, '\\"');

  const queryMessageBubble = (messageId: string): HTMLElement | null =>
    body.querySelector<HTMLElement>(
      `[data-message-id="${escapeMessageId(messageId)}"]`
    );

  // Anchor-top mode: scroll `messageId` to rest `anchorTopOffset` px below the
  // viewport top and hold it there while the response streams in beneath it.
  // Deferred one frame so the message bubble has been rendered and laid out.
  const scheduleAnchorToMessage = (messageId: string) => {
    if (anchorRAF !== null) {
      cancelAnimationFrame(anchorRAF);
    }
    anchorRAF = requestAnimationFrame(() => {
      anchorRAF = null;
      const bubble = queryMessageBubble(messageId);
      if (!bubble) return;

      // Bubble top relative to the scroll content. offsetTop is used instead
      // of getBoundingClientRect so in-flight entrance animations (transforms)
      // can't skew the target.
      const anchorOffsetTop = offsetTopWithinBody(bubble);

      const previousSpacerHeight = anchorState?.spacerHeight ?? 0;
      const contentHeight = measureContentHeight(previousSpacerHeight);
      const { targetScrollTop, spacerHeight } = computeAnchorScrollState({
        anchorOffsetTop,
        topOffset: getAnchorTopOffset(),
        viewportHeight: body.clientHeight,
        contentHeight,
        // Slack past the held position so transient content dips during
        // streaming (indicator swaps) never clamp scrollTop and bounce the
        // viewport. Capped at BOTTOM_THRESHOLD so the slack region still
        // counts as "near bottom" and never surfaces the jump affordance.
        slack: BOTTOM_THRESHOLD
      });

      anchorState = {
        pending: 0,
        initialSpacerHeight: spacerHeight,
        contentHeightAtAnchor: contentHeight,
        spacerHeight
      };
      setAnchorSpacerHeight(spacerHeight);
      // Live target: layout above the bubble can shrink mid-ease (the welcome
      // starter row hides right after this measurement frame), and a frozen
      // snapshot would carry the bubble past the offset and under the header.
      // animateScrollTo re-resolves per frame, so re-derive the pin from the
      // bubble's current position.
      animateScrollTo(
        body,
        () =>
          bubble.isConnected
            ? Math.max(0, offsetTopWithinBody(bubble) - getAnchorTopOffset())
            : targetScrollTop,
        220
      );
    });
  };

  // Welcome dismissal removes the host's layout (and the roomier body gap) in
  // a single frame after its fade-out — after the anchor ease already scrolled
  // against the pre-collapse layout, so the browser would clamp scrollTop and
  // visibly yank the pinned message. Recompute the anchor against the
  // collapsed layout and jump instead of easing: the bubble is already
  // resting at the offset, so the re-pin is visually a no-op.
  const repinAnchoredMessage = () => {
    if (getScrollMode() !== "anchor-top") return;
    if (!anchorState || !anchoredMessageId) return;
    const bubble = queryMessageBubble(anchoredMessageId);
    if (!bubble) return;
    cancelSmoothScroll();
    const previousSpacerHeight = anchorState.spacerHeight;
    const contentHeight = measureContentHeight(previousSpacerHeight);
    const { targetScrollTop, spacerHeight } = computeAnchorScrollState({
      anchorOffsetTop: offsetTopWithinBody(bubble),
      topOffset: getAnchorTopOffset(),
      viewportHeight: body.clientHeight,
      contentHeight,
      slack: BOTTOM_THRESHOLD
    });
    anchorState = {
      pending: 0,
      initialSpacerHeight: spacerHeight,
      contentHeightAtAnchor: contentHeight,
      spacerHeight
    };
    setAnchorSpacerHeight(spacerHeight);
    isAutoScrolling = true;
    body.scrollTop = targetScrollTop;
    lastScrollTop = body.scrollTop;
    isAutoScrolling = false;
    syncScrollToBottomButton();
  };

  // Content growth handler (ResizeObserver-driven). In follow mode this is
  // what keeps the transcript pinned when content grows *without* a render
  // event: images/embeds finishing loading mid-stream, fonts swapping,
  // the panel or composer resizing. In anchor-top mode it gives spacer room
  // back as the streamed response grows (shrink-only, so total scroll height
  // stays constant and nothing jumps).
  const handleContentResize = () => {
    if (isFollowEffective()) {
      syncScrollToBottomButton();
      if (!autoFollow.isFollowing()) return;
      if (isElementNearBottom(body, 1)) return;
      scheduleAutoScroll(!isStreaming);
      return;
    }
    if (anchorState && anchorState.initialSpacerHeight > 0 && !anchorState.pending) {
      const currentContentHeight = body.scrollHeight - anchorState.spacerHeight;
      const next = computeShrunkSpacerHeight({
        initialSpacerHeight: anchorState.initialSpacerHeight,
        contentHeightAtAnchor: anchorState.contentHeightAtAnchor,
        currentContentHeight
      });
      if (next !== anchorState.spacerHeight) {
        setAnchorSpacerHeight(next);
      }
    }
    syncScrollToBottomButton();
  };

  // Re-applies a user bubble's collapse state in place, holding the viewport.
  // A toggle holds the bubble's top (a collapse whose top scrolled out of view
  // brings it back to the anchor offset, so the toggle stays in view). A measurement
  // change holds what the reader sees: the top when it is in view, otherwise
  // the bubble's bottom edge, so the content below never moves. While
  // anchored, the spacer is re-reserved first so a shrink can't clamp
  // scrollTop, then trimmed to the held position with a fresh drain baseline.
  const applyUserCollapseChange = (
    bubble: HTMLElement,
    region: HTMLElement,
    messageId: string,
    render: UserMessageCollapseRender,
    intent: "toggle" | "measure"
  ) => {
    messageCache.delete(messageId);
    const holdViewport = intent === "toggle" || !(isFollowEffective() && autoFollow.isFollowing());
    if (!holdViewport) {
      applyUserCollapseState(bubble, region, messageId, render);
      return;
    }
    const bodyTop = body.getBoundingClientRect().top;
    const before = bubble.getBoundingClientRect();
    const anchor = anchorState;
    if (anchor) {
      cancelSmoothScroll();
      setAnchorSpacerHeight(anchor.spacerHeight + bubble.offsetHeight);
    }
    applyUserCollapseState(bubble, region, messageId, render);
    const after = bubble.getBoundingClientRect();
    const prevTop = before.top - bodyTop;
    const shift = intent === "toggle"
      ? after.top - bodyTop - (render.state === "collapsed" && prevTop < 0 ? getAnchorTopOffset() : prevTop)
      : prevTop >= 0 ? after.top - before.top : after.bottom - before.bottom;
    const target = Math.max(0, body.scrollTop + shift);
    if (anchor && anchorState === anchor) {
      setAnchorSpacerHeight(target + body.clientHeight + BOTTOM_THRESHOLD - measureContentHeight(anchor.spacerHeight));
      anchor.initialSpacerHeight = anchor.spacerHeight;
      anchor.contentHeightAtAnchor = body.scrollHeight - anchor.spacerHeight;
    }
    if (Math.abs(target - body.scrollTop) >= 1) {
      isAutoScrolling = true;
      body.scrollTop = target;
      lastScrollTop = body.scrollTop;
      isAutoScrolling = false;
    }
    syncScrollToBottomButton();
  };

  // Measures every built-in user bubble's real rendered height (markdown,
  // code, wrapping, attachments) after a morph or resize and collapses the
  // ones that overflow. Unlaid regions (a closed panel, jsdom) measure 0 and
  // keep their state until a later pass can measure them.
  const syncUserMessageCollapse = () => {
    if (!isUserMessageCollapseEnabled(config)) return;
    const maxHeight = resolveUserMessageCollapseMaxHeight(config);
    for (const region of Array.from(
      messagesWrapper.querySelectorAll<HTMLElement>(`[${USER_COLLAPSE_REGION_ATTR}]`)
    )) {
      const bubble = region.closest<HTMLElement>("[data-message-id]");
      const messageId = bubble?.getAttribute("data-message-id");
      if (!bubble || !messageId) continue;
      const naturalHeight = region.scrollHeight;
      if (naturalHeight > 0) {
        userCollapseOverflow.set(messageId, userMessageOverflows(naturalHeight, maxHeight));
      }
      const overflows = userCollapseOverflow.get(messageId) ?? false;
      const current = region.getAttribute(USER_COLLAPSE_REGION_ATTR);
      // An interim voice transcript stays expanded so the speaker can watch it.
      if (current === "live") continue;
      if (current === "settling") userCollapseLive.delete(messageId);
      const state: UserMessageCollapseState = !overflows
        ? "fit"
        : userCollapseToggled.get(messageId) ? "expanded" : "collapsed";
      if (state === current) continue;
      applyUserCollapseChange(bubble, region, messageId, {
        state, maxHeight, ...resolveUserMessageCollapseLabels(config),
      }, "measure");
    }
  };

  const pruneUserMessageCollapse = (messages: AgentWidgetMessage[]) => {
    if (userCollapseOverflow.size === 0 && userCollapseToggled.size === 0 && userCollapseLive.size === 0) return;
    const ids = new Set(messages.map((message) => message.id));
    for (const map of [userCollapseOverflow, userCollapseToggled, userCollapseLive]) {
      for (const id of map.keys()) if (!ids.has(id)) map.delete(id);
    }
  };

  // Reacts to a user message the user just sent (seeded so restored history
  // never triggers it). Follow mode re-sticks to the bottom even if the user
  // had scrolled up: sending is an unambiguous "take me to the latest"
  // signal. Anchor-top mode pins the sent message near the viewport top.
  const handleUserMessageSent = (messageId: string) => {
    const mode = getScrollMode();
    if (mode === "follow") {
      resumeAutoScroll();
      scheduleAutoScroll(true);
    } else if (mode === "anchor-top") {
      // A real anchor now drives the conversation: disarm the no-anchor
      // fallback. Every follow-on assistant message stays anchored until the
      // next user send.
      followFallbackActive = false;
      currentTurnAnchored = true;
      // A send is an explicit "take me along": it re-arms anchoring even if
      // the reader had scrolled away during the last turn.
      readerEngagedThisTurn = false;
      anchoredMessageId = messageId;
      scheduleAnchorToMessage(messageId);
    }
  };

  // Reacts to a new assistant message that arrived without a fresh user send.
  // Only meaningful in anchor-top. While the conversation is anchored (a user
  // has sent at least once), follow-on assistant content — the response, a
  // multi-part reply, an injected embed, a tool result — keeps the anchor so a
  // late-loading embed never yanks the viewport. Only when nothing has anchored
  // yet (first-load / proactive-first streaming) does it fall back to
  // follow-to-bottom so the content isn't stranded off-screen.
  const handleAssistantTurnStarted = () => {
    if (getScrollMode() !== "anchor-top") return;
    if (currentTurnAnchored) {
      followFallbackActive = false;
      return;
    }
    followFallbackActive = true;
    resetAnchorState();
    resumeAutoScroll();
    scheduleAutoScroll(true);
  };

  const trackMessages = (messages: AgentWidgetMessage[]) => {
    const nextState = new Map<
      string,
      { streaming?: boolean; role: AgentWidgetMessage["role"] }
    >();

    messages.forEach((message) => {
      const previous = messageState.get(message.id);
      nextState.set(message.id, {
        streaming: message.streaming,
        role: message.role
      });

      if (!previous && message.role === "assistant") {
        eventBus.emit("assistant:message", message);
        // Count messages the user hasn't seen for the scroll-to-bottom badge.
        // Skipped in anchor-top (the user is already reading the latest turn
        // from its top, so a "new" count would normally mislead) and during
        // history hydration (restored messages aren't "missed"). When
        // `showActivityWhilePinned` is opted in, anchor-top *does* count so the
        // reader is told content is arriving offscreen below (Principle 8).
        if (
          !suppressScrollSend &&
          (getScrollMode() !== "anchor-top" || isActivityWhilePinnedEnabled()) &&
          isAwayFromLatest()
        ) {
          newMessagesSincePause += 1;
          updateScrollToBottomCountBadge();
          syncScrollToBottomButton();
          announce(
            newMessagesSincePause === 1
              ? "1 new message below."
              : `${newMessagesSincePause} new messages below.`
          );
        }
      }

      if (
        message.role === "assistant" &&
        (previous?.streaming || coalescedStreamingIds.has(message.id)) &&
        message.streaming === false
      ) {
        eventBus.emit("assistant:complete", message);
      }

      // Emit approval events
      if (message.variant === "approval" && message.approval) {
        if (!previous) {
          eventBus.emit("approval:requested", { approval: message.approval, message });
        } else if (message.approval.status !== "pending") {
          eventBus.emit("approval:resolved", { approval: message.approval, decision: message.approval.status });
        }
      }
    });

    messageState.clear();
    nextState.forEach((value, key) => {
      messageState.set(key, value);
    });
    coalescedStreamingIds.clear();
  };


  // Message rendering with plugin support (implementation)
  const applyMessageRowLayout = (
    wrapper: HTMLElement,
    role: AgentWidgetMessage["role"]
  ): void => {
    // Operator rows sit on the assistant's side and share its max width, but
    // always shrink-wrap: a full-column operator bubble reads as an input.
    const sizingRole = role === "user" ? "user" : "assistant";
    const roleLayout = config.layout?.messages?.[sizingRole];
    const configuredWidth = roleLayout?.width ?? "content";
    const width = role === "operator" ? "content" : configuredWidth;
    // Only an explicit config value is stamped inline; the 85%/100% defaults
    // live in widget.css so `components.message.<role>.maxWidth` can win.
    const fullMaxWidth = role === "operator"
      ? "var(--persona-message-operator-max-width, var(--persona-message-assistant-max-width, 100%))"
      : `var(--persona-message-${sizingRole}-max-width, 100%)`;
    const maxWidth =
      roleLayout?.maxWidth ?? (configuredWidth === "full" ? fullMaxWidth : undefined);

    wrapper.classList.add("persona-message-row");
    wrapper.classList.remove(
      "persona-message-row-user",
      "persona-message-row-assistant",
      "persona-message-row-system",
      "persona-message-row-operator",
      "persona-message-width-content",
      "persona-message-width-full"
    );
    wrapper.classList.add(
      `persona-message-row-${role}`,
      `persona-message-width-${width}`
    );
    wrapper.classList.toggle("persona-justify-end", role === "user");
    wrapper.setAttribute("data-message-role", role);
    wrapper.setAttribute("data-message-width", width);
    if (maxWidth) wrapper.style.setProperty("--persona-message-row-max-width", maxWidth);
    else wrapper.style.removeProperty("--persona-message-row-max-width");
  };

  const createMessageRow = (
    id: string,
    role: AgentWidgetMessage["role"]
  ): HTMLElement => {
    const wrapper = document.createElement("div");
    wrapper.className = "persona-flex";
    wrapper.id = `wrapper-${id}`;
    wrapper.setAttribute("data-wrapper-id", id);
    applyMessageRowLayout(wrapper, role);
    return wrapper;
  };

  // Attribute lookup stays local even when sibling widgets use identical ids.
  const findMessageRow = (container: HTMLElement, id: string): HTMLElement | undefined =>
    Array.from(container.querySelectorAll<HTMLElement>("[data-wrapper-id]"))
      .find((row) => row.getAttribute("data-wrapper-id") === id);

  const renderMessagesWithPluginsImpl = (
    container: HTMLElement,
    messages: AgentWidgetMessage[],
    transform: MessageTransform
  ) => {
    const activityIds = new Set<string>();
    ensureActivityUi();
    activityLifecycle?.observeMessages(messages, activityIds);
    // Build new content in a temporary container for morphing
    const tempContainer = document.createElement("div");

    // Unchanged messages keep their live row: the pass appends an empty
    // `data-persona-reuse` stub that `morphMessages` matches by id and skips,
    // so a render costs O(changed rows) rather than a deep clone, serialize,
    // and morph of the whole transcript. Tool grouping restyles and re-parents
    // rows after this loop, so grouped transcripts always rebuild.
    const reuseLiveRows = !config.features?.toolCallDisplay?.grouped;
    const liveRows = new Set<string>();
    if (reuseLiveRows) {
      for (const child of Array.from(container.children)) {
        const id = child.getAttribute("data-wrapper-id");
        if (id) liveRows.add(id);
      }
    }
    const nextLiveRowFingerprints = new Map<string, string>();

    // Custom renderers take precedence; the built-in path remains in the bubble.
    const inlineLoadingRenderer = plugins.find(plugin => plugin.renderLoadingIndicator)?.renderLoadingIndicator
      ?? config.loadingIndicator?.render;
    // Track active message IDs for cache pruning
    const activeMessageIds = new Set<string>();
    const messageRolesById = new Map<string, AgentWidgetMessage["role"]>();
    // Track ask_user_question tool-call ids whose bubbles were rendered this
    // pass: used to prune stale sheets from the composer overlay afterward.
    const liveAskToolIds = new Set<string>();

    // Plugins that render `ask_user_question` typically attach DOM listeners
    // directly to their buttons. The wrapper cache uses `cloneNode(true)` and
    // idiomorph inserts new nodes via `document.importNode`: both strip
    // listeners. For plugin-handled ask messages we therefore append an empty
    // stub during the morph pass and hydrate the live plugin bubble into the
    // morphed wrapper afterward (see post-morph loop below). The stub carries
    // `data-preserve-runtime` so subsequent passes leave the live wrapper
    // (with its listener-bearing bubble) untouched.
    const hasAskPlugin = plugins.some((p) => p.renderAskUserQuestion);
    type AskPluginHydrate = {
      messageId: string;
      fingerprint: string;
      bubble: HTMLElement | null;
    };
    const askPluginHydrate: AskPluginHydrate[] = [];

    // Component-directive bubbles use the same stub-and-hydrate pattern as
    // ask_user_question plugins: the renderer's HTMLElement is built live and
    // injected into the morphed wrapper afterward, so listeners attached via
    // `addEventListener` (e.g. form `submit` handlers) survive transcript
    // morphs. `bubble: null` means the fingerprint matched a previous pass and
    // the live wrapper is reused as-is.
    type ComponentDirectiveHydrate = {
      messageId: string;
      fingerprint: string;
      bubble: HTMLElement | null;
    };
    const componentDirectiveHydrate: ComponentDirectiveHydrate[] = [];
    const componentStreamingEnabled = config.enableComponentStreaming !== false;

    // Plugin-rendered approval bubbles use the same stub-and-hydrate pattern:
    // `renderApproval` may attach listeners (the built-in bubble resolves via
    // delegation on `messagesWrapper`, but a custom element owns its own
    // interactivity), and idiomorph imports nodes via `document.importNode`,
    // which strips them. So we build the live element, append a stub during
    // morph, and inject the live element afterward.
    // The built-in approval renderer is always available (as a fallback plugin),
    // so every approval flows through the stub-and-hydrate path whenever
    // approvals are enabled — a user `renderApproval` plugin just overrides it.
    const hasApprovalPlugin = config.approval !== false;
    type ApprovalPluginHydrate = {
      messageId: string;
      fingerprint: string;
      bubble: HTMLElement | null;
      focusDefault?: boolean;
    };
    const approvalPluginHydrate: ApprovalPluginHydrate[] = [];

    // History actions are per-transcript decisions, resolved once per pass: the
    // regenerate control lives on exactly one message and only while the thread
    // is settled.
    const actionsCfg = config.messageActions;
    let lastAssistantId: string | undefined;
    if (config.future?.v5Defaults && actionsCfg?.visibility === undefined) {
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === "assistant" && !messages[i].variant) {
          lastAssistantId = messages[i].id;
          break;
        }
      }
    }
    const retryableAssistantId =
      (actionsCfg?.showRegenerate === true || config.errorDisplay === "block") &&
      actionsCfg?.enabled !== false &&
      // The first pass paints restored history before the session exists.
      sessionRef.current?.isStreaming() !== true
        ? findRetryableAssistantMessageId(messages)
        : null;
    const editEnabled = actionsCfg?.showEdit === true && actionsCfg.enabled !== false;
    let lastOperatorIndex = -1;
    messages.forEach((message, index) => {
      if (message.role === "operator") lastOperatorIndex = index;
    });
    const quoteEnabled = actionsCfg?.showQuote === true && actionsCfg.enabled !== false;
    /** The live editor, re-grafted into its morphed wrapper after the pass. */
    let editHydrate: { messageId: string; element: HTMLElement } | null = null;

    // `reasoningDisplay.completedVisibility: "removed"` (or "removed-when-short"
    // for a short completed trace): no row for the finished trace, so the
    // transcript gap closes with it. Checked before the row is registered, so
    // the cached wrapper is pruned too.
    const reasoningCompletedVisibility =
      config.features?.reasoningDisplay?.completedVisibility;
    const dropReasoningRow = (reasoning: AgentWidgetReasoning): boolean => {
      if (reasoningCompletedVisibility === "removed") return true;
      if (reasoningCompletedVisibility === "removed-when-short") {
        return isShortReasoning(
          reasoning,
          config.features?.reasoningDisplay?.shortThinkThreshold
        );
      }
      return false;
    };

    // `toolCallDisplay.completedVisibility: "removed"`: same mechanism as
    // reasoning removal, no threshold variant.
    const dropCompletedToolCalls =
      config.features?.toolCallDisplay?.completedVisibility === "removed";
    /** A finished reasoning/tool row that `completedVisibility` removes from the transcript. */
    const isDroppedCompletedRow = (message: AgentWidgetMessage): boolean => {
      if (
        message.variant === "reasoning" &&
        message.reasoning?.status === "complete"
      ) {
        return dropReasoningRow(message.reasoning);
      }
      return (
        dropCompletedToolCalls &&
        message.variant === "tool" &&
        message.toolCall?.status === "complete" &&
        !message.toolCall.interrupted
      );
    };

    messages.forEach((message, messageIndex) => {
      if (isDroppedCompletedRow(message)) {
        return;
      }
      activeMessageIds.add(message.id);
      messageRolesById.set(message.id, message.role);
      if (((message.variant === "tool" && message.toolCall && showToolCalls) ||
           (message.variant === "reasoning" && message.reasoning && showReasoning)) && !ensureActivityUi()) {
        tempContainer.appendChild(createMessageRow(message.id, message.role));
        return;
      }


      // Edited message: append a runtime-preserved stub now and graft the live
      // editor in post-morph. The editor element is built once per edit session,
      // so the typed value and its listeners outlive every render.
      if (editingMessageId === message.id && messageEditEditor) {
        const stub = createMessageRow(message.id, message.role);
        stub.setAttribute("data-persona-message-edit-stub", "true");
        stub.setAttribute("data-preserve-runtime", "true");
        tempContainer.appendChild(stub);
        editHydrate = { messageId: message.id, element: messageEditEditor.element };
        return;
      }

      const askWithPlugin = hasAskPlugin && isAskUserQuestionMessage(message);
      const approvalWithPlugin =
        hasApprovalPlugin && message.variant === "approval" && !!message.approval;
      const hasDirectiveBubble =
        !askWithPlugin &&
        message.role === "assistant" &&
        !message.variant &&
        componentStreamingEnabled &&
        hasComponentDirective(message);

      // If a message stops being an approval-plugin bubble, strip
      // `data-preserve-runtime` so the next morph can replace the live wrapper.
      if (!approvalWithPlugin && lastApprovalBubbleFingerprint.has(message.id)) {
        const existing = findMessageRow(container, message.id);
        existing?.removeAttribute("data-preserve-runtime");
        disposeApprovalRender(message.id);
      }

      // If a message previously rendered as a directive bubble but no longer
      // does (e.g. content was rewritten), strip `data-preserve-runtime` from
      // the live wrapper so the next morph can replace it.
      if (!hasDirectiveBubble && lastComponentDirectiveFingerprint.has(message.id)) {
        const existing = findMessageRow(container, message.id);
        existing?.removeAttribute("data-preserve-runtime");
        disposeComponentRender(message.id);
      }

      // Fingerprint cache: skip re-rendering unchanged messages. Append the
      // ask-user-question answered/answers state so flipping `askUserQuestionAnswered`
      // (or accumulating answers) busts both the wrapper cache and the plugin's
      // `lastAskBubbleFingerprint` check, forcing a re-render of the review UX.
      const askMeta = isAskUserQuestionMessage(message)
        ? `:${message.agentMetadata?.askUserQuestionAnswered ? "a" : "u"}:${
            message.agentMetadata?.askUserQuestionAnswers
              ? Object.keys(message.agentMetadata.askUserQuestionAnswers).length
              : 0
          }`
        : "";
      const actionEligibility: MessageActionEligibility = {
        isLastAssistant: message.id === lastAssistantId,
        canRegenerate: retryableAssistantId === message.id &&
          (actionsCfg?.showRegenerate === true || message.dispatchError === true),
        canEdit: editEnabled && messageIndex > lastOperatorIndex && isEditableUserMessage(message),
        canQuote:
          quoteEnabled && !message.variant && (message.content?.trim().length ?? 0) > 0,
      };
      // Eligibility is config- and position-derived, so it must bust the wrapper
      // cache: the same message can gain or lose the retry control as the
      // transcript grows.
      const eligibilityKey = `${actionEligibility.canRegenerate ? "r" : ""}${
        actionEligibility.canEdit ? "e" : ""
      }${actionEligibility.canQuote ? "q" : ""}${actionEligibility.isLastAssistant ? "l" : ""}`;
      // A turn/execution id can contain many calls. Only an explicit per-call
      // link is safe; approval acceptance never implies tool completion.
      const linkedTools = message.approval?.toolCallId
        ? messages.filter((candidate) =>
            candidate.variant === "tool" &&
            candidate.toolCall?.id === message.approval?.toolCallId &&
            (!candidate.agentMetadata?.executionId || !message.approval?.executionId ||
              candidate.agentMetadata.executionId === message.approval.executionId))
        : [];
      const linkedTool = linkedTools.length === 1 ? linkedTools[0].toolCall : undefined;
      const execution: Parameters<NonNullable<AgentWidgetPlugin["renderApproval"]>>[0]["execution"] =
        linkedTool && linkedTool.status !== "pending"
          ? { toolCallId: linkedTool.id, status: linkedTool.status === "running"
              ? "running" : linkedTool.success === false || linkedTool.error ? "failed" : "completed" }
          : undefined;
      const userCollapse = resolveUserCollapse(message);
      const fingerprint =
        computeMessageFingerprint(message, configVersion) + askMeta + `:${eligibilityKey}` +
        (userCollapse ? `:uc-${userCollapse.state}` : "") +
        (approvalWithPlugin ? `:${message.approval?.toolCallId ?? ""}:${JSON.stringify(execution)}` : "");
      const cachedWrapper = (askWithPlugin || approvalWithPlugin || hasDirectiveBubble ||
        (message.dispatchError && config.errorDisplay === "block" && !approvalUi))
        ? null
        : getCachedWrapper(messageCache, message.id, fingerprint);
      if (cachedWrapper) {
        if (
          liveRows.has(message.id) &&
          liveRowFingerprints.get(message.id) === fingerprint
        ) {
          const stub = document.createElement("div");
          stub.id = `wrapper-${message.id}`;
          stub.setAttribute("data-wrapper-id", message.id);
          stub.setAttribute("data-persona-reuse", "true");
          tempContainer.appendChild(stub);
        } else {
          tempContainer.appendChild(cachedWrapper.cloneNode(true));
        }
        if (reuseLiveRows) nextLiveRowFingerprints.set(message.id, fingerprint);
        // Keep the overlay sheet alive only while the server is actively
        // waiting on the user (awaitingLocalTool === true). Before await
        // fires, or after the answer resumes the flow, omit from
        // liveAskToolIds so the prune loop below removes any stale DOM sheet.
        // Guards against lingering skeleton sheets from tool_start events
        // that never get a matching await (e.g. LLM-hallucinated trailing
        // ask_user_question calls at end-of-turn).
        if (
          isAskUserQuestionMessage(message) &&
          message.toolCall?.id &&
          message.agentMetadata?.awaitingLocalTool === true &&
          !message.agentMetadata?.askUserQuestionAnswered
        ) {
          liveAskToolIds.add(message.toolCall.id);
          mountAskUserSheet(message);
        }
        return;
      }

      let bubble: HTMLElement | null = null;

      const renderDefaultMessage = (): HTMLElement => {
        if (message.dispatchError && config.errorDisplay === "block") {
          const mod = ensureApprovalUi();
          if (mod) return mod.createErrorBlock(message, transform, actionEligibility.canRegenerate === true);
        }
        // Keep the detail readable even while the secondary UI chunk loads.
        const bubble = createStandardBubble(message, transform, config.layout?.messages,
          config.messageActions, messageActionCallbacks, {
            loadingIndicatorRenderer: inlineLoadingRenderer, widgetConfig: config, actionEligibility,
            userCollapse
          });
        bubble.dataset.personaDefaultMessage = "true";
        return bubble;
      };

      // Try plugins first
      const matchingPlugin = plugins.find((p) => {
        if (message.variant === "reasoning" && p.renderReasoning) {
          return true;
        }
        if (message.variant === "tool" && p.renderToolCall) {
          return true;
        }
        // Approval plugins are handled via the stub-and-hydrate path below
        // (see `approvalWithPlugin`), not this inline morph path, so their
        // listeners survive, so they are intentionally excluded here.
        if (!message.variant && p.renderMessage) {
          return true;
        }
        return false;
      });

      // ask_user_question has two rendering modes while waiting for an answer:
      //   1. Plugin `renderAskUserQuestion`: returns an inline transcript
      //      element with its own UI; the composer-overlay sheet is suppressed.
      //   2. Built-in composer-overlay answer-pill sheet: no transcript stub.
      // Plugins win when they return a non-null element; otherwise fall
      // through to the built-in overlay.
      //
      // Once answered, the original tool message is suppressed entirely from
      // the transcript. `session.resolveAskUserQuestion` injects one assistant
      // bubble per question and one user bubble per answer (skipped questions
      // become an italic `*Skipped*` user bubble), so the transcript reads
      // like a normal Q→A conversation. Plugins do not render the answered
      // state.
      if (
        isAskUserQuestionMessage(message) &&
        message.agentMetadata?.askUserQuestionAnswered === true
      ) {
        // Drop any previously-mounted plugin bubble so the morph pass
        // removes the now-stale interactive sheet.
        lastAskBubbleFingerprint.delete(message.id);
        const existing = findMessageRow(container, message.id);
        existing?.removeAttribute("data-preserve-runtime");
        return;
      }

      // suggest_replies renders no transcript bubble: the chips above the
      // composer are the only UI, and the session auto-resumes the call.
      // When the feature is disabled the message falls through to the generic
      // tool bubble (and is never auto-resumed), keeping the parked execution
      // visible instead of silently swallowed.
      if (
        isSuggestRepliesMessage(message) &&
        resolveFollowUpsFeature(config).enabled
      ) {
        return;
      }

      if (
        isAskUserQuestionMessage(message) &&
        config.features?.askUserQuestion?.enabled !== false
      ) {
        const askPlugin = plugins.find((p) => typeof p.renderAskUserQuestion === "function");
        if (askPlugin && sessionRef.current) {
          const lastFp = lastAskBubbleFingerprint.get(message.id);
          // Whether to actually call the plugin renderer this pass. We do it
          // on first sight of this message, or when its fingerprint changed
          // (e.g. payload streamed in more options). Otherwise we rely on the
          // already-mounted bubble in `container`.
          const needsRebuild = lastFp !== fingerprint;

          let pluginBubble: HTMLElement | null = null;
          if (needsRebuild) {
            const { payload, complete } = parseAskUserQuestionPayload(message);
            const messageId = message.id;
            const liveMessage = (): AgentWidgetMessage | undefined =>
              sessionRef.current?.getMessages().find((m) => m.id === messageId);
            pluginBubble = askPlugin.renderAskUserQuestion!({
              message,
              payload,
              complete,
              resolve: (answer) => {
                const live = liveMessage();
                if (live) sessionRef.current?.resolveAskUserQuestion(live, answer);
              },
              dismiss: () => {
                const live = liveMessage();
                if (live?.agentMetadata?.awaitingLocalTool) {
                  sessionRef.current?.markAskUserQuestionResolved(live);
                  sessionRef.current?.resolveAskUserQuestion(live, "(dismissed)");
                }
              },
              config,
            });
          }

          // If the plugin opted out (returned null on a fresh build) AND we
          // have no previously-mounted bubble for this message, fall back to
          // the built-in overlay sheet. If we already have a mounted bubble
          // and the plugin didn't run this pass (cached), keep using it.
          const previouslyMounted = lastFp != null;
          if (needsRebuild && pluginBubble === null && !previouslyMounted) {
            if (
              message.agentMetadata?.awaitingLocalTool === true &&
              !message.agentMetadata?.askUserQuestionAnswered
            ) {
              liveAskToolIds.add(message.toolCall!.id);
              mountAskUserSheet(message);
            }
            return;
          }

          // Append a stub wrapper for the morph pass; hydrate the real bubble
          // into it post-morph so its event listeners survive.
          const stub = createMessageRow(message.id, message.role);
          stub.setAttribute("data-ask-plugin-stub", "true");
          stub.setAttribute("data-preserve-runtime", "true");
          tempContainer.appendChild(stub);
          askPluginHydrate.push({
            messageId: message.id,
            fingerprint,
            bubble: pluginBubble,
          });
          return;
        } else {
          if (
            message.agentMetadata?.awaitingLocalTool === true &&
            !message.agentMetadata?.askUserQuestionAnswered
          ) {
            liveAskToolIds.add(message.toolCall!.id);
            mountAskUserSheet(message);
          }
          return;
        }
      } else if (approvalWithPlugin) {
        // Plugin-rendered approval bubble. Build the live element with its
        // listeners, append a stub for the morph pass, and hydrate the live
        // element into the morphed wrapper afterward (same trick as
        // `renderAskUserQuestion` / component directives) so Approve/Deny and
        // any accordion listeners survive idiomorph's `importNode`. Gate the
        // rebuild on fingerprint so interactive state (e.g. a collapsed
        // accordion) is preserved while the approval stays pending.
        const approvalMod = ensureApprovalUi();
        if (!approvalMod) {
          // Chunk still in flight: hold the slot with an empty row;
          // `onApprovalUiReady` clears the cache and re-renders the real card.
          tempContainer.appendChild(createMessageRow(message.id, message.role));
          return;
        }
        const approvalPlugin =
          plugins.find((p) => typeof p.renderApproval === "function") ?? builtInApprovalPlugin;
        const lastFp = lastApprovalBubbleFingerprint.get(message.id);
        const needsRebuild = lastFp !== fingerprint;
        let liveBubble: HTMLElement | null = null;
        let focusDefault = false;

        if (needsRebuild && approvalPlugin?.renderApproval) {
          // Re-find the live message at decision time so we resolve against
          // current state, and route WebMCP gate approvals to the local
          // resolver: mirroring the built-in delegated handler.
          const approvalMessageId = message.id;
          let decisionSent = false;
          const resolveDecision = (
            decision: "approved" | "denied",
            options?: AgentWidgetApprovalDecisionOptions
          ): void => {
            const live = sessionRef.current
              ?.getMessages()
              .find((m) => m.id === approvalMessageId);
            if (decisionSent || live?.approval?.status !== "pending") return;
            decisionSent = true;
            if (isBrowserLocalApproval(live.approval)) {
              sessionRef.current?.resolveWebMcpApproval(live.id, decision);
            } else {
              sessionRef.current?.resolveApproval(live.approval, decision, options);
            }
          };
          // Retire the previous lifetime before any replacement renderer runs.
          disposeApprovalRender(message.id);
          const lifetime = new AbortController();
          approvalRenderControllers.set(message.id, lifetime);
          const context: Parameters<NonNullable<AgentWidgetPlugin["renderApproval"]>>[0] = {
            message,
            execution,
            signal: lifetime.signal,
            // The configured built-in plugin owns defaults, shortcuts and cleanup.
            defaultRenderer: () => renderDefault(),
            config,
            approve: (options) => {
              if (!lifetime.signal.aborted) resolveDecision("approved", options);
            },
            deny: (options) => {
              if (!lifetime.signal.aborted) resolveDecision("denied", options);
            }
          };
          let defaultBubble: HTMLElement | null = null;
          const defaultLifetime = new AbortController();
          lifetime.signal.addEventListener("abort", () => defaultLifetime.abort(), { once: true });
          const renderDefault = (): HTMLElement => {
            defaultBubble ??= builtInApprovalPlugin?.renderApproval?.({ ...context, signal: defaultLifetime.signal }) ??
              approvalMod.createApprovalBubble(message, config, approvalDetailsExpansionState);
            return defaultBubble;
          };
          liveBubble = approvalPlugin === builtInApprovalPlugin
            ? renderDefault()
            : approvalPlugin.renderApproval(context) ?? renderDefault();
          const usesDefault = defaultBubble !== null &&
            (liveBubble === defaultBubble || liveBubble.contains(defaultBubble));
          if (!usesDefault) defaultLifetime.abort();
          const root = mount.getRootNode() as Document | ShadowRoot;
          focusDefault = usesDefault && lastFp === undefined &&
            message.approval?.status === "pending" && config.approval !== false &&
            config.approval?.keyboardShortcuts === "focused" &&
            mount.contains(root.activeElement);
        }

        const stub = createMessageRow(message.id, message.role);
        stub.setAttribute("data-approval-plugin-stub", "true");
        stub.setAttribute("data-preserve-runtime", "true");
        tempContainer.appendChild(stub);
        approvalPluginHydrate.push({ messageId: message.id, fingerprint, bubble: liveBubble, focusDefault });
        return;
      } else if (matchingPlugin) {
        if (message.variant === "reasoning" && message.reasoning && matchingPlugin.renderReasoning) {
          if (!showReasoning) return;
          bubble = matchingPlugin.renderReasoning({
            message,
            defaultRenderer: () => activityUi!.createReasoningBubble(message, config, reasoningExpansionState),
            config
          });
        } else if (message.variant === "tool" && message.toolCall && matchingPlugin.renderToolCall) {
          if (!showToolCalls) return;
          bubble = matchingPlugin.renderToolCall({
            message,
            defaultRenderer: () => activityUi!.createToolBubble(message, config, toolExpansionState),
            config
          });
        } else if (matchingPlugin.renderMessage) {
          bubble = matchingPlugin.renderMessage({
            message,
            defaultRenderer: () => {
              const b = renderDefaultMessage();
              if (message.role !== "user") {
                enhanceWithFormsLazy(b, message, config, session);
              }
              return b;
            },
            config
          });
        }
      }

      if (bubble && componentLifetimes.has(message.id)) disposeComponentRender(message.id);

      // Check for component directive if no plugin handled it. We use the
      // same stub-and-hydrate trick as ask_user_question plugins (see comment
      // above `componentDirectiveHydrate`): build the live element with its
      // listeners, append a stub for the morph pass, then inject the live
      // element into the morphed wrapper afterward.
      if (!bubble && hasDirectiveBubble) {
        const directive = extractComponentDirectiveFromMessage(message);
        if (directive) {
          const lastFp = lastComponentDirectiveFingerprint.get(message.id);
          const wrapChrome =
            config.wrapComponentDirectiveInBubble !== false &&
            resolveComponentOptions(config, directive.component)?.bubbleChrome !== false;
          // Raw-length-only fingerprints miss same-length prop replacements.
          const directiveFingerprint = `${computeMessageFingerprint(message, configVersion)}:${wrapChrome}:${directive.raw}`;
          const previousLifetime = componentLifetimes.get(message.id);
          const renderer = resolveComponentRenderer(config, directive.component);
          const rendererChanged = previousLifetime?.renderer !== renderer ||
            previousLifetime?.component !== directive.component;
          const needsRebuild = lastFp !== directiveFingerprint || rendererChanged;
          if (previousLifetime) {
            previousLifetime.context.message = message;
            previousLifetime.context.config = config;
          }
          // Wrap only when the global default allows it AND the component has
          // not opted out of bubble chrome (e.g. the artifact card carries its
          // own border, so it renders bare).
          let liveBubble: HTMLElement | null = null;

          if (needsRebuild) {
            let componentBubble: HTMLElement | null = null;
            if (previousLifetime && !rendererChanged && previousLifetime.handle.update) {
              previousLifetime.context.message = message;
              previousLifetime.context.config = config;
              try {
                previousLifetime.handle.update(directive.props, previousLifetime.context);
                componentBubble = previousLifetime.handle.element;
              } catch (error) {
                console.error("[AgentWidget] Component update failed:", error);
                disposeComponentRender(message.id);
              }
            } else {
              disposeComponentRender(message.id);
              const lifetime = new AbortController();
              const context: AgentWidgetComponentRenderContext = {
                config,
                message,
                signal: lifetime.signal,
                updateProps: (props) => {
                  // A renderer may write while it is being mounted or updated.
                  // Queue until hydration has completed, and reject retired writes.
                  queueMicrotask(() => {
                    if (!lifetime.signal.aborted) sessionRef.current?.updateComponentProps(message.id, props);
                  });
                },
              };
              const result = renderComponentDirective(directive, {
                config,
                message,
                transform,
                signal: lifetime.signal,
                onPropsUpdate: context.updateProps,
                context,
              });
              if (result) {
                const handle = "element" in result ? result : { element: result };
                componentBubble = handle.element;
                componentLifetimes.set(message.id, {
                  renderer, component: directive.component, controller: lifetime, handle, context,
                });
              } else lifetime.abort();
            }
            // Reuse a mounted inline artifact block instead of the fresh
            // render: replacing it would cut the collapse-to-card animation
            // short and reload live file-preview iframes.
            if (
              componentBubble &&
              directive.component === "PersonaArtifactInline"
            ) {
              const fresh = componentBubble.hasAttribute(
                "data-artifact-inline"
              )
                ? componentBubble
                : componentBubble.querySelector<HTMLElement>(
                    "[data-artifact-inline]"
                  );
              const artifactId =
                fresh?.getAttribute("data-artifact-inline") ?? "";
              const escapedId =
                typeof CSS !== "undefined" && typeof CSS.escape === "function"
                  ? CSS.escape(artifactId)
                  : artifactId;
              const live = artifactId
                ? (container
                    .querySelector<HTMLElement>(`#wrapper-${message.id}`)
                    ?.querySelector<HTMLElement>(
                      `[data-artifact-inline="${escapedId}"]`
                    ) ?? null)
                : null;
              if (
                fresh &&
                live &&
                live !== fresh &&
                getArtifactsUiSync()?.hasLiveInlineArtifactBlock(live)
              ) {
                if (fresh === componentBubble) componentBubble = live;
                else fresh.replaceWith(live);
              }
            }
            if (componentBubble) {
              if (wrapChrome) {
                const componentWrapper = document.createElement("div");
                componentWrapper.className = [
                  "persona-message-bubble",
                  "persona-rounded-2xl",
                  "persona-bg-persona-surface",
                  "persona-border",
                  "persona-border-persona-message-border",
                  "persona-p-4"
                ].join(" ");
                componentWrapper.id = `bubble-${message.id}`;
                componentWrapper.setAttribute("data-message-id", message.id);

                if (message.content && message.content.trim()) {
                  const textDiv = document.createElement("div");
                  textDiv.className = "persona-mb-3 persona-text-sm persona-leading-relaxed";
                  textDiv.innerHTML = transform({
                    text: message.content,
                    message,
                    streaming: Boolean(message.streaming),
                    raw: message.rawContent
                  });
                  componentWrapper.appendChild(textDiv);
                }

                componentWrapper.appendChild(componentBubble);
                liveBubble = componentWrapper;
              } else {
                const stack = document.createElement("div");
                stack.className =
                  "persona-flex persona-flex-col persona-w-full persona-max-w-full persona-gap-3 persona-items-stretch";
                stack.id = `bubble-${message.id}`;
                stack.setAttribute("data-message-id", message.id);
                stack.setAttribute("data-persona-component-directive", "true");

                if (message.content && message.content.trim()) {
                  const textDiv = document.createElement("div");
                  textDiv.className =
                    "persona-text-sm persona-leading-relaxed persona-text-persona-primary persona-w-full";
                  textDiv.innerHTML = transform({
                    text: message.content,
                    message,
                    streaming: Boolean(message.streaming),
                    raw: message.rawContent
                  });
                  stack.appendChild(textDiv);
                }

                stack.appendChild(componentBubble);
                liveBubble = stack;
              }
            }
          }

          // If the directive is registered (live bubble built or already
          // mounted from a previous pass), use the stub-and-hydrate path.
          // Otherwise fall through to the standard render path so the message
          // text is at least visible.
          if (liveBubble || componentLifetimes.has(message.id)) {
            const stub = createMessageRow(message.id, message.role);
            stub.setAttribute("data-component-directive-stub", "true");
            stub.setAttribute("data-preserve-runtime", "true");
            tempContainer.appendChild(stub);
            componentDirectiveHydrate.push({
              messageId: message.id,
              fingerprint: directiveFingerprint,
              bubble: liveBubble
            });
            return;
          }
        }
      }

      // Fallback to default rendering if plugin returned null or no plugin matched
      if (!bubble) {
        if (message.variant === "reasoning" && message.reasoning) {
          if (!showReasoning) return;
          bubble = activityUi!.createReasoningBubble(message, config, reasoningExpansionState);
        } else if (message.variant === "tool" && message.toolCall) {
          if (!showToolCalls) return;
          bubble = activityUi!.createToolBubble(message, config, toolExpansionState);
        } else if (message.variant === "approval" && message.approval) {
          if (config.approval === false) return;
          const approvalMod = ensureApprovalUi();
          if (!approvalMod) return;
          bubble = approvalMod.createApprovalBubble(message, config, approvalDetailsExpansionState);
        } else {
          // Check for custom message renderers in layout config
          const messageLayoutConfig = config.layout?.messages;
          if (messageLayoutConfig?.renderUserMessage && message.role === "user") {
            bubble = messageLayoutConfig.renderUserMessage({
              message,
              config,
              streaming: Boolean(message.streaming)
            });
          } else if (messageLayoutConfig?.renderAssistantMessage && message.role === "assistant") {
            bubble = messageLayoutConfig.renderAssistantMessage({
              message,
              config,
              streaming: Boolean(message.streaming)
            });
          } else {
            bubble = renderDefaultMessage();
          }

          if (message.role !== "user" && bubble) {
            enhanceWithFormsLazy(bubble, message, config, session);
          }
        }
      }

      const wrapper = createMessageRow(message.id, message.role);
      wrapper.appendChild(bubble);
      setCachedWrapper(messageCache, message.id, fingerprint, wrapper);
      tempContainer.appendChild(wrapper);
      if (reuseLiveRows) nextLiveRowFingerprints.set(message.id, fingerprint);
    });

    // Prune any ask_user_question sheets whose source message is no longer in
    // the message list (e.g. after clearChat or a splice).
    if (panelElements.composerOverlay) {
      const sheets = panelElements.composerOverlay.querySelectorAll<HTMLElement>(
        "[data-persona-ask-sheet-for]"
      );
      sheets.forEach((sheet) => {
        const id = sheet.getAttribute("data-persona-ask-sheet-for");
        if (id && !liveAskToolIds.has(id)) {
          removeAskUserQuestionSheet(panelElements.composerOverlay, id);
        }
      });
    }

    const hasTurnActivity = ensureActivityUi()?.groupToolRows({ root: mount, config, messages, tempContainer, streaming: sessionInitialized ? session.isStreaming() : false,
      runActivity: session?.getRunActivity(),
      showToolCalls, showReasoning,
      dropCompletedToolCalls, dropReasoningRow, createMessageRow, activityIds, activityLifecycle: activityLifecycle!, toolExpansionState });

    activityLifecycle?.prune(activityIds);

    for (const id of componentLifetimes.keys()) {
      if (!activeMessageIds.has(id)) disposeComponentRender(id);
    }

    // Abort removed renderers before the morph detaches their live elements.
    for (const id of approvalRenderControllers.keys()) {
      if (!activeMessageIds.has(id)) disposeApprovalRender(id);
    }

    // Remove cache entries for messages that no longer exist
    pruneCache(messageCache, activeMessageIds);

    // Add standalone typing indicator only if streaming but no assistant message is streaming yet
    // (This shows while waiting for the stream to start)
    // Check for ANY streaming assistant message, even if empty (to avoid duplicate bubbles)
    //
    // Tool and reasoning rows hidden by `features.showToolCalls: false` /
    // `features.showReasoning: false`, or removed once complete by their
    // `completedVisibility` setting, never render, so they must not count as
    // "the assistant is already visibly responding": otherwise the transcript
    // goes blank for the whole tool phase. When those rows are shown, their own
    // bubble carries the loading animation and the standalone dots stay hidden.
    //
    // A pending `ask_user_question` call is visible only once its question has
    // rendered: a plugin bubble mounted for it this pass, or the built-in sheet
    // mounted for its tool call. Until then (arguments still streaming, a plugin
    // returning null for a partial payload, the sheet waiting on the pause) it
    // renders nothing, so it must not hide the dots either.
    const isUnrenderedAskRow = (msg: AgentWidgetMessage): boolean =>
      !(msg.toolCall?.id && liveAskToolIds.has(msg.toolCall.id)) &&
      !askPluginHydrate.some((entry) => entry.messageId === msg.id);
    const isHiddenActivityRow = (msg: AgentWidgetMessage): boolean =>
      isAskUserQuestionMessage(msg) &&
      msg.agentMetadata?.askUserQuestionAnswered !== true &&
      config.features?.askUserQuestion?.enabled !== false
        ? isUnrenderedAskRow(msg)
        : (msg.variant === "tool" && !showToolCalls) ||
          (msg.variant === "reasoning" && !showReasoning) ||
          isDroppedCompletedRow(msg);
    const hasStreamingAssistantMessage = messages.some(
      (msg) => msg.role === "assistant" && msg.streaming && !isHiddenActivityRow(msg)
    );

    // Also check if there's a recently completed assistant message (streaming just ended)
    // This prevents flicker when the message completes but isStreaming hasn't updated yet
    // Approval-variant messages are UI controls, not content: exclude them so the typing
    // indicator still shows while the agent resumes after approval. Hidden tool/reasoning
    // rows are skipped for the same reason (the gap between a tool finishing and the
    // model's next text would otherwise show nothing), and a hidden row *after* the
    // last visible message means the agent has moved on to more work, so that visible
    // message is not the turn's final response.
    let lastVisibleIndex = messages.length - 1;
    while (lastVisibleIndex >= 0 && isHiddenActivityRow(messages[lastVisibleIndex])) {
      lastVisibleIndex--;
    }
    const lastMessage = lastVisibleIndex >= 0 ? messages[lastVisibleIndex] : undefined;
    const hasHiddenActivityAfterLastVisible = lastVisibleIndex < messages.length - 1;
    const hasRecentAssistantResponse =
      lastMessage?.role === "assistant" &&
      !lastMessage.streaming &&
      lastMessage.variant !== "approval" &&
      !hasHiddenActivityAfterLastVisible;

    if (!hasTurnActivity && isStreaming && messages.some((msg) => msg.role === "user") && !hasStreamingAssistantMessage && !hasRecentAssistantResponse) {
      // Get loading indicator using priority chain: plugin -> config -> default
      const loadingIndicatorContext: LoadingIndicatorRenderContext = {
        config,
        streaming: true,
        location: 'standalone',
        defaultRenderer: () => createTypingIndicator(config)
      };

      const typingIndicator = plugins.find(plugin => plugin.renderLoadingIndicator)?.renderLoadingIndicator?.(loadingIndicatorContext)
        ?? config.loadingIndicator?.render?.(loadingIndicatorContext)
        ?? createTypingIndicator(config);

      // Only render if we have an indicator (allows hiding via returning null)
      if (typingIndicator) {
        // Dots and custom renderers borrow the assistant bubble's chrome.
        // CSS removes it around a built-in V5 activity label, just as it does
        // for the same label inside an empty streaming assistant message.
        const typingBubble = document.createElement("div");
        const showBubble = config.loadingIndicator?.showBubble !== false; // default true
        const messageLayout = config.layout?.messages?.layout ?? "bubble";
        typingBubble.className = showBubble
          ? getBubbleClasses("assistant", messageLayout).join(" ")
          : [
              "persona-text-sm",
              "persona-leading-relaxed",
              "persona-text-persona-text"
            ].join(" ");
        typingBubble.setAttribute("data-typing-indicator", "true");
        if (showBubble) {
          typingBubble.style.color =
            "var(--persona-message-assistant-text, var(--persona-text))";
          if (messageLayout !== "flat") {
            typingBubble.style.backgroundColor =
              "var(--persona-message-assistant-bg, var(--persona-container))";
            typingBubble.style.borderColor =
              "var(--persona-message-assistant-border, var(--persona-border, #e5e7eb))";
          }
        }

        typingBubble.appendChild(typingIndicator);

        const typingWrapper = createMessageRow(
          "typing-indicator",
          "assistant"
        );
        typingWrapper.appendChild(typingBubble);
        tempContainer.appendChild(typingWrapper);
      }
    }

    // Render idle state indicator when not streaming and has messages
    if (!isStreaming && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];

      // Create context for idle indicator render functions
      const idleIndicatorContext: IdleIndicatorRenderContext = {
        config,
        lastMessage,
        messageCount: messages.length
      };

      // Get idle indicator using priority chain: plugin -> config -> null (default)
      const idleIndicator = plugins.find(plugin => plugin.renderIdleIndicator)?.renderIdleIndicator?.(idleIndicatorContext)
        ?? config.loadingIndicator?.renderIdle?.(idleIndicatorContext);

      // Only render if we have an indicator (default is null - no idle indicator)
      if (idleIndicator) {
        // Create a wrapper for the idle indicator (similar to typing indicator)
        const idleBubble = document.createElement("div");
        const showBubble = config.loadingIndicator?.showBubble !== false; // default true
        idleBubble.className = showBubble
          ? [
              "persona-rounded-2xl",
              "persona-text-sm",
              "persona-leading-relaxed",
              "persona-shadow-sm",
              "persona-bg-persona-surface",
              "persona-border",
              "persona-border-persona-message-border",
              "persona-text-persona-primary",
              "persona-px-5",
              "persona-py-3"
            ].join(" ")
          : [
              "persona-text-sm",
              "persona-leading-relaxed",
              "persona-text-persona-primary"
            ].join(" ");
        idleBubble.setAttribute("data-idle-indicator", "true");

        idleBubble.appendChild(idleIndicator);

        const idleWrapper = createMessageRow("idle-indicator", "assistant");
        idleWrapper.appendChild(idleBubble);
        tempContainer.appendChild(idleWrapper);
      }
    }

    // Wrap wide tables in a horizontal-scroll container before morphing so the
    // wrapper exists on both sides of the diff (survives streaming re-renders).
    wrapScrollableTables(tempContainer);

    // Dispose replaced effects before cleanup can disturb the new default DOM.
    loadingLabelLifecycle?.prepare(tempContainer);
    activityLifecycle?.prepare(tempContainer);
    // Use idiomorph to morph the container contents
    morphMessages(container, tempContainer);
    liveRowFingerprints = nextLiveRowFingerprints;
    // A stub idiomorph inserted instead of matching to its live row would
    // leave an empty row; restore it from the cache.
    for (const stub of Array.from(
      container.querySelectorAll<HTMLElement>(":scope > [data-persona-reuse]")
    )) {
      const cached = messageCache.get(stub.getAttribute("data-wrapper-id") ?? "");
      if (cached) stub.replaceWith(cached.wrapper.cloneNode(true));
      else stub.remove();
    }

    // Set initial edge-fade state on the live table wrappers and attach the
    // delegated scroll listener (once) that keeps the fades in sync.
    refreshTableScrollFades(container);

    // Graft the live edit editor back into its morphed wrapper. It is the same
    // element across every pass, so the typed value, caret, and listeners are
    // never rebuilt (and never handed to idiomorph's `importNode`).
    if (editHydrate) {
      const { messageId, element } = editHydrate as {
        messageId: string;
        element: HTMLElement;
      };
      const wrapper = findMessageRow(container, messageId);
      if (wrapper && element.parentElement !== wrapper) {
        applyMessageRowLayout(wrapper, messageRolesById.get(messageId) ?? "user");
        wrapper.replaceChildren(element);
      }
    }

    // Hydrate plugin-rendered ask-question bubbles into their stub wrappers.
    // Idiomorph imports new nodes via `document.importNode`, which strips
    // listeners, so we built only an empty stub during morph and now inject
    // the real, listener-bearing bubble directly into the live DOM.
    if (askPluginHydrate.length > 0) {
      for (const { messageId, fingerprint, bubble } of askPluginHydrate) {
        const wrapper = findMessageRow(container, messageId);
        if (!wrapper) continue;
        applyMessageRowLayout(
          wrapper,
          messageRolesById.get(messageId) ?? "assistant"
        );
        if (bubble === null) {
          // No fresh bubble built this pass: either the plugin opted out
          // and a previously-mounted bubble already lives here (preserved by
          // `data-preserve-runtime`), or we skipped the rebuild because the
          // fingerprint matched. Either way, leave the live wrapper alone.
          continue;
        }
        wrapper.replaceChildren(bubble);
        wrapper.setAttribute("data-bubble-fp", fingerprint);
        lastAskBubbleFingerprint.set(messageId, fingerprint);
      }
    }

    // Drop fingerprints for messages that are no longer present so a future
    // re-appearance triggers a fresh plugin render.
    if (lastAskBubbleFingerprint.size > 0) {
      for (const id of lastAskBubbleFingerprint.keys()) {
        if (!activeMessageIds.has(id)) lastAskBubbleFingerprint.delete(id);
      }
    }

    // Hydrate component-directive bubbles into their stub wrappers, mirroring
    // the ask-question hydration above.
    if (componentDirectiveHydrate.length > 0) {
      for (const { messageId, fingerprint, bubble } of componentDirectiveHydrate) {
        const wrapper = findMessageRow(container, messageId);
        if (!wrapper) continue;
        applyMessageRowLayout(
          wrapper,
          messageRolesById.get(messageId) ?? "assistant"
        );
        if (bubble === null) {
          // Fingerprint matched the previous pass: the live wrapper (kept
          // alive by `data-preserve-runtime`) still holds the listener-bearing
          // bubble from a prior render. Leave it untouched.
          continue;
        }
        wrapper.replaceChildren(bubble);
        wrapper.setAttribute("data-bubble-fp", fingerprint);
        lastComponentDirectiveFingerprint.set(messageId, fingerprint);
      }
    }

    if (lastComponentDirectiveFingerprint.size > 0) {
      for (const id of lastComponentDirectiveFingerprint.keys()) {
        if (!activeMessageIds.has(id)) lastComponentDirectiveFingerprint.delete(id);
      }
    }

    // Hydrate plugin-rendered approval bubbles into their stub wrappers,
    // mirroring the ask-question / component-directive hydration above.
    if (approvalPluginHydrate.length > 0) {
      for (const { messageId, fingerprint, bubble, focusDefault } of approvalPluginHydrate) {
        const wrapper = findMessageRow(container, messageId);
        if (!wrapper) continue;
        applyMessageRowLayout(
          wrapper,
          messageRolesById.get(messageId) ?? "assistant"
        );
        if (bubble === null) {
          // Fingerprint matched the previous pass (or the plugin opted out
          // after a prior render): the live wrapper, kept alive by
          // `data-preserve-runtime`, still holds the listener-bearing bubble.
          continue;
        }
        wrapper.replaceChildren(bubble);
        wrapper.setAttribute("data-bubble-fp", fingerprint);
        lastApprovalBubbleFingerprint.set(messageId, fingerprint);
        // Never park focus (and Enter-to-allow) on a card the reader can't
        // see: a paused reader below the fold gets the "Approval needed" jump.
        const following = isFollowEffective() && autoFollow.isFollowing();
        if (focusDefault && (following || !isBelowFold(wrapper))) {
          bubble.querySelector<HTMLButtonElement>('[data-action="allow"]')?.focus();
        }
      }
    }

    if (lastApprovalBubbleFingerprint.size > 0) {
      for (const id of lastApprovalBubbleFingerprint.keys()) {
        if (!activeMessageIds.has(id)) lastApprovalBubbleFingerprint.delete(id);
      }
    }
    ensureActivityUi();
    loadingLabelLifecycle?.sync();
    activityLifecycle?.sync();
  };

  // Alias for clarity - the implementation handles flicker prevention via typing indicator logic.
  // Re-apply read-aloud button state after each render so a playing/paused
  // message keeps its icon across idiomorph DOM morphs.
  // Renderers and parsers are prepared before reveal. Warm request processing
  // on panel visibility so sending does not wait for another download.
  const warmClientStream = () => { void loadClientStream().catch(() => {}); };

  const renderMessagesWithPlugins = (
    container: HTMLElement,
    messages: AgentWidgetMessage[],
    transform: MessageTransform
  ) => {
    if (messages.length > 0) warmClientStream();
    renderMessagesWithPluginsImpl(container, messages, transform);
    pruneUserMessageCollapse(messages);
    syncUserMessageCollapse();
    refreshReadAloudButtons();
    if (revealedToolCalls.size > 0) {
      for (const [id, timer] of revealedToolCalls) {
        if (!messages.some((message) => message.id === id)) {
          window.clearTimeout(timer);
          revealedToolCalls.delete(id);
        }
      }
      refreshRevealedToolCalls();
    }
  };

  /**
   * Composer-bar outside-click dismiss. While the chat is expanded, clicking
   * anywhere outside the wrapper (i.e. NOT inside the chat panel chrome and
   * NOT inside the pill) collapses back to just the pill. Uses `pointerdown`
   * + capture so we run before host-page click handlers (and before any
   * stop-propagation upstream); composedPath() includes the shadow DOM
   * subtree, so clicks inside the wrapper (which lives in the shadow root)
   * are correctly identified as inside.
   */
  let composerBarOutsideClickListener: ((e: PointerEvent) => void) | null = null;

  const attachComposerBarOutsideClickDismiss = () => {
    if (composerBarOutsideClickListener) return;
    const listener: (e: PointerEvent) => void = (event) => {
      const path = event.composedPath();
      // pillRoot is a viewport-fixed sibling of the wrapper, so a click on
      // the pill or peek wouldn't be in `wrapper`'s composedPath even
      // though it's logically "inside" the widget.
      if (path.includes(wrapper)) return;
      if (pillRoot && path.includes(pillRoot)) return;
      setOpenState(false, "user");
    };
    composerBarOutsideClickListener = listener;
    const targetDoc = mount.ownerDocument ?? document;
    targetDoc.addEventListener("pointerdown", listener, true);
  };

  const detachComposerBarOutsideClickDismiss = () => {
    if (!composerBarOutsideClickListener) return;
    const targetDoc = mount.ownerDocument ?? document;
    targetDoc.removeEventListener(
      "pointerdown",
      composerBarOutsideClickListener,
      true
    );
    composerBarOutsideClickListener = null;
  };

  destroyCallbacks.push(() => detachComposerBarOutsideClickDismiss());

  /**
   * Composer-bar ESC dismiss. While the chat is expanded, pressing Escape
   * collapses back to just the pill: same end state as outside-click.
   * Matches the WAI-ARIA dialog pattern (modal mode is literally a dialog)
   * and the dominant chat-widget convention (Intercom, Drift, Crisp).
   * Guards on `event.isComposing` so dismissing an IME suggestion doesn't
   * also collapse the panel.
   */
  let composerBarEscapeListener: ((e: KeyboardEvent) => void) | null = null;

  const attachComposerBarEscapeDismiss = () => {
    if (composerBarEscapeListener) return;
    const listener: (e: KeyboardEvent) => void = (event) => {
      if (event.key !== "Escape") return;
      if (event.isComposing) return;
      setOpenState(false, "user");
    };
    composerBarEscapeListener = listener;
    const targetDoc = mount.ownerDocument ?? document;
    targetDoc.addEventListener("keydown", listener, true);
  };

  const detachComposerBarEscapeDismiss = () => {
    if (!composerBarEscapeListener) return;
    const targetDoc = mount.ownerDocument ?? document;
    targetDoc.removeEventListener(
      "keydown",
      composerBarEscapeListener,
      true
    );
    composerBarEscapeListener = null;
  };

  destroyCallbacks.push(() => detachComposerBarEscapeDismiss());

  /**
   * Composer-bar "peek" affordance: a chrome-less row above the pill that
   * shows a chat-bubble icon, the trailing 100 chars of the most recent
   * assistant message, and a chevron-up. It is the user's path back into the
   * expanded chat from the collapsed pill.
   *
   * Visible when (collapsed) AND (there is an assistant message with content)
   * AND (`isStreaming` OR `composerHovered`). Otherwise hidden. The hover
   * zone is the whole `panel` (not just the pill) so the cursor moving
   * between the pill and the peek doesn't trigger fade-out.
   *
   * Driven from a single `syncComposerBarPeek()` invoked from
   * `onMessagesChanged`, `onStreamingChanged`, `updateOpenState`, the
   * pointerenter/pointerleave on `panel`, and once at end-of-init.
   */
  let composerHovered = false;
  // Track which peek-plugins we've already attached for this widget root.
  // `ensurePluginActive` is idempotent, but the call is guarded behind a flag
  // so we don't pay the lookup cost on every chunk.
  const peekActivatedPlugins = new Set<string>();

  /**
   * Resolve the effective stream animation feature for the peek surface.
   * `composerBar.peek.streamAnimation` overrides; otherwise the peek inherits
   * `features.streamAnimation` so the surface for devs is consistent across
   * the main bubble and the peek banner.
   */
  const resolvePeekStreamAnimationFeature = () => {
    const peekFeature = config.launcher?.composerBar?.peek?.streamAnimation;
    if (peekFeature) return peekFeature;
    return config.features?.streamAnimation;
  };

  const syncComposerBarPeek = () => {
    if (!isComposerBar()) return;
    const peekBanner = panelElements.peekBanner;
    const peekTextNode = panelElements.peekTextNode;
    if (!peekBanner || !peekTextNode) return;

    if (open) {
      peekBanner.classList.remove("persona-pill-peek--visible");
      return;
    }

    const messages = session?.getMessages() ?? [];
    let lastAssistant: AgentWidgetMessage | undefined;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === "assistant" && m.content) {
        lastAssistant = m;
        break;
      }
    }
    if (!lastAssistant) {
      peekBanner.classList.remove("persona-pill-peek--visible");
      return;
    }

    const text = lastAssistant.content;
    const streaming = Boolean(lastAssistant.streaming);

    // Resolve the same animation surface used by the main bubble. The peek
    // ignores `bubbleClass` (carve-out: peek has no bubble) but honors
    // `containerClass`, `wrap`, `useCaret`, `buffer`, `placeholder`,
    // `speed`/`duration`, and custom plugins.
    const feature = resolvePeekStreamAnimationFeature();
    const streamAnimation = resolveStreamAnimation(feature);
    const plugin =
      streamAnimation.type !== "none"
        ? resolveStreamAnimationPluginLazy(streamAnimation.type, feature?.plugins)
        : null;
    const pluginStillAnimating =
      plugin?.isAnimating?.(lastAssistant) === true;
    const animationActive =
      plugin !== null && (streaming || pluginStillAnimating);

    if (animationActive && plugin && !peekActivatedPlugins.has(plugin.name)) {
      ensurePluginActive(plugin, mount);
      peekActivatedPlugins.add(plugin.name);
    }

    // Manage `containerClass` on the peek text node. We track which class is
    // currently applied so a config swap (or animation deactivating after
    // stream completion) cleans up the previous class instead of stacking.
    const desiredContainerClass =
      animationActive && plugin?.containerClass ? plugin.containerClass : null;
    const currentContainerClass =
      peekTextNode.dataset.personaPeekStreamClass ?? null;
    if (currentContainerClass && currentContainerClass !== desiredContainerClass) {
      peekTextNode.classList.remove(currentContainerClass);
      delete peekTextNode.dataset.personaPeekStreamClass;
    }
    if (desiredContainerClass && currentContainerClass !== desiredContainerClass) {
      peekTextNode.classList.add(desiredContainerClass);
      peekTextNode.dataset.personaPeekStreamClass = desiredContainerClass;
    }

    if (animationActive) {
      peekTextNode.style.setProperty(
        "--persona-stream-step",
        `${streamAnimation.speed}ms`
      );
      peekTextNode.style.setProperty(
        "--persona-stream-duration",
        `${streamAnimation.duration}ms`
      );
    } else {
      peekTextNode.style.removeProperty("--persona-stream-step");
      peekTextNode.style.removeProperty("--persona-stream-duration");
    }

    // Apply buffering (word/line/plugin custom). If the buffer trims content
    // to empty AND the placeholder is "skeleton", show the skeleton: that's
    // the "line buffer between completions" affordance. Otherwise no
    // pre-content placeholder on the peek (a typing-dots indicator inside a
    // 1-line ticker would feel cramped).
    const buffered = animationActive
      ? applyStreamBuffer(text, streamAnimation.buffer, plugin, lastAssistant, streaming)
      : text;

    const skeletonEnabled =
      animationActive && streamAnimation.placeholder === "skeleton";
    const showSkeletonOnly =
      skeletonEnabled && streaming && (!buffered || !buffered.trim());

    if (showSkeletonOnly) {
      // Replace text node contents with just a peek-sized skeleton bar. The
      // bar carries `data-preserve-animation` so idiomorph keeps its shimmer
      // running across morph passes.
      const tempContainer = document.createElement("div");
      const skeleton = createSkeletonPlaceholder();
      skeleton.classList.add("persona-pill-peek__skeleton");
      tempContainer.appendChild(skeleton);
      morphMessages(peekTextNode, tempContainer);
    } else {
      // Trailing 100 chars; for animated modes we keep the slice but use
      // ABSOLUTE indices so per-char/per-word span IDs stay stable as the
      // window shifts each chunk: idiomorph then preserves animations on
      // already-revealed units instead of restarting them. Plain "none" mode
      // keeps the legacy `…` ellipsis prefix for visual continuity with the
      // pre-animation behavior.
      const sliceStart = Math.max(0, buffered.length - 100);
      const slice = buffered.length > 100 ? buffered.slice(-100) : buffered;
      const escaped = escapeHtml(slice);

      if (!animationActive || !plugin) {
        const preview = buffered.length > 100 ? `…${slice}` : slice;
        if (peekTextNode.textContent !== preview) {
          peekTextNode.textContent = preview;
        }
      } else {
        let html = escaped;
        if (plugin.wrap === "char" || plugin.wrap === "word") {
          html = wrapStreamAnimation(
            escaped,
            plugin.wrap,
            // Namespace span IDs to the peek surface so they don't collide
            // with the main bubble's spans for the same message id.
            `peek-${lastAssistant.id}`,
            { skipTags: plugin.skipTags, startIndex: sliceStart }
          );
        }

        const tempContainer = document.createElement("div");
        tempContainer.innerHTML = html;

        if (plugin.useCaret && slice.length > 0) {
          const caret = createStreamCaret();
          const spans = tempContainer.querySelectorAll(
            ".persona-stream-char, .persona-stream-word"
          );
          const lastSpan = spans[spans.length - 1];
          if (lastSpan?.parentNode) {
            lastSpan.parentNode.insertBefore(caret, lastSpan.nextSibling);
          } else {
            tempContainer.appendChild(caret);
          }
        }

        morphMessages(peekTextNode, tempContainer);

        // Fire the plugin's per-render hook so glyph-cycle / wipe / custom
        // plugins get a chance to mutate the peek's spans the same way they
        // mutate the main bubble's. The carve-out: `bubble` here is the peek
        // banner root, not a message bubble: plugins that target
        // `bubbleClass` should no-op on that surface.
        plugin.onAfterRender?.({
          container: peekTextNode,
          bubble: peekBanner,
          messageId: lastAssistant.id,
          message: lastAssistant,
          speed: streamAnimation.speed,
          duration: streamAnimation.duration,
        });
      }
    }

    const shouldShow = isStreaming || composerHovered;
    peekBanner.classList.toggle("persona-pill-peek--visible", shouldShow);
  };

  if (isComposerBar()) {
    const peekBanner = panelElements.peekBanner;
    if (peekBanner) {
      // pointerdown (not click) so this competes correctly with the
      // outside-click listener (also pointerdown, capture phase). The
      // outside-click composedPath check passes for events inside `wrapper`
      // or `pillRoot` (peek's parent), so the peek can stop propagation
      // here without breaking dismissal.
      const onPeekPointerDown = (e: PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setOpenState(true, "user");
      };
      peekBanner.addEventListener("pointerdown", onPeekPointerDown);
      destroyCallbacks.push(() => {
        peekBanner.removeEventListener("pointerdown", onPeekPointerDown);
      });
    }

    const onPanelPointerEnter = () => {
      if (composerHovered) return;
      composerHovered = true;
      syncComposerBarPeek();
    };
    const onPanelPointerLeave = () => {
      if (!composerHovered) return;
      composerHovered = false;
      syncComposerBarPeek();
    };
    panel.addEventListener("pointerenter", onPanelPointerEnter);
    panel.addEventListener("pointerleave", onPanelPointerLeave);
    destroyCallbacks.push(() => {
      panel.removeEventListener("pointerenter", onPanelPointerEnter);
      panel.removeEventListener("pointerleave", onPanelPointerLeave);
    });

    // pillRoot now hosts the pill + peek as viewport-level siblings, so the
    // panel's pointerenter/leave above no longer fires when the cursor is
    // over the pill area. Mirror the handlers onto pillRoot so hovering
    // either surface still drives `composerHovered`. Both handlers are
    // idempotent against the shared flag, so cross-traffic between panel
    // and pillRoot doesn't cause spurious flips.
    if (pillRoot) {
      pillRoot.addEventListener("pointerenter", onPanelPointerEnter);
      pillRoot.addEventListener("pointerleave", onPanelPointerLeave);
      destroyCallbacks.push(() => {
        pillRoot.removeEventListener("pointerenter", onPanelPointerEnter);
        pillRoot.removeEventListener("pointerleave", onPanelPointerLeave);
      });
    }
  }

  /**
   * Composer-bar geometry, owned in one place so collapsed → expanded (and
   * back) transitions don't leave stale inline styles from a previous state.
   * `createWrapper` no longer sets any geometry; everything flows through
   * here.
   *
   * Width is expressed as `width: <configured>; max-width: calc(100vw -
   * 32px)`. The two combine such that `width` wins on wide viewports and
   * `max-width` clamps on narrow ones: same effect as `min(...)` but
   * jsdom-compatible. `100vw` is always the viewport, so the containing-
   * block edge case (host with `transform`/`filter` causing `100%` to
   * resolve against the host instead of the viewport) is neutralized.
   */
  const applyComposerBarGeometry = (isOpen: boolean) => {
    const cb = config.launcher?.composerBar ?? {};
    const expandedSize = cb.expandedSize ?? "anchored";
    const bottomOffset = cb.bottomOffset ?? "16px";
    // No hardcoded default: when undefined, CSS media queries provide the
    // responsive width (90vw / 70vw / 50vw at <640 / <1024 / >=1024) on
    // pillRoot.
    const collapsedMaxWidth = cb.collapsedMaxWidth;
    const expandedMaxWidth = cb.expandedMaxWidth ?? "880px";
    const expandedTopOffset = cb.expandedTopOffset ?? "5vh";
    const modalMaxWidth = cb.modalMaxWidth ?? "880px";
    const modalMaxHeight = cb.modalMaxHeight ?? "min(90vh, 800px)";
    const viewportClamp = "calc(100vw - 32px)";
    // Retain the authored override and use measured clearance as the default.
    // ResizeObserver updates this when suggestions or composer content grow.
    syncComposerBarClearance();
    const pillAreaClearance = "var(--persona-pill-area-height, var(--persona-measured-pill-area-height, 80px))";

    // Reset everything geometry-related so each branch sets exactly what it
    // needs. Using empty strings drops the inline declaration entirely so
    // CSS rules can take over (relevant for fullscreen).
    const s = wrapper.style;
    s.left = "";
    s.right = "";
    s.top = "";
    s.bottom = "";
    s.transform = "";
    s.width = "";
    s.maxWidth = "";
    s.height = "";
    s.maxHeight = "";

    // pillRoot owns its own geometry (bottom offset + collapsed width
    // override). Reset and re-apply per-config every call so config edits
    // (e.g. via the demo's mode-switch) propagate cleanly.
    if (pillRoot) {
      const ps = pillRoot.style;
      ps.bottom = bottomOffset;
      // CSS media queries handle responsive width when no override is set.
      ps.width = collapsedMaxWidth ?? "";
    }

    if (!isOpen) {
      // Collapsed: wrapper has nothing visible to render: the container
      // inside is `display: none` (via CSS keyed on `[data-state="collapsed"]`)
      // and the pill lives in pillRoot. Leave wrapper geometry empty so it
      // collapses to a zero-size positioning frame at the default fixed
      // origin. The container's fade-in keyframe handles the perceptible
      // expand animation, so there's no chrome to lose during this state.
      return;
    }

    if (expandedSize === "fullscreen") {
      // Leave inline styles cleared so the CSS rule for fullscreen takes over.
      return;
    }

    if (expandedSize === "modal") {
      s.top = "50%";
      s.left = "50%";
      s.transform = "translate(-50%, -50%)";
      s.bottom = "auto";
      s.right = "auto";
      s.width = modalMaxWidth;
      s.maxWidth = viewportClamp;
      s.maxHeight = modalMaxHeight;
      s.height = modalMaxHeight;
      return;
    }

    // Default: anchored: pill stays at the viewport bottom (in pillRoot);
    // wrapper's bottom edge clears the pill area so the chrome doesn't
    // overlap it.
    s.left = "50%";
    s.transform = "translateX(-50%)";
    s.bottom = `calc(${bottomOffset} + ${pillAreaClearance})`;
    s.top = expandedTopOffset;
    s.width = expandedMaxWidth;
    s.maxWidth = viewportClamp;
  };

  const updateOpenState = () => {
    // Before the toggleable check: docked/fullscreen panels are visible from
    // init but never toggle, and must still warm the markdown chunk.
    if (open) warmClientStream();
    if (!isPanelToggleable()) return;

    // Composer-bar mode morphs the wrapper between collapsed pill and
    // expanded panel via data-attrs + per-state inline geometry. The chat
    // body and header are hidden in the collapsed state so only the
    // composer footer remains visible in the pill.
    if (isComposerBar()) {
      const cb = config.launcher?.composerBar ?? {};
      const expandedSize = cb.expandedSize ?? "anchored";
      const nextState = open ? "expanded" : "collapsed";
      wrapper.dataset.state = nextState;
      wrapper.dataset.expandedSize = expandedSize;
      // pillRoot mirrors wrapper's state attributes so CSS rules keyed off
      // [data-state] / [data-expanded-size] cascade to pill + peek even
      // though they live outside the wrapper subtree.
      if (pillRoot) {
        pillRoot.dataset.state = nextState;
        pillRoot.dataset.expandedSize = expandedSize;
      }
      wrapper.style.removeProperty("display");
      wrapper.classList.remove("persona-pointer-events-none", "persona-opacity-0");
      panel.classList.remove(
        "persona-scale-95",
        "persona-opacity-0",
        "persona-scale-100",
        "persona-opacity-100"
      );

      applyComposerBarGeometry(open);

      // Toggle the entire container (chat chrome + body + close button) so
      // the collapsed pill only shows the footer (which lives as a SIBLING
      // of the container in the panel: see panel.appendChild(footer) above).
      // The footer is always visible / interactive.
      container.style.display = open ? "flex" : "none";

      // Re-run chrome application now that data-state has flipped: collapsed
      // clears container chrome (pill stands alone), expanded paints it via
      // the same theme.components.panel.* contract as floating mode. Full
      // sync: applyFullHeightStyles alone wipes the theme vars with cssText.
      syncPanelChrome();

      // Outside-click dismiss: while expanded, clicking anywhere outside the
      // wrapper (panel chrome + pill) collapses back to just the pill.
      if (open) {
        attachComposerBarOutsideClickDismiss();
        attachComposerBarEscapeDismiss();
      } else {
        detachComposerBarOutsideClickDismiss();
        detachComposerBarEscapeDismiss();
      }
      // Peek banner is hidden when expanded (`open === true` short-circuits
      // visibility); re-sync so collapsing back re-evaluates immediately.
      syncComposerBarPeek();
      return;
    }

    const dockedMode = isDockedMountMode(config);
    const ownerWindow = mount.ownerDocument.defaultView ?? window;
    const mobileBreakpoint = config.launcher?.mobileBreakpoint ?? 640;
    const mobileFullscreen = config.launcher?.mobileFullscreen ?? true;
    const isMobileViewport = ownerWindow.innerWidth <= mobileBreakpoint;
    const shouldGoFullscreen = mobileFullscreen && isMobileViewport && launcherEnabled;
    const dockReveal = resolveDockConfig(config).reveal;
    const dockRevealUsesTransform =
      dockedMode && (dockReveal === "overlay" || dockReveal === "push") && !shouldGoFullscreen;

    if (open) {
      // Clear any display:none !important from a closed docked state so mobile fullscreen
      // (display:flex !important) and dock layout can apply in recalcPanelHeight.
      wrapper.style.removeProperty("display");
      wrapper.style.display = dockedMode ? "flex" : "";
      wrapper.classList.remove("persona-pointer-events-none", "persona-opacity-0");
      panel.classList.remove("persona-scale-95", "persona-opacity-0");
      panel.classList.add("persona-scale-100", "persona-opacity-100");
      // Hide launcher button when widget is open
      if (launcherSurfaceInstance) {
        launcherSurfaceInstance.setPanelOpen(true);
        launcherSurfaceInstance.element.style.display = "none";
      } else if (customLauncherElement) {
        customLauncherElement.style.display = "none";
      }
    } else {
      if (dockedMode) {
        if (dockRevealUsesTransform) {
          // Slide/push reveal: keep the panel painted so host-layout `transform` can animate.
          wrapper.style.removeProperty("display");
          wrapper.style.display = "flex";
          wrapper.classList.remove("persona-pointer-events-none", "persona-opacity-0");
          panel.classList.remove("persona-scale-100", "persona-opacity-100", "persona-scale-95", "persona-opacity-0");
        } else {
          // Must beat applyFullHeightStyles() mobile shell: display:flex !important on wrapper
          wrapper.style.setProperty("display", "none", "important");
          wrapper.classList.remove("persona-pointer-events-none", "persona-opacity-0");
          panel.classList.remove("persona-scale-100", "persona-opacity-100", "persona-scale-95", "persona-opacity-0");
        }
      } else {
        wrapper.style.display = "";
        wrapper.classList.add("persona-pointer-events-none", "persona-opacity-0");
        panel.classList.remove("persona-scale-100", "persona-opacity-100");
        panel.classList.add("persona-scale-95", "persona-opacity-0");
      }
      // Show launcher when closed, except docked mode (0px column: use controller.open()).
      if (launcherSurfaceInstance) {
        launcherSurfaceInstance.setPanelOpen(false);
        launcherSurfaceInstance.element.style.display = dockedMode ? "none" : "";
      } else if (customLauncherElement) {
        customLauncherElement.style.display = dockedMode ? "none" : "";
      }
    }
  };

  const setOpenState = (nextOpen: boolean, source: "user" | "auto" | "api" | "system" = "user") => {
    if (!isPanelToggleable()) return;
    if (open === nextOpen) return;

    const prevOpen = open;
    open = nextOpen;
    updateOpenState();

    // Sync host stacking and scroll lock for viewport-covering modes
    const isViewportCovering = (() => {
      const sm = config.launcher?.sidebarMode ?? false;
      const ow = mount.ownerDocument.defaultView ?? window;
      const mf = config.launcher?.mobileFullscreen ?? true;
      const mb = config.launcher?.mobileBreakpoint ?? 640;
      const isMobile = ow.innerWidth <= mb;
      const dockedMF = isDockedMountMode(config) && mf && isMobile;
      // Composer-bar in expanded fullscreen mode covers the viewport: lock
      // background scroll and elevate host stacking to match other
      // viewport-covering modes (mobile fullscreen, sidebar).
      const composerBarFS =
        isComposerBar() &&
        (config.launcher?.composerBar?.expandedSize ?? "fullscreen") === "fullscreen";
      return sm || (mf && isMobile && launcherEnabled) || dockedMF || composerBarFS;
    })();

    if (open && isViewportCovering) {
      if (!teardownHostStacking) {
        const root = mount.getRootNode();
        const hostEl = root instanceof ShadowRoot
          ? (root.host as HTMLElement)
          : mount.closest<HTMLElement>(".persona-host");
        if (hostEl) {
          teardownHostStacking = syncOverlayHostStacking(
            hostEl,
            config.launcher?.zIndex ?? DEFAULT_OVERLAY_Z_INDEX
          );
        }
      }
      if (!releaseScrollLock) {
        releaseScrollLock = acquireScrollLock(mount.ownerDocument);
      }
    } else if (!open) {
      teardownHostStacking?.();
      teardownHostStacking = null;
      releaseScrollLock?.();
      releaseScrollLock = null;
    }

    if (open) {
      recalcPanelHeight();
      // Reopen-where-left-off takes precedence when opted in (Principle 11);
      // otherwise fall back to the historical per-mode positioning.
      if (!restoreScrollPosition()) {
        if (getScrollMode() === "follow") {
          scheduleAutoScroll(true);
        } else {
          // Non-follow modes still start at the latest content when the panel
          // opens; they just never chase it during streaming.
          jumpToBottomInstant();
        }
      }
    }

    // Emit widget state events
    const stateEvent: AgentWidgetStateEvent = {
      open,
      source,
      timestamp: Date.now()
    };

    if (open && !prevOpen) {
      eventBus.emit("widget:opened", stateEvent);
    } else if (!open && prevOpen) {
      eventBus.emit("widget:closed", stateEvent);
    }

    // Emit general state snapshot
    eventBus.emit("widget:state", {
      open,
      launcherEnabled,
      voiceActive: voiceState.active,
      streaming: session.isStreaming()
    });
  };

  // --- Composer locks ------------
  // Streaming is NOT a lock: it turns the primary button into Stop and keeps
  // the input editable. `inputDisabled` locks composition; `sendDisabled`
  // blocks every submission path while composition stays available.
  type ComposerLockState = {
    inputDisabled: boolean;
    inputReason?: string;
    sendDisabled: boolean;
    sendReason?: string;
  };
  let composerLock: ComposerLockState = {
    inputDisabled: false,
    sendDisabled: false,
  };

  const isComposerInputDisabled = (): boolean => composerLock.inputDisabled;
  const isActiveSessionVoice = (): boolean =>
    usesSessionVoice(config.voiceRecognition?.provider) &&
    !!session && (session.isBargeInActive() ||
      ["listening", "processing", "speaking"].includes(session.getVoiceStatus()));
  /** Any submission path is blocked; stop is not a submission. */
  const isComposerSendBlocked = (): boolean =>
    composerLock.inputDisabled || composerLock.sendDisabled;

  /** inputDisabled's reason wins; a bare `true` falls through to sendDisabled. */
  const composerLockReason = (): string | undefined =>
    (composerLock.inputDisabled ? composerLock.inputReason : undefined) ??
    (composerLock.sendDisabled ? composerLock.sendReason : undefined);

  /**
   * Send stays live while streaming (it is Stop), and is otherwise gated by the
   * locks plus attachment readiness.
   */
  function syncComposerSendAvailability(): void {
    if (!sendButton) return;
    if (composerStore.getState().phase === "streaming") {
      sendButton.disabled = false;
      return;
    }
    const attachmentsBlocked = attachmentManager
      ? !attachmentManager.isReady()
      : false;
    sendButton.disabled = isComposerSendBlocked() || attachmentsBlocked;
  }

  /**
   * `sendButton.visibility: "when-text"`: hide the control while there is
   * nothing to send. Streaming always shows it, because it is Stop then.
   */
  function syncSendButtonVisibility(): void {
    if (!sendButtonWrapper) return;
    const state = composerStore.getState();
    const hidden =
      config.sendButton?.visibility === "when-text" &&
      state.phase !== "streaming" &&
      state.text.trim().length === 0 &&
      state.attachments.length === 0;
    sendButtonWrapper.toggleAttribute("data-persona-send-hidden", hidden);
  }

  /** Render the active reason. Pill mode has no status row, so it gets a title. */
  function renderComposerLockReason(): void {
    const reason = composerLockReason();
    if (isComposerBar()) {
      const root = composerForm ?? footer;
      if (reason) root.setAttribute("title", reason);
      else root.removeAttribute("title");
      footer.toggleAttribute(COMPOSER_REASON_ATTR, Boolean(reason));
      return;
    }
    if (reason) {
      statusText.setAttribute("role", "status");
      statusText.setAttribute("aria-live", "polite");
      statusText.setAttribute(COMPOSER_REASON_ATTR, "");
      statusText.removeAttribute(COMPOSER_NOTICE_ATTR); // the reason outranks a notice
      statusText.textContent = reason;
      applyStatusIndicatorState(statusText, config.statusIndicator ?? {}, "idle");
      return;
    }
    if (!statusText.hasAttribute(COMPOSER_REASON_ATTR)) return;
    statusText.removeAttribute("role");
    statusText.removeAttribute("aria-live");
    statusText.removeAttribute(COMPOSER_REASON_ATTR);
    if (sessionInitialized) {
      const status = session.getStatus();
      applyStatusToElement(
        statusText,
        _getStatusText(status),
        config.statusIndicator ?? {},
        status
      );
    }
  }

  /**
   * Re-read the locks from config and push them across every control. Called on
   * mount, every streaming flip, composer rebuild, and `controller.update()`.
   */
  function applyComposerLock(): void {
    const input = resolveComposerLock(config.composer?.inputDisabled);
    const send = resolveComposerLock(config.composer?.sendDisabled);
    composerLock = {
      inputDisabled: input.disabled,
      inputReason: input.reason,
      sendDisabled: send.disabled,
      sendReason: send.reason,
    };
    composerStore.setInputDisabled(input.disabled);
    composerStore.setSendDisabled(send.disabled);

    const streaming = composerStore.getState().phase === "streaming";
    const locked = input.disabled;
    // Ancillary controls also disable while streaming so the user can't race a
    // send against an in-flight stream.
    const blocked = streaming || locked;

    if (textarea) {
      if (textarea instanceof HTMLTextAreaElement) textarea.disabled = locked;
      // Inline contenteditable: `disabled` is not a thing, editability is.
      else (textarea as HTMLElement).setAttribute("contenteditable", locked ? "false" : "true");
      (textarea as HTMLElement).setAttribute("aria-disabled", locked ? "true" : "false");
    }
    if (attachmentButton) attachmentButton.disabled = locked;
    if (attachmentInput) attachmentInput.disabled = locked;
    // Affordance entries are wrappers around their button.
    for (const wrapper of mentionOrchestrator?.affordanceButtons ?? []) {
      wrapper.querySelectorAll<HTMLButtonElement>("button").forEach((button) => {
        button.disabled = locked;
      });
    }
    if (micButton) micButton.disabled = locked || (streaming && !isActiveSessionVoice());

    suggestionManagers.forEach((manager) => {
      manager.elements.forEach((element) => {
        element.toggleAttribute("data-disabled", blocked);
        if (!(element instanceof HTMLButtonElement)) {
          element.setAttribute("aria-disabled", blocked ? "true" : "false");
        }
      });
      manager.buttons.forEach((button) => {
        button.disabled = blocked;
      });
    });
    // Plugin renderStarter output: created with the streaming flag of its
    // render moment, so a stream ending must re-enable it here.
    welcomeStarterElements.forEach((element) => {
      element.toggleAttribute("data-disabled", blocked);
      if (element instanceof HTMLButtonElement) {
        element.disabled = blocked;
      } else {
        element.setAttribute("aria-disabled", blocked ? "true" : "false");
      }
      element.querySelectorAll<HTMLButtonElement>("button").forEach((button) => {
        button.disabled = blocked;
      });
    });

    footer.dataset.personaComposerStreaming = streaming ? "true" : "false";
    footer.dataset.personaComposerInputDisabled = locked ? "true" : "false";
    footer.dataset.personaComposerSendDisabled = isComposerSendBlocked()
      ? "true"
      : "false";
    footer.querySelectorAll<HTMLElement>("[data-persona-composer-disable-when-streaming]").forEach((el) => {
      if (
        el instanceof HTMLButtonElement ||
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        el instanceof HTMLSelectElement
      ) {
        el.disabled = streaming;
      }
    });

    syncComposerSendAvailability();
    renderComposerLockReason();
  }

  const setComposerDisabled = (disabled: boolean) => {
    // `disabled` is the session's streaming flag at every call site; the store's
    // phase derives from it.
    composerStore.setStreaming(disabled);
    // The send button stays enabled while streaming: it doubles as a stop
    // button.
    setSendButtonMode(disabled ? "stop" : "send");
    applyComposerLock();
  };

  const maybeFocusInput = () => {
    if (mount.hasAttribute("data-persona-preparing")) {
      void renderReadiness.ready.then(maybeFocusInput, () => {});
      return;
    }
    if (voiceState.active) return;
    if (!textarea) return;
    textarea.focus();
  };

  eventBus.on("widget:opened", () => {
    if (config.autoFocusInput) setTimeout(() => maybeFocusInput(), 200);
  });

  // Declared here, not beside the rest of the conversation-open state below:
  // `updateWelcome` reads them during the session's first render.
  let conversationOpenPendingEl: HTMLElement | null = null;
  // False during the stand-in's show delay, when the surface it replaces
  // (welcome or previous transcript) is still the one on screen.
  let conversationOpenTakeover = false;

  // Welcome visibility is derived, never stored: `resolveWelcomeConfig` owns
  // the config and the session's transcript activity owns the dismissal.
  let welcomeShown = !welcomeHost.hidden;
  // The dismiss runs across many renders (assistant placeholder, every
  // streaming chunk), so the in-flight animation owns the host until it settles.
  let welcomeDismissAnimation: Animation | null = null;
  // Restored history must hide the hero outright, not animate it out.
  let welcomeHydrated = !pendingStoredState;
  let composerLiftAnimation: Animation | null = null;
  destroyCallbacks.push(() => {
    composerLiftAnimation?.cancel();
    composerLiftAnimation = null;
  });
  // Derived from the transcript on every welcome render, so restore,
  // injection, and clearChat all fall out with no extra wiring.
  const syncConversationState = (
    messages: readonly AgentWidgetMessage[] | undefined
  ) => {
    const next = resolveConversationState(messages);
    if (next === conversationState) return;
    conversationState = next;
    mount.setAttribute("data-persona-conversation-state", next);
    const previousLift = readLiftPx();
    syncComposerOverlayMetrics();
    // Restored history lands `active` before first paint; the same flag that
    // suppresses the hero fade suppresses the drop.
    if (welcomeHydrated) {
      composerLiftAnimation?.cancel();
      composerLiftAnimation = animateComposerLiftChange(
        footer,
        next === "active" ? previousLift : readLiftPx(),
        next === "active" ? "drop" : "rise",
        getWelcomeConfig().layout === "centered" ? 320 : 260
      );
    }
    // The reservation shrinks by the lift in one frame, above the anchor.
    repinAnchoredMessage();
  };
  // Content is re-applied only when the resolved config changes: this runs on
  // every message change, including each streaming chunk.
  let lastWelcomeKey: string | null = null;
  let lastWelcomeIcon: AgentWidgetWelcomeIcon | undefined;
  let lastGreetingKey: string | null = null;
  // Element returned by a `renderWelcome` plugin, or null when the default
  // renderer owns the host. A plugin element ignores derived visibility.
  let welcomePluginContent: HTMLElement | null = null;
  // True while plugin content stays mounted but unmounted from the overlay,
  // so the pending stand-in the overlay body class would hide stays visible.
  let welcomePluginSuppressed = false;
  const updateWelcome = (messages?: AgentWidgetMessage[]) => {
    const resolved = getWelcomeConfig();
    const welcomeKey = `${resolved.variant}|${resolved.layout}|${resolved.dismiss}|${resolved.anchor ?? "bottom"}|${resolved.align ?? ""}|${resolved.iconPlacement}|${resolved.kicker ?? ""}|${resolved.title}|${resolved.subtitle}`;
    if (welcomeKey !== lastWelcomeKey || resolved.icon !== lastWelcomeIcon) {
      lastWelcomeKey = welcomeKey;
      lastWelcomeIcon = resolved.icon;
      applyWelcomeConfig(
        {
          host: welcomeHost,
          iconHolder: welcomeIconHolder,
          kicker: introKicker,
          title: introTitle,
          subtitle: introSubtitle,
          starterSuggestions,
        },
        resolved
      );
    }
    const greetingKey =
      resolved.message === undefined
        ? null
        : `${config.layout?.messages?.layout ?? "bubble"}|${resolved.message}`;
    if (greetingKey !== lastGreetingKey) {
      lastGreetingKey = greetingKey;
      renderWelcomeGreeting(greetingHost, resolved.message, config);
    }

    const current =
      messages ?? (session ? session.getMessages() : config.initialMessages);
    // Before every return path below: a plugin welcome must get the drop too.
    syncConversationState(current);
    // An engaged conversation-open stand-in owns the surface: the transcript is
    // replaced only at commit, so derived visibility would hold the welcome
    // above the stand-in for the rest of the fetch.
    const openTakeover = conversationOpenTakeover;
    const visible = !openTakeover && isWelcomeVisible(resolved, current);
    const flipped = visible !== welcomeShown;
    welcomeShown = visible;
    // Plugin content renders regardless of derived visibility and owns the
    // host until the plugin returns null again.
    if (welcomePluginContent) {
      welcomeDismissAnimation?.cancel();
      welcomeDismissAnimation = null;
      if (openTakeover !== welcomePluginSuppressed) {
        welcomePluginSuppressed = openTakeover;
        // The overlay body class hides every transcript sibling, the engaged
        // stand-in included, so navigation drops it and the return restores it.
        if (openTakeover) clearWelcomePluginContent(body, welcomeHost, null);
        else mountWelcomePluginContent(body, welcomeHost, welcomePluginContent);
      }
      // Plugin content is an absolute overlay, so hiding it collapses no layout.
      applyWelcomeVisibility(body, welcomeHost, !openTakeover);
      return;
    }
    if (
      flipped &&
      !visible &&
      // Navigation is not a first message: that hide is instant.
      !openTakeover &&
      resolved.dismiss === "on-first-message" &&
      welcomeHydrated
    ) {
      welcomeDismissAnimation = animateWelcomeOut(welcomeHost, (animation) => {
        if (welcomeDismissAnimation === animation) {
          welcomeDismissAnimation = null;
        }
        // A re-show (clearChat, or plugin content claiming the host
        // mid-animation) wins the race.
        if (!welcomeShown && !welcomePluginContent) {
          applyWelcomeVisibility(body, welcomeHost, false);
          repinAnchoredMessage();
        }
        // The forwards fill would pin opacity 0 across a later re-show.
        animation?.cancel();
      });
      return;
    }
    if (welcomeDismissAnimation) {
      // An engaged open takes the host instantly; every other render must not
      // hide it out from under the animation.
      if (!visible && !openTakeover) return;
      welcomeDismissAnimation.cancel();
      welcomeDismissAnimation = null;
    }
    // The host still holds layout while a cancelled dismiss was running, so the
    // repin keys off the applied state rather than the derived flip.
    const wasLaidOut = !welcomeHost.hidden;
    applyWelcomeVisibility(body, welcomeHost, visible);
    if (!visible && wasLaidOut) repinAnchoredMessage();
  };

  // `renderWelcome` arbitration. The core owns the host; a re-render runs the
  // registered cleanups, drops the previous plugin element, and re-arbitrates
  // with a fresh ctx.
  const welcomeCleanups: Array<() => void> = [];
  // renderStarter output is one-shot DOM the suggestion managers never see;
  // tracked here so setComposerDisabled can sync it on streaming flips.
  const welcomeStarterElements: HTMLElement[] = [];
  let welcomeArbitrating = false;
  let lastWelcomeArbitrationKey: string | null = null;
  let lastWelcomeArbitrationIcon: AgentWidgetWelcomeIcon | undefined;

  const runWelcomeCleanups = () => {
    welcomeStarterElements.length = 0;
    const callbacks = welcomeCleanups.splice(0, welcomeCleanups.length);
    for (const callback of callbacks) {
      try {
        callback();
      } catch (error) {
        console.warn("[persona] renderWelcome cleanup threw", error);
      }
    }
  };
  destroyCallbacks.push(runWelcomeCleanups);

  /** Default starter renderer for plugins: full select pipeline, welcome geometry. */
  const renderWelcomeStarter = (
    suggestion: AgentWidgetSuggestion,
    index: number
  ): HTMLElement => {
    const normalized = normalizeSuggestion(suggestion, index);
    // An unlabeled suggestion has nothing to render or send.
    if (!normalized) {
      if (config.debug === true) {
        console.warn("[persona] renderStarter received a suggestion with no label");
      }
      return createElement("span");
    }
    const starters = config.suggestions?.starters;
    const element = createSuggestionElement({
      item: {
        ...normalized,
        behavior: normalized.behavior ?? starters?.behavior ?? "send",
      },
      index,
      surface: "starter",
      source: "config",
      variant: starters?.variant ?? "card",
      streaming: sessionInitialized ? session.isStreaming() : false,
      config,
      plugins,
      chipsConfig: config.suggestionChipsConfig,
      session,
      getTextarea: () => textarea,
      eventTarget: welcomeHost,
      submitPrompt: submitSuggestionPrompt,
    });
    welcomeStarterElements.push(element);
    return element;
  };

  const renderWelcomeSurface = () => {
    // A plugin calling requestRender() from inside its own render would recurse.
    if (welcomeArbitrating) return;
    welcomeArbitrating = true;
    const resolved = getWelcomeConfig();
    lastWelcomeArbitrationKey = `${resolved.variant}|${resolved.layout}|${resolved.dismiss}|${resolved.title}|${resolved.subtitle}|${resolved.message ?? ""}`;
    lastWelcomeArbitrationIcon = resolved.icon;
    try {
      runWelcomeCleanups();
      clearWelcomePluginContent(body, welcomeHost, welcomePluginContent);
      welcomePluginContent = null;
      welcomePluginSuppressed = false;

      const current = session ? session.getMessages() : config.initialMessages;
      const visible = isWelcomeVisible(resolved, current);
      let starterIndex = 0;
      let element: HTMLElement | null = null;
      for (const plugin of plugins) {
        if (!plugin.renderWelcome) continue;
        element = plugin.renderWelcome({
          config: resolved,
          variant: resolved.variant,
          visible,
          defaultRenderer: () => welcomeHost,
          sendMessage: (text: string) => {
            if (!session || session.isStreaming()) return;
            const value = text.trim();
            if (!value) return;
            session.sendMessage(value);
          },
          requestRender: () => renderWelcomeSurface(),
          renderStarter: (suggestion) =>
            renderWelcomeStarter(suggestion, starterIndex++),
          storage: pluginStorageFor(plugin.id),
          onCleanup: (fn) => {
            welcomeCleanups.push(fn);
          },
        });
        if (element) break;
      }
      // Returning `defaultRenderer()` is composition, not a takeover: the
      // default content stays and derived visibility still governs.
      if (element && element !== welcomeHost) {
        welcomePluginContent = element;
        mountWelcomePluginContent(body, welcomeHost, element);
      }
    } finally {
      welcomeArbitrating = false;
    }
    updateWelcome();
  };

  /** Re-arbitrate only when the resolved welcome config actually changed. */
  const refreshWelcomePlugins = () => {
    const resolved = getWelcomeConfig();
    const key = `${resolved.variant}|${resolved.layout}|${resolved.dismiss}|${resolved.title}|${resolved.subtitle}|${resolved.message ?? ""}`;
    if (
      key === lastWelcomeArbitrationKey &&
      resolved.icon === lastWelcomeArbitrationIcon
    ) {
      return;
    }
    renderWelcomeSurface();
  };

  const updateCopy = () => {
    updateWelcome();
    // Plugin-owned composers set their own placeholder and send label (the
    // pre-chat gate keeps its lock reason in the placeholder, for example).
    if (!composerIsPluginOwned) {
      // An active mode may override the placeholder; priority is `composer.modes`
      // order, and clearing every such mode restores the configured copy.
      textarea.placeholder =
        resolveComposerModePlaceholder(
          composerStore.getState().activeModeIds,
          config.composer?.modes
        ) ??
        config.copy?.inputPlaceholder ??
        "How can I help...";

      // Only update send button text if NOT using icon mode. Skip while
      // streaming so we don't stomp on the "Stop" label.
      const useIcon = config.sendButton?.useIcon ?? false;
      if (!useIcon && !session?.isStreaming()) {
        sendButton.textContent = config.copy?.sendButtonLabel ?? "Send";
      }
    }

    textarea.style.fontFamily =
      'var(--persona-input-font-family, var(--persona-font-family, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif))';
    textarea.style.fontWeight = "var(--persona-input-font-weight, var(--persona-font-weight, 400))";
  };

  // Add session ID persistence callbacks for client token mode
  // These allow the widget to resume conversations by passing session_id to /client/init
  // Conversation-id callbacks defer to host-supplied ones; only the internal
  // revision writer is synthesized alongside a synthesized id writer.
  const hostOwnsConversationId = Boolean(config.setStoredConversationId);
  const dropMetadataKey = (key: string) =>
    updateSessionMetadata((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  const readMetadataString = (key: string): string | null => {
    const stored = persistentMetadata[key];
    return typeof stored === 'string' ? stored : null;
  };
  const readStoredResumableHandle = (): {
    executionId: string;
    after: string;
  } | null => {
    const stored = persistentMetadata.durableResume;
    if (!stored || typeof stored !== 'object') return null;
    const candidate = stored as Record<string, unknown>;
    return typeof candidate.executionId === 'string' &&
      candidate.executionId.length > 0 &&
      typeof candidate.after === 'string' &&
      candidate.after.length > 0
      ? { executionId: candidate.executionId, after: candidate.after }
      : null;
  };
  if (config.clientToken) {
    config = {
      ...config,
      getStoredSessionId: () => readMetadataString('sessionId'),
      setStoredSessionId: (sessionId: string) => {
        updateSessionMetadata((prev) => ({
          ...prev,
          sessionId: sessionId,
        }));
      },
      clearStoredSessionId:
        config.clearStoredSessionId ?? (() => dropMetadataKey('sessionId')),
      getStoredConversationId:
        config.getStoredConversationId ?? (() => readMetadataString('conversationId')),
      setStoredConversationId:
        config.setStoredConversationId ??
        ((conversationId: string) => {
          updateSessionMetadata((prev) => ({ ...prev, conversationId }));
        }),
      clearStoredConversationId:
        config.clearStoredConversationId ?? (() => dropMetadataKey('conversationId')),
    };
  }

  // Exact-browser visitor credential store, keyed on (clientToken, keyPrefix,
  // persistence-disabled). Durable reconnect and optional visitor history use
  // the same credential but negotiate independent server capabilities.
  const historyStoreToken = (): string | null =>
    config.clientToken ?? null;
  const buildVisitorStore = (token: string) =>
    createVisitorStore(token, currentKeyPrefix(), config.persistState === false);
  const initialStoreToken = historyStoreToken();
  let visitorStore: VisitorStore | null = initialStoreToken
    ? buildVisitorStore(initialStoreToken)
    : null;
  // Internal history metadata rides the same serialized persistence path as the
  // transcript, so a reload reconstructs revision, prepend cursor, and any
  // display projection still owed to the server.
  const readPendingProjections = (): PendingDisplayProjections | null => {
    const stored = persistentMetadata.pendingDisplayProjections;
    if (!stored || typeof stored !== 'object') return null;
    const candidate = stored as Partial<PendingDisplayProjections>;
    if (typeof candidate.conversationId !== 'string') return null;
    if (!Array.isArray(candidate.messageIds)) return null;
    return {
      conversationId: candidate.conversationId,
      messageIds: candidate.messageIds.filter(
        (id): id is string => typeof id === 'string'
      ),
    };
  };
  let historyInternals: WidgetHistoryInternals = {
    ...(visitorStore ? { visitorStore } : {}),
    historyBootstrapReady:
      config.features?.history?.enabled === true && !getHistoryShellSync()
        ? Promise.all([historyBootstrapReady, historyShellGate]).then(() => {})
        : historyBootstrapReady,
    getStoredMessageCursor: () => readMetadataString('historyMessageCursor'),
    setStoredMessageCursor: (cursor: string | null) => {
      if (cursor === null) {
        dropMetadataKey('historyMessageCursor');
        return;
      }
      updateSessionMetadata((prev) => ({
        ...prev,
        historyMessageCursor: cursor,
      }));
    },
    getPendingDisplayProjections: readPendingProjections,
    setPendingDisplayProjections: (pending) => {
      if (!pending) {
        dropMetadataKey('pendingDisplayProjections');
        return;
      }
      updateSessionMetadata((prev) => ({
        ...prev,
        pendingDisplayProjections: pending,
      }));
    },
    getStoredResumableHandle: readStoredResumableHandle,
    setStoredResumableHandle: (handle) => {
      if (!handle) {
        dropMetadataKey('durableResume');
        return;
      }
      updateSessionMetadata((prev) => ({ ...prev, durableResume: handle }));
    },
    shouldResumeDurableConversation: () =>
      personaOwnsClientTokenRecovery && readStoredResumableHandle() !== null,
    ...(hostOwnsConversationId
      ? {}
      : {
          getStoredConversationRevision: () =>
            readMetadataString('conversationRevision'),
          setStoredConversationRevision: (revision: string | null) => {
            if (revision === null) {
              dropMetadataKey('conversationRevision');
              return;
            }
            updateSessionMetadata((prev) => ({
              ...prev,
              conversationRevision: revision,
            }));
          },
        }),
  };

  // Runtype client-token widgets already possess the session, conversation,
  // and exact-browser visitor credential needed by the public reconnect route.
  // Install Persona's existing reconnect hooks by default so this mode follows
  // the same path as host-configured durable dashboard chats.
  if (personaOwnsClientTokenRecovery) {
    const hostExecutionState = config.onExecutionState;
    const recoveryWindow = mount.ownerDocument.defaultView;
    let pageExitInProgress = false;
    const markPageExit = () => (pageExitInProgress = true);
    const markPageActive = () => (pageExitInProgress = false);
    recoveryWindow?.addEventListener("pagehide", markPageExit);
    recoveryWindow?.addEventListener("pageshow", markPageActive);
    destroyCallbacks.push(() => {
      recoveryWindow?.removeEventListener("pagehide", markPageExit);
      recoveryWindow?.removeEventListener("pageshow", markPageActive);
    });
    config = {
      ...config,
      reconnectStream:
        config.reconnectStream ??
        ((ctx) => session.getClient().reconnectClientTokenStream(ctx)),
      onExecutionState: (handle) => {
        if (handle || !pageExitInProgress) {
          historyInternals.setStoredResumableHandle?.(
            handle
              ? { executionId: handle.executionId, after: handle.lastEventId }
              : null
          );
        }
        hostExecutionState?.(handle);
      },
    };
  }
  const syncVisitorStore = () => {
    const token = historyStoreToken();
    if (
      token &&
      visitorStore?.matches(token, currentKeyPrefix(), config.persistState === false)
    ) {
      return;
    }
    if (!token && !visitorStore) return;
    // Never clear the old namespace: it belongs to the prior surface/config.
    visitorStore?.destroy();
    visitorStore = token ? buildVisitorStore(token) : null;
    const nextInternals: WidgetHistoryInternals = { ...historyInternals };
    if (visitorStore) nextInternals.visitorStore = visitorStore;
    else delete nextInternals.visitorStore;
    historyInternals = nextInternals;
    session.setHistoryInternals(historyInternals);
  };

  // Global timer for live-updating tool elapsed time spans.
  // Runs at 100ms while any [data-tool-elapsed] span exists in the message area,
  // auto-stops when none remain. Operates on real DOM after morph, not temp elements.
  let toolElapsedTimerId: ReturnType<typeof setInterval> | null = null;
  const ensureToolElapsedTimer = () => {
    if (toolElapsedTimerId != null) return;
    toolElapsedTimerId = setInterval(() => {
      const spans = messagesWrapper.querySelectorAll<HTMLElement>("[data-tool-elapsed]");
      if (spans.length === 0) {
        clearInterval(toolElapsedTimerId!);
        toolElapsedTimerId = null;
        return;
      }
      const now = Date.now();
      spans.forEach((span) => {
        activityUi?.updateActivityElapsed(span, now);
      });
    }, 100);
  };

  /**
   * Changes that must reach the DOM (and storage) in the same task rather than
   * on the next animation frame: a new or edited user message drives send
   * anchoring and `user:message`, and an approval or a pending local tool
   * (ask_user_question, WebMCP) is waiting on the user and must be durable
   * before the page can unload. Everything else in a streaming burst is
   * coalesced.
   */
  const needsImmediateApply = (message: AgentWidgetMessage) =>
    message.role === "user" ||
    message.variant === "approval" ||
    message.agentMetadata?.awaitingLocalTool === true ||
    isAskUserQuestionMessage(message);

  /**
   * One pass over a streaming update: returns false when a changed entry needs
   * an immediate apply, and otherwise remembers assistant messages that
   * streamed inside the coalesced burst, so `trackMessages` still emits
   * `assistant:complete` when a message starts and finishes between two
   * applied snapshots. The session copies only the messages it changes, so
   * identity marks the changed entries; this pointer scan costs no more than
   * the array copy the session makes for every update.
   */
  const coalesceStreamingChange = (
    previous: AgentWidgetMessage[],
    next: AgentWidgetMessage[]
  ) => {
    if (next.length < previous.length) return false;
    for (let index = 0; index < next.length; index += 1) {
      const message = next[index];
      if (message === previous[index]) continue;
      if (needsImmediateApply(message)) return false;
      if (message.role === "assistant" && message.streaming) {
        coalescedStreamingIds.add(message.id);
      }
    }
    return true;
  };

  let lastAppliedMessages: AgentWidgetMessage[] | null = null;
  let pendingMessages: AgentWidgetMessage[] | null = null;
  let pendingMessagesRAF: number | null = null;
  let pendingDurablePersistenceMessages: AgentWidgetMessage[] | null = null;

  const persistMessages = (messages: AgentWidgetMessage[]) => {
    if (!personaOwnsClientTokenRecovery) return persistState(messages);
    pendingDurablePersistenceMessages = messages;
    queueMicrotask(() => {
      if (pendingDurablePersistenceMessages === messages) {
        pendingDurablePersistenceMessages = null;
        // The resume cursor is read now, so save the newest snapshot: updates
        // coalesced after this one (still waiting on a frame) already advanced it.
        persistState(pendingMessages ?? messages);
      }
    });
  };

  const applyMessagesChanged = (messages: AgentWidgetMessage[]) => {
    if (!messages.length && config.future?.v5Defaults) hostFollowUps = null;
    lastAppliedMessages = messages;
    let lastUserMessage: AgentWidgetMessage | undefined;
    let lastAssistantMessage: AgentWidgetMessage | undefined;
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (!lastUserMessage && message.role === "user") lastUserMessage = message;
      if (!lastAssistantMessage && message.role === "assistant") {
        lastAssistantMessage = message;
      }
      if (lastUserMessage && lastAssistantMessage) break;
    }
    renderMessagesWithPlugins(messagesWrapper, messages, postprocess);
    // Freshly (re)built inline artifact blocks render from their persisted
    // props; sync them with the live registry so a block created after the
    // last onArtifactsState emission still shows current content.
    getArtifactsUiSync()?.updateInlineArtifactBlocks(messagesWrapper, lastArtifactsState.artifacts, {
      suppressTransition: isStreaming,
    });
    ensureToolElapsedTimer();
    updateWelcome(messages);
    renderSuggestions(messages);
    scheduleAutoScroll(!isStreaming);
    trackMessages(messages);
    syncScrollToBottomButton();
    eventStreamView?.update();

    if (messages.length === 0) {
      resetAnchorState();
      followFallbackActive = true;
      currentTurnAnchored = false;
      anchoredMessageId = null;
      readerEngagedThisTurn = false;
    }
    if (!scrollSendSeeded || suppressScrollSend) {
      scrollSendSeeded = true;
      lastSentUserMessageId = lastUserMessage?.id ?? null;
      lastHandledAssistantId = lastAssistantMessage?.id ?? null;
    } else if (lastUserMessage && lastUserMessage.id !== lastSentUserMessageId) {
      lastSentUserMessageId = lastUserMessage.id;
      handleUserMessageSent(lastUserMessage.id);
    } else if (
      lastAssistantMessage &&
      lastAssistantMessage.id !== lastHandledAssistantId
    ) {
      handleAssistantTurnStarted();
    }
    if (lastAssistantMessage) lastHandledAssistantId = lastAssistantMessage.id;

    const prevLastUserMessageId = voiceState.lastUserMessageId;
    if (lastUserMessage && lastUserMessage.id !== prevLastUserMessageId) {
      voiceState.lastUserMessageId = lastUserMessage.id;
      eventBus.emit("user:message", lastUserMessage);
    }

    voiceState.lastUserMessageWasVoice = Boolean(lastUserMessage?.viaVoice);
    persistMessages(messages);
    syncComposerBarPeek();
    eventBus.emit("messages:changed", messages);
  };

  const flushPendingMessages = () => {
    if (pendingMessagesRAF !== null) {
      cancelAnimationFrame(pendingMessagesRAF);
      pendingMessagesRAF = null;
    }
    const pending = pendingMessages;
    pendingMessages = null;
    if (pending) applyMessagesChanged(pending);
  };

  const discardPendingMessages = () => {
    if (pendingMessagesRAF !== null) cancelAnimationFrame(pendingMessagesRAF);
    pendingMessagesRAF = null;
    pendingMessages = null;
  };

  // While a turn streams, every SSE frame changes the transcript, and a render
  // pass costs O(messages). Applying each frame synchronously makes a burst of
  // N frames (a long tool run, or a finished transcript replayed through
  // `connectStream`) cost O(N²) and block the main thread. Coalesce streaming
  // updates to one render per animation frame; the stream ending, a clear, or
  // a change in `needsImmediateApply` flushes synchronously.
  const handleMessagesChanged = (messages: AgentWidgetMessage[]) => {
    if (messages.length === 0) {
      discardPendingMessages();
      applyMessagesChanged(messages);
      return;
    }
    const comparison = pendingMessages ?? lastAppliedMessages;
    if (isStreaming && comparison && coalesceStreamingChange(comparison, messages)) {
      pendingMessages = messages;
      if (pendingMessagesRAF === null) {
        pendingMessagesRAF = requestAnimationFrame(() => {
          pendingMessagesRAF = null;
          const pending = pendingMessages;
          pendingMessages = null;
          if (pending) applyMessagesChanged(pending);
        });
      }
      return;
    }
    flushPendingMessages();
    applyMessagesChanged(messages);
  };

  session = new AgentWidgetSession(config, {
    onRunStarted: (event) => eventBus.emit("run:started", event),
    onRunFinished(event) {
      flushPendingMessages();
      if (config.turnActivity?.enabled) renderMessagesWithPlugins(messagesWrapper, session.getMessages(), postprocess);
      eventBus.emit("run:finished", event);
    },
    onMessagesChanged(messages) {
      handleMessagesChanged(messages);
    },
    onStatusChanged(status) {
      const currentStatusConfig = config.statusIndicator ?? {};
      const getCurrentStatusText = (s: AgentWidgetSessionStatus): string => {
        if (s === "idle") return currentStatusConfig.idleText ?? statusCopy.idle;
        if (s === "connecting") return currentStatusConfig.connectingText ?? statusCopy.connecting;
        if (s === "connected") return currentStatusConfig.connectedText ?? statusCopy.connected;
        if (s === "error") return currentStatusConfig.errorText ?? statusCopy.error;
        if (s === "paused") return currentStatusConfig.pausedText ?? statusCopy.paused;
        if (s === "resuming") return currentStatusConfig.resumingText ?? statusCopy.resuming;
        return statusCopy[s];
      };
      applyStatusToElement(statusText, getCurrentStatusText(status), currentStatusConfig, status);
      syncScrollToBottomButton();
      eventStreamView?.update();
      // A paused/resuming durable turn gates the history button too.
      historyChromeSync();
    },
    onStreamingChanged(streaming) {
      if (!streaming) {
        if (session?.getMessages().length === 0) {
          discardPendingMessages();
        } else {
          flushPendingMessages();
        }
      }
      isStreaming = streaming;
      if (streaming) dismissFollowUps();
      else if (config.future?.v5Defaults) renderSuggestions();
      setComposerDisabled(streaming);
      historyChromeSync();
      // Re-render messages to show/hide typing indicator
      if (session) {
        renderMessagesWithPlugins(messagesWrapper, session.getMessages(), postprocess);
      }
      if (!streaming) {
        scheduleAutoScroll(true);
        trimAnchorSpacerToRest();
        // setStreaming only fires on a change, so this is always a real stream
        // ending. Evaluate the never-called hint here: no later render is
        // guaranteed on a settled transcript.
        completedStreamThisSession = true;
        if (session) {
          hintFollowUpsNeverCalled(
            session.getMessages(),
            resolveFollowUpsFeature(config)
          );
        }
      }
      // Keep the "streaming below" hint and its announcement in sync with the
      // streaming lifecycle (Principles 8 + 15).
      syncScrollToBottomButton();
      eventStreamView?.update();
      announce(streaming ? "Responding…" : "Response complete.");
      // Composer-bar peek: streaming state is one of the two visibility
      // triggers (the other is composer hover), so re-evaluate now.
      syncComposerBarPeek();
    },
    onVoiceStatusChanged(status: VoiceStatus) {
      // Surface the granular status publicly so consumers can render their own
      // per-state UI (e.g. a listening/speaking status dock). Fires for every
      // provider; the mic-button styling below is runtype-specific.
      eventBus.emit("voice:status", { status, timestamp: Date.now() });
      if (status !== 'disconnected' && !usesSessionVoice(config.voiceRecognition?.provider)) return;
      // Why the server ended the call, or that it is reconnecting.
      const voiceNotice = session.getVoiceNotice();
      if (voiceNotice) showComposerNotice(voiceNotice, 10_000);

      switch (status) {
        case 'listening':
          // A speech-to-speech call discloses that it's an AI, once per call.
          if (!voiceDisclosed) {
            const notice = session.getVoiceDisclosure();
            if (notice) {
              voiceDisclosed = true;
              if (isComposerBar()) {
                placeholderBeforeDisclosure = textarea.placeholder;
                textarea.placeholder = notice;
                announce(notice);
              } else {
                showComposerNotice(notice, 10_000);
              }
            }
          }
          // A continuous realtime call re-enters `listening` after every spoken
          // reply, so reassert the recording styles here (they were replaced by
          // the `processing`/`speaking` states during the turn). The initial
          // listen is also styled by the toggleVoice()/startVoiceRecognition()
          // flows; reapplying is idempotent.
          removeRuntypeMicStateStyles();
          applyRuntypeMicRecordingStyles();
          break;
        case 'processing':
          removeRuntypeMicStateStyles();
          applyRuntypeMicProcessingStyles();
          break;
        case 'speaking':
          removeRuntypeMicStateStyles();
          applyRuntypeMicSpeakingStyles();
          break;
        default:
          // idle, connected, disconnected, error
          if (status === 'idle' && session.isBargeInActive()) {
            // Barge-in mic is still hot between turns: show it as active
            removeRuntypeMicStateStyles();
            applyRuntypeMicRecordingStyles();
            micButton?.setAttribute("aria-label", "End voice session");
          } else {
            voiceState.active = false;
            voiceDisclosed = false;
            if (placeholderBeforeDisclosure !== null) {
              textarea.placeholder = placeholderBeforeDisclosure;
              placeholderBeforeDisclosure = null;
            }
            removeRuntypeMicStateStyles();
            emitVoiceState("system");
            if (!pageHiding) persistVoiceMetadata();
          }
          break;
      }
    },
    onArtifactsState(state) {
      lastArtifactsState = state;
      // A cleared registry ends any explicit-open override: the next artifact
      // decides pane visibility purely from its own display mode.
      if (state.artifacts.length === 0) {
        artifactsPaneUserOpened = false;
      }
      // Route streaming registry updates (artifact_delta / artifact_complete)
      // into any inline artifact blocks in the transcript. Suppress the
      // streaming→complete View Transition while the session is still
      // streaming: it captures the whole document, and cross-fading a stale
      // snapshot over still-moving message text reads as ghosting/motion blur
      // on the transcript.
      getArtifactsUiSync()?.updateInlineArtifactBlocks(messagesWrapper, state.artifacts, {
        suppressTransition: isStreaming,
      });
      syncArtifactPane();
      persistState();
    },
    // Late-bound: the history block installs the real handlers below.
    onHistoryStateChanged(state) {
      historyStateHandler(state);
    },
    onHistoryNotice(notice) {
      historyNoticeHandler(notice);
    },
    onHistoryScopeInvalidated() {
      discardConversationContinuity();
      historyShell?.clearConversationOpenPending();
    },
    onConversationExecutionState(state) {
      eventBus.emit("conversation:executionState", state);
    },
    onReconnect(event) {
      // Map the durable-reconnect lifecycle to public controller events.
      const { executionId, lastEventId } = event.handle;
      if (event.phase === "paused") {
        eventBus.emit("stream:paused", { executionId, after: lastEventId });
      } else if (event.phase === "resuming") {
        eventBus.emit("stream:resuming", {
          executionId,
          after: lastEventId,
          attempt: event.attempt ?? 1,
        });
      } else {
        eventBus.emit("stream:resumed", { executionId, after: lastEventId });
      }
    }
  }, historyInternals);

  sessionRef.current = session;

  // On teardown, cancel any in-flight turn/reconnect so a pending durable
  // reconnect's backoff timer and focus/online listeners don't outlive the
  // widget (cancel() → teardownReconnect()).
  destroyCallbacks.push(() => session.cancel());
  // Drop the visitor store's `storage` listener and the session's subscription
  // to it; a client rebuild must not leak either.
  destroyCallbacks.push(() => {
    session.destroy();
    visitorStore?.destroy();
    visitorStore = null;
  });

  // Mirror read-aloud playback state into the action buttons, and surface it as
  // a controller event (parallel to message:copy / message:feedback).
  let lastReadAloudId: string | null = null;
  session.onReadAloudChange((activeId, state) => {
    readAloudActiveId = activeId;
    readAloudActiveState = state;
    refreshReadAloudButtons();

    // On the terminal `idle` transition activeId is null, so fall back to the
    // last active id to identify the message that just finished/stopped.
    const messageId = activeId ?? lastReadAloudId;
    if (activeId) lastReadAloudId = activeId;
    const message = messageId
      ? session.getMessages().find((m) => m.id === messageId) ?? null
      : null;
    eventBus.emit("message:read-aloud", {
      messageId,
      message,
      state,
      timestamp: Date.now(),
    });
    if (state === "idle") lastReadAloudId = null;
  });

  sessionInitialized = true;

  // The constructor only emits onMessagesChanged when it has initial
  // messages, so seed send-detection explicitly for the empty-session case:  // otherwise the user's very first send would be mistaken for the seed.
  scrollSendSeeded = true;

  // Setup Runtype voice provider when configured (connects WebSocket for server-side STT)
  if (config.voiceRecognition?.enabled === true && usesSessionVoiceForConfig(config)) {
    try {
      session.setupVoice();
    } catch (err) {
      if (typeof console !== 'undefined') {
        // eslint-disable-next-line no-console
        console.warn('[AgentWidget] Runtype voice setup failed:', err);
      }
    }
  }

  // ==========================================================================
  // Visitor conversation history shell
  // (docs/visitor-history-implementation-plan.md D6/D7/D8)
  //
  // The shell owns placement, open/close, inertness of the obscured
  // conversation, focus orchestration, confirmations, and every session
  // mutation. The lazily loaded Messages view owns the list itself.
  // ==========================================================================

  let historyShell: HistoryShell | null = null;
  let historyShellLoad: Promise<HistoryShell | null> | null = null;
  let historyShellTornDown = false;
  let historySessionState: SessionHistoryState = session.getHistoryState();

  const historyFeatureEnabled = (): boolean =>
    config.features?.history?.enabled === true;

  /** Unconditional: `announce()` is gated on an unrelated scroll opt-in. */
  const announceHistory = (message: string): void => {
    if (!message) return;
    liveRegion.textContent = "";
    liveRegion.textContent = message;
  };

  /**
   * Every binding this instance owns, re-derived so a live `update()` of the
   * config or the plugin list lands. Config registers before plugins, so a
   * collision leaves the config combo standing.
   */
  const syncShortcuts = (): void => {
    releaseShortcuts.forEach((release) => release());
    releaseShortcuts = [];
    // The rail collapse combo belongs to the (lazy) history shell.
    const shell = historyShell;
    const shortcut = shell?.railCollapseShortcut();
    if (shell && shortcut) {
      releaseShortcuts.push(
        shortcuts.register({
          id: "history-rail-collapse",
          combo: shortcut.combo,
          scope:
            config.features?.history?.rail?.collapseShortcutScope === "page"
              ? "page"
              : "widget",
          // Overlay mode answers at rest too: the combo is how a keyboard
          // visitor pins a rail that is currently only a trigger.
          when: () =>
            (historyVisible &&
              historyPresentation === "rail" &&
              shell.railCollapsible()) ||
            shell.railTriggerApplies(),
          run: () =>
            shell.railOverlayMode()
              ? shell.toggleRailPinned()
              : shell.toggleRailCollapsed(),
        })
      );
    }
    for (const action of config.layout?.header?.trailingActions ?? []) {
      if (!action.shortcut) continue;
      releaseShortcuts.push(
        shortcuts.register({
          id: `header-action-${action.id}`,
          combo: action.shortcut,
          // The click path, so a menu action still opens its dropdown; a
          // header rebuild swaps the button, so it is found per keypress.
          run: () => {
            const button = mount.querySelector<HTMLButtonElement>(
              `[data-persona-header-action="${action.id}"]`
            );
            if (button) button.click();
            else config.layout?.header?.onAction?.(action.id);
          },
        })
      );
    }
    for (const plugin of plugins) {
      for (const shortcut of plugin.shortcuts ?? []) {
        releaseShortcuts.push(
          shortcuts.register({ ...shortcut, id: `${plugin.id}:${shortcut.id}` })
        );
      }
    }
  };
  syncShortcuts();

  // ---- header title binding (layout.header.titleSource: "conversation") ----
  // The history view reports the active conversation's list summary through
  // onActiveConversationChange; the shell owns the fallback and the write, so
  // the binding works for both the plain title span and the titleMenu label.
  // `activeConversationStarred` additionally feeds the built-in title-menu
  // star toggle.
  let activeConversationTitle: string | null = null;
  let activeConversationStarred = false;
  const applyHeaderTitle = (): void => {
    if (!headerTitle) return;
    const conversationMode = config.layout?.header?.titleSource === "conversation";
    const bound = conversationMode
      ? activeConversationTitle?.trim() || null
      : null;
    headerTitle.textContent = bound ?? config.launcher?.title ?? "Chat Assistant";
    // A conversation-bound titleMenu acts on the ACTIVE conversation, so with
    // none open the combo locks into a plain title (no chevron, no menu)
    // instead of a menu of no-ops. Static titles keep their menu untouched.
    const combo = headerTitle.closest<HTMLElement>(".persona-combo-btn");
    if (!combo) return;
    const menuAvailable =
      !conversationMode || session.getActiveConversationId() !== null;
    combo.setAttribute("data-persona-menu-locked", menuAvailable ? "false" : "true");
    combo.setAttribute("aria-haspopup", menuAvailable ? "true" : "false");
    if (!menuAvailable) combo.setAttribute("aria-expanded", "false");
    combo.style.cursor = menuAvailable ? "pointer" : "default";
    const chevron = combo.querySelector("svg");
    if (chevron) chevron.style.display = menuAvailable ? "" : "none";
  };
  const setActiveConversationTitle = (title: string | null): void => {
    if (title === activeConversationTitle) return;
    activeConversationTitle = title;
    applyHeaderTitle();
  };
  const setActiveConversationSummary = (
    summary: HistoryConversationSummary | null
  ): void => {
    activeConversationStarred = summary?.starred === true;
    setActiveConversationTitle(summary ? summary.title.trim() || null : null);
    // The title can be unchanged (null to null) while the ACTIVE id flipped,
    // e.g. start-new; the menu lock keys on the id, so always resync.
    applyHeaderTitle();
  };

  // Built-in title-menu actions, raised as a bubbling DOM event by the header
  // combo (the initial header is built by the pure panel builder, out of the
  // shell's reach). One listener on the stable container survives header
  // rebuilds and rail re-homing.
  // Initial stamp: with no active conversation the conversation-bound menu
  // starts locked (and a restored session starts unlocked).
  applyHeaderTitle();

  container.addEventListener("persona:title-menu-builtin", (event) => {
    const actionId = (event as CustomEvent<{ actionId?: string }>).detail
      ?.actionId;
    const activeId = session.getActiveConversationId();
    if (!activeId) return;
    if (actionId === "delete") {
      void withHistoryShell((shell) => shell.requestDeleteConversation(activeId));
    } else if (actionId === "star" && session.canUpdateConversations()) {
      void withHistoryShell((shell) =>
        shell.updateHistoryConversation(activeId, {
          starred: !activeConversationStarred,
        })
      ).catch(() => {});
    }
  });

  // Volatile state belongs to this widget/provider/visitor, never to storage.
  type ConversationReadingState = {
    top: number;
    anchorId?: string;
    anchorOffset: number;
    following: boolean;
    followFallback: boolean;
    anchored: boolean;
    anchoredId: string | null;
    readerEngaged: boolean;
    anchor: { initialSpacerHeight: number; contentHeightAtAnchor: number; spacerHeight: number } | null;
    unread: number;
  };
  type ConversationContinuity = {
    draft: AgentWidgetStoredDraft;
    document?: import("./utils/composer-document").ComposerDocument;
    mentions: import("./utils/context-mention-manager").ContextMentionDraft;
    attachments: AttachmentManager | null;
    pending: Readonly<ComposerSubmissionSnapshot> | undefined;
    reading: ConversationReadingState;
  };
  const conversationContinuity = new Map<string, ConversationContinuity>();
  const conversationStateKey = (id = session.getActiveConversationId(), scope = historyShell?.historyOperationScope() ?? config.features?.history?.scope ?? (config.getIdentityProof ? "verified-user" : "browser")): string =>
    JSON.stringify([scope, id]);
  let pendingConversationReading: { key: string; reading: ConversationReadingState } | null = null;
  let composerConversationGeneration = 0;

  const captureConversationReading = (): ConversationReadingState => {
    const viewportTop = body.getBoundingClientRect().top;
    const anchor = body.clientHeight > 0
      ? Array.from(messagesWrapper.querySelectorAll<HTMLElement>("[data-message-id]")).find(
        (node) => node.getBoundingClientRect().bottom > viewportTop)
      : undefined;
    return {
      top: body.scrollTop,
      anchorId: anchor?.dataset.messageId,
      anchorOffset: anchor ? anchor.getBoundingClientRect().top - viewportTop : 0,
      following: autoFollow.isFollowing(),
      followFallback: followFallbackActive,
      anchored: currentTurnAnchored,
      anchoredId: anchoredMessageId,
      readerEngaged: readerEngagedThisTurn,
      anchor: anchorState ? { ...anchorState } : null,
      unread: newMessagesSincePause,
    };
  };

  const saveConversationContinuity = (): void => {
    // cancel() synchronously restores the pre-recording draft and invalidates
    // provider callbacks, so capture only after that restoration.
    dismissDictation();
    stopVoiceRecognition("system");
    if (usesSessionVoiceForConfig(config)) session.cleanupVoice();
    ++composerConversationGeneration;
    submissionAbort?.abort();
    submissionAbort = null;
    composerStore.setPreparing(false);
    cancelMessageEdit();
    flushDraftPersist();
    const state = composerStore.getState();
    const key = conversationStateKey();
    conversationContinuity.set(key, {
      draft: {
        text: state.text,
        mentionRefs: [...state.mentionRefs],
        contentSegments: readInlineContentSegments(),
        quote: state.quote,
        selectedModelId: state.selectedModelId,
        activeModeIds: [...state.activeModeIds],
      },
      document: mentionOrchestrator?.getInlineInput()?.getDocument?.(),
      mentions: mentionOrchestrator?.captureDraft() ?? [],
      attachments: attachmentManager,
      pending: state.pendingSubmission,
      reading: pendingConversationReading?.key === key
        ? pendingConversationReading.reading : captureConversationReading(),
    });
    draftWriter.cancel();
  };

  const restoreConversationReading = (reading: ConversationReadingState): void => {
    followFallbackActive = reading.followFallback;
    currentTurnAnchored = reading.anchored;
    anchoredMessageId = reading.anchoredId;
    readerEngagedThisTurn = reading.readerEngaged;
    anchorState = reading.anchor ? { ...reading.anchor, pending: 0 } : null;
    setAnchorSpacerHeight(reading.anchor?.spacerHeight ?? 0);
    if (reading.following) autoFollow.resume(); else autoFollow.pause();
    newMessagesSincePause = reading.unread;
    const anchor = Array.from(messagesWrapper.querySelectorAll<HTMLElement>("[data-message-id]"))
      .find((node) => node.dataset.messageId === reading.anchorId);
    isAutoScrolling = true;
    if (reading.following && isFollowEffective()) body.scrollTop = getScrollBottomOffset(body);
    else if (anchor) body.scrollTop += anchor.getBoundingClientRect().top - body.getBoundingClientRect().top - reading.anchorOffset;
    else body.scrollTop = reading.top;
    isAutoScrolling = false;
    lastScrollTop = body.scrollTop;
    lastBottomOffset = getScrollBottomOffset(body);
    updateScrollToBottomCountBadge();
    syncTranscriptEdgeFades();
    syncScrollToBottomButton();

  };

  const restoreConversationContinuity = (fresh = false, persist = true): void => {
    const saved = fresh ? undefined : conversationContinuity.get(conversationStateKey());
    mentionOrchestrator?.clear();
    attachmentManager?.setPreviewsContainer(null);
    attachmentManager = saved?.attachments ?? (config.attachments?.enabled ? createScopedAttachmentManager() : null);
    attachmentManager?.updateConfig(attachmentManagerConfig());
    attachmentManager?.remountPreviews(attachmentPreviewsContainer);
    syncComposerAttachments(attachmentManager?.getAttachments() ?? []);
    const draft = saved?.draft;
    const input = mentionOrchestrator?.getInlineInput();
    if (saved?.document && input?.setDocument) input.setDocument(saved.document);
    else if (input) input.setValueWithCaret(draft?.text ?? "", (draft?.text ?? "").length);
    else textarea.value = draft?.text ?? "";
    mentionOrchestrator?.restoreDraft(saved?.mentions ?? []);
    syncComposerText();
    syncComposerMentions();
    composerStore.setSelectedModelId(draft
      ? (config.composer?.models?.some((model) => model.id === draft.selectedModelId) ? draft.selectedModelId : undefined)
      : config.composer?.selectedModelId);
    composerCoreControls?.repaint();
    setActiveComposerModes(pruneComposerModes(draft?.activeModeIds ?? config.composer?.defaultActiveModeIds ?? [], composerModes()));
    setComposerQuote(draft?.quote);
    setPendingSubmission(saved?.pending);
    resetHistoryNavigation();
    resetAnchorState();
    cancelAutoScroll();
    // Slow opens hide the transcript; restore its layout before measuring.
    historyShell?.cancelConversationOpenTakeover();
    if (conversationOpenPendingEl) conversationOpenPendingEl.hidden = true;
    conversationOpenTakeover = false;
    messagesWrapper.style.removeProperty("display");
    if (saved) {
      restoreConversationReading(saved.reading);
    } else {
      resumeAutoScroll();
      if (!restoreScrollPosition()) jumpToBottomInstant();
    }
    // Persistence resumes only with the complete destination transcript and
    // composer. Draft opt-out still applies to this in-memory restoration.
    if (persist) currentStoredDraft = draftPersistenceEnabled() ? buildStoredDraft({ ...composerStore.getState(), contentSegments: readInlineContentSegments() }) : undefined;
    draftWriter.cancel();
    if (persist) persistState();
    pendingConversationReading = { key: conversationStateKey(), reading: saved?.reading ?? captureConversationReading() };
  };

  const discardConversationContinuity = (id?: string | null, restore = true, persist = true): void => {
    for (const [key, saved] of conversationContinuity) {
      if (id !== undefined && (JSON.parse(key) as [HistoryScope, string | null])[1] !== id) continue;
      saved.attachments?.destroy({ release: true });
      if (attachmentManager === saved.attachments) attachmentManager = null;
      conversationContinuity.delete(key);
    }
    if (id === undefined || id === session.getActiveConversationId()) {
      ++composerConversationGeneration;
      conversationPersistenceOwner = null;
      submissionAbort?.abort();
      submissionAbort = null;
      dismissDictation();
      attachmentManager?.destroy({ release: true });
      attachmentManager = null;
      if (restore) restoreConversationContinuity(true, persist);
      pendingConversationReading = null;
    }
  };


  // --- session-driven state ------------------------------------------------

  historyStateHandler = (state) => {
    historySessionState = state;
    historyShell?.historySurface?.view.setNewConversationRequired(
      state.recovery === "new_conversation_required"
    );
    // Sending stays blocked through a destructive/continuity transition.
    setComposerDisabled(state.sendBlocked || isStreaming);
    historyShell?.syncEarlierMessagesPill();
  };

  historyNoticeHandler = (notice) => {
    if (
      notice.code === "history_continuity_reset" ||
      notice.code === "conversation_deleted_recovered"
    ) {
      discardConversationContinuity();
      historyShell?.clearConversationOpenPending();
      messageCache.clear();
      resetAnchorState();
      resumeAutoScroll();
    }
    announceHistory(notice.message);
    historyShell?.historySurface?.view.refresh();
  };

  // --- lazy shell ----------------------------------------------------------
  // Placement, the rail, open/close and the conversation operations live in
  // `history-shell.ts`: inlined for npm builds, the lazy `history-shell.js`
  // chunk in the IIFE/CDN bundle. The shell is created when
  // `features.history` is enabled (at mount or by a later `update()`), or on
  // the first history API call. Until then history is simply unavailable.
  const historyShellContext: HistoryShellContext = {
    getBubbleClasses,
    parseCombo, formatCombo, ariaCombo, usesSessionVoiceForConfig,
    applyBubbleGeometry,
    resolveThemeDefaults,
    composerStore,
    get attachmentManager() { return attachmentManager; },
    set attachmentManager(value) { attachmentManager = value; },
    get composerConversationGeneration() { return composerConversationGeneration; },
    set composerConversationGeneration(value) { composerConversationGeneration = value; },
    get submissionAbort() { return submissionAbort; },
    set submissionAbort(value) { submissionAbort = value; },
    persistState,
    applyMessageRowLayout,
    SessionHistoryError,

    instanceId,
    discardConversationContinuity,
    clearPersistedDraft,
    saveConversationContinuity,
    restoreConversationContinuity,
    get pendingConversationReading() { return pendingConversationReading; },
    set pendingConversationReading(value) { pendingConversationReading = value; },
    conversationStateKey,
    captureConversationReading,
    restoreConversationReading,
    get conversationPersistenceOwner() { return conversationPersistenceOwner; },
    set conversationPersistenceOwner(value) { conversationPersistenceOwner = value; },
    setComposerDisabled,
    get actionManager() { return actionManager; },
    get activeConversationTitle() { return activeConversationTitle; },
    announceHistory,
    get applyRailChrome() { return applyRailChrome; },
    set applyRailChrome(value) { applyRailChrome = value; },
    get artifactSplitRoot() { return artifactSplitRoot; },
    get body() { return body; },
    get config() { return config; },
    get container() { return container; },
    get conversationOpenPendingEl() { return conversationOpenPendingEl; },
    set conversationOpenPendingEl(value) { conversationOpenPendingEl = value; },
    get conversationOpenTakeover() { return conversationOpenTakeover; },
    set conversationOpenTakeover(value) { conversationOpenTakeover = value; },
    currentKeyPrefix,
    destroyCallbacks,
    eventBus,
    get footer() { return footer; },
    get header() { return header; },
    get historyChromeSync() { return historyChromeSync; },
    set historyChromeSync(value) { historyChromeSync = value; },
    historyFeatureEnabled,
    get historyInternals() { return historyInternals; },
    set historyInternals(value) { historyInternals = value; },
    get historyPresentation() { return historyPresentation; },
    set historyPresentation(value) { historyPresentation = value; },
    get historySessionState() { return historySessionState; },
    get historyVisible() { return historyVisible; },
    set historyVisible(value) { historyVisible = value; },
    get isStreaming() { return isStreaming; },
    jumpToBottomInstant,
    get lastAppliedMessages() { return lastAppliedMessages; },
    set lastAppliedMessages(value) { lastAppliedMessages = value; },
    get launcherEnabled() { return launcherEnabled; },
    get launcherSurfaceInstance() { return launcherSurfaceInstance; },
    maybeFocusInput,
    messageCache,
    get messagesWrapper() { return messagesWrapper; },
    mount,
    get open() { return open; },
    panel,
    panelElements,
    panelSuggestions,
    panelSuggestionsFade,
    get persistentMetadata() { return persistentMetadata; },
    set persistentMetadata(value) { persistentMetadata = value; },
    plugins,
    get reapplyHistoryHostChrome() { return reapplyHistoryHostChrome; },
    set reapplyHistoryHostChrome(value) { reapplyHistoryHostChrome = value; },
    repinAnchoredMessage,
    resetAnchorState,
    restoreScrollPosition,
    resumeAutoScroll,
    runStorageMutation,
    get session() { return session; },
    setActiveConversationSummary,
    storageAdapter,
    get suppressScrollSend() { return suppressScrollSend; },
    set suppressScrollSend(value) { suppressScrollSend = value; },
    syncScrollToBottomButton,
    scrollToBottomButton,
    updateWelcome,
    getHistoryProviderFactory,
    historyErrors: { HistoryClientError, HistoryProviderError },
    isDockedMountMode,
    renderLucideIcon,
    loadHistoryView,
    createHeaderIconButton,
    attachTooltip,
  };

  const startHistoryShell = (module: HistoryShellModule): HistoryShell | null => {
    if (!historyShell && !historyShellTornDown) {
      historyShell = module.createHistoryShell(historyShellContext);
      // The rail collapse combo registers through the shell that owns the rail.
      if (historyShell.railCollapseShortcut()) syncShortcuts();
    }
    return historyShell;
  };
  const ensureHistoryShell = (): Promise<HistoryShell | null> => {
    if (historyShell) return Promise.resolve(historyShell);
    const ready = getHistoryShellSync();
    if (ready) return Promise.resolve(startHistoryShell(ready));
    historyShellLoad ??= loadHistoryShell().then(startHistoryShell, (error) => {
      historyShellLoad = null;
      throw error;
    });
    return historyShellLoad;
  };
  /** Runs a history API call, synchronously once the shell is available. */
  const withHistoryShell = <T>(
    run: (shell: HistoryShell) => Promise<T>
  ): Promise<T> => {
    const ready = getHistoryShellSync();
    const shell = historyShell ?? (ready ? startHistoryShell(ready) : null);
    if (shell) return run(shell);
    return ensureHistoryShell().then((loaded) => {
      if (!loaded) throw new Error("[AgentWidget] The widget was destroyed");
      return run(loaded);
    });
  };
  const syncHistoryPresentation = (): void => {
    historyShell?.syncHistoryPresentation();
  };
  destroyCallbacks.push(() => {
    historyShellTornDown = true;
  });
  if (historyFeatureEnabled()) {
    void ensureHistoryShell()
      .catch(() => {})
      .finally(resolveHistoryShellGate);
  }

  // Client-token init on mount is reserved for widgets that need a session at
  // boot: visitor history (boot reconciliation) and `sessionInit: 'mount'`.
  // Everything else inits on intent (the `sessionInit` triggers below) or at
  // send, since init is no longer free server-side (it pre-allocates the
  // conversation's session owner).
  if (
    config.clientToken &&
    (config.features?.history?.enabled === true || config.sessionInit === "mount")
  ) {
    session
      .initClientSession()
      .then(() => {
        // Boot resume is authoritative only after reconciliation; the client
        // already gated its init on `historyBootstrapReady`.
        const shell = historyShell;
        if (!shell?.historyAvailable()) return undefined;
        return session.reconcileBootConversation({ scope: shell.historyScope() });
      })
      .then(() => {
        historyShell?.syncEarlierMessagesPill();
      })
      .catch((err) => {
        if (config.debug) {
          // eslint-disable-next-line no-console
          console.warn("[AgentWidget] Pre-init client session failed:", err);
        }
      });
  }

  // Early session init on visitor intent (`sessionInit`). Delegated on the
  // mount so composer rebuilds stay covered, and read `config` at event time so
  // `update()` applies without re-wiring.
  const warmSession = () => session.warmClientSession();
  // One delegated listener. `input` counts only real typing, paste, drop or
  // IME (an InputEvent with an inputType; the widget's programmatic edits
  // dispatch a plain Event) that leaves text. In 'focus' mode a pointer-down in
  // the composer or a Tab into it also counts; `.focus()` (autofocus on
  // open/mount) produces neither.
  const handleSessionInitIntent = (event: Event) => {
    const mode = config.sessionInit ?? "input";
    const input = composerBindings?.input ?? textarea;
    const target = event.target as Node;
    if (
      input &&
      (event.type === "input"
        ? (mode === "input" || mode === "focus") &&
          target === input &&
          !!(event as InputEvent).inputType &&
          !!(input as HTMLTextAreaElement).value.trim()
        : mode === "focus" &&
          (event.type === "keyup"
            ? target === input && (event as KeyboardEvent).key === "Tab"
            : !!(composerBindings?.form ?? composerForm)?.contains(target)))
    ) {
      warmSession();
    }
  };
  const sessionInitEvents = ["input", "pointerdown", "keyup"];
  sessionInitEvents.forEach((type) => mount.addEventListener(type, handleSessionInitIntent));
  // The custom trigger (a `sessionInit` function) runs on mount and again
  // whenever `update()` swaps in a different function, cleaning up the old one.
  let sessionInitHook: AgentWidgetSessionInitHook | undefined;
  let sessionInitHookCleanup: ReturnType<AgentWidgetSessionInitHook>;
  // Host hook code may throw; neither its setup nor its cleanup may leave
  // mount, update() or destroy() half done. The send still inits either way.
  const releaseSessionInitHook = () => {
    const cleanup = sessionInitHookCleanup;
    sessionInitHookCleanup = undefined;
    try {
      if (typeof cleanup === "function") cleanup();
    } catch {
      // ignored, see above
    }
  };
  const syncSessionInitHook = () => {
    const next =
      config.clientToken && typeof config.sessionInit === "function"
        ? config.sessionInit
        : undefined;
    if (next === sessionInitHook) return;
    releaseSessionInitHook();
    sessionInitHook = next;
    try {
      sessionInitHookCleanup = next?.({ warm: warmSession, mount });
    } catch {
      // ignored, see above
    }
  };
  syncSessionInitHook();
  destroyCallbacks.push(() => {
    sessionInitEvents.forEach((type) =>
      mount.removeEventListener(type, handleSessionInitIntent)
    );
    releaseSessionInitHook();
  });

  // Wire up optional SSE tap (host) + event stream buffer to capture SSE
  // events. `feedEventStream` stages events while the lazy chunk is in flight.
  if (showEventStreamToggle || config.onSSEEvent) {
    session.setSSEEventCallback((type: string, payload: unknown) => {
      config.onSSEEvent?.(type, payload);
      feedEventStream(type, payload);
    });
  }

  // Durable-session reconnect boot path: once history is in place,
  // if the host passed a non-terminal resume handle (+ a reconnect transport),
  // immediately re-enter `resuming` and replay everything past the cursor into
  // the restored conversation. Fires AFTER hydration so the trailing partial
  // assistant bubble exists for the replay to append to.
  const maybeBootResume = () => {
    const resume =
      config.resume ??
      (personaOwnsClientTokenRecovery
        ? historyInternals.getStoredResumableHandle?.()
        : null);
    if (resume && typeof config.reconnectStream === "function") {
      session.resumeFromHandle(resume);
    }
  };

  if (pendingStoredState) {
    pendingStoredState
      .then((state) => {
        if (!state) return;
        if (state.metadata) {
          persistentMetadata = ensureRecord(state.metadata);
          actionManager.syncFromMetadata();
        }
        if (state.messages?.length) {
          // Restored history must not read as a fresh send (scroll-on-send /
          // anchor-top would fire for the last restored user message).
          suppressScrollSend = true;
          try {
            session.hydrateMessages(state.messages);
          } finally {
            suppressScrollSend = false;
          }
          // Restored markdown exists before the first open: warm the parsers
          // now so opening paints markdown instead of flashing escaped text.
          warmClientStream();
        }
        if (state.artifacts?.length) {
          session.hydrateArtifacts(
            state.artifacts,
            state.selectedArtifactId ?? null
          );
        }
        restoreComposerDraft(state.draft);
      })
      .catch((error) => {
        if (typeof console !== "undefined") {
          // eslint-disable-next-line no-console
          console.error("[AgentWidget] Failed to hydrate stored state:", error);
        }
      })
      .finally(() => {
        welcomeHydrated = true;
        resolveHistoryBootstrap();
        maybeBootResume();
      });
  } else {
    restoreComposerDraft(loadedStoredDraft);
    maybeBootResume();
  }

  // Centralized so both the default composer (`handleSubmit`) and the plugin
  // composer (`renderComposer.onSubmit`) auto-expand the composer-bar wrapper
  // when a message is sent while the panel is collapsed. Without a single
  // helper the two submit paths drift over time.
  const maybeExpandComposerBar = () => {
    if (!isComposerBar()) return;
    if (open) return;
    const expandOnSubmit = config.launcher?.composerBar?.expandOnSubmit ?? true;
    if (!expandOnSubmit) return;
    setOpenState(true, "auto");
  };

  // Combine `@`-chip mentions with an inline server-command's context bundle so
  // both reach the message in one `mentions` payload. Either side may be null.
  type SubmitMentions = NonNullable<
    ReturnType<ContextMentionOrchestrator["collectForSubmit"]>
  >;
  const mergeSubmitMentions = (
    a: SubmitMentions | null,
    b: SubmitMentions | null
  ): SubmitMentions | null => {
    if (!a) return b;
    if (!b) return a;
    return {
      refs: [...a.refs, ...b.refs],
      // allSettled, not all: one side rejecting must not discard the other
      // side's already-resolved context (the bubble already echoed its chips).
      finalize: async () => {
        const [ra, rb] = await Promise.allSettled([a.finalize(), b.finalize()]);
        const fulfilled: MentionSubmitBundle[] = [];
        if (ra.status === "fulfilled") fulfilled.push(ra.value);
        else console.warn("[Persona] a mention bundle failed to finalize; sending without it", ra.reason);
        if (rb.status === "fulfilled") fulfilled.push(rb.value);
        else console.warn("[Persona] a mention bundle failed to finalize; sending without it", rb.reason);
        return mergeFinalizedMentions(fulfilled);
      },
    };
  };

  // Inline-mode composer element: after the contenteditable swap, `textarea`
  // additionally exposes `getInlineMessageFields()` (built in the inline chunk,
  // which owns the document model — the core never imports it). Structural shape
  // only, so no runtime import crosses the bundle boundary.
  type InlineComposerFieldsHost = {
    getInlineMessageFields?: () => {
      content: string;
      contextMentions: AgentWidgetContextMentionRef[];
      contentSegments: AgentWidgetContentSegment[];
    };
  };
  // Read the ordered display segments from the live inline composer, but only
  // when at least one mention is present: a plain-text inline message must keep
  // the normal markdown-rendered bubble (segments bypass that path), so we return
  // undefined for pure prose and let the bubble render `content` as usual.
  const readInlineContentSegments = (): AgentWidgetContentSegment[] | undefined => {
    const host = textarea as unknown as InlineComposerFieldsHost;
    const fields = host.getInlineMessageFields?.();
    if (!fields) return undefined;
    const hasMention = fields.contentSegments.some((s) => s.kind === "mention");
    return hasMention ? fields.contentSegments : undefined;
  };

  // Re-entrancy guard for the async submit path: `takeInlineCommand` awaits (a
  // lazy chunk load + host resolve) before the composer clears, and
  // `isStreaming()` is still false in that window, so a second Enter would
  // otherwise dispatch the same text/command twice. It also covers an async
  // `onBeforeSend` (the `preparing` phase).
  let submitInFlight = false;
  let submitEpoch = 0;
  /** Aborts the in-flight preparation on destroy or a superseding stop. */
  let submissionAbort: AbortController | null = null;

  /** One send. `overrideText` sends something other than the live draft. */
  type SubmissionIntent = {
    overrideText?: string;
    /** Clear the draft once the message is accepted locally. Default true. */
    consumeDraft?: boolean;
    /** Resolve a leading inline slash command first (draft sends only). */
    resolveInlineCommand?: boolean;
    viaVoice?: boolean;
    /** Replays a captured `defer-one` snapshot; never re-captures. */
    pending?: Readonly<ComposerSubmissionSnapshot>;
  };

  const clearComposerDraft = () => {
    textarea.value = "";
    textarea.style.height = "auto"; // Reset height after clearing
    syncComposerText();
    resetHistoryNavigation();
  };

  let composerNoticeTimer: ReturnType<typeof setTimeout> | null = null;

  /** Transient message in the status region; a lock reason always outranks it. */
  function showComposerNotice(text: string, ms = 4000): void {
    announce(text);
    if (isComposerBar() || !statusText) return;
    if (statusText.hasAttribute(COMPOSER_REASON_ATTR)) return;
    statusText.setAttribute(COMPOSER_NOTICE_ATTR, "");
    statusText.setAttribute("role", "status");
    statusText.setAttribute("aria-live", "polite");
    statusText.textContent = text;
    applyStatusIndicatorState(statusText, config.statusIndicator ?? {}, "idle");
    if (composerNoticeTimer) clearTimeout(composerNoticeTimer);
    composerNoticeTimer = setTimeout(() => {
      composerNoticeTimer = null;
      if (!statusText?.hasAttribute(COMPOSER_NOTICE_ATTR)) return;
      statusText.removeAttribute(COMPOSER_NOTICE_ATTR);
      statusText.removeAttribute("role");
      statusText.removeAttribute("aria-live");
      if (sessionInitialized) {
        const status = session.getStatus();
        applyStatusToElement(
          statusText,
          _getStatusText(status),
          config.statusIndicator ?? {},
          status
        );
      }
    }, ms);
  }

  /** Put the deferred submission back in the editable draft and drop the card. */
  function restorePendingSubmissionToDraft(): void {
    const pending = composerStore.getState().pendingSubmission;
    if (!pending) return;
    setPendingSubmission(undefined);
    if (textarea) {
      textarea.value = pending.text;
      textarea.dispatchEvent(new Event("input", { bubbles: true }));
      syncComposerText();
    }
    if (pending.options.selectedModelId) {
      composerStore.setSelectedModelId(pending.options.selectedModelId);
      composerCoreControls?.repaint();
    }
    if (pending.options.activeModeIds?.length) {
      setActiveComposerModes(pending.options.activeModeIds);
    }
    if (pending.options.quote) setComposerQuote(pending.options.quote);
    textarea?.focus();
  }

  const reportSubmissionError = (error: unknown) => {
    if (typeof console !== "undefined") {
      // eslint-disable-next-line no-console
      console.error(
        "[AgentWidget] composer.onBeforeSend failed; the draft was preserved:",
        error
      );
    }
  };

  /**
   * The one submission path. Every
   * send — form submit, Enter, suggestion, dictation, plugin composer,
   * controller — runs these steps against a single immutable snapshot:
   *
   *   1 read the draft without mutating it
   *   2 resolve an inline slash command
   *   3 verify attachment readiness + send eligibility
   *   4 run `onBeforeSend` on the frozen public snapshot
   *   5 cancel / abort / throw leaves the draft, attachments, and chips intact
   *   6 resolve mention bodies and async prep while `phase === "preparing"`
   *   7 append the user message, consume the draft, dispatch
   *   8 clear one-shot state only after local acceptance
   *
   * Returns synchronously unless step 2 or step 4 actually needs to await, so
   * send paths that never awaited before still don't.
   */
  const performSubmit = (intent: SubmissionIntent = {}): void | Promise<void> => {
    if (dictationCapturing() || conversationOpenPendingEl) return;
    const generation = composerConversationGeneration;
    const draft = (intent.overrideText ?? textarea?.value ?? "").trim();
    if (intent.resolveInlineCommand && draft) {
      return (
        mentionOrchestrator?.takeInlineCommand(draft) ?? Promise.resolve(null)
      ).then((inline) => { if (generation === composerConversationGeneration) return continueSubmit(intent, draft, inline); });
    }
    return continueSubmit(intent, draft, null);
  };

  const continueSubmit = (
    intent: SubmissionIntent,
    draft: string,
    inline: Awaited<ReturnType<ContextMentionOrchestrator["takeInlineCommand"]>>
  ): void | Promise<void> => {
    if (inline?.kind === "action") {
      // Ran in the browser; nothing to send. Clear the composer + any chips.
      clearComposerDraft();
      mentionOrchestrator?.clear();
      syncComposerMentions();
      return;
    }

    const serverMentions = inline?.kind === "server" ? inline.mentions : null;
    // A prompt command replaces the outgoing text with its resolved macro.
    const sendText = inline?.kind === "prompt" ? inline.sendText : draft;
    // A replayed pending item carries its own already-resolved parts: the live
    // attachment manager was cleared at capture time.
    const pendingParts = intent.pending?.contentParts;
    const hasAttachments =
      (attachmentManager?.hasAttachments() ?? false) || (pendingParts?.length ?? 0) > 0;

    if (
      !canSubmitComposer({
        text: sendText,
        hasAttachments,
        attachmentsReady: attachmentManager?.isReady() ?? true,
        hasMentions: mentionOrchestrator?.hasMentions() ?? false,
        hasServerMentions: !!serverMentions,
        inputDisabled: isComposerInputDisabled(),
        sendDisabled: isComposerSendBlocked(),
      })
    ) {
      return;
    }

    // Submit while a response streams (roadmap section 11). `block` never gets
    // here: the Enter path stays inert and the button is Stop. A replay of an
    // already-captured pending item is not a new capture.
    const streamingBehavior = session.isStreaming()
      ? resolveStreamingSubmitBehavior()
      : "block";
    if (streamingBehavior === "defer-one" && !intent.pending) {
      if (composerStore.getState().pendingSubmission) {
        // Never a silent replace: the queued item is edited or removed first.
        showComposerNotice("One message is already queued. Edit or remove it first.");
        return;
      }
      capturePendingSubmission({ intent, sendText, serverMentions });
      return;
    }

    // The snapshot: built once, never re-read from the DOM below. Chips are
    // still attached here (they are only detached after acceptance), so their
    // refs are read non-destructively.
    const snapshot: InternalSubmissionSnapshot = {
      text: sendText,
      mentionRefs: [
        ...(mentionOrchestrator?.getMentionRefs() ?? []),
        ...(serverMentions?.refs ?? []),
      ],
      // Inline display segments must be captured before the composer clears
      // (clearing rebuilds the document as empty text). A `prompt` macro
      // replaced the text, so its segments no longer match.
      contentSegments:
        inline?.kind === "prompt" ? undefined : readInlineContentSegments(),
      // Step 2 of section 9: each send snapshots the current model/mode
      // selection. `onBeforeSend` may patch it; the live selection is unchanged.
      options: {
        ...(composerStore.getState().selectedModelId && {
          selectedModelId: composerStore.getState().selectedModelId,
        }),
        ...(composerStore.getState().activeModeIds.length > 0 && {
          activeModeIds: [...composerStore.getState().activeModeIds],
        }),
        ...(composerStore.getState().quote && {
          quote: { ...composerStore.getState().quote! },
        }),
      },
      viaVoice: intent.viaVoice,
    };
    // A replayed pending item sends exactly what was captured, including the
    // model/mode/quote selection at capture time.
    if (intent.pending) {
      snapshot.options = { ...intent.pending.options };
      snapshot.contentSegments = intent.pending.contentSegments;
      snapshot.mentionRefs = [...intent.pending.mentionRefs];
      snapshot.viaVoice = intent.pending.viaVoice;
    }
    // Rebuilt after a patch: the trailing text part must match the sent text.
    const buildContentParts = (): ContentPart[] | undefined => {
      if (pendingParts?.length) return [...pendingParts];
      if (!attachmentManager?.hasAttachments()) return undefined;
      const parts: ContentPart[] = [...attachmentManager.getContentParts()];
      if (snapshot.text) parts.push(createTextPart(snapshot.text));
      return parts;
    };
    snapshot.contentParts = buildContentParts();

    const dispatchSubmission = () => {
      // Editing is independent of the composer, but a send supersedes it.
      cancelMessageEdit();
      maybeExpandComposerBar();
      // Detaches the chips and captures the composer text; `finalize()` resolves
      // the mention bodies inside `sendMessage`.
      const chipMentions = mentionOrchestrator?.collectForSubmit() ?? null;
      const mentions = mergeSubmitMentions(chipMentions, serverMentions);
      const contentParts = buildContentParts();
      if (intent.consumeDraft !== false) clearComposerDraft();
      const composerOptions: ComposerOptionsPayload = {
        selectedModelId: snapshot.options.selectedModelId,
        activeModeIds: snapshot.options.activeModeIds,
      };
      // Interrupt supersedes the in-flight run instead of queueing behind it.
      const interrupt = streamingBehavior === "interrupt";
      if (interrupt) session.cancel();
      session.sendMessage(snapshot.text, {
        contentParts,
        mentions: mentions ?? undefined,
        contentSegments: snapshot.contentSegments,
        viaVoice: snapshot.viaVoice,
        composerOptions,
        ...(snapshot.options.quote && { quote: snapshot.options.quote }),
        ...(interrupt && { interrupt: true }),
      });
      if (attachmentManager?.hasAttachments()) attachmentManager.clearAttachments();
      if (chipMentions) mentionOrchestrator?.clear();
      syncComposerMentions();
      // Step 8: one-shot modes and the quote clear only after local acceptance.
      const activeModes = composerStore.getState().activeModeIds;
      const remaining = clearOnceComposerModes(activeModes, config.composer?.modes);
      if (remaining.length !== activeModes.length) setActiveComposerModes(remaining);
      if (composerStore.getState().quote) setComposerQuote(undefined);
      // The accepted draft is no longer worth restoring on the next mount.
      clearPersistedDraft();
      // A sent draft releases the wrap latch: the next single line is compact.
      composerCompactLatch.release();
      syncComposerCompact();
    };

    const hook = config.composer?.onBeforeSend;
    if (!hook) {
      dispatchSubmission();
      return;
    }

    const abort = new AbortController();
    submissionAbort = abort;
    const generation = composerConversationGeneration;
    const settle = (outcome: BeforeSendOutcome): void => {
      if (abort.signal.aborted || generation !== composerConversationGeneration) return;
      if (submissionAbort === abort) submissionAbort = null;
      if (outcome.status === "error") {
        reportSubmissionError(outcome.error);
        return;
      }
      if (outcome.status !== "proceed") return;
      dispatchSubmission();
    };

    const outcome = runBeforeSend(hook, snapshot, abort.signal);
    if (!(outcome instanceof Promise)) {
      settle(outcome);
      return;
    }
    composerStore.setPreparing(true);
    return outcome
      .then(settle)
      .finally(() => { if (generation === composerConversationGeneration) composerStore.setPreparing(false); });
  };

  /**
   * `defer-one` capture: one full immutable snapshot (text, attachment parts,
   * mentions, inline segments, model, modes, quote), then the editable draft is
   * cleared. Nothing typed afterwards can reach the captured item, so what
   * eventually sends is exactly what the user submitted.
   */
  function capturePendingSubmission(input: {
    intent: SubmissionIntent;
    sendText: string;
    serverMentions: { refs: AgentWidgetContextMentionRef[] } | null;
  }): void {
    const state = composerStore.getState();
    const hasAttachments = attachmentManager?.hasAttachments() ?? false;
    const parts: ContentPart[] = hasAttachments
      ? [...attachmentManager!.getContentParts()]
      : [];
    if (hasAttachments && input.sendText) parts.push(createTextPart(input.sendText));
    const captured = toPublicSubmissionSnapshot({
      text: input.sendText,
      contentParts: parts.length ? parts : undefined,
      mentionRefs: [
        ...(mentionOrchestrator?.getMentionRefs() ?? []),
        ...(input.serverMentions?.refs ?? []),
      ],
      contentSegments: readInlineContentSegments(),
      options: {
        ...(state.selectedModelId && { selectedModelId: state.selectedModelId }),
        ...(state.activeModeIds.length > 0 && {
          activeModeIds: [...state.activeModeIds],
        }),
        ...(state.quote && { quote: { ...state.quote } }),
      },
      viaVoice: input.intent.viaVoice,
    });
    setPendingSubmission(captured);
    if (input.intent.consumeDraft !== false) clearComposerDraft();
    if (hasAttachments) attachmentManager!.clearAttachments();
    mentionOrchestrator?.clear();
    syncComposerMentions();
    if (state.quote) setComposerQuote(undefined);
    clearPersistedDraft();
    composerCompactLatch.release();
    syncComposerCompact();
    announce("Message queued. It sends when the response finishes.");
  }

  /** Send the captured item through the normal pipeline (hooks included). */
  function sendPendingSubmission(): void {
    const pending = composerStore.getState().pendingSubmission;
    if (!pending) return;
    setPendingSubmission(undefined);
    submitText(pending.text, { consumeDraft: false, pending });
  }

  // --- Retry / edit / quote (roadmap section 10 + 13) ------------------------

  /** Open the mini editor on a text-only user message. */
  function beginMessageEdit(messageId: string): void {
    if (config.messageActions?.showEdit !== true) return;
    const message = session.getMessages().find((m) => m.id === messageId);
    if (!message || !isEditableUserMessage(message) ||
      !canResubmitUserMessage(session.getMessages(), messageId)) return;
    if (editingMessageId === messageId) return;
    cancelMessageEdit();
    editingMessageId = messageId;
    messageEditEditor = createMessageEditEditor({
      messageId,
      initialText: message.content,
      onSave: (text) => saveMessageEdit(messageId, text),
      onCancel: () => cancelMessageEdit(),
    });
    messageCache.delete(messageId);
    renderMessagesWithPlugins(messagesWrapper, session.getMessages(), postprocess);
    messageEditEditor.focus();
  }

  /** Commit the edit: resubmit from that turn with the edited text. */
  function saveMessageEdit(messageId: string, text: string): void {
    const editor = messageEditEditor;
    editingMessageId = null;
    messageEditEditor = null;
    editor?.destroy();
    messageCache.delete(messageId);
    const accepted = session.resubmitFrom(messageId, {
      reason: "edit",
      replacement: {
        text,
        mentionRefs: [],
        options: {},
      },
    });
    if (!accepted) {
      renderMessagesWithPlugins(messagesWrapper, session.getMessages(), postprocess);
      return;
    }
    // The tail collapsed above the anchor; re-pin against the new layout.
    repinAnchoredMessage();
  }

  function regenerateFromAssistantMessage(messageId: string): void {
    if (session.isStreaming()) return;
    const userMessageId = findPrecedingUserMessageId(session.getMessages(), messageId);
    if (!userMessageId) return;
    cancelMessageEdit();
    if (!session.resubmitFrom(userMessageId, { reason: "retry" })) return;
    repinAnchoredMessage();
  }

  historyActions = {
    regenerate: regenerateFromAssistantMessage,
    edit: beginMessageEdit,
    quote: (messageId) => {
      const message = session.getMessages().find((m) => m.id === messageId);
      const text = message?.content?.trim();
      if (!text) return;
      setComposerQuote({ text, messageId });
      textarea?.focus();
    },
  };

  const doSubmit = async (submitOptions?: SubmissionIntent) => {
    if (dictationCapturing()) {
      if (config.voiceRecognition?.dictation?.showSend && !isComposerSendBlocked()) finishDictation(true);
      return;
    }
    if (submitInFlight) return;
    submitInFlight = true;
    const epoch = ++submitEpoch;
    try {
      await performSubmit({ resolveInlineCommand: true, ...submitOptions });
    } finally {
      if (epoch === submitEpoch) submitInFlight = false;
    }
  };

  /**
   * Suggestion "send" behavior: drop the current draft, then send the chip's
   * prompt through the shared pipeline. A function declaration so the
   * suggestion renderers (defined earlier in this closure) can reference it.
   */
  function submitSuggestionPrompt(prompt: string): void {
    // A blocked send must not destroy the draft on its way to being refused.
    if (isComposerSendBlocked()) return;
    clearComposerDraft();
    submitText(prompt, { consumeDraft: false });
  }

  /**
   * Sends that carry their own text (suggestion chips, plugin composers, the
   * controller) join the same pipeline; they skip inline-command resolution,
   * which belongs to what the user typed.
   */
  const submitText = (
    text: string,
    options?: Omit<SubmissionIntent, "overrideText" | "resolveInlineCommand">
  ): void => {
    if (submitInFlight) return;
    submitInFlight = true;
    const epoch = ++submitEpoch;
    const finish = () => {
      if (epoch === submitEpoch) submitInFlight = false;
    };
    try {
      const result = performSubmit({ ...options, overrideText: text });
      if (result instanceof Promise) void result.finally(finish);
      else finish();
    } catch (error) {
      finish();
      throw error;
    }
  };

  const handleSubmit = (event: Event) => {
    event.preventDefault();

    // While a response is streaming, the submit button acts as a stop button.
    // Abort the in-flight stream and leave textarea contents / attachments
    // intact so the user can edit and resend without retyping.
    if (session.isStreaming()) {
      session.cancel();
      // Cancelling emits no terminal/error SSE frame, so reset the throughput
      // tracker (as clear-chat does) to avoid a stale `running` row lingering.
      throughputTracker?.reset();
      eventStreamView?.update();
      return;
    }

    if (submitInFlight) return;
    void doSubmit();
  };

  // --- Composer message-history navigation (Up/Down arrows) ---
  // Lets users recall and edit previously sent messages, shell/Slack style.
  // The pure state machine lives in utils/composer-history.ts; here we feed it
  // caret info and apply the value it returns. Text-only recall: attachments
  // on past messages are not restored.
  const historyNavigationEnabled = () =>
    config.features?.composerHistory !== false;

  let composerHistoryState: ComposerHistoryState = { ...INITIAL_HISTORY_STATE };
  // Guards the reset-on-edit listener so our own programmatic value sets (which
  // dispatch an `input` event for auto-resize) don't exit navigation mode.
  let suppressHistoryReset = false;

  const resetHistoryNavigation = () => {
    composerHistoryState = { ...INITIAL_HISTORY_STATE };
  };

  const getUserMessageHistory = (): string[] =>
    session
      .getMessages()
      .filter((message) => message.role === "user")
      .map((message) => message.content ?? "")
      .filter((text) => text.length > 0);

  const applyHistoryValue = (value: string) => {
    if (!textarea) return;
    suppressHistoryReset = true;
    textarea.value = value;
    // Trigger the auto-resize handler (it listens on `input`).
    textarea.dispatchEvent(new Event("input", { bubbles: true }));
    suppressHistoryReset = false;
    // Caret to end for natural editing / appending.
    const end = textarea.value.length;
    textarea.setSelectionRange(end, end);
  };

  const handleComposerInput = (event: Event) => {
    // The synthetic input from history recall (guarded by `suppressHistoryReset`)
    // is not user typing: it must neither open the mention menu (an undefined
    // inputType reads as menu-opening) nor exit history-navigation mode.
    if (suppressHistoryReset) return;
    dismissFollowUps();
    // Drive the mention menu (open/update/close) + lazy-load on first trigger.
    // Skip while an IME composition is active: the intermediate value isn't the
    // user's committed text, so it must not open or filter the menu.
    if (!(event as InputEvent).isComposing) {
      mentionOrchestrator?.handleInput((event as InputEvent).inputType ?? undefined);
    }
    // A real edit leaves history-navigation mode.
    resetHistoryNavigation();
  };

  /** Current submit-key policy, re-read per event so `update()` takes effect. */
  const composerSubmitKeyOptions = () => ({
    submitKey: config.composer?.submitKey,
    insertNewlineOnTouchEnter: config.composer?.insertNewlineOnTouchEnter,
    coarsePointer: isCoarsePointer(textarea),
  });

  const handleComposerKeydown = (event: KeyboardEvent) => {
    if (!textarea) return;
    // A locked input takes no mention trigger, history recall, or submit. The
    // element itself is disabled / non-editable, so nothing is typed either.
    if (isComposerInputDisabled()) return;

    // Mention menu takes precedence when open (↑/↓ nav, Enter/Tab select, Esc
    // close) and handles Backspace-removes-last-chip on an empty composer. One
    // handler, no competing capture-phase listener. Skip during IME composition
    // so Enter-to-confirm-composition isn't swallowed as a menu selection.
    if (!event.isComposing && mentionOrchestrator?.handleKeydown(event)) return;

    // Up/Down: walk through previously sent user messages.
    if (
      historyNavigationEnabled() &&
      (event.key === "ArrowUp" || event.key === "ArrowDown") &&
      !event.shiftKey &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey &&
      !event.isComposing
    ) {
      const atStart =
        textarea.selectionStart === 0 && textarea.selectionEnd === 0;
      const result = navigateComposerHistory({
        direction: event.key === "ArrowUp" ? "up" : "down",
        history: getUserMessageHistory(),
        currentValue: textarea.value,
        atStart,
        state: composerHistoryState
      });
      composerHistoryState = result.state;
      if (result.handled) {
        event.preventDefault();
        if (result.value !== undefined) {
          applyHistoryValue(result.value);
        }
        return;
      }
      // Not handled: fall through to default cursor movement.
    }

    // The submit combination follows `composer.submitKey`. Anything that is not
    // the submit combination (Shift+Enter, plain Enter under "mod-enter" or
    // "none", Enter on a coarse pointer with insertNewlineOnTouchEnter) falls
    // through to native editing, which inserts a newline in both the textarea
    // and the inline contenteditable adapter.
    if (!isSubmitKeydown(event, composerSubmitKeyOptions())) return;

    // While streaming, the submit key is inert under the default `block`
    // policy (never a stop trigger): the visible Stop button / Esc stop it.
    // `defer-one` and `interrupt` route it into the submission pipeline instead;
    // the button stays Stop either way.
    if (session.isStreaming()) {
      event.preventDefault();
      if (resolveStreamingSubmitBehavior() === "block") return;
      if (submitInFlight || isComposerSendBlocked()) return;
      resetHistoryNavigation();
      void doSubmit();
      return;
    }
    // A submit is already awaiting its async pre-send work; a second Enter in
    // that window would dispatch the same text twice.
    if (submitInFlight) {
      event.preventDefault();
      return;
    }
    // Send is blocked but composition is not: swallow the key rather than
    // inserting a stray newline the user did not ask for.
    if (isComposerSendBlocked()) {
      event.preventDefault();
      return;
    }
    resetHistoryNavigation();
    event.preventDefault();
    sendButton.click();
  };

  // Esc-to-stop: while a response streams, Escape within this widget aborts it.
  // Capture phase + registered at init so it runs before the composer-bar Esc
  // collapse listener (attached later on open); stopImmediatePropagation keeps
  // a stream-stop from also collapsing the panel. Scoped via composedPath so a
  // page-wide Escape elsewhere doesn't hijack.
  const handleEscStop = (event: KeyboardEvent) => {
    if (event.key !== "Escape" || event.isComposing) return;
    if (!event.composedPath().includes(container)) return;
    // Escape while a send is still preparing supersedes it: the draft survives.
    if (submissionAbort) {
      submissionAbort.abort();
      submissionAbort = null;
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    if (!session.isStreaming()) return;
    session.cancel();
    // Cancelling emits no terminal/error SSE frame: reset throughput so the
    // Events row doesn't keep showing a live rate from the stopped stream.
    throughputTracker?.reset();
    eventStreamView?.update();
    resetHistoryNavigation();
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  const handleInputPaste = async (event: ClipboardEvent) => {
    if (config.attachments?.enabled !== true || !attachmentManager) return;
    if (isComposerInputDisabled()) return;

    const clipboardImageFiles = getClipboardImageFiles(event.clipboardData);
    if (clipboardImageFiles.length === 0) return;

    // Prevent browser text/html paste when handling clipboard images as attachments.
    event.preventDefault();
    await attachmentManager.handleFiles(clipboardImageFiles);
  };

  // Independent opt-in STT capture. The implementation and renderer share the lazy voice chunk.
  let dictation: DictationSession | null = null;
  let dictationRender: DictationRenderHandle | null = null;
  let dictationLoading = false;
  let dictationLoadFailure: DictationState | null = null;
  let dictationQueuedFinish: boolean | null = null;
  let dictationRequest = 0;
  const idleDictation: DictationState = Object.freeze({ phase: "idle", recordingId: 0, transcript: "", interimTranscript: "", error: null, audioLevel: null });
  const usesDedicatedDictation = () => !!config.voiceRecognition?.dictation ||
    (config.voiceRecognition?.provider?.type === "custom" && dictationReviewMode());
  const dictationCapturing = () => dictationLoading || ["recording", "processing"].includes(dictation?.getState().phase ?? "idle");
  const dismissDictation = () => {
    ++dictationRequest;
    dictationLoading = false;
    dictationQueuedFinish = null;
    if (dictationLoadFailure) {
      dictationLoadFailure = null;
      dictationRender?.element.remove(); dictationRender = null;
      const { audioLevel: _level, ...state } = idleDictation;
      eventBus.emit("dictation:state", { ...state, instanceId, timestamp: Date.now() });
      return true;
    }
    return dictation?.cancel() ?? false;
  };
  const finishDictation = (send = false): boolean => {
    if (dictationLoading) { dictationQueuedFinish = send || dictationQueuedFinish === true; return true; }
    return dictation?.finish(send) || (send && dictation?.send()) || false;
  };
  const startDictation = (): boolean => {
    if (isComposerInputDisabled() || session.isStreaming() || dictationCapturing() || isActiveSessionVoice()) return false;
    const voice = config.voiceRecognition;
    if (voice?.enabled === false) return false;
    const provider: DictationProviderConfig = voice?.dictation?.provider ??
      (voice?.provider?.type === "custom" ? { type: "custom", custom: voice.provider.custom } : { type: "browser", browser: voice?.provider?.browser });
    if (provider.type === "browser" && !getSpeechRecognitionClass()) return false;
    if (provider.type === "custom" && !provider.custom) return false;
    if (!open && isPanelToggleable()) setOpenState(true, "system");
    if (dictationLoadFailure) dismissDictation();
    const draft = { text: textarea.value,
      logicalEnd: mentionOrchestrator?.getInlineInput()?.getLogicalText().length ?? null,
      readOnly: textarea.readOnly, contentEditable: textarea.getAttribute("contenteditable") };
    dictationLoading = true;
    dictationQueuedFinish = null;
    const request = ++dictationRequest;
    void loadDictationRuntime().then((runtime) => {
      if (request !== dictationRequest) return;
      dictationLoading = false;
      dictation ??= runtime.createDictationSession({
        instanceId, getTextarea: () => textarea, getFooter: () => footer, getConfig: () => config, plugins,
        getInlineInput: () => mentionOrchestrator?.getInlineInput(),
        isInputDisabled: isComposerInputDisabled, isSendBlocked: isComposerSendBlocked,
        syncText: syncComposerText, onSend: () => { void doSubmit({ viaVoice: true }); },
        rendererDeps: { renderLucideIcon, attachTooltip },
        onState: (state, capturing) => {
          const { audioLevel: _level, ...eventState } = state;
          eventBus.emit("dictation:state", { ...eventState, instanceId, timestamp: Date.now() });
          voiceState.active = state.phase === "recording";
          setMicState(capturing ? (state.phase === "recording" ? "recording" : "processing") : "idle");
          emitVoiceState("system");
        },
      });
      session.stopSpeaking();
      dictation.start(provider, { autoSend: !dictationReviewMode(), pauseDuration: voice?.pauseDuration }, draft);
      if (dictationQueuedFinish !== null) { dictation.finish(dictationQueuedFinish); dictationQueuedFinish = null; }
    }).catch((error: unknown) => {
      if (request !== dictationRequest) return;
      dictationLoading = false;
      const message = error instanceof Error ? error.message : "Unable to load dictation.";
      dictationLoadFailure = Object.freeze({ ...idleDictation, recordingId: request, phase: "error", error: message });
      const element = createElement("div", "persona-dictation");
      element.setAttribute("role", "alert"); element.textContent = message;
      const close = document.createElement("button"); close.type = "button"; close.textContent = "Dismiss dictation";
      close.addEventListener("click", () => { dismissDictation(); }); element.append(close);
      dictationRender = { element }; footer.append(element);
      const { audioLevel: _level, ...state } = dictationLoadFailure;
      eventBus.emit("dictation:state", { ...state, instanceId, timestamp: Date.now() });
    });
    return true;
  };
  destroyCallbacks.push(() => {
    ++dictationRequest;
    dictation?.destroy();
    dictationRender?.destroy?.();
    dictationRender?.element.remove();
  });

  if (config.voiceRecognition?.dictation) void loadDictationRuntime().catch(() => {});

  // Voice recognition state and logic
  let speechRecognition: any = null;
  let isRecording = false;
  let disposeRecognitionHandlers: (() => void) | undefined;

  /**
   * `voiceRecognition.completionBehavior` (roadmap section 14). Default stays
   * `"send"`; `"review"` leaves the transcript in the composer. Read per call
   * so `controller.update()` takes effect mid-session. Browser (Web Speech)
   * path only: the realtime `runtype` provider owns its own turn taking.
   */
  const dictationReviewMode = (): boolean =>
    config.voiceRecognition?.completionBehavior === "review";

  /**
   * Publish the mic's animatable state (roadmap: every animatable state is a
   * data attribute, all motion is CSS keyed off it). Stamped at every site that
   * swaps the mic's icon or colors, on both the builder-created and the
   * runtime-created mic, so CSS never has to infer state from styling.
   */
  function setMicState(
    state: "idle" | "recording" | "processing" | "speaking"
  ): void {
    if (micButton) applyMicButtonStyle(micButton, config, state, session.getVoiceInterruptionMode());
    footer?.setAttribute("data-persona-voice-state", state);
    if (state === "recording") startVoiceLevelLoop();
    else stopVoiceLevelLoop();
  }

  let voiceLevelIndicator: ReturnType<DictationRuntimeModule["createVoiceLevelIndicator"]> | undefined;
  let voiceLevelActive = false;
  function startVoiceLevelLoop(): void {
    voiceLevelActive = true;
    const start = (runtime: DictationRuntimeModule) => {
      if (!voiceLevelActive) return;
      voiceLevelIndicator ??= runtime.createVoiceLevelIndicator({
        targets: () => [micButtonWrapper ?? micButton?.parentElement, footer].filter((node): node is HTMLElement => !!node),
        sample: () => {
          const state = dictation?.getState();
          return state?.phase === "recording"
            ? { level: state.audioLevel, fallback: false }
            : { level: sessionInitialized ? session.getVoiceLevel() : null, fallback: true };
        },
      });
      voiceLevelIndicator.start();
    };
    const ready = getDictationRuntimeSync();
    if (ready) start(ready);
    else void loadDictationRuntime().then(start, () => {});
  }
  function stopVoiceLevelLoop(): void {
    voiceLevelActive = false;
    voiceLevelIndicator?.stop();
  }

  const getSpeechRecognitionClass = (): any => {
    if (typeof window === 'undefined') return null;
    return (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition || null;
  };

  const startVoiceRecognition = (
    source: AgentWidgetVoiceStateEvent["source"] = "user"
  ) => {
    if (isRecording || session.isStreaming()) return;

    const SpeechRecognitionClass = getSpeechRecognitionClass();
    if (!SpeechRecognitionClass) return;

    speechRecognition = new SpeechRecognitionClass();
    const recognition = speechRecognition;
    const voiceConfig = config.voiceRecognition ?? {};
    const pauseDuration = voiceConfig.pauseDuration ?? 2000;

    // Both honor `provider.browser`; the fallbacks preserve the historical
    // hardcoded values.
    speechRecognition.continuous = voiceConfig.provider?.browser?.continuous ?? true;
    speechRecognition.interimResults = true;
    speechRecognition.lang = voiceConfig.provider?.browser?.language ?? 'en-US';

    // Store the initial text that was in the textarea
    const initialText = textarea.value;

    let handlers: ReturnType<DictationRuntimeModule["createBrowserRecognitionHandlers"]> | undefined;
    const route = (kind: "result" | "error" | "end", event: BrowserRecognitionEvent) => {
      const apply = (runtime: DictationRuntimeModule) => {
        if (speechRecognition !== recognition || !isRecording) return;
        handlers ??= runtime.createBrowserRecognitionHandlers({
          textarea, initialText, pauseDuration,
          isActive: () => speechRecognition === recognition && isRecording,
          syncText: syncComposerText, stop: stopVoiceRecognition, review: dictationReviewMode,
          submit: () => { void doSubmit({ viaVoice: true }); },
        });
        disposeRecognitionHandlers = () => handlers?.dispose();
        handlers[kind](event);
      };
      const ready = getDictationRuntimeSync();
      if (ready) apply(ready);
      else void loadDictationRuntime().then(apply, () => {
        if (speechRecognition === recognition) stopVoiceRecognition("system");
      });
    };
    recognition.onresult = (event: BrowserRecognitionEvent) => route("result", event);
    recognition.onerror = (event: BrowserRecognitionEvent) => route("error", event);
    recognition.onend = (event: BrowserRecognitionEvent) => route("end", event);

    try {
      speechRecognition.start();
      isRecording = true;
      voiceState.active = true;
      if (source !== "system") {
        voiceState.manuallyDeactivated = false;
      }
      emitVoiceState(source);
      persistVoiceMetadata();
      // Reconcile content geometry without treating recording as extra content.
      syncComposerCompact();
      if (micButton) {
        micButton.classList.add("persona-voice-recording");
        setMicState("recording");

        micButton.setAttribute("aria-label", "Stop voice recognition");
      }
    } catch (error) {
      stopVoiceRecognition("system");
    }
  };

  const stopVoiceRecognition = (
    source: AgentWidgetVoiceStateEvent["source"] = "user"
  ) => {
    if (!isRecording) return;

    isRecording = false;
    disposeRecognitionHandlers?.();
    disposeRecognitionHandlers = undefined;

    if (speechRecognition) {
      try {
        speechRecognition.stop();
      } catch (error) {
        // Ignore errors when stopping
      }
      speechRecognition = null;
    }

    voiceState.active = false;
    emitVoiceState(source);
    if (!pageHiding) persistVoiceMetadata();
    syncComposerCompact();

    if (micButton) {
      micButton.classList.remove("persona-voice-recording");
      setMicState("idle");

      micButton.setAttribute("aria-label", config.voiceRecognition?.tooltipText ?? "Start voice recognition");
    }
  };


  // --- Session-voice (Runtype / custom) mic state styling ---
  // The per-state styling ships in the lazy voice-runtime chunk
  // (`voice/mic-state-styles.ts`). These states only arise from a provider,
  // which setupVoice builds from that same chunk, so it is already loaded here;
  // the async fallback only covers a call that races the first load.
  let micStateStyles: MicStateStyles | null = null;
  const withMicStateStyles = (run: (styles: MicStateStyles) => void): void => {
    const apply = (mod: VoiceRuntimeModule) => {
      micStateStyles ??= mod.createMicStateStyles({
        mic: () => micButton,
        config: () => config,
        session: () => session,
        setMicState,
      });
      run(micStateStyles);
    };
    const mod = getVoiceRuntimeSync();
    if (mod) apply(mod);
    else loadVoiceRuntime().then(apply, () => {});
  };
  const applyRuntypeMicRecordingStyles = () => withMicStateStyles((s) => s.recording());
  const applyRuntypeMicProcessingStyles = () => withMicStateStyles((s) => s.processing());
  const applyRuntypeMicSpeakingStyles = () => withMicStateStyles((s) => s.speaking());
  /** Restore mic button to idle state (icon, colors, aria-label, cursor) */
  const removeRuntypeMicStateStyles = () => withMicStateStyles((s) => s.reset());

  // Wire up mic button click handler
  const handleMicButtonClick = () => {
    // Dictation is composition: a locked input takes none of it.
    if (isComposerInputDisabled()) return;
    if (usesDedicatedDictation()) {
      if (dictation?.getState().phase === "recording") dictation.finish();
      else if (!dictationCapturing()) startDictation();
      return;
    }
    // Runtype provider: use session.toggleVoice() (WebSocket-based STT)
    if (usesSessionVoice(config.voiceRecognition?.provider)) {
      const voiceStatus = session.getVoiceStatus();
      const interruptionMode = session.getVoiceInterruptionMode();

      // In "none" mode, ignore clicks while processing or speaking
      if (interruptionMode === "none" &&
          (voiceStatus === "processing" || voiceStatus === "speaking")) {
        return;
      }

      // In "cancel" mode during processing/speaking: stop playback only
      if (interruptionMode === "cancel" &&
          (voiceStatus === "processing" || voiceStatus === "speaking")) {
        session.stopVoicePlayback();
        return;
      }

      // In barge-in mode, clicking mic = "hang up" (any state: speaking, idle, etc.)
      // Stops playback if active, tears down the always-on mic.
      if (session.isBargeInActive()) {
        session.stopVoicePlayback();
        session.deactivateBargeIn().then(() => {
          voiceState.active = false;
          voiceState.manuallyDeactivated = true;
          persistVoiceMetadata();
          emitVoiceState("user");
          removeRuntypeMicStateStyles();
        });
        return;
      }

      session.toggleVoice().then(() => {
        voiceState.active = isSessionVoiceLive();
        voiceState.manuallyDeactivated = !voiceState.active;
        persistVoiceMetadata();
        emitVoiceState("user");
        if (voiceState.active) {
          applyRuntypeMicRecordingStyles();
        } else {
          removeRuntypeMicStateStyles();
        }
      });
      return;
    }

    // Browser provider: use SpeechRecognition
    if (isRecording) {
      // Stop recording and submit
      const finalValue = textarea.value.trim();
      voiceState.manuallyDeactivated = true;
      persistVoiceMetadata();
      stopVoiceRecognition("user");
      if (finalValue && !dictationReviewMode()) {
        // Same submission path as every other send; the transcript already
        // lives in the composer, so the draft is the snapshot source.
        void doSubmit();
      }
    } else {
      // Start recording
      voiceState.manuallyDeactivated = false;
      persistVoiceMetadata();
      startVoiceRecognition("user");
    }
  };

  composerVoiceBridge = handleMicButtonClick;

  // Voice prewarm on mic intent (`voiceRecognition.prewarm`). Delegated on the
  // mount so composer rebuilds stay covered, and `config` is read at event time
  // so `update()` applies without re-wiring. Default: 'hover' in client token
  // mode, off otherwise.
  const warmVoice = () => session.prewarmVoice();
  const voicePrewarmSetting = () => {
    const voice = config.voiceRecognition;
    if (voice?.enabled !== true) return false;
    return voice.prewarm ?? (config.clientToken ? "hover" : false);
  };
  // `pointerover` covers mouse hover and precedes a touch's pointer-down;
  // `focusin` covers keyboard focus.
  const handleVoicePrewarmIntent = (event: Event) => {
    if (voicePrewarmSetting() !== "hover") return;
    if (micButton?.contains(event.target as Node)) warmVoice();
  };
  const voicePrewarmEvents = ["pointerover", "pointerdown", "focusin"];
  voicePrewarmEvents.forEach((type) => mount.addEventListener(type, handleVoicePrewarmIntent));
  // A custom prewarm hook is bound to one mic button: it re-runs (after its
  // cleanup) when the button is rebuilt or `update()` swaps the function.
  let voicePrewarmHook: AgentWidgetVoicePrewarmHook | undefined;
  let voicePrewarmHookButton: HTMLButtonElement | null = null;
  let voicePrewarmHookCleanup: ReturnType<AgentWidgetVoicePrewarmHook>;
  // Host hook code may throw; neither setup nor cleanup may break the widget.
  const releaseVoicePrewarmHook = () => {
    const cleanup = voicePrewarmHookCleanup;
    voicePrewarmHookCleanup = undefined;
    try {
      if (typeof cleanup === "function") cleanup();
    } catch {
      // ignored, see above
    }
  };
  const syncVoicePrewarmHook = () => {
    const setting = voicePrewarmSetting();
    const next = typeof setting === "function" && micButton ? setting : undefined;
    const button = next ? micButton : null;
    if (next === voicePrewarmHook && button === voicePrewarmHookButton) return;
    releaseVoicePrewarmHook();
    voicePrewarmHook = next;
    voicePrewarmHookButton = button;
    if (!next || !button) return;
    try {
      voicePrewarmHookCleanup = next({ warm: warmVoice, micButton: button, mount });
    } catch {
      // ignored, see above
    }
  };
  syncVoicePrewarmHook();
  destroyCallbacks.push(() => {
    voicePrewarmEvents.forEach((type) =>
      mount.removeEventListener(type, handleVoicePrewarmIntent)
    );
    releaseVoicePrewarmHook();
  });

  if (micButton) {
    // The click listener itself is registered by `wireComposerSurface`, so a
    // composer rebuild unwires it with the rest of the surface.
    destroyCallbacks.push(() => {
      if (usesSessionVoice(config.voiceRecognition?.provider)) {
        if (session.isVoiceActive()) session.toggleVoice();
        removeRuntypeMicStateStyles();
      } else {
        stopVoiceRecognition("system");
      }
    });
  }

  // `defer-one` auto-send: a turn that COMPLETED releases the queued item. An
  // explicit Stop or a stream error never reaches this seam, so the item stays
  // pending for the user to send, edit, or remove.
  const pendingSubmissionUnsub = eventBus.on("assistant:complete", () => {
    const pending = composerStore.getState().pendingSubmission;
    const generation = composerConversationGeneration;
    const restart = restartGeneration;
    if (!pending) return;
    setTimeout(() => {
      if (!canRestart(restart) || session.isStreaming() || generation !== composerConversationGeneration || composerStore.getState().pendingSubmission !== pending) return;
      sendPendingSubmission();
    }, 0);
  });
  destroyCallbacks.push(pendingSubmissionUnsub);

  const autoResumeUnsub = eventBus.on("assistant:complete", () => {
    if (!voiceAutoResumeMode) return;
    if (voiceState.active || voiceState.manuallyDeactivated) return;
    if (voiceAutoResumeMode === "assistant" && !voiceState.lastUserMessageWasVoice) {
      return;
    }
    const generation = composerConversationGeneration;
    const restart = restartGeneration;
    setTimeout(() => {
      if (!canRestart(restart) || generation !== composerConversationGeneration) return;
      if (!voiceState.active && !voiceState.manuallyDeactivated) {
        if (usesSessionVoice(config.voiceRecognition?.provider)) {
          session.toggleVoice().then(() => {
            if (!canRestart(restart) || generation !== composerConversationGeneration) return;
            voiceState.active = session.isVoiceActive();
            emitVoiceState("auto");
            if (session.isVoiceActive()) applyRuntypeMicRecordingStyles();
          });
        } else {
          startVoiceRecognition("auto");
        }
      }
    }, 600);
  });
  destroyCallbacks.push(autoResumeUnsub);

  // A cached document retains its DOM and controller, but must release live
  // transports and capture before it freezes. A fresh navigation still uses
  // the existing teardown/voice-metadata path above.
  let cachedPage = false;
  const suspendCachedPage = (event: PageTransitionEvent) => {
    if (!event.persisted || cachedPage) return;
    cachedPage = true;
    pageHiding = true;
    invalidatePendingRestarts();
    composerConversationGeneration++;
    submitEpoch++;
    submitInFlight = false;
    submissionAbort?.abort();
    submissionAbort = null;
    composerStore.setPreparing(false);
    dismissDictation();
    stopVoiceRecognition("system");
    session.cancel();
    session.stopSpeaking();
    session.cleanupVoice();
    throughputTracker?.reset();
    eventStreamView?.update();
  };
  const restoreCachedPage = (event: PageTransitionEvent) => {
    if (!event.persisted || !cachedPage) return;
    cachedPage = false;
    pageHiding = false;
    // Rebuild the provider lazily so the same controls can start a new call.
    // Neither stopped replies nor calls restart automatically on Back.
    if (config.voiceRecognition?.enabled === true && usesSessionVoiceForConfig(config)) session.setupVoice();
  };
  window.addEventListener("pagehide", suspendCachedPage);
  window.addEventListener("pageshow", restoreCachedPage);
  destroyCallbacks.push(() => {
    window.removeEventListener("pagehide", suspendCachedPage);
    window.removeEventListener("pageshow", restoreCachedPage);
  });

  // Handle action:resubmit event - automatically trigger another model call
  // when an action handler needs the model to continue processing (e.g., analyzing search results)
  const resubmitUnsub = eventBus.on("action:resubmit", () => {
    // Short delay to allow UI to update with any injected messages
    // Handlers should call context.triggerResubmit() AFTER their async work completes
    scheduleResubmit();
  });
  destroyCallbacks.push(resubmitUnsub);

  const toggleOpen = () => {
    setOpenState(!open, "user");
  };

  // Plugin hook: renderLauncher - allow plugins to provide custom launcher
  let launcherSurfaceInstance: LauncherSurface | null = null;
  let customLauncherElement: HTMLElement | null = null;

  // Composer-bar mode is launcher-less by design: the persistent pill IS the
  // entry point, so skip creating any launcher button (default or plugin).
  if (launcherEnabled && !isComposerBar()) {
    const { instance, element } = resolveLauncher({ config, plugins, onToggle: toggleOpen });
    launcherSurfaceInstance = instance;
    // A plugin-provided launcher returns no controller instance; track its
    // element separately so the update path can manage it.
    if (!instance) customLauncherElement = element;
  }

  if (launcherSurfaceInstance) {
    mount.appendChild(launcherSurfaceInstance.element);
  } else if (customLauncherElement) {
    mount.appendChild(customLauncherElement);
  }
  updateOpenState();
  renderSuggestions();
  updateCopy();
  renderWelcomeSurface();
  setComposerDisabled(session.isStreaming());
  // Reopen-where-left-off takes precedence when opted in (Principle 11);
  // otherwise fall back to the historical per-mode positioning.
  if (!restoreScrollPosition()) {
    if (getScrollMode() === "follow") {
      scheduleAutoScroll(true);
    } else {
      jumpToBottomInstant();
    }
  }
  maybeRestoreVoiceFromMetadata();

  if (autoFocusInput) {
    // Composer-bar's pill exposes the textarea immediately, so focus it on
    // init like the inline embed does: even though the panel is collapsed.
    if (!launcherEnabled || isComposerBar()) {
      setTimeout(() => maybeFocusInput(), 0);
    } else if (open) {
      setTimeout(() => maybeFocusInput(), 200);
    }
  }

  // `footer` is reassigned on a renderComposer swap, so the observer has to be
  // re-pointed. `container` is observed too: its height feeds the lift.
  let footerResizeObserver: ResizeObserver | null = null;
  const observeComposerFooter = () => {
    if (!footerResizeObserver) return;
    footerResizeObserver.disconnect();
    footerResizeObserver.observe(footer);
    footerResizeObserver.observe(container);
    footerResizeObserver.observe(welcomeHost);
    footerResizeObserver.observe(panelSuggestions);
  };

  let lastWelcomeFullscreen = isWelcomeFullscreen();
  const recalcPanelHeight = () => {
    // Composer-bar mode lets CSS own all sizing: collapsed pill is auto-sized
    // by the footer; expanded fullscreen/modal are driven by CSS attribute
    // selectors plus inline maxWidth/maxHeight set in updateOpenState. JS
    // sizing here would fight the morph transitions.
    if (isComposerBar()) {
      updateScrollToBottomButtonOffset();
      updateOpenState();
      return;
    }

    const dockedMode = isDockedMountMode(config);
    const sidebarMode = config.launcher?.sidebarMode ?? false;

    // Mobile fullscreen: re-apply fullscreen styles on resize (handles orientation changes)
    const ownerWindow = mount.ownerDocument.defaultView ?? window;
    const mobileFullscreen = config.launcher?.mobileFullscreen ?? true;
    const mobileBreakpoint = config.launcher?.mobileBreakpoint ?? 640;
    const isMobileViewport = ownerWindow.innerWidth <= mobileBreakpoint;
    const shouldGoFullscreen = mobileFullscreen && isMobileViewport && launcherEnabled;

    try {
      if (shouldGoFullscreen) {
        // syncPanelChrome re-applies theme + artifact layout vars that the
        // cssText reset in applyFullHeightStyles wipes; bare re-style flickers.
        syncPanelChrome();
        appliedSplitMode = splitChromeMode();
        return;
      }

      // Exiting mobile fullscreen (e.g., orientation change to landscape): reset all styles
      let chromeResynced = false;
      if (wasMobileFullscreen) {
        wasMobileFullscreen = false;
        syncPanelChrome();
        chromeResynced = true;
      }

      // Width-only resize can cross the 640 split boundary without changing
      // panel geometry (pane ResizeObserver stays silent) or hitting the
      // fullscreen branches, so resync chrome when the split mode flips.
      const modeNow = splitChromeMode();
      if (!chromeResynced && modeNow !== appliedSplitMode) {
        syncPanelChrome();
        chromeResynced = true;
      }
      appliedSplitMode = modeNow;
      // syncPanelChrome does not touch the resize handle, so a width-only flip
      // between welded (seam overlay) and detached/flex handle would leave it in
      // the old mode. reconcileArtifactResize re-places it; it never re-enters
      // syncArtifactPane, so no recursion. No-op when artifacts are disabled.
      if (chromeResynced) reconcileArtifactResize();

      if (!launcherEnabled && !dockedMode) {
        panel.style.height = "";
        if (!sidebarMode) {
          wrapper.style.width = resolveInlinePanelWidth(config);
          panel.style.width = "100%";
          panel.style.maxWidth = "100%";
        }
        return;
      }

      // In sidebar/fullHeight mode, don't override the width - it's handled by applyFullHeightStyles
      if (!sidebarMode && !dockedMode) {
        const geometry = resolvePanelGeometry(config);
        panel.style.width = geometry.width;
        panel.style.maxWidth = geometry.maxWidth;
      }
      applyLauncherArtifactPanelWidth();

      // In fullHeight mode, don't set a fixed height
      applyFloatingPanelFixedHeight();
    } finally {
      // applyFullHeightStyles() assigns wrapper.style.cssText (e.g. display:flex !important), which
      // overwrites updateOpenState()'s display:none when docked+closed. Re-sync after every recalc.
      updateScrollToBottomButtonOffset();
      syncComposerOverlayMetrics();
      const welcomeFullscreen = isWelcomeFullscreen();
      if (welcomeFullscreen !== lastWelcomeFullscreen) {
        lastWelcomeFullscreen = welcomeFullscreen;
        updateWelcome();
        renderSuggestions();
      }
      updateOpenState();

      // Sync scroll lock and host stacking when viewport mode changes (e.g. orientation change)
      if (open && launcherEnabled) {
        const ow = mount.ownerDocument.defaultView ?? window;
        const isMobile = ow.innerWidth <= (config.launcher?.mobileBreakpoint ?? 640);
        const sm = config.launcher?.sidebarMode ?? false;
        const mf = config.launcher?.mobileFullscreen ?? true;
        const dockedMF = isDockedMountMode(config) && mf && isMobile;
        const isVC = sm || (mf && isMobile && launcherEnabled) || dockedMF;

        if (isVC && !releaseScrollLock) {
          const root = mount.getRootNode();
          const hostEl = root instanceof ShadowRoot
            ? (root.host as HTMLElement)
            : mount.closest<HTMLElement>(".persona-host");
          if (hostEl && !teardownHostStacking) {
            teardownHostStacking = syncOverlayHostStacking(
              hostEl,
              config.launcher?.zIndex ?? DEFAULT_OVERLAY_Z_INDEX
            );
          }
          releaseScrollLock = acquireScrollLock(mount.ownerDocument);
        } else if (!isVC) {
          teardownHostStacking?.();
          teardownHostStacking = null;
          releaseScrollLock?.();
          releaseScrollLock = null;
        }
      }
    }
  };

  recalcPanelHeight();
  const ownerWindow = mount.ownerDocument.defaultView ?? window;
  ownerWindow.addEventListener("resize", recalcPanelHeight);
  destroyCallbacks.push(() => ownerWindow.removeEventListener("resize", recalcPanelHeight));
  if (typeof ResizeObserver !== "undefined") {
    footerResizeObserver = new ResizeObserver(() => {
      syncComposerOverlayMetrics();
      syncScrollToBottomButton();
    });
    observeComposerFooter();
    destroyCallbacks.push(() => {
      footerResizeObserver?.disconnect();
      footerResizeObserver = null;
    });
    // Rail/panel resolves against the host container, not the viewport.
    const historyHostObserver = new ResizeObserver(() => {
      syncHistoryPresentation();
    });
    historyHostObserver.observe(container);
    destroyCallbacks.push(() => historyHostObserver.disconnect());
  }
  ownerWindow.addEventListener("resize", syncHistoryPresentation);
  destroyCallbacks.push(() =>
    ownerWindow.removeEventListener("resize", syncHistoryPresentation)
  );

  lastScrollTop = body.scrollTop;
  let lastBottomOffset = getScrollBottomOffset(body);

  const handleScroll = () => {
    syncTranscriptEdgeFades();
    const scrollTop = body.scrollTop;
    // When content mutates (e.g. stream-animation plugins re-rendering text)
    // or the viewport grows (composer shrinking back), the maximum scroll
    // position can shrink and force the browser to clamp scrollTop downward.
    // That emits a scroll event with a negative delta that would otherwise be
    // misread as the user scrolling up, pausing auto-follow and flashing the
    // scroll-to-bottom button. Treat those as non-user events. Tracking the
    // bottom offset (scrollHeight - clientHeight) rather than scrollHeight
    // alone also covers clientHeight-driven clamps.
    const currentBottomOffset = getScrollBottomOffset(body);
    const bottomOffsetShrank = currentBottomOffset < lastBottomOffset;
    lastBottomOffset = currentBottomOffset;

    if (latestScroll.interrupted) {
      lastScrollTop = scrollTop;
      syncScrollToBottomButton();
      return;
    }

    if (!isFollowEffective()) {
      // No follow state to manage (anchored anchor-top / none): just keep the
      // scroll-to-bottom affordance in sync with the user's position.
      //
      // Reader intent is deliberately NOT inferred from scroll deltas here.
      // Growing the transcript under a pinned anchor makes the browser clamp
      // scrollTop and then restore it, which lands as a matched pair of real
      // scroll events (e.g. -12 then +12) with no user involved. The restore
      // arrives while the bottom offset is GROWING, so the shrink mask above
      // doesn't cover it, and it reads as a deliberate scroll. Engagement is
      // tracked from actual input instead (see `markReaderEngaged` callers).
      lastScrollTop = scrollTop;
      syncScrollToBottomButton();
      return;
    }

    const { action, nextLastScrollTop } = resolveFollowStateFromScroll({
      following: autoFollow.isFollowing(),
      currentScrollTop: scrollTop,
      lastScrollTop,
      nearBottom: isElementNearBottom(body, BOTTOM_THRESHOLD),
      userScrollThreshold: USER_SCROLL_THRESHOLD,
      isAutoScrolling: latestScroll.active || isAutoScrolling || hasPendingAutoScroll || bottomOffsetShrank,
      pauseOnUpwardScroll: true,
      pauseWhenAwayFromBottom: false,
      resumeRequiresDownwardScroll: true
    });
    lastScrollTop = nextLastScrollTop;

    // Drag-selecting near the bottom can look like a resume gesture. Keep
    // follow paused during selection, but still refresh content visibility.
    if (action === "resume" && !hasActiveTranscriptSelection()) {
      resumeAutoScroll();
    } else if (action === "pause") {
      pauseAutoScroll();
    } else {
      syncScrollToBottomButton();
    }
  };

  body.addEventListener("scroll", handleScroll, { passive: true });
  destroyCallbacks.push(() => body.removeEventListener("scroll", handleScroll));

  // Content-growth follow. Render events already schedule auto-scroll, but
  // content can also grow without one: images/embeds finishing loading
  // mid-stream, web fonts swapping, the panel or composer resizing. Observe
  // the messages wrapper (content growth) and the scroll container itself
  // (viewport resize) so the pin survives all of them.
  if (typeof ResizeObserver !== "undefined") {
    const contentResizeObserver = new ResizeObserver(() => {
      // Width changes rewrap text and late images grow attachments.
      syncUserMessageCollapse();
      handleContentResize();
      syncTranscriptEdgeFades();
      // A resize can start or stop a table overflowing without a render or a
      // scroll, which are the only other things that recompute the fades.
      refreshTableScrollFades(messagesWrapper);
    });
    contentResizeObserver.observe(messagesWrapper);
    contentResizeObserver.observe(body);
    destroyCallbacks.push(() => contentResizeObserver.disconnect());
  }

  // Pause auto-follow while the user selects transcript text so the
  // streaming scroll doesn't move content out from under the selection.
  // Driven purely by selectionchange (no pointer gating) so keyboard
  // selection (Shift+arrows, select-all) pauses too; a stale selection
  // left in the transcript fires no further events, so it can't re-pause
  // after the user resumes following.
  const handleSelectionChange = () => {
    if (!isFollowEffective()) return;
    if (!autoFollow.isFollowing()) return;
    if (hasActiveTranscriptSelection()) {
      pauseAutoScroll();
    }
  };
  const selectionDocument = body.ownerDocument;
  selectionDocument.addEventListener("selectionchange", handleSelectionChange);
  destroyCallbacks.push(() => {
    selectionDocument.removeEventListener("selectionchange", handleSelectionChange);
  });

  // Principle 3: every interaction is intent. Beyond wheel/scroll/selection,
  // opting into `pauseOnInteraction` also treats keyboard navigation within the
  // transcript and focusing an interactive element (a link, button, etc.) as
  // "the reader is doing something here" — pause auto-follow so the stream
  // doesn't move content out from under them. Opt-in; off by default.
  const NAV_KEYS = new Set([
    "PageUp",
    "PageDown",
    "Home",
    "End",
    "ArrowUp",
    "ArrowDown",
  ]);
  const handleTranscriptKeydown = (event: KeyboardEvent) => {
    if (!NAV_KEYS.has(event.key)) return;
    // Reader intent for anchor re-pins is tracked unconditionally;
    // only the follow-mode *pause* below stays behind the opt-in flag.
    markReaderEngaged();
    if (!isPauseOnInteractionEnabled()) return;
    if (!isFollowEffective()) return;
    if (!autoFollow.isFollowing()) return;
    pauseAutoScroll();
  };
  const handleTranscriptFocusIn = (event: FocusEvent) => {
    const target = event.target as Element | null;
    if (!target?.closest("a, button, [tabindex], input, textarea, select")) return;
    markReaderEngaged();
    if (!isPauseOnInteractionEnabled()) return;
    if (!isFollowEffective()) return;
    if (!autoFollow.isFollowing()) return;
    pauseAutoScroll();
  };
  body.addEventListener("keydown", handleTranscriptKeydown);
  body.addEventListener("focusin", handleTranscriptFocusIn);
  destroyCallbacks.push(() => {
    body.removeEventListener("keydown", handleTranscriptKeydown);
    body.removeEventListener("focusin", handleTranscriptFocusIn);
  });

  const handleWheel = (event: WheelEvent) => {
    if (latestScroll.interruptedBy(event)) { markReaderEngaged(); return; }
    // A wheel tick is unambiguous reader intent in every mode — including
    // anchored anchor-top, where there is no follow state to pause.
    if (event.deltaY !== 0) markReaderEngaged();
    if (!isFollowEffective()) return;
    const action = resolveFollowStateFromWheel({
      following: autoFollow.isFollowing(),
      deltaY: event.deltaY,
      nearBottom: isElementNearBottom(body, BOTTOM_THRESHOLD),
      resumeWhenNearBottom: true
    });

    if (action === "pause") {
      pauseAutoScroll();
    } else if (action === "resume" && !hasActiveTranscriptSelection()) {
      resumeAutoScroll();
    }
  };
  body.addEventListener("wheel", handleWheel, { passive: true });
  // Touch drag and pointer press (scrollbar drags, tapping a tool bubble open)
  // are the remaining ways a reader takes over without a wheel or key event.
  body.addEventListener("touchmove", markReaderEngaged, { passive: true });
  body.addEventListener("pointerdown", markReaderEngaged, { passive: true });
  destroyCallbacks.push(() => {
    body.removeEventListener("touchmove", markReaderEngaged);
    body.removeEventListener("pointerdown", markReaderEngaged);
  });
  destroyCallbacks.push(() => body.removeEventListener("wheel", handleWheel));
  scrollToBottomButton.addEventListener("click", () => {
    const targets = pendingApprovalBubbles().filter(isBelowFold)
      .map((bubble) => bubble.getAttribute("data-message-id")!);
    // Jumping to the latest abandons the current anchor: drop the spacer
    // first so "bottom" means the real end of content, not spacer padding
    // that would keep shrinking underneath the reader.
    resetAnchorState();
    approvalJumpTargets = targets;
    cancelAutoScroll();
    autoFollow.pause();
    latestScroll.start(config);
    syncScrollToBottomButton();
  });
  destroyCallbacks.push(() => latestScroll.destroy());
  destroyCallbacks.push(() => scrollToBottomControl.destroy());
  destroyCallbacks.push(() => {
    cancelAutoScroll();
    resetAnchorState();
  });

  const refreshCloseButton = () => {
    if (!closeButton) return;
    if (closeHandler) {
      closeButton.removeEventListener("click", closeHandler);
      closeHandler = null;
    }
    if (isPanelToggleable()) {
      closeButton.style.display = "";
      closeHandler = () => {
        setOpenState(false, "user");
      };
      closeButton.addEventListener("click", closeHandler);
    } else {
      closeButton.style.display = "none";
    }
  };

  refreshCloseButton();

  // Setup clear chat button click handler
  const setupClearChatButton = () => {
    const { clearChatButton } = panelElements;
    if (!clearChatButton) return;

    clearChatButton.addEventListener("click", () => {
      // With history available this affordance is "New conversation": a local
      // view clear would leave the server record open and unreachable.
      const shell = historyShell;
      if (shell?.historyAvailable()) {
        void shell.startNewConversation({ focusComposer: true }).catch((error) => {
          if (config.debug) {
            // eslint-disable-next-line no-console
            console.warn("[AgentWidget] New conversation failed:", error);
          }
        });
        return;
      }
      // Clear messages in session (this will trigger onMessagesChanged which re-renders)
      session.clearMessages();
      if (config.layout?.topFade) body.scrollTop = 0;
      body.removeAttribute("data-persona-top-fade");
      messageCache.clear();
      resumeAutoScroll();

      // Drop any open ask_user_question sheets: their source messages are gone.
      removeAskUserQuestionSheet(panelElements.composerOverlay);

      // Always clear the default localStorage key
      try {
        localStorage.removeItem(DEFAULT_CHAT_HISTORY_STORAGE_KEY);
        if (config.debug) {
          console.log(`[AgentWidget] Cleared default localStorage key: ${DEFAULT_CHAT_HISTORY_STORAGE_KEY}`);
        }
      } catch (error) {
        console.error("[AgentWidget] Failed to clear default localStorage:", error);
      }

      // Also clear custom localStorage key if configured
      if (config.clearChatHistoryStorageKey && config.clearChatHistoryStorageKey !== DEFAULT_CHAT_HISTORY_STORAGE_KEY) {
        try {
          localStorage.removeItem(config.clearChatHistoryStorageKey);
          if (config.debug) {
            console.log(`[AgentWidget] Cleared custom localStorage key: ${config.clearChatHistoryStorageKey}`);
          }
        } catch (error) {
          console.error("[AgentWidget] Failed to clear custom localStorage:", error);
        }
      }

      // Dispatch custom event for external handlers (e.g., localStorage clearing in examples)
      const clearEvent = new CustomEvent("persona:clear-chat", {
        detail: { instanceId, timestamp: new Date().toISOString() }
      });
      window.dispatchEvent(clearEvent);

      // Remove any stored draft before the terminal clear mutation is queued.
      clearPersistedDraft();
      if (storageAdapter?.clear) {
        runStorageMutation(
          () => storageAdapter.clear!(),
          "[AgentWidget] Failed to clear storage adapter:"
        );
      }
      persistentMetadata = {};
      actionManager.syncFromMetadata();
      // Pending attachments belong to the cleared conversation: drop the tiles
      // and abort anything still uploading.
      discardConversationContinuity(session.getActiveConversationId(), true, false);
      clearComposerDraft();
      // The draft, quote, and any deferred submission belong to it too.
      clearPersistedDraft();
      setPendingSubmission(undefined);
      setComposerQuote(undefined);
      cancelMessageEdit();

      // Clear event stream buffer and store, and reset throughput tracking
      eventStreamBuffer?.clear();
      throughputTracker?.reset();
      eventStreamView?.update();
    });
  };

  setupClearChatButton();

  // Single registry of composer listeners. The initial attach and the inline
  // contenteditable swap-reattach both consume this array, so a listener added
  // here is mechanically included in both — no hand-maintained enumeration to
  // drift. Add new composer listeners here, not as ad-hoc addEventListener calls.
  // Every registration goes through the active bindings, so one
  // `composerBindings.destroy()` unwires the whole surface.
  type ComposerListener = [event: string, handler: (event: Event) => void];
  const composerListeners: ComposerListener[] = [
    ["keydown", handleComposerKeydown as unknown as (event: Event) => void],
    ["input", handleComposerInput as (event: Event) => void],
    // Mirror the input adapter's value into the store. Separate from
    // `handleComposerInput` (which ignores synthetic history-recall input) and
    // IME-agnostic: the store is a mirror, never a gate.
    ["input", syncComposerText as (event: Event) => void],
    // Committing a mention strips the `@query` and fires `input`, so this is the
    // only signal a MOUSE selection produces: the menu is portaled outside the
    // footer, and no keyup follows a click. The store drops an unchanged ref
    // list, so per-keystroke cost is a comparison and no extra state event.
    ["input", syncComposerMentions as (event: Event) => void],
    ["keyup", syncComposerMentions as (event: Event) => void],
    ["paste", handleInputPaste as unknown as (event: Event) => void],
    // Warm the mention chunk on first focus so the first `@` is instant.
    ["focus", mentionPrefetch as (event: Event) => void],
  ];
  const attachComposerListeners = (el: HTMLElement | null): void => {
    if (!el) return;
    for (const [event, handler] of composerListeners) {
      composerBindings?.addListener(el, event, handler);
    }
  };
  const detachComposerListeners = (el: HTMLElement | null): void => {
    if (!el) return;
    for (const [event, handler] of composerListeners) {
      composerBindings?.removeListener(el, event, handler);
    }
  };

  /** Attach every composer-scoped listener to the freshly bound surface. */
  const wireComposerSurface = (): void => {
    if (!composerBindings) return;
    composerBindings.addListener(composerBindings.form, "submit", handleSubmit);
    // Chip removal happens inside the composer subtree; resync after the chip's
    // own click handler has run.
    composerBindings.addListener(
      composerBindings.footer,
      "click",
      syncComposerMentions
    );
    attachComposerListeners(textarea);
    if (micButton) {
      composerBindings.addListener(micButton, "click", handleMicButtonClick);
    }
    syncVoicePrewarmHook();
  };

  wireComposerSurface();

  // Inline mention mode swaps the textarea for a contenteditable surface (loaded
  // lazily). When that happens, move the composer listeners onto the new element
  // and repoint `textarea` (the swapped element shims the textarea API the rest of
  // the composer code relies on). Fires immediately if the swap already occurred.
  const registerComposerSwapHandler = () => {
    mentionOrchestrator?.onComposerSwap((next, prev) => {
      detachComposerListeners(prev);
      textarea = next as unknown as HTMLTextAreaElement;
      if (composerBindings) composerBindings.input = next;
      attachComposerListeners(next);
      // The swapped surface is a fresh element: sizing, attributes, submit-key
      // hint, and the input lock all have to land on it too.
      applyComposerInputConfig();
      applyComposerLock();
      syncComposerText();
      dictation?.refresh();
    });
  };
  registerComposerSwapHandler();

  /**
   * Re-run composer plugin arbitration and swap the footer in place. Exposed to
   * composer plugins as `requestRender()`. The seven-step lifecycle from section
   * 5 of the composer roadmap: snapshot state, destroy the outgoing bindings,
   * re-arbitrate against the current registry, build and bind, remount previews
   * and mention UI, render contributions (none yet), restore focus and sync.
   */
  let composerRebuilding = false;
  function rebuildComposer(): void {
    if (composerRebuilding) return;
    composerRebuilding = true;
    try {
      slots.detach(["footer-top", "footer-bottom", "composer-start", "composer-end", "composer-before-send"]);
      // 1. Snapshot text/focus/state.
      const previousValue = textarea?.value ?? "";
      const activeDoc = footer.ownerDocument ?? document;
      const hadFocus = textarea ? activeDoc.activeElement === textarea : false;

      // The loop writes to the OUTGOING footer and mic wrapper; both are about
      // to be detached, so it is stopped here and re-armed by `setMicState`
      // when the rebuilt mic re-enters recording.
      stopVoiceLevelLoop();
      // 2. Destroy the outgoing bindings and every composer-scoped listener.
      composerBindings?.destroy();
      composerBindings = null;
      // Its bindings point at the outgoing textarea; the new footer gets a new one.
      mentionOrchestrator?.destroy();
      mentionOrchestrator = null;

      // 3. Re-arbitrate plugins against the CURRENT registry.
      let defaultElements: _ComposerElements | null = null;
      const defaultRenderer = () => {
        defaultElements = isComposerBar()
          ? buildPillComposer({ config })
          : buildComposer({ config });
        return defaultElements.footer;
      };
      const nextFooter =
        renderComposerFromPlugins(defaultRenderer) ?? defaultRenderer();

      view.replaceComposer(nextFooter);
      footer = view.composer.footer;
      // A rebuilt footer starts visible; keep honoring layout.showFooter.
      if (config.layout?.showFooter === false) footer.style.display = "none";
      // A plugin composer owns its own send affordance, so the built-in
      // send/stop swap has nothing to drive.
      const isDefaultFooter =
        defaultElements !== null &&
        nextFooter === (defaultElements as _ComposerElements).footer;
      composerIsPluginOwned = !isDefaultFooter;
      setSendButtonMode = isDefaultFooter
        ? (defaultElements as unknown as _ComposerElements).setSendButtonMode
        : () => {};

      // 4. Build and bind the new surface.
      ensureComposerAttachmentSurface(footer);
      bindComposerRefsFromFooter(footer);
      applyComposerContentMaxWidth();
      // 5. Remount attachment previews + mention UI from composer state.
      setupComposerAttachments();
      setupMentionOrchestrator();
      registerComposerSwapHandler();
      // 6. Render action contributions against the new bindings. Custom-action
      //    cleanups run as the previous renderer is destroyed.
      setupComposerActions();
      setupComposerModeChips();
      setupComposerHeaderCards();
      wireComposerSurface();

      // The composer suggestion manager is bound to its container element, and
      // the new footer ships a new one.
      const managerIndex = suggestionManagers.indexOf(composerSuggestionsManager);
      composerSuggestionsManager.destroy();
      composerSuggestionsManager = createSuggestions(suggestions);
      if (managerIndex >= 0) suggestionManagers[managerIndex] = composerSuggestionsManager;

      slots.remount();

      // 7. Restore focus and synchronize state.
      if (textarea) textarea.value = previousValue;
      syncComposerText();
      syncComposerMentions();
      updateCopy();
      renderSuggestions();
      setComposerDisabled(session.isStreaming());
      if (config.statusIndicator?.mode === "transient" && !isComposerBar()) {
        const status = session.getStatus();
        applyStatusToElement(statusText, _getStatusText(status), config.statusIndicator, status);
      }
      // The rebuilt mic is stamped idle by its builder. A rebuild mid-recording
      // has to restore the live state, which also re-arms the level loop
      // against the new footer and wrapper.
      if (voiceState.active) setMicState("recording");
      dictation?.refresh();
      // The new footer carries none of the per-mode inline styles. Full
      // chrome sync, not bare applyFullHeightStyles: the latter resets
      // mount.style.cssText, which silently drops every theme variable.
      syncPanelChrome();
      observeComposerFooter();
      updateScrollToBottomButtonOffset();
      syncComposerCompact();
      syncSendButtonVisibility();
      if (hadFocus) textarea?.focus();
    } finally {
      composerRebuilding = false;
    }
  }

  const escStopDoc = mount.ownerDocument ?? document;
  escStopDoc.addEventListener("keydown", handleEscStop, true);

  // A debounced draft write must not be lost to a navigation or a bfcache
  // freeze. `pagehide` covers navigation and bfcache; `visibilitychange` covers
  // the mobile tab/app switch that never fires `pagehide`.
  const handleDraftLifecycleFlush = () => {
    flushDraftPersist();
  };
  const handleDraftVisibilityFlush = () => {
    if (escStopDoc.visibilityState === "hidden") flushDraftPersist();
  };
  if (typeof window !== "undefined") {
    window.addEventListener("pagehide", handleDraftLifecycleFlush);
    escStopDoc.addEventListener("visibilitychange", handleDraftVisibilityFlush);
    destroyCallbacks.push(() => {
      window.removeEventListener("pagehide", handleDraftLifecycleFlush);
      escStopDoc.removeEventListener("visibilitychange", handleDraftVisibilityFlush);
    });
  }

  const ATTACHMENT_DROP_ACTIVE_CLASS = "persona-attachment-drop-active";
  let attachmentFileDragDepth = 0;

  const clearAttachmentDropVisual = () => {
    attachmentFileDragDepth = 0;
    container.classList.remove(ATTACHMENT_DROP_ACTIVE_CLASS);
  };

  const attachmentDropHandlingActive = (): boolean =>
    config.attachments?.enabled === true &&
    attachmentManager !== null &&
    !isComposerInputDisabled();

  // Visual highlight tracked on `container` (the chat column).
  const handleAttachmentDragEnterCapture = (e: DragEvent) => {
    if (!dataTransferHasFiles(e.dataTransfer) || !attachmentDropHandlingActive()) return;
    attachmentFileDragDepth++;
    if (attachmentFileDragDepth === 1) {
      container.classList.add(ATTACHMENT_DROP_ACTIVE_CLASS);
    }
  };

  const handleAttachmentDragLeaveCapture = (e: DragEvent) => {
    if (!dataTransferHasFiles(e.dataTransfer) || !attachmentDropHandlingActive()) return;
    attachmentFileDragDepth--;
    if (attachmentFileDragDepth <= 0) {
      clearAttachmentDropVisual();
    }
  };

  // dragover + drop registered on `mount` so the browser default (open file)
  // is suppressed across the entire widget surface (artifact pane, gaps, etc.).
  const handleAttachmentDragOverCapture = (e: DragEvent) => {
    if (!dataTransferHasFiles(e.dataTransfer) || !attachmentDropHandlingActive()) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleAttachmentDropCapture = (e: DragEvent) => {
    if (!dataTransferHasFiles(e.dataTransfer) || !attachmentDropHandlingActive()) return;
    e.preventDefault();
    e.stopPropagation();
    clearAttachmentDropVisual();
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length === 0) return;
    void attachmentManager!.handleFiles(files);
  };

  const attachmentDropCapture = true;
  container.addEventListener("dragenter", handleAttachmentDragEnterCapture, attachmentDropCapture);
  container.addEventListener("dragleave", handleAttachmentDragLeaveCapture, attachmentDropCapture);
  mount.addEventListener("dragover", handleAttachmentDragOverCapture, attachmentDropCapture);
  mount.addEventListener("drop", handleAttachmentDropCapture, attachmentDropCapture);

  // Prevent the browser from navigating to/opening a dropped file anywhere on
  // the page while this widget instance has attachments enabled.  These guards
  // intentionally skip the `dataTransferHasFiles` check because real OS drags
  // may expose `dataTransfer.types` as a DOMStringList or restrict access
  // during certain drag phases.  The cost is minimal: we suppress the native
  // "open file" default for ALL drag-overs while the widget is alive and
  // attachments are on: text drags into the textarea still work because
  // element-level handlers are unaffected (we don't stopPropagation here).
  const ownerDoc = mount.ownerDocument;
  const handleDocDragOver = (e: DragEvent) => {
    if (!attachmentDropHandlingActive()) return;
    e.preventDefault();
  };
  const handleDocDrop = (e: DragEvent) => {
    if (!attachmentDropHandlingActive()) return;
    e.preventDefault();
  };
  ownerDoc.addEventListener("dragover", handleDocDragOver);
  ownerDoc.addEventListener("drop", handleDocDrop);

  destroyCallbacks.push(() => {
    // One object owns every composer-scoped listener.
    composerBindings?.destroy();
    composerBindings = null;
    // No rAF may outlive the widget.
    stopVoiceLevelLoop();
    // Runs every custom action's `destroy()` and tears down the overflow menu.
    composerActionsTornDown = true;
    composerActionRenderer?.destroy();
    composerActionRenderer = null;
    composerModeChips?.destroy();
    composerModeChips = null;
    composerQuoteBanner?.destroy();
    composerQuoteBanner = null;
    composerPendingCard?.destroy();
    composerPendingCard = null;
    composerCoreControls = null;
    // Flush before the store is destroyed: the pending write reads its state.
    flushDraftPersist();
    draftWriter.destroy();
    if (composerNoticeTimer) clearTimeout(composerNoticeTimer);
    cancelMessageEdit();
    submissionAbort?.abort();
    submissionAbort = null;
    historyShell?.clearConversationOpenPending();
    discardConversationContinuity(undefined, false);
    // Aborts every in-flight upload; late completions write no state.
    attachmentManager?.destroy();
    attachmentManager = null;
    composerStore.destroy();
    escStopDoc.removeEventListener("keydown", handleEscStop, true);
    mentionOrchestrator?.destroy();
  });

  destroyCallbacks.push(() => {
    container.removeEventListener("dragenter", handleAttachmentDragEnterCapture, attachmentDropCapture);
    container.removeEventListener("dragleave", handleAttachmentDragLeaveCapture, attachmentDropCapture);
    mount.removeEventListener("dragover", handleAttachmentDragOverCapture, attachmentDropCapture);
    mount.removeEventListener("drop", handleAttachmentDropCapture, attachmentDropCapture);
    ownerDoc.removeEventListener("dragover", handleDocDragOver);
    ownerDoc.removeEventListener("drop", handleDocDrop);
    clearAttachmentDropVisual();
  });

  destroyCallbacks.push(() => {
    session.cancel();
  });

  if (launcherSurfaceInstance) {
    destroyCallbacks.push(() => {
      launcherSurfaceInstance?.destroy();
    });
  } else if (customLauncherElement) {
    destroyCallbacks.push(() => {
      customLauncherElement?.remove();
    });
  }

  // Relocation is deliberately narrower than update(): no renderer, composer,
  // session, or subscription is rebuilt. Snapshot reader intent before the
  // browser can clamp scrollTop while moving/reflowing the host.
  runtimeOptions?.registerHostMove?.((reparent) => {
    const top = body.scrollTop;
    const following = isFollowEffective() && autoFollow.isFollowing();
    const viewportTop = body.getBoundingClientRect().top;
    const readingAnchor = !following && body.clientHeight > 0
      ? Array.from(messagesWrapper.querySelectorAll<HTMLElement>("[data-message-id]")).find(
        (node) => node.getBoundingClientRect().bottom > viewportTop
      ) : undefined;
    const anchorOffset = readingAnchor ? readingAnchor.getBoundingClientRect().top - viewportTop : 0;
    cancelAutoScroll();
    reparent();
    getComposerActionsSync()?.ensureComposerActionStyles(mount);
    getArtifactsUiSync()?.ensureArtifactStyles(mount);
    recalcPanelHeight();
    applyRailChrome();
    if (following) body.scrollTop = getScrollBottomOffset(body);
    else if (readingAnchor) body.scrollTop += readingAnchor.getBoundingClientRect().top - body.getBoundingClientRect().top - anchorOffset;
    else body.scrollTop = top;
    if (!following && !readerEngagedThisTurn) repinAnchoredMessage();
    lastScrollTop = body.scrollTop;
    lastBottomOffset = getScrollBottomOffset(body);
    syncTranscriptEdgeFades();
    syncScrollToBottomButton();
    // Open popovers/tooltips listen at their own instance root, including
    // lazy chunks with separate module copies. Other widgets are untouched.
    mount.dispatchEvent(new Event("persona:internal:host-moved"));
  });

  const showFeedback = (create: () => HTMLElement) => {
    if (!open && isPanelToggleable()) setOpenState(true, "system");
    messagesWrapper.querySelector(".persona-feedback-container")?.remove();
    const element = create();
    messagesWrapper.appendChild(element);
    element.scrollIntoView({ behavior: "smooth", block: "end" });
  };

  let presentationPrepared = false;
  let queuedUpdates: AgentWidgetConfigPatch[] = [];
  let applyingPreparedUpdates = false;
  const applyQueuedUpdates = () => {
    const updates = queuedUpdates;
    queuedUpdates = [];
    applyingPreparedUpdates = true;
    try { for (const patch of updates) controller.update(patch); }
    finally { applyingPreparedUpdates = false; }
  };
  const controller: Controller = {
    get ready() { return renderReadiness.ready; },
    get instanceId() { return instanceId; },
    update(nextConfig: AgentWidgetConfigPatch) {
      // Keep a complete existing view while a newly enabled renderer loads.
      // Replay raw patches so explicit-undefined resets and provenance survive.
      if (!applyingPreparedUpdates) {
        queuedUpdates.push(nextConfig);
        const nextPresentation = queuedUpdates.reduce(mergeConfigUpdate, { ...config, features: baseFeatures });
        const pending = prepareRendererModules(resolveConfigPreferences(nextPresentation));
        if (pending.length) {
          renderReadiness.prepare(pending, applyQueuedUpdates, presentationPrepared);
          return;
        }
        if (queuedUpdates.length > 1) {
          renderReadiness.prepare([], applyQueuedUpdates, presentationPrepared);
          return;
        }
        queuedUpdates = [];
      }
      if (!runtimeOptions?.diagnostics) updateLegacyDiagnostics(diagnostics, nextConfig);
      const nextVoice = nextConfig.voiceRecognition;
      if (nextVoice && (nextVoice.enabled === false ||
        (nextVoice.dictation?.provider && nextVoice.dictation.provider !== config.voiceRecognition?.dictation?.provider) ||
        (nextVoice.provider?.custom && nextVoice.provider.custom !== config.voiceRecognition?.provider?.custom))) dismissDictation();
      const previousDefaultsVersion = config.future?.v5Defaults === true;
      const previousMessageActions = config.messageActions;
      const previousLayoutMessages = config.layout?.messages;
      const previousColorScheme = config.colorScheme;
      const previousIterationDisplay = config.iterationDisplay;
      const previousShowReasoning = config.features?.showReasoning;
      const previousShowToolCalls = config.features?.showToolCalls;
      const previousToolCallDisplay = config.features?.toolCallDisplay;
      const previousReasoningDisplay = config.features?.reasoningDisplay;
      const userCollapseConfigKey = () => JSON.stringify([
        config.features?.userMessageCollapse, config.copy?.showMoreLabel, config.copy?.showLessLabel,
      ]);
      const previousUserCollapseConfig = userCollapseConfigKey();
      const previousStreamAnimationType = config.features?.streamAnimation?.type;
      // History provider identity: enablement, eligibility, and advertised
      // scopes are all resolved at construction, so a change rebuilds it.
      const previousHistoryEnabled = config.features?.history?.enabled === true;
      const previousHistoryProvider = config.features?.history?.provider;
      const previousIdentityProof = config.getIdentityProof;
      const previousClientToken = config.clientToken;
      // The mention orchestrator is built once from the whole config object, so
      // any live edit needs a composer rebuild. The merge keeps this reference
      // when the patch never mentioned it, so unrelated updates never rebuild.
      const previousContextMentions = config.contextMentions;
      // One consistent recursive patch policy across the live controller and the
      // init handle. See utils/config-merge.ts for the replace-leaf list and
      // explicit-undefined reset semantics. The patch merges over the
      // pre-preference base features, then preferences re-resolve on top.
      config = mergeConfigUpdate({ ...config, features: baseFeatures }, nextConfig);
      baseFeatures = config.features;
      config = resolveConfigPreferences(config);
      applyTooltipTiming();
      // The shared chrome pass restores owned variables after resetting mount styles,
      // including the welded radius even when the artifact split mode stays unchanged.
      syncPanelChrome();
      syncArtifactPane();

      // Re-setup theme observer if colorScheme changed
      if (config.colorScheme !== previousColorScheme) {
        setupThemeObserver();
      }

      // Update plugins
      const newPlugins = pluginRegistry.getForInstance(config.plugins);
      plugins.length = 0;
      plugins.push(...newPlugins);
      // Bindings are config- and plugin-derived, so they re-derive after both.
      syncShortcuts();

      launcherEnabled = config.launcher?.enabled ?? true;
      autoExpand = config.launcher?.autoExpand ?? false;
      showReasoning = config.features?.showReasoning ?? true;
      showToolCalls = config.features?.showToolCalls ?? true;
      latestScroll.cancel();
      const prevScrollMode = getScrollMode();
      scrollBehaviorFeature = config.features?.scrollBehavior ?? {};
      if (prevScrollMode !== getScrollMode()) {
        // Leaving anchor-top drops any live spacer; entering a new mode
        // starts from a clean follow state.
        resetAnchorState();
        resumeAutoScroll();
      }
      applyScrollbarPolicy();
      syncScrollToBottomButton();
      const prevShowEventStreamToggle = showEventStreamToggle;
      showEventStreamToggle = config.features?.showEventStreamToggle ?? false;

      // Handle dynamic event stream feature flag toggling
      if (showEventStreamToggle && !prevShowEventStreamToggle) {
        // Flag changed from false to true - fetch the chunk and construct the
        // runtime at adoption; stage events tapped meanwhile.
        if (!eventStreamBuffer) {
          ensureEventStreamRuntime();
          // Register the SSE event callback (host tap + buffer + throughput)
          session.setSSEEventCallback((type: string, payload: unknown) => {
            config.onSSEEvent?.(type, payload);
            feedEventStream(type, payload);
          });
        }
        // Add header toggle button if not present
        if (!eventStreamToggleBtn && header) {
          const dynEsClassNames = config.features?.eventStream?.classNames;
          const dynToggleBtnClasses = "persona-inline-flex persona-items-center persona-justify-center persona-rounded-full hover:persona-opacity-80 persona-cursor-pointer persona-border-none persona-bg-transparent persona-p-1" + (dynEsClassNames?.toggleButton ? " " + dynEsClassNames.toggleButton : "");
          eventStreamToggleBtn = createElement("button", dynToggleBtnClasses) as HTMLButtonElement;
          eventStreamToggleBtn.style.width = "28px";
          eventStreamToggleBtn.style.height = "28px";
          eventStreamToggleBtn.style.color = HEADER_THEME_CSS.actionIconColor;
          eventStreamToggleBtn.type = "button";
          eventStreamToggleBtn.setAttribute("aria-label", "Event Stream");
          eventStreamToggleBtn.title = "Event Stream";
          const activityIcon = renderIconNode(Activity, "18px", "currentColor", 1.5);
          if (activityIcon) eventStreamToggleBtn.appendChild(activityIcon);
          const clearChatWrapper = panelElements.clearChatButtonWrapper;
          const closeWrapper = panelElements.closeButtonWrapper;
          const insertBefore = clearChatWrapper || closeWrapper;
          if (insertBefore && insertBefore.parentNode === header) {
            header.insertBefore(eventStreamToggleBtn, insertBefore);
          } else {
            header.appendChild(eventStreamToggleBtn);
          }
          eventStreamToggleBtn.addEventListener("click", () => {
            if (eventStreamVisible) {
              toggleEventStreamOff();
            } else {
              toggleEventStreamOn();
            }
          });
        }
      } else if (!showEventStreamToggle && prevShowEventStreamToggle) {
        // Flag changed from true to false - hide and clean up
        toggleEventStreamOff();
        eventStreamView?.destroy();
        eventStreamView = null;
        if (eventStreamToggleBtn) {
          eventStreamToggleBtn.remove();
          eventStreamToggleBtn = null;
        }
        eventStreamBuffer?.clear();
        eventStreamStore?.destroy();
        eventStreamBuffer = null;
        eventStreamStore = null;
        throughputTracker?.reset();
        throughputTracker = null;
        pendingEventStreamEvents = null;
      }

      if (config.launcher?.enabled === false && launcherSurfaceInstance) {
        launcherSurfaceInstance.destroy();
        launcherSurfaceInstance = null;
      }
      if (config.launcher?.enabled === false && customLauncherElement) {
        customLauncherElement.remove();
        customLauncherElement = null;
      }

      if (config.launcher?.enabled !== false && !launcherSurfaceInstance && !customLauncherElement) {
        // Resolve the launcher again when re-enabling (honors renderLauncher plugin).
        const { instance, element } = resolveLauncher({ config, plugins, onToggle: toggleOpen });
        launcherSurfaceInstance = instance;
        if (!instance) customLauncherElement = element;
        mount.appendChild(element);
      }

      if (launcherSurfaceInstance) {
        launcherSurfaceInstance.update(config);
      }
      // Note: Custom launcher updates are handled by the plugin's own logic

      // Update panel header title and subtitle. applyHeaderTitle respects the
      // titleSource binding, so a static launcher.title update never clobbers
      // a bound conversation title (and a titleSource flip applies live).
      applyHeaderTitle();
      if (headerSubtitle && config.launcher?.subtitle !== undefined) {
        headerSubtitle.textContent = config.launcher.subtitle;
      }

      // Update header layout if it changed
      const headerLayoutConfig = config.layout?.header;
      const headerLayoutChanged = headerLayoutConfig?.layout !== prevHeaderLayout;

      if (headerLayoutChanged && header) {
        // Rebuild header with new layout
        const newHeaderElements = headerLayoutConfig
          ? buildHeaderWithLayout(config, headerLayoutConfig, {
              showClose: isPanelToggleable(),
              onClose: () => setOpenState(false, "user")
            })
          : buildHeader({
              config,
              showClose: isPanelToggleable(),
              onClose: () => setOpenState(false, "user")
            });

        // Replace the old header with the new one (keeps view.header in sync).
        view.replaceHeader(newHeaderElements);

        // Mirror the view's refreshed header refs into the local bindings.
        header = view.header.element;
        iconHolder = view.header.iconHolder;
        headerTitle = view.header.headerTitle;
        headerSubtitle = view.header.headerSubtitle;
        closeButton = view.header.closeButton;

        prevHeaderLayout = headerLayoutConfig?.layout;
        // The rebuilt title span starts static; restamp the bound title.
        applyHeaderTitle();
      } else if (headerLayoutConfig) {
        // Apply visibility settings without rebuilding
        if (iconHolder) {
          iconHolder.style.display = headerLayoutConfig.showIcon === false ? "none" : "";
        }
        if (headerTitle) {
          headerTitle.style.display = headerLayoutConfig.showTitle === false ? "none" : "";
        }
        if (headerSubtitle) {
          headerSubtitle.style.display = headerLayoutConfig.showSubtitle === false ? "none" : "";
        }
        if (closeButton) {
          // showCloseButton (defaulted true) filters on top of toggleability;
          // it must not reveal the close button on non-closeable panels.
          const showClose = isPanelToggleable() && headerLayoutConfig.showCloseButton !== false;
          closeButton.style.display = showClose ? "" : "none";
        }
        if (panelElements.clearChatButtonWrapper) {
          // showClearChat explicitly controls visibility when set
          const showClearChat = headerLayoutConfig.showClearChat;
          if (showClearChat !== undefined) {
            panelElements.clearChatButtonWrapper.style.display = showClearChat ? "" : "none";
            // When clear chat is hidden, close button needs ml-auto to stay right-aligned
            const { closeButtonWrapper } = panelElements;
            if (closeButtonWrapper && !closeButtonWrapper.classList.contains("persona-absolute")) {
              if (showClearChat) {
                closeButtonWrapper.classList.remove("persona-ml-auto");
              } else {
                closeButtonWrapper.classList.add("persona-ml-auto");
              }
            }
          }
        }
      }

      // Update header visibility based on layout.showHeader
      const showHeader = isHeaderVisible(config);
      if (header) {
        header.style.display = showHeader ? "" : "none";
      }

      // Update footer visibility based on layout.showFooter
      const showFooter = config.layout?.showFooter !== false; // default to true
      if (footer) {
        footer.style.display = showFooter ? "" : "none";
      }
      updateScrollToBottomButtonOffset();
      syncScrollToBottomButton();

      // Only update open state if launcher enabled state changed or autoExpand value changed
      const launcherEnabledChanged = launcherEnabled !== prevLauncherEnabled;
      const autoExpandChanged = autoExpand !== prevAutoExpand;

      if (launcherEnabledChanged) {
        // Launcher was enabled/disabled - update state accordingly
        if (!launcherEnabled) {
          // When launcher is disabled, always keep panel open
          open = true;
          updateOpenState();
        } else {
          // Launcher was just enabled - respect autoExpand setting
          setOpenState(autoExpand, "auto");
        }
      } else if (autoExpandChanged) {
        // autoExpand value changed - update state to match
        setOpenState(autoExpand, "auto");
      }
      // Otherwise, preserve current open state (user may have manually opened/closed)

      // Update previous values for next comparison
      prevAutoExpand = autoExpand;
      prevLauncherEnabled = launcherEnabled;
      recalcPanelHeight();
      refreshCloseButton();

      // Re-render messages if config affecting message rendering changed
      const messageActionsChanged = JSON.stringify(config.messageActions) !== JSON.stringify(previousMessageActions);
      const layoutMessagesChanged = JSON.stringify(config.layout?.messages) !== JSON.stringify(previousLayoutMessages)
        || config.layout?.messages?.renderAssistantMessage !== previousLayoutMessages?.renderAssistantMessage
        || config.layout?.messages?.renderUserMessage !== previousLayoutMessages?.renderUserMessage;
      const iterationDisplayChanged = config.iterationDisplay !== previousIterationDisplay;
      const featuresChanged = (config.features?.showReasoning ?? true) !== (previousShowReasoning ?? true)
        || (config.features?.showToolCalls ?? true) !== (previousShowToolCalls ?? true)
        || JSON.stringify(config.features?.toolCallDisplay) !== JSON.stringify(previousToolCallDisplay)
        || JSON.stringify(config.features?.reasoningDisplay) !== JSON.stringify(previousReasoningDisplay)
        || userCollapseConfigKey() !== previousUserCollapseConfig;
      const messagesConfigChanged = (config.future?.v5Defaults === true) !== previousDefaultsVersion
        || "toolCall" in nextConfig || messageActionsChanged || layoutMessagesChanged
        || "loadingIndicator" in nextConfig || iterationDisplayChanged || featuresChanged
        || "turnActivity" in nextConfig || "loadingLabel" in nextConfig || "expansion" in nextConfig
        || "errorDisplay" in nextConfig || "theme" in nextConfig || "darkTheme" in nextConfig
        || "approval" in nextConfig || config.colorScheme !== previousColorScheme
        || "askUserQuestion" in (nextConfig.features ?? {})
        || ("features" in nextConfig && nextConfig.features === undefined);
      if (messagesConfigChanged && session) {
        configVersion++;
        renderMessagesWithPlugins(messagesWrapper, session.getMessages(), postprocess);
      }

      // Re-activate the stream-animation plugin when the type changes via
      // update(). Built-in animations (typewriter, word-fade, letter-rise,
      // pop-bubble) carry their CSS in widget.css, so swapping the type is
      // enough. Plugin animations (wipe, glyph-cycle, and custom plugins
      // registered via registerStreamAnimationPlugin) inject their styles and
      // run onAttach only through ensurePluginActive, which the initial mount
      // calls but update() otherwise skips. Without this, switching to a plugin
      // animation live sets the type but never injects the CSS, so it silently
      // does nothing. ensurePluginActive is idempotent (styles inject once per
      // root), so re-selecting a previously-activated plugin is a no-op.
      const nextStreamAnimationType = config.features?.streamAnimation?.type;
      if (
        nextStreamAnimationType !== previousStreamAnimationType &&
        nextStreamAnimationType &&
        nextStreamAnimationType !== "none"
      ) {
        const streamAnimationPlugin = resolveStreamAnimationPluginLazy(
          nextStreamAnimationType,
          config.features?.streamAnimation?.plugins
        );
        if (streamAnimationPlugin) ensurePluginActive(streamAnimationPlugin, mount);
      }

      // Update panel icon sizes
      const launcher = config.launcher ?? {};
      const headerIconHidden = launcher.headerIconHidden ?? false;
      const layoutShowIcon = config.layout?.header?.showIcon;
      // Hide icon if either headerIconHidden is true OR layout.header.showIcon is false
      const shouldHideIcon = headerIconHidden || layoutShowIcon === false;
      const headerIconName = launcher.headerIconName;
      const headerIconSize = launcher.headerIconSize ?? DEFAULT_HEADER_ICON_SIZE;

      if (iconHolder) {
        const headerEl = header;
        const headerCopy = headerEl?.querySelector(".persona-flex-col");

        // Handle hide/show
        if (shouldHideIcon) {
          // Hide iconHolder
          iconHolder.style.display = "none";
          // Ensure headerCopy is still in header
          if (headerEl && headerCopy && !headerEl.contains(headerCopy)) {
            headerEl.insertBefore(headerCopy, headerEl.firstChild);
          }
        } else {
          // Show iconHolder
          iconHolder.style.display = "";
          iconHolder.style.height = headerIconSize;
          iconHolder.style.width = headerIconSize;

          // Ensure iconHolder is before headerCopy in header
          if (headerEl && headerCopy) {
            if (!headerEl.contains(iconHolder)) {
              headerEl.insertBefore(iconHolder, headerCopy);
            } else if (iconHolder.nextSibling !== headerCopy) {
              // Reorder if needed
              iconHolder.remove();
              headerEl.insertBefore(iconHolder, headerCopy);
            }
          }

          // Update icon content based on priority: Lucide icon > iconUrl > agentIconText
          if (headerIconName) {
            // Use Lucide icon. Stroke `currentColor` (not a hardcoded white) so the
            // glyph inherits iconHolder's `color: var(--persona-header-icon-fg, …)`,
            // matching the initial render in header-builder.ts. Without this, any
            // controller.update() (e.g. a theme-editor change) re-rendered the icon
            // as white and the configured header icon color "wouldn't stick".
            const iconSize = parseFloat(headerIconSize) || 24;
            const iconSvg = renderLucideIcon(headerIconName, iconSize * 0.6, "currentColor", 1);
            if (iconSvg) {
              iconSvg.style.width = `calc(${iconSize}px * var(--persona-components-header-iconScale, 0.6))`;
              iconSvg.style.height = `calc(${iconSize}px * var(--persona-components-header-iconScale, 0.6))`;
              iconHolder.replaceChildren(iconSvg);
            } else {
              // Fallback to agentIconText if Lucide icon fails
              iconHolder.textContent = launcher.agentIconText ?? "💬";
            }
          } else if (launcher.iconUrl) {
            // Use image URL
            const img = iconHolder.querySelector("img");
            if (img) {
              img.src = launcher.iconUrl;
              img.style.height = headerIconSize;
              img.style.width = headerIconSize;
            } else {
              // Create new img if it doesn't exist
              const newImg = document.createElement("img");
              newImg.src = launcher.iconUrl;
              newImg.alt = "";
              newImg.className = "persona-rounded-xl persona-object-cover";
              newImg.style.height = headerIconSize;
              newImg.style.width = headerIconSize;
              iconHolder.replaceChildren(newImg);
            }
          } else {
            // Use text/emoji - clear any SVG or img first
            const existingSvg = iconHolder.querySelector("svg");
            const existingImg = iconHolder.querySelector("img");
            if (existingSvg || existingImg) {
              iconHolder.replaceChildren();
            }
            iconHolder.textContent = launcher.agentIconText ?? "💬";
          }

          // Update image size if present
          const img = iconHolder.querySelector("img");
          if (img) {
            img.style.height = headerIconSize;
            img.style.width = headerIconSize;
          }
        }
      }

      // Handle title/subtitle visibility from layout config
      const layoutShowTitle = config.layout?.header?.showTitle;
      const layoutShowSubtitle = config.layout?.header?.showSubtitle;
      if (headerTitle) {
        headerTitle.style.display = layoutShowTitle === false ? "none" : "";
      }
      if (headerSubtitle) {
        headerSubtitle.style.display = layoutShowSubtitle === false ? "none" : "";
      }

      if (closeButton) {
        // showCloseButton (defaulted true) filters on top of toggleability;
        // it must not reveal the close button on non-closeable panels.
        const layoutShowCloseButton =
          isPanelToggleable() && config.layout?.header?.showCloseButton !== false;
        closeButton.style.display = layoutShowCloseButton ? "" : "none";

        const closeButtonPlacement = launcher.closeButtonPlacement ?? "inline";

        // Update placement if changed - move the wrapper (not just the button) to preserve tooltip
        const { closeButtonWrapper } = panelElements;
        const isTopRight = closeButtonPlacement === "top-right";
        const currentlyTopRight = closeButtonWrapper?.classList.contains("persona-absolute");

        if (closeButtonWrapper && isTopRight !== currentlyTopRight) {
          // Placement changed - need to move wrapper and update classes
          closeButtonWrapper.remove();

          // Update wrapper classes
          if (isTopRight) {
            closeButtonWrapper.className = "persona-absolute persona-top-4 persona-right-4 persona-z-50";
            // A mounted rail owns the conversation column; anchoring to the
            // container would float the button over the rail.
            const anchor = historyShell?.railColumn ?? container;
            anchor.style.position = "relative";
            anchor.appendChild(closeButtonWrapper);
          } else {
            // Check if clear chat is inline to determine if we need ml-auto
            const clearChatPlacement = launcher.clearChat?.placement ?? "inline";
            const clearChatEnabled = launcher.clearChat?.enabled ?? true;
            closeButtonWrapper.className = (clearChatEnabled && clearChatPlacement === "inline") ? "" : "persona-ml-auto";
            if (header) {
              header.appendChild(closeButtonWrapper);
            }
          }
        }

        applyHeaderControlStyle(closeButton, config, "close", {
          buttonSize: isComposerBar() ? closeButton.style.width : undefined,
          iconSize: isComposerBar() ? COMPOSER_BAR_CLOSE_ICON_SIZE : undefined,
        });

        // Update tooltip
        const closeButtonTooltipText = launcher.closeButtonTooltipText ?? "Close chat";
        const closeButtonShowTooltip = launcher.closeButtonShowTooltip ?? true;

        closeButton.setAttribute("aria-label", closeButtonTooltipText);

        if (closeButtonWrapper) {
          attachTooltip({
            anchor: closeButton,
            trigger: closeButtonWrapper,
            text: () =>
              closeButton?.getAttribute("aria-label") ?? closeButtonTooltipText,
            enabled: closeButtonShowTooltip,
          });
        }
      }

      // Update clear chat button styling from config
      const { clearChatButton, clearChatButtonWrapper } = panelElements;
      if (clearChatButton) {
        const clearChatConfig = launcher.clearChat ?? {};
        const clearChatEnabled = clearChatConfig.enabled ?? true;
        const layoutShowClearChat = config.layout?.header?.showClearChat;
        // layout.header.showClearChat takes precedence if explicitly set
        // Otherwise fall back to launcher.clearChat.enabled
        const shouldShowClearChat = layoutShowClearChat !== undefined
          ? layoutShowClearChat
          : clearChatEnabled;
        const clearChatPlacement = clearChatConfig.placement ?? "inline";

        // Show/hide button based on layout config (primary) or launcher config (fallback)
        if (clearChatButtonWrapper) {
          clearChatButtonWrapper.style.display = shouldShowClearChat ? "" : "none";

          // When clear chat is hidden, close button needs ml-auto to stay right-aligned.
          // Composer-bar mode positions the close button absolutely, so the
          // ml-auto layout shim doesn't apply and is skipped below.
          const { closeButtonWrapper } = panelElements;
          if (
            !isComposerBar() &&
            closeButtonWrapper &&
            !closeButtonWrapper.classList.contains("persona-absolute")
          ) {
            if (shouldShowClearChat) {
              closeButtonWrapper.classList.remove("persona-ml-auto");
            } else {
              closeButtonWrapper.classList.add("persona-ml-auto");
            }
          }

          // Update placement if changed. Composer-bar mode owns the clear
          // button's position via panel.ts (absolute, top-right next to ×)
          // and must not get reshuffled into the floating launcher's
          // header strip.
          const isTopRight = clearChatPlacement === "top-right";
          const currentlyTopRight = clearChatButtonWrapper.classList.contains("persona-absolute");

          if (!isComposerBar() && isTopRight !== currentlyTopRight && shouldShowClearChat) {
            clearChatButtonWrapper.remove();

            if (isTopRight) {
              // Don't use persona-clear-chat-button-wrapper class for top-right mode as its
              // display: inline-flex causes alignment issues with the close button
              clearChatButtonWrapper.className = "persona-absolute persona-top-4 persona-z-50";
              // Position to the left of the close button (which is at right: 1rem/16px)
              // Close button is ~32px wide, plus small gap = 48px from right
              clearChatButtonWrapper.style.right = "48px";
              const anchor = historyShell?.railColumn ?? container;
              anchor.style.position = "relative";
              anchor.appendChild(clearChatButtonWrapper);
            } else {
              clearChatButtonWrapper.className = "persona-relative persona-ml-auto persona-clear-chat-button-wrapper";
              // Clear the inline right style when switching back to inline mode
              clearChatButtonWrapper.style.right = "";
              // Find header and insert before close button
              const closeButtonWrapperEl = panelElements.closeButtonWrapper;
              if (header && closeButtonWrapperEl && closeButtonWrapperEl.parentElement === header) {
                header.insertBefore(clearChatButtonWrapper, closeButtonWrapperEl);
              } else if (header) {
                header.appendChild(clearChatButtonWrapper);
              }
            }

            // Also update close button's ml-auto class based on clear chat position
            const closeButtonWrapperEl = panelElements.closeButtonWrapper;
            if (closeButtonWrapperEl && !closeButtonWrapperEl.classList.contains("persona-absolute")) {
              if (isTopRight) {
                // Clear chat moved to top-right, close needs ml-auto
                closeButtonWrapperEl.classList.add("persona-ml-auto");
              } else {
                // Clear chat is inline, close doesn't need ml-auto
                closeButtonWrapperEl.classList.remove("persona-ml-auto");
              }
            }
          }
        }

        if (shouldShowClearChat) {
          applyHeaderControlStyle(clearChatButton, config, "clear", {
            buttonSize: isComposerBar() ? clearChatButton.style.width : undefined,
            iconSize: isComposerBar() ? COMPOSER_BAR_CLEAR_CHAT_ICON_SIZE : undefined,
          });

          const clearChatTooltipText = clearChatConfig.tooltipText ?? "Clear chat";
          const clearChatShowTooltip = clearChatConfig.showTooltip ?? true;

          clearChatButton.setAttribute("aria-label", clearChatTooltipText);

          if (clearChatButtonWrapper) {
            attachTooltip({
              anchor: clearChatButton,
              trigger: clearChatButtonWrapper,
              text: () =>
                clearChatButton?.getAttribute("aria-label") ??
                clearChatTooltipText,
              enabled: clearChatShowTooltip,
            });
          }
        }
      }

      const nextParsers =
        config.actionParsers && config.actionParsers.length
          ? config.actionParsers
          : [defaultJsonActionParser];
      const nextHandlers =
        config.actionHandlers && config.actionHandlers.length
          ? config.actionHandlers
          : [defaultActionHandlers.message, defaultActionHandlers.messageAndClick];

      actionManager = createActionManager({
        parsers: nextParsers,
        handlers: nextHandlers,
        getSessionMetadata,
        updateSessionMetadata,
        emit: eventBus.emit,
        captureResubmitGuard: () => {
          const generation = restartGeneration;
          return () => canRestart(generation);
        },
        documentRef: typeof document !== "undefined" ? document : null
      });

      postprocess = buildPostprocessor(config, actionManager, handleResubmitRequested);
      // Re-key the visitor store before any client rebuild sees the new config.
      syncVisitorStore();
      session.updateConfig(config);
      syncSessionInitHook();
      if (
        (config.features?.history?.enabled === true) !== previousHistoryEnabled ||
        config.features?.history?.provider !== previousHistoryProvider ||
        config.getIdentityProof !== previousIdentityProof ||
        config.clientToken !== previousClientToken
      ) {
        historyShell?.closeHistory({ restoreFocus: false });
        if (config.getIdentityProof !== previousIdentityProof || config.clientToken !== previousClientToken) {
          historyShell?.discardConversationContinuity();
        }
        historyShell?.installHistoryProvider();
      }
      historyShell?.syncHistoryChromeImpl();
      // Presentation may have flipped between panel and rail; the open view
      // instance moves hosts rather than being recreated.
      syncHistoryPresentation();
      // Turning history on loads the shell, which installs and syncs itself.
      if (historyFeatureEnabled() && !historyShell) {
        void ensureHistoryShell().catch(() => {});
      }
      renderMessagesWithPlugins(
        messagesWrapper,
        session.getMessages(),
        postprocess
      );
      renderSuggestions();
      updateCopy();
      // Live `welcome` updates reach `renderWelcome` plugins; unrelated
      // update() calls leave plugin-owned content (and its state) alone.
      refreshWelcomePlugins();
      setComposerDisabled(session.isStreaming());

      // A live `contextMentions` edit (enable/disable, display mode, sources,
      // trigger channels) only reaches the composer through a rebuild: the
      // orchestrator, its affordance buttons, and the inline contenteditable
      // swap are all built at construction. Runs before the composer work
      // below so the mic button, send button, mode chips, and action row all
      // land on the new footer.
      if (previousContextMentions !== config.contextMentions) {
        rebuildComposer();
      }

      // Update voice recognition mic button visibility
      const voiceRecognitionEnabled = config.voiceRecognition?.enabled === true && config.voiceRecognition.showButton !== false;
      const hasSpeechRecognition =
        typeof window !== 'undefined' &&
        (typeof (window as any).webkitSpeechRecognition !== 'undefined' ||
         typeof (window as any).SpeechRecognition !== 'undefined');
      const hasSessionProvider =
        usesSessionVoice(config.voiceRecognition?.provider);
      const hasVoiceInput = hasSpeechRecognition || hasSessionProvider || config.voiceRecognition?.dictation?.provider?.type === "custom";

      if (voiceRecognitionEnabled && hasVoiceInput) {
        // Create or update mic button
        if (!micButton || !micButtonWrapper) {
          // Create new mic button
          const micButtonResult = createMicButton(config);
          if (micButtonResult) {
            // Update the mutable references
            micButton = micButtonResult.button;
            micButtonWrapper = micButtonResult.wrapper;

            // Insert into the CURRENT right-action cluster, before the send
            // button; fall back to the form when a plugin composer has neither.
            const micAnchor =
              sendButtonWrapper && sendButtonWrapper.parentElement === rightActions
                ? sendButtonWrapper
                : null;
            if (rightActions) rightActions.insertBefore(micButtonWrapper, micAnchor);
            else composerForm?.appendChild(micButtonWrapper);


            // Wire up click handler
            // Through the bindings so a later rebuild unwires it, and keep the
            // binding object coherent with the locals it mirrors.
            if (composerBindings) composerBindings.micButton = micButton;
            composerBindings?.addListener(
              micButton,
              "click",
              handleMicButtonClick
            );

            // Set disabled state
            micButton.disabled = isComposerInputDisabled() || (session.isStreaming() && !isActiveSessionVoice());
          }
        } else {
          // Update existing mic button with new config
          const voiceConfig = config.voiceRecognition ?? {};
          const micState = micButton.getAttribute("data-state");
          applyMicButtonStyle(micButton, config,
            micState === "recording" || micState === "processing" || micState === "speaking" ? micState : "idle",
            session.getVoiceInterruptionMode());

          const tooltipText = voiceConfig.tooltipText ?? "Start voice recognition";
          const showTooltip = voiceConfig.showTooltip ?? false;
          if (micState === "idle") micButton.setAttribute("aria-label", tooltipText);
          if (micButtonWrapper) {
            attachTooltip({
              anchor: micButton,
              trigger: micButtonWrapper,
              text: () => micButton?.getAttribute("aria-label") ?? tooltipText,
              enabled: showTooltip,
            });
          }

          // Show and update disabled state
          micButtonWrapper.style.display = "";
          micButton.disabled = isComposerInputDisabled() || (session.isStreaming() && !isActiveSessionVoice());
        }
      } else {
        // Hide mic button
        if (micButton && micButtonWrapper) {
          micButtonWrapper.style.display = "none";
          // Hiding the button retains programmatic voice controls; disabling voice stops capture.
          if (config.voiceRecognition?.enabled !== true || !hasVoiceInput) {
            if (usesSessionVoice(config.voiceRecognition?.provider)) {
              if (session.isVoiceActive()) session.toggleVoice();
            } else if (isRecording) {
              stopVoiceRecognition();
            }
          }
        }
      }
      syncVoicePrewarmHook();

      // Update attachment button visibility based on attachments config
      const attachmentsEnabled = config.attachments?.enabled === true;
      if (attachmentsEnabled) {
        // Create or show attachment button
        if (!attachmentButtonWrapper || !attachmentButton) {
          // Need to create the attachment elements dynamically
          const attachmentsConfig = config.attachments ?? {};

          // Create previews container if not exists
          if (!attachmentPreviewsContainer) {
            attachmentPreviewsContainer = createElement("div", "persona-attachment-previews persona-flex persona-flex-wrap persona-gap-2 persona-mb-2");
            attachmentPreviewsContainer.style.display = "none";
            (textarea.parentElement ?? composerForm).insertBefore(
              attachmentPreviewsContainer,
              textarea
            );
          }

          // Create file input if not exists
          if (!attachmentInput) {
            attachmentInput = document.createElement("input");
            attachmentInput.type = "file";
            attachmentInput.accept = (attachmentsConfig.allowedTypes ?? ALL_SUPPORTED_MIME_TYPES).join(",");
            attachmentInput.multiple = (attachmentsConfig.maxFiles ?? 4) > 1;
            attachmentInput.style.display = "none";
            attachmentInput.setAttribute("aria-label", "Attach files");
            (textarea.parentElement ?? composerForm).insertBefore(
              attachmentInput,
              textarea
            );
          }

          // Create attachment button wrapper
          attachmentButtonWrapper = createElement("div", "persona-send-button-wrapper");

          // Create attachment button
          attachmentButton = createElement(
            "button",
            `persona-rounded-button persona-flex persona-items-center persona-justify-center disabled:persona-opacity-50 persona-cursor-pointer persona-attachment-button ${COMPOSER_CONTROL_CLASS} ${COMPOSER_CONTROL_GLYPH_CLASS}`
          ) as HTMLButtonElement;
          attachmentButton.type = "button";
          attachmentButton.setAttribute("aria-label", attachmentsConfig.buttonTooltipText ?? "Attach file");

          // Default to paperclip icon
          const attachIconName = attachmentsConfig.buttonIconName ?? "paperclip";

          attachmentButton.style.fontSize = "18px";
          attachmentButton.style.lineHeight = "1";
          // Appearance (bg / fg / border / radius / hover) is themed from the
          // shared `.persona-attachment-button` CSS rule via the
          // `--persona-button-ghost-*` tokens, and the box from
          // `--persona-composer-control-size` — matching the static
          // createAttachmentControls path, so this runtime-created button
          // restyles identically to the built-in one.

          const attachIconSvg = renderLucideIcon(
            attachIconName,
            COMPOSER_CONTROL_ICON_FALLBACK_PX,
            "currentColor",
            1.5
          );
          if (attachIconSvg) {
            attachmentButton.appendChild(attachIconSvg);
          } else {
            attachmentButton.textContent = "📎";
          }

          attachmentButton.addEventListener("click", (e) => {
            e.preventDefault();
            attachmentInput?.click();
          });

          attachmentButtonWrapper.appendChild(attachmentButton);

          const attachTooltipText = attachmentsConfig.buttonTooltipText ?? "Attach file";
          attachTooltip({
            anchor: attachmentButton,
            trigger: attachmentButtonWrapper,
            text: () =>
              attachmentButton?.getAttribute("aria-label") ?? attachTooltipText,
          });

          // Insert into left actions container (fall back to the form when a
          // custom composer has no left cluster).
          if (leftActions) leftActions.append(attachmentButtonWrapper);
          else composerForm.appendChild(attachmentButtonWrapper);

          if (composerBindings) {
            composerBindings.attachmentButton = attachmentButton;
            composerBindings.attachmentInput = attachmentInput ?? undefined;
            composerBindings.attachmentPreviews =
              attachmentPreviewsContainer ?? undefined;
          }

          // Initialize attachment manager
          if (!attachmentManager && attachmentInput && attachmentPreviewsContainer) {
            attachmentManager = createScopedAttachmentManager();
            attachmentManager.setPreviewsContainer(attachmentPreviewsContainer);

            composerBindings?.addListener(attachmentInput, "change", async () => {
              if (attachmentManager && attachmentInput?.files) {
                await attachmentManager.handleFileSelect(attachmentInput.files);
                attachmentInput.value = "";
              }
            });
          }

        } else {
          // Show existing attachment button and update config
          attachmentButtonWrapper.style.display = "";

          // Update file input accept attribute when config changes
          const attachmentsConfig = config.attachments ?? {};
          if (attachmentInput) {
            attachmentInput.accept = (attachmentsConfig.allowedTypes ?? ALL_SUPPORTED_MIME_TYPES).join(",");
            attachmentInput.multiple = (attachmentsConfig.maxFiles ?? 4) > 1;
          }
        }

        // Rebuild the overlay so live dropOverlay updates restyle it; visibility
        // is owned by the container's drop-active class, so this is drag-safe.
        container.querySelector(".persona-attachment-drop-overlay")?.remove();
        container.appendChild(buildDropOverlay(config.attachments?.dropOverlay));

        // Re-render icon/tooltip so live buttonIconName/buttonTooltipText
        // updates apply; the button element itself is created once and kept.
        if (attachmentButton) {
          const attCfg = config.attachments ?? {};
          const tooltipText = attCfg.buttonTooltipText ?? "Attach file";
          attachmentButton.setAttribute("aria-label", tooltipText);
          attachmentButton.textContent = "";
          const iconSvg = renderLucideIcon(
            attCfg.buttonIconName ?? "paperclip",
            COMPOSER_CONTROL_ICON_FALLBACK_PX,
            "currentColor",
            1.5
          );
          if (iconSvg) attachmentButton.appendChild(iconSvg);
          else attachmentButton.textContent = "📎";
          if (attachmentButtonWrapper) {
            attachTooltip({
              anchor: attachmentButton,
              trigger: attachmentButtonWrapper,
              text: () =>
                attachmentButton?.getAttribute("aria-label") ?? tooltipText,
            });
          }
        }
      } else {
        // Hide attachment button if disabled
        if (attachmentButtonWrapper) {
          attachmentButtonWrapper.style.display = "none";
        }
        // Clear any pending attachments
        if (attachmentManager) {
          attachmentManager.clearAttachments();
        }
        // Remove drop overlay
        container.querySelector(".persona-attachment-drop-overlay")?.remove();
      }

      // Update send button styling
      const tooltipText = config.sendButton?.tooltipText ?? "Send message";
      const showTooltip = config.sendButton?.showTooltip ?? false;
      applySendButtonStyle(sendButton, config);

      if (!session.isStreaming()) {
        sendButton.setAttribute("aria-label", tooltipText);
      }
      syncSendButtonVisibility();
      if (sendButtonWrapper) {
        attachTooltip({
          anchor: sendButton,
          trigger: sendButtonWrapper,
          text: () => sendButton.getAttribute("aria-label") ?? tooltipText,
          enabled: showTooltip,
        });
      }

      const attachmentManagers = new Set([attachmentManager, ...Array.from(conversationContinuity.values(), saved => saved.attachments)]);
      for (const manager of attachmentManagers) {
        manager?.updateConfig(attachmentManagerConfig());
      }
      if ("plugins" in nextConfig) attachmentManager?.remountPreviews(attachmentPreviewsContainer);
      syncComposerLayout();
      // Placement may change through a live option or defaults-version switch.
      applyComposerPlacement();

      // Modes can be added or removed live: drop selections whose mode is gone
      // before anything reads `activeModeIds`, and mount/unmount the mode chips.
      const prunedModes = pruneComposerModes(
        composerStore.getState().activeModeIds,
        config.composer?.modes
      );
      composerStore.setActiveModeIds(prunedModes);
      const wantsModeChips = composerChipModes().length > 0;
      if (wantsModeChips !== Boolean(composerModeChips)) setupComposerModeChips();
      composerModeChips?.render(prunedModes, composerChipModes());
      composerCoreControls?.repaint();

      // Re-resolve the action row LAST of the composer work: `composer.actions`,
      // the plugin list, and the built-in elements above have all settled, so
      // added/removed/changed actions re-render and every `visible`/`disabled`
      // predicate is re-evaluated against the current state.
      if (composerActionRenderer) composerActionRenderer.resolve();
      else setupComposerActions();

      // Update contentMaxWidth on messages wrapper and composer. Same
      // composer-bar fallback as the initial read above.
      const updatedContentMaxWidth = resolveContentMaxWidth(
        config,
        isComposerBar()
      );
      applyContentMaxWidthVar();
      if (updatedContentMaxWidth) {
        messagesWrapper.style.maxWidth = updatedContentMaxWidth;
        messagesWrapper.style.marginLeft = "auto";
        messagesWrapper.style.marginRight = "auto";
        messagesWrapper.style.width = "100%";
        greetingHost.style.maxWidth = updatedContentMaxWidth;
        greetingHost.style.marginLeft = "auto";
        greetingHost.style.marginRight = "auto";
        greetingHost.style.width = "100%";
        // Same four targets, and the same `width: 100%`, as the mount-time
        // `applyComposerContentMaxWidth`: the previews row is a flex item, so
        // auto margins alone shrink-wrap it instead of centering the column.
        for (const element of [
          composerForm,
          suggestions,
          attachmentPreviewsContainer,
          statusText,
        ]) {
          if (!element) continue;
          element.style.maxWidth = updatedContentMaxWidth;
          element.style.marginLeft = "auto";
          element.style.marginRight = "auto";
          element.style.width = "100%";
        }
      } else {
        messagesWrapper.style.maxWidth = "";
        messagesWrapper.style.marginLeft = "";
        messagesWrapper.style.marginRight = "";
        messagesWrapper.style.width = "";
        greetingHost.style.maxWidth = "";
        greetingHost.style.marginLeft = "";
        greetingHost.style.marginRight = "";
        greetingHost.style.width = "";
        for (const element of [
          composerForm,
          suggestions,
          attachmentPreviewsContainer,
          statusText,
        ]) {
          if (!element) continue;
          element.style.maxWidth = "";
          element.style.marginLeft = "";
          element.style.marginRight = "";
          element.style.width = "";
        }
      }

      // Update status indicator visibility and text
      const statusIndicatorConfig = config.statusIndicator ?? {};
      placeStatusIndicator(statusText, composerForm, statusIndicatorConfig);
      applyStatusIndicatorState(statusText, statusIndicatorConfig, session?.getStatus() ?? "idle");

      // Update status text if status is currently set
      if (session) {
        const currentStatus = session.getStatus();
        const getCurrentStatusText = (s: AgentWidgetSessionStatus): string => {
          if (s === "idle") return statusIndicatorConfig.idleText ?? statusCopy.idle;
          if (s === "connecting") return statusIndicatorConfig.connectingText ?? statusCopy.connecting;
          if (s === "connected") return statusIndicatorConfig.connectedText ?? statusCopy.connected;
          if (s === "error") return statusIndicatorConfig.errorText ?? statusCopy.error;
          if (s === "paused") return statusIndicatorConfig.pausedText ?? statusCopy.paused;
          if (s === "resuming") return statusIndicatorConfig.resumingText ?? statusCopy.resuming;
          return statusCopy[s];
        };
        applyStatusToElement(statusText, getCurrentStatusText(currentStatus), statusIndicatorConfig, currentStatus);
      }

      // Update status text alignment
      statusText.classList.remove("persona-text-left", "persona-text-center", "persona-text-right");
      const alignClass = statusIndicatorConfig.align === "left" ? "persona-text-left"
        : statusIndicatorConfig.align === "center" ? "persona-text-center"
        : "persona-text-right";
      statusText.classList.add(alignClass);

      // Composer baseline config is read at build time, so it re-syncs here.
      // After the control updates above, so controls this update created (mic,
      // attachment button) are already mounted when the locks sweep them.
      applyComposerInputConfig();
      applyComposerLock();
      syncComposerCompact();
      // Last: this pass rebuilds the header and rewrites header/footer
      // visibility, any of which would expose an open panel host's chrome.
      reapplyHistoryHostChrome();
      warnInertSlots(config.layout?.slots ?? {});
      if (dictationCapturing() && isComposerInputDisabled()) dismissDictation();
      dictation?.refresh();
      slots.update();
      preparePresentation();
    },
    open() {
      if (!isPanelToggleable()) return;
      setOpenState(true, "api");
    },
    close() {
      if (!isPanelToggleable()) return;
      setOpenState(false, "api");
    },
    toggle() {
      if (!isPanelToggleable()) return;
      setOpenState(!open, "api");
    },
    reconnect() {
      session.reconnectNow();
    },
    clearChat() {
      dismissDictation();
      // Clear messages in session (this will trigger onMessagesChanged which re-renders)
      disposeApprovalRenders();
      artifactsPaneUserHidden = false;
      activityLifecycle?.clear();
      clearRevealedToolCalls();
      toolExpansionState.clear();
      reasoningExpansionState.clear();
      approvalDetailsExpansionState.clear();
      session.clearMessages();
      if (config.layout?.topFade) body.scrollTop = 0;
      body.removeAttribute("data-persona-top-fade");
      messageCache.clear();
      resumeAutoScroll();

      // Always clear the default localStorage key
      try {
        localStorage.removeItem(DEFAULT_CHAT_HISTORY_STORAGE_KEY);
        if (config.debug) {
          console.log(`[AgentWidget] Cleared default localStorage key: ${DEFAULT_CHAT_HISTORY_STORAGE_KEY}`);
        }
      } catch (error) {
        console.error("[AgentWidget] Failed to clear default localStorage:", error);
      }

      // Also clear custom localStorage key if configured
      if (config.clearChatHistoryStorageKey && config.clearChatHistoryStorageKey !== DEFAULT_CHAT_HISTORY_STORAGE_KEY) {
        try {
          localStorage.removeItem(config.clearChatHistoryStorageKey);
          if (config.debug) {
            console.log(`[AgentWidget] Cleared custom localStorage key: ${config.clearChatHistoryStorageKey}`);
          }
        } catch (error) {
          console.error("[AgentWidget] Failed to clear custom localStorage:", error);
        }
      }

      // Dispatch custom event for external handlers (e.g., localStorage clearing in examples)
      const clearEvent = new CustomEvent("persona:clear-chat", {
        detail: { instanceId, timestamp: new Date().toISOString() }
      });
      window.dispatchEvent(clearEvent);

      // Remove any stored draft before the terminal clear mutation is queued.
      clearPersistedDraft();
      if (storageAdapter?.clear) {
        runStorageMutation(
          () => storageAdapter.clear!(),
          "[AgentWidget] Failed to clear storage adapter:"
        );
      }
      persistentMetadata = {};
      actionManager.syncFromMetadata();
      // Pending attachments belong to the cleared conversation: drop the tiles
      // and abort anything still uploading.
      discardConversationContinuity(session.getActiveConversationId(), true, false);
      clearComposerDraft();
      // The draft, quote, and any deferred submission belong to it too.
      clearPersistedDraft();
      setPendingSubmission(undefined);
      setComposerQuote(undefined);
      cancelMessageEdit();

      // Clear event stream buffer and store, and reset throughput tracking
      eventStreamBuffer?.clear();
      throughputTracker?.reset();
      eventStreamView?.update();
    },
    setMessage(message: string): boolean {
      if (!textarea || dictationCapturing()) return false;
      if (session.isStreaming()) return false;
      if (isComposerInputDisabled()) return false;

      // Auto-open widget if closed and the panel is toggleable
      if (!open && isPanelToggleable()) {
        setOpenState(true, "system");
      }

      textarea.value = message;
      // Trigger input event for any listeners
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
      return true;
    },
    submitMessage(message?: string): boolean {
      if (session.isStreaming() || dictationCapturing()) return false;
      // Every submission path is blocked, including the programmatic one.
      if (isComposerSendBlocked()) return false;

      const valueToSubmit = message?.trim() || textarea.value.trim();
      if (!valueToSubmit) return false;

      // Auto-open widget if closed and the panel is toggleable
      if (!open && isPanelToggleable()) {
        setOpenState(true, "system");
      }

      // Composer submission (no explicit text) runs the full snapshot pipeline;
      // an explicit message is a programmatic send that still consumes the draft.
      textarea.value = "";
      textarea.style.height = "auto"; // Reset height after clearing
      syncComposerText();
      submitText(valueToSubmit, { consumeDraft: false });
      return true;
    },
    getComposerState(): Readonly<ComposerState> {
      return composerStore.getState();
    },
    setComposerModes(ids: readonly string[]): void {
      setActiveComposerModes(selectComposerModes(ids, composerModes(), config.composer?.modeGroups));
    },
    setQuote(quote: ComposerQuote): void {
      const text = quote?.text?.trim();
      if (!text) return;
      setComposerQuote({ ...quote, text });
    },
    clearQuote(): void {
      setComposerQuote(undefined);
    },
    startDictation,
    finishDictation: () => finishDictation(),
    cancelDictation: () => { const loading = dictationLoading; return dismissDictation() || loading; },
    sendDictation: () => {
      if (!config.voiceRecognition?.dictation?.showSend || isComposerSendBlocked()) return false;
      return finishDictation(true);
    },
    getDictationState: () => dictationLoadFailure ?? dictation?.getState() ?? idleDictation,
    startVoiceRecognition(): boolean {
      if (session.isStreaming() || dictationCapturing()) return false;
      if (usesDedicatedDictation() && !config.voiceRecognition?.dictation?.provider && config.voiceRecognition?.provider?.type !== "runtype") return startDictation();
      if (usesSessionVoice(config.voiceRecognition?.provider)) {
        if (session.isVoiceActive() || session.isBargeInActive()) return true;
        if (!open && isPanelToggleable()) setOpenState(true, "system");
        voiceState.manuallyDeactivated = false;
        persistVoiceMetadata();
        session.toggleVoice().then(() => {
          voiceState.active = isSessionVoiceLive();
          persistVoiceMetadata();
          emitVoiceState("user");
          if (voiceState.active) applyRuntypeMicRecordingStyles();
        });
        return true;
      }
      if (isRecording) return true;
      const SpeechRecognitionClass = getSpeechRecognitionClass();
      if (!SpeechRecognitionClass) return false;
      if (!open && isPanelToggleable()) setOpenState(true, "system");
      voiceState.manuallyDeactivated = false;
      persistVoiceMetadata();
      startVoiceRecognition("user");
      return true;
    },
    stopVoiceRecognition(): boolean {
      if (dictationCapturing()) return finishDictation();
      if (usesSessionVoice(config.voiceRecognition?.provider)) {
        if (!session.isVoiceActive() && !session.isBargeInActive()) return false;
        const stop = session.isBargeInActive()
          ? session.deactivateBargeIn()
          : session.toggleVoice();
        stop.then(() => {
          voiceState.active = false;
          voiceState.manuallyDeactivated = true;
          persistVoiceMetadata();
          emitVoiceState("user");
          removeRuntypeMicStateStyles();
        });
        return true;
      }
      if (!isRecording) return false;

      voiceState.manuallyDeactivated = true;
      persistVoiceMetadata();
      stopVoiceRecognition("user");
      return true;
    },
    injectMessage(options: InjectMessageOptions): AgentWidgetMessage {
      // Auto-open widget if closed and the panel is toggleable
      if (!open && isPanelToggleable()) {
        setOpenState(true, "system");
      }
      return session.injectMessage(options);
    },
    injectAssistantMessage(options: InjectAssistantMessageOptions): AgentWidgetMessage {
      // Auto-open widget if closed and the panel is toggleable
      if (!open && isPanelToggleable()) {
        setOpenState(true, "system");
      }
      const result = session.injectAssistantMessage(options);

      // Check if we should trigger resubmit after injection
      // This handles the case where a handler returned resubmit: true and then
      // injected a message - we wait until after injection to trigger resubmit
      if (pendingResubmit) {
        pendingResubmit = false;
        if (pendingResubmitTimeout) {
          clearTimeout(pendingResubmitTimeout);
          pendingResubmitTimeout = null;
        }
        // Short delay to ensure message is in context
        scheduleResubmit();
      }

      return result;
    },
    injectUserMessage(options: InjectUserMessageOptions): AgentWidgetMessage {
      // Auto-open widget if closed and the panel is toggleable
      if (!open && isPanelToggleable()) {
        setOpenState(true, "system");
      }
      return session.injectUserMessage(options);
    },
    injectSystemMessage(options: InjectSystemMessageOptions): AgentWidgetMessage {
      // Auto-open widget if closed and the panel is toggleable
      if (!open && isPanelToggleable()) {
        setOpenState(true, "system");
      }
      return session.injectSystemMessage(options);
    },
    injectMessageBatch(optionsList: InjectMessageOptions[]): AgentWidgetMessage[] {
      if (!open && isPanelToggleable()) {
        setOpenState(true, "system");
      }
      return session.injectMessageBatch(optionsList);
    },
    injectComponentDirective(
      options: InjectComponentDirectiveOptions
    ): AgentWidgetMessage {
      // Auto-open widget if closed and the panel is toggleable
      if (!open && isPanelToggleable()) {
        setOpenState(true, "system");
      }
      return session.injectComponentDirective(options);
    },
    updateComponentProps(messageId, props) {
      return session.updateComponentProps(messageId, props);
    },
    removeComponentMessage(messageId) {
      return session.removeComponentMessage(messageId);
    },
    /** @deprecated Use injectMessage() instead */
    injectTestMessage(event: AgentWidgetEvent) {
      // Auto-open widget if closed and the panel is toggleable
      if (!open && isPanelToggleable()) {
        setOpenState(true, "system");
      }
      session.injectTestEvent(event);
    },
    async connectStream(
      stream: ReadableStream<Uint8Array>,
      options?: { assistantMessageId?: string }
    ): Promise<void> {
      return session.connectStream(stream, options);
    },
    /** Push a raw event into the event stream buffer (for testing/debugging) */
    __pushEventStreamEvent(event: { type: string; payload: unknown }): void {
      if (!showEventStreamToggle) return;
      ensureEventStreamRuntime();
      feedEventStream(event.type, event.payload);
    },
    showEventStream(): void {
      if (!showEventStreamToggle) return;
      toggleEventStreamOn();
    },
    hideEventStream(): void {
      if (!eventStreamVisible) return;
      toggleEventStreamOff();
    },
    isEventStreamVisible(): boolean {
      return eventStreamVisible;
    },
    listConversations(opts) {
      return session.listConversations(opts);
    },
    getActiveConversationId(): string | null {
      return session.getActiveConversationId();
    },
    openConversation(
      conversationId: string,
      opts?: AgentWidgetOpenConversationOptions
    ): Promise<void> {
      return withHistoryShell((shell) =>
        shell.openHistoryConversation(conversationId, {
          focusComposer: opts?.focus === true,
          busy: opts?.busy,
        })
      );
    },
    startNewConversation(opts?: { focus?: boolean }): Promise<void> {
      return withHistoryShell((shell) =>
        shell.startNewConversation({ focusComposer: opts?.focus === true })
      );
    },
    warmSession(): void {
      warmSession();
    },
    deleteConversation(conversationId: string): Promise<void> {
      return withHistoryShell((shell) =>
        shell.deleteHistoryConversation(conversationId)
      );
    },
    renameConversation(conversationId, title) {
      return withHistoryShell((shell) =>
        shell.updateHistoryConversation(conversationId, { title })
      );
    },
    setConversationStarred(conversationId, starred) {
      return withHistoryShell((shell) =>
        shell.updateHistoryConversation(conversationId, { starred })
      );
    },
    clearConversationHistory(opts) {
      return withHistoryShell((shell) => shell.clearConversationHistory(opts));
    },
    resetHistoryIdentity(): Promise<{ remoteRevocationConfirmed: boolean }> {
      return withHistoryShell((shell) => shell.resetHistoryIdentity());
    },
    async getVisitorToken(): Promise<string | null> {
      const store = visitorStore;
      if (!store) return null;
      const token = await store.get();
      return store === visitorStore ? token : null;
    },
    getHistoryIdentityStatus(): HistoryIdentityStatus {
      const historyProvider = historyShell?.historyProvider;
      if (!historyProvider) {
        return historyFeatureEnabled()
          ? { state: "unavailable", reason: "ineligible_mode" }
          : { state: "unavailable", reason: "history_disabled" };
      }
      return historyProvider.getIdentityStatus();
    },
    showHistory(opts): Promise<void> {
      // Disabled history never loads the shell just to report unavailable.
      if (!historyFeatureEnabled()) return Promise.resolve();
      return withHistoryShell((shell) =>
        shell.openHistory({
          ...(opts?.returnSurface ? { returnSurface: opts.returnSurface } : {}),
          ...(opts?.focus !== undefined ? { focus: opts.focus } : {}),
        })
      );
    },
    hideHistory(opts): void {
      historyShell?.closeHistory(
        opts?.restoreFocus !== undefined
          ? { restoreFocus: opts.restoreFocus }
          : undefined
      );
    },
    isHistoryVisible(): boolean {
      return historyVisible;
    },
    showArtifacts(): void {
      if (!artifactsSidebarEnabled(config)) return;
      artifactsPaneUserHidden = false;
      artifactsPaneUserOpened = true;
      syncArtifactPane();
      artifactPaneApi?.setMobileOpen(true);
    },
    hideArtifacts(): void {
      if (!artifactsSidebarEnabled(config)) return;
      artifactsPaneUserHidden = true;
      syncArtifactPane();
    },
    upsertArtifact(manual: PersonaArtifactManualUpsert): PersonaArtifactRecord | null {
      if (!artifactsSidebarEnabled(config)) return null;
      // Programmatic upserts match the streamed UX: only "panel"-mode
      // artifacts auto-open the pane (overriding a previous Close), while
      // "collapsed"/"inline" stay calm — the injected transcript block is the
      // affordance. Independent of `transcript: false`: pane-only callers
      // (e.g. the theme editor preview) rely on the auto-open default
      // surfacing; callers that want the pane in another mode call
      // showArtifacts().
      const mode = resolveArtifactDisplayMode(
        config.features?.artifacts,
        {
          artifactType: manual.artifactType,
          ...(manual.artifactType === "markdown" && manual.file
            ? { file: manual.file }
            : {}),
          ...(manual.presentation ? { presentation: manual.presentation } : {}),
        }
      );
      if (mode === "panel") {
        artifactsPaneUserHidden = false;
        artifactsPaneUserOpened = true;
      }
      return session.upsertArtifact(manual);
    },
    selectArtifact(id: string): void {
      if (!artifactsSidebarEnabled(config)) return;
      session.selectArtifact(id);
    },
    clearArtifacts(): void {
      if (!artifactsSidebarEnabled(config)) return;
      session.clearArtifacts();
    },
    getArtifacts(): PersonaArtifactRecord[] {
      return session?.getArtifacts() ?? [];
    },
    getSelectedArtifactId(): string | null {
      return session?.getSelectedArtifactId() ?? null;
    },
    focusInput(): boolean {
      // Composer-bar's textarea is always reachable in the collapsed pill,
      // so don't gate focus behind `open` for that mode.
      if (launcherEnabled && !open && !isComposerBar()) return false;
      if (!textarea) return false;
      textarea.focus();
      return true;
    },
    revealToolCall(toolCallId: string, options: AgentWidgetRevealToolCallOptions = {}): boolean {
      const bubble = Array.from(body.querySelectorAll<HTMLElement>(".persona-tool-bubble[data-tool-call-id]"))
        .find((el) => el.dataset.toolCallId === toolCallId);
      if (!bubble) return false;
      const ownHeader = (owner: HTMLElement) =>
        Array.from(owner.querySelectorAll<HTMLElement>('button[data-expand-header="true"]'))
          .find((header) => header.closest(".persona-tool-bubble") === owner);
      const openHeader = (header: HTMLElement | undefined) => {
        if (header?.getAttribute("aria-expanded") === "false") toggleBubbleHeader(header);
      };
      const groups: HTMLElement[] = [];
      for (let group = bubble.parentElement?.closest<HTMLElement>(".persona-tool-bubble"); group;
        group = group.parentElement?.closest<HTMLElement>(".persona-tool-bubble")) groups.unshift(group);
      groups.forEach((group) => openHeader(ownHeader(group)));
      const header = ownHeader(bubble);
      if (options.expand !== false) openHeader(header);

      markReaderEngaged();
      // A prior jump owns a separate animation whose completion resumes follow.
      latestScroll.cancel();
      pauseAutoScroll();
      const revealGeneration = revealScrollGeneration;
      const anchor = header ?? bubble;
      const shouldContinue = () => !destroyed && revealGeneration === revealScrollGeneration && body.contains(anchor);
      const reduceMotion = typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const targetTop = () => {
        const raw = offsetTopWithinBody(anchor) - (body.clientHeight - anchor.offsetHeight) / 2;
        return Math.max(0, Math.min(raw, getScrollBottomOffset(body)));
      };
      const center = () => {
        if (!shouldContinue()) return;
        // Use the same programmatic-scroll tracking as automatic following so
        // centering near the bottom cannot be mistaken for reader intent.
        if (reduceMotion) {
          cancelSmoothScroll();
          isAutoScrolling = true;
          body.scrollTop = targetTop();
          lastScrollTop = body.scrollTop;
          isAutoScrolling = false;
        } else animateScrollTo(body, targetTop, REVEAL_SETTLE_MS, shouldContinue);
      };
      window.clearTimeout(revealCenterTimer);
      center();
      // Disclosure height transitions move the row after the first pass; center again once they settle.
      revealCenterTimer = window.setTimeout(() => {
        revealCenterTimer = undefined;
        center();
      }, REVEAL_SETTLE_MS);

      const highlightMs = options.highlightMs ?? 2000;
      if (highlightMs > 0) {
        const messageId = bubble.dataset.messageId!;
        window.clearTimeout(revealedToolCalls.get(messageId));
        revealedToolCalls.set(messageId, window.setTimeout(() => {
          revealedToolCalls.delete(messageId);
          refreshRevealedToolCalls();
        }, highlightMs));
        bubble.setAttribute("data-persona-revealed", "");
      }
      return true;
    },
    async resolveApproval(
      approvalId: string,
      decision: 'approved' | 'denied',
      options?: AgentWidgetApprovalDecisionOptions
    ): Promise<void> {
      const messages = session.getMessages();
      const approvalMessage = messages.find(
        m => m.variant === "approval" && m.approval?.id === approvalId
      );
      if (!approvalMessage?.approval) {
        throw new Error(`Approval not found: ${approvalId}`);
      }
      // Mirror the in-panel click handler: WebMCP gate bubbles resolve a local
      // Promise the bridge is parked on (no server round-trip and they carry an
      // empty executionId/agentId), so they must NOT hit the server approval
      // API. Server-backed approvals retain their execution id and use the API.
      if (isBrowserLocalApproval(approvalMessage.approval)) {
        session.resolveWebMcpApproval(approvalMessage.id, decision);
        return;
      }
      return session.resolveApproval(approvalMessage.approval, decision, options);
    },
    /**
     * Show follow-up suggestions from host code. Ephemeral UI state only: the
     * items never enter the transcript, the wire payload, or persistence, so
     * they do not survive a refresh. Cleared when the next user message
     * appends. Latest writer wins against agent `suggest_replies` payloads.
     * Renders through the `suggestions.followUps` presentation config and the
     * full plugin and DOM-event pipeline with source `"host"`.
     */
    setFollowUpSuggestions(items: AgentWidgetSuggestion[]): void {
      setHostFollowUps(items);
    },
    /** Remove host-set follow-ups. Agent-set chips are untouched. */
    clearFollowUpSuggestions(): void {
      clearHostFollowUps();
    },
    getMessages() {
      return session.getMessages();
    },
    getStatus() {
      return session.getStatus();
    },
    getPersistentMetadata() {
      return { ...persistentMetadata };
    },
    updatePersistentMetadata(
      updater: (prev: Record<string, unknown>) => Record<string, unknown>
    ) {
      updateSessionMetadata(updater);
    },
    on(event, handler) {
      return eventBus.on(event, handler);
    },
    off(event, handler) {
      eventBus.off(event, handler);
    },
    // State query methods
    isOpen(): boolean {
      return isPanelToggleable() && open;
    },
    isVoiceActive(): boolean {
      return voiceState.active;
    },
    /**
     * Toggle "Read aloud" for an assistant message: play → pause → resume (or
     * play → stop when the engine can't pause). Speaks via the configured
     * speech engine (browser Web Speech API by default).
     */
    toggleReadAloud(messageId: string): void {
      session.toggleReadAloud(messageId);
    },
    /** Stop any in-progress read-aloud / text-to-speech playback. */
    stopReadAloud(): void {
      session.stopSpeaking();
    },
    /** Current read-aloud playback state for a message (`idle` unless active). */
    getReadAloudState(messageId: string): ReadAloudState {
      return session.getReadAloudState(messageId);
    },
    /** Subscribe to read-aloud state changes. Returns an unsubscribe function. */
    onReadAloudChange(
      listener: (activeId: string | null, state: ReadAloudState) => void
    ): () => void {
      return session.onReadAloudChange(listener);
    },
    getState(): AgentWidgetStateSnapshot {
      return {
        open: isPanelToggleable() && open,
        launcherEnabled,
        voiceActive: voiceState.active,
        streaming: session.isStreaming()
      };
    },
    // Feedback methods (CSAT/NPS)
    showCSATFeedback(options?: Partial<CSATFeedbackOptions>) {
      showFeedback(() => createCSATFeedback({
        onSubmit: async (rating, comment) => {
          if (session.isClientTokenMode()) {
            await session.submitCSATFeedback(rating, comment);
          }
          options?.onSubmit?.(rating, comment);
        },
        onDismiss: options?.onDismiss,
        ...options,
      }));
    },
    showNPSFeedback(options?: Partial<NPSFeedbackOptions>) {
      showFeedback(() => createNPSFeedback({
        onSubmit: async (rating, comment) => {
          if (session.isClientTokenMode()) {
            await session.submitNPSFeedback(rating, comment);
          }
          options?.onSubmit?.(rating, comment);
        },
        onDismiss: options?.onDismiss,
        ...options,
      }));
    },
    async submitCSATFeedback(rating: number, comment?: string): Promise<void> {
      return session.submitCSATFeedback(rating, comment);
    },
    async submitNPSFeedback(rating: number, comment?: string): Promise<void> {
      return session.submitNPSFeedback(rating, comment);
    },
    destroy() {
      destroyed = true;
      invalidatePendingRestarts();
      renderReadiness.destroy();
      slots.destroy();
      mount.removeEventListener("click", routeLinkClick);
      // Commit the latest coalesced transcript while the live DOM and storage
      // pipeline still exist, then let the normal teardown cancel any work
      // scheduled by that final apply.
      flushPendingMessages();
      // Cancel session work before the final effect/timer cleanup: cancellation
      // can render a terminal activity state.
      destroyCallbacks.forEach((cb) => cb());
      if (toolElapsedTimerId != null) {
        clearInterval(toolElapsedTimerId);
        toolElapsedTimerId = null;
      }
      activityLifecycle?.destroy();
      loadingLabelLifecycle?.destroy();
      clearRevealedToolCalls();
      toolExpansionState.clear();
      reasoningExpansionState.clear();
      approvalDetailsExpansionState.clear();
      eventBus.dispose();
      wrapper.remove();
      pillRoot?.remove();
      launcherSurfaceInstance?.destroy();
      customLauncherElement?.remove();
      if (closeHandler) {
        closeButton.removeEventListener("click", closeHandler);
      }
    }
  };

  const shouldExposeDebugApi =
    (runtimeOptions?.debugTools ?? false) || Boolean(config.debug);

  if (shouldExposeDebugApi && typeof window !== "undefined") {
    const previousDebug = (window as any).AgentWidgetBrowser;
    const debugApi = {
      controller,
      getMessages: controller.getMessages,
      getStatus: controller.getStatus,
      getMetadata: controller.getPersistentMetadata,
      updateMetadata: controller.updatePersistentMetadata,
      clearHistory: () => controller.clearChat(),
      setVoiceActive: (active: boolean) =>
        active
          ? controller.startVoiceRecognition()
          : controller.stopVoiceRecognition()
    };
    (window as any).AgentWidgetBrowser = debugApi;
    destroyCallbacks.push(() => {
      if ((window as any).AgentWidgetBrowser === debugApi) {
        (window as any).AgentWidgetBrowser = previousDebug;
      }
    });
  }

  // ============================================================================
  // INSTANCE-SCOPED WINDOW EVENTS FOR PROGRAMMATIC CONTROL
  // ============================================================================
  if (typeof window !== "undefined") {
    type CommandDetail = { instanceId?: string; artifact?: PersonaArtifactManualUpsert; id?: string };
    const bindCommand = (name: string, run: (detail?: CommandDetail) => unknown) => {
      const listener = (event: Event) => {
        const detail = (event as CustomEvent<CommandDetail>).detail;
        if (!detail?.instanceId || detail.instanceId === instanceId) run(detail);
      };
      window.addEventListener(name, listener);
      destroyCallbacks.push(() => window.removeEventListener(name, listener));
    };
    bindCommand("persona:focusInput", () => controller.focusInput());
    if (showEventStreamToggle) {
      bindCommand("persona:showEventStream", () => controller.showEventStream());
      bindCommand("persona:hideEventStream", () => controller.hideEventStream());
    }
    // Show/hide only. Identity status stays on the instance-scoped controller
    // bus so authentication state is never broadcast page-wide.
    bindCommand("persona:showHistory", () => controller.showHistory());
    bindCommand("persona:hideHistory", () => controller.hideHistory());
    bindCommand("persona:showArtifacts", () => controller.showArtifacts());
    bindCommand("persona:hideArtifacts", () => controller.hideArtifacts());
    bindCommand("persona:upsertArtifact", (detail) => {
      if (detail?.artifact) controller.upsertArtifact(detail.artifact);
    });
    bindCommand("persona:selectArtifact", (detail) => {
      if (typeof detail?.id === "string") controller.selectArtifact(detail.id);
    });
    bindCommand("persona:clearArtifacts", () => controller.clearArtifacts());
  }

  // ============================================================================
  // STATE PERSISTENCE ACROSS PAGE NAVIGATIONS
  // ============================================================================
  const persistConfig = normalizePersistStateConfig(config.persistState);

  if (persistConfig && isPanelToggleable()) {
    const storage = getPersistStorage(persistConfig.storage!);
    const openKey = `${persistConfig.keyPrefix}widget-open`;
    const voiceKey = `${persistConfig.keyPrefix}widget-voice`;
    const voiceModeKey = `${persistConfig.keyPrefix}widget-voice-mode`;

    if (storage) {
      // Restore state from previous page
      const wasOpen = persistConfig.persist?.openState && storage.getItem(openKey) === 'true';
      const wasVoiceActive = persistConfig.persist?.voiceState && storage.getItem(voiceKey) === 'true';
      // Also check if user was in voice mode (last message was via voice)
      const wasInVoiceMode = persistConfig.persist?.voiceState && storage.getItem(voiceModeKey) === 'true';

      if (wasOpen) {
        // The teaser's own timer is already queued, so suppress it synchronously
        // here: the deferred open below would otherwise run one task too late.
        launcherSurfaceInstance?.setPanelOpen(true, "auto");
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          controller.open();

          // After opening, restore input mode
          setTimeout(() => {
            // Restore voice if it was actively recording OR if user was in voice mode
            if (wasVoiceActive || wasInVoiceMode) {
              controller.startVoiceRecognition();
            } else if (persistConfig.persist?.focusInput) {
              const textarea = mount.querySelector('textarea') as HTMLTextAreaElement | null;
              if (textarea) {
                textarea.focus();
              }
            }
          }, 100);
        }, 0);
      }

      // Persist open/close state changes
      if (persistConfig.persist?.openState) {
        eventBus.on('widget:opened', () => {
          storage.setItem(openKey, 'true');
        });
        eventBus.on('widget:closed', () => {
          storage.setItem(openKey, 'false');
        });
      }

      // Persist voice state changes
      if (persistConfig.persist?.voiceState) {
        eventBus.on('voice:state', (event) => {
          storage.setItem(voiceKey, event.active ? 'true' : 'false');
        });

        // Persist whether user is in voice mode based on their messages
        // This allows voice to resume after navigation even when recording was stopped for submission
        eventBus.on('user:message', (message) => {
          storage.setItem(voiceModeKey, message.viaVoice ? 'true' : 'false');
        });
      }

      // Clear persisted state on chat clear
      if (persistConfig.clearOnChatClear) {
        const clearPersistState = () => {
          storage.removeItem(openKey);
          storage.removeItem(voiceKey);
          storage.removeItem(voiceModeKey);
        };

        // Listen for clear chat event
        const handleClearChat = (event: Event) => {
          const detail = (event as CustomEvent).detail;
          if (detail?.instanceId === instanceId) clearPersistState();
        };
        window.addEventListener('persona:clear-chat', handleClearChat);

        // Clean up listener on destroy
        destroyCallbacks.push(() => {
          window.removeEventListener('persona:clear-chat', handleClearChat);
        });
      }
    }
  }

  // If onStateLoaded signalled open: true, open the panel after init.
  // Mirrors the same setTimeout(0) pattern used by persistState restore so both
  // can fire independently without interfering with each other.
  if (shouldOpenAfterStateLoaded && isPanelToggleable()) {
    // Same one-task-early suppression as the persistState restore above.
    launcherSurfaceInstance?.setPanelOpen(true, "auto");
    setTimeout(() => { controller.open(); }, 0);
  }

  // Initial sync of the composer-bar peek banner so it reflects any
  // restored history. Subsequent updates flow through `onMessagesChanged`,
  // `onStreamingChanged`, `updateOpenState`, and pointerenter/leave on
  // the panel.
  syncComposerBarPeek();

  // Approval rows rendered while the approval-ui chunk was in flight are empty
  // stubs; once it lands, rebuild them into real cards. (`approvalUiDisposed` guards a post-destroy resolution.)
  onApprovalUiReady = () => {
    if (!session) return;
    configVersion++;
    messageCache.clear();
    renderMessagesWithPlugins(messagesWrapper, session.getMessages(), postprocess);
  };

  // Seeded/restored transcripts may send immediately after reveal.
  if (session.getMessages().length > 0) warmClientStream();

  // Form placeholders rendered while the forms-ui chunk was in flight are the
  // bare divs the postprocessor emitted; once the chunk lands, rebuild them
  // into real forms. (`formsUiDisposed`
  // guards a post-destroy resolution.)
  onFormsUiReady = onApprovalUiReady;

  // Artifact directives rendered while the artifacts-ui chunk was in flight
  // are empty placeholders; once it's adopted, rebuild them into real
  // cards/inline blocks and sync the pane. Same heal shape as above.
  const unsubscribeArtifactsUi = onArtifactsUiReady(() => {
    onApprovalUiReady?.();
    getArtifactsUiSync()?.updateInlineArtifactBlocks(
      messagesWrapper,
      lastArtifactsState.artifacts,
      { suppressTransition: true }
    );
    syncArtifactPane();
  });
  destroyCallbacks.push(unsubscribeArtifactsUi);

  const preparePresentation = () => {
    const pending = prepareRendererModules(config);
    if (!presentationPrepared) pending.push(...(runtimeOptions?.preparation ?? []));
    if (historyFeatureEnabled() && !historyShell) pending.push(ensureHistoryShell());
    if (!pending.length && !mount.hasAttribute("data-persona-preparing")) {
      presentationPrepared = true;
      return;
    }
    renderReadiness.prepare(pending, () => {
      if (!composerActionRenderer) setupComposerActions();
      if (!composerModeChips) setupComposerModeChips();
      ensureActivityUi();
      getComposerActionsSync()?.ensureComposerActionStyles(mount);
      getArtifactsUiSync()?.ensureArtifactStyles(mount);
      onApprovalUiReady?.();
      presentationPrepared = true;
    });
  };
  preparePresentation();
  return controller;
};

export type AgentWidgetController = Controller;
